/*!
 * ===========================================================================
 * order-form-v2.js  (Order Forms v2 shared base)
 * ===========================================================================
 *
 * The single shared JavaScript module behind every v2 order form (SSL, S/MIME,
 * CaaS, additional-SAN). It REPLACES the ~4,800-line inline <script> that each
 * v1 form carried, and it REMOVES the ~250 KB of browser crypto (node-forge +
 * jsrsasign) : all CSR-aware work now goes to the middleware `/api/csr/validate`
 * endpoint, all Public-Suffix-List work to `/api/domain/*`, the country list to
 * `/api/reference/countries`, and SKU parsing to `/api/sku/:sku`.
 *
 * MODULES IN THIS FILE
 * --------------------
 * constants, ApiClient (fetch + retry + backoff), FormValidator (the single submit
 * gate : required-field contract + primary-input / per-SAN / DCV checks),
 * DomainNormalizer, CsrValidator, SubscriptionChecker (the CaaS-extension
 * eligibility check), DcvManager, SanBuilder, SkuParser, UI helpers, and
 * the bootstrap + per-form wiring (wireForm). Everything the form requires / validates
 * / offers is driven by GET /api/sku ; the form makes no product decisions.
 * The bootstrap orchestration (the per-form wiring) has landed : bootstrap() finds every
 * `.order-form` and wires it (wireOneForm -> wireForm), driven entirely by GET /api/sku.
 *
 * MODULE FORMAT (why the UMD-lite wrapper at the bottom)
 * -----------------------------------------------------
 * Shopify serves this as a classic `<script defer>` (see
 * snippets/order-form-v2-config.liquid). In the browser it self-bootstraps. Under
 * Jest (CommonJS) it must be importable WITHOUT touching the DOM, so the wrapper
 * exports the module surface when `module.exports` exists and only bootstraps in
 * a real browser. Defining the factory never touches the DOM ; only bootstrap()
 * does, and bootstrap() is called only on the browser branch.
 *
 * LOGGING STYLE
 * -------------
 * Log / error MESSAGE text is UPPERCASE with a space before colons, per the
 * Trustico company-wide logging convention. Info-level logs are gated behind
 * `window.ORDER_FORM_DEBUG` (mirrors the v1 `window.SSL_DEBUG` gate) ; warnings
 * and errors always emit.
 *
 * BROWSER BASELINE (PR A round-12 note)
 * -------------------------------------
 * This file is written in ES5 syntax (var + function expressions) but uses a few
 * ES2015 built-ins WITHOUT feature-detection : Object.assign, Object.freeze,
 * Number.isFinite, Promise, and URLSearchParams (the pre-fill link). That is intentional : the Trade theme's own
 * assets already require a modern-browser baseline (its assets/*.js use ES6
 * `class`, arrow functions, and optional chaining `?.`), so IE11 is dropped and
 * these built-ins are always present. AbortController IS feature-detected (it is
 * newer and only used for the per-attempt timeout). If the theme's supported-
 * browser matrix ever regressed to include IE11, these built-ins would need
 * polyfilling -- but the theme baseline rules that out.
 * ===========================================================================
 */
(function (root, factory) {
  'use strict';

  // Build the module surface. This step is DOM-free and side-effect-free so it
  // is safe to run under Jest / Node as well as in the browser. NOTE (PR A
  // round-12 note) : factory(root) runs UNCONDITIONALLY, before the
  // double-injection guard below. So if the asset is injected twice, the second
  // execution builds a SECOND module instance and then discards it via the
  // `if (!root.OrderForm)` guard (keeping the first). The throwaway construction
  // is cheap (it only defines functions, touches no DOM) and the per-form marker
  // in bootstrap() prevents double-wiring ; building once and guarding the
  // assignment is the simplest correct shape. (The larger single-global config
  // divergence on a hypothetical multi-form page is the Phase-3 assembler's
  // concern, documented in snippets/order-form-v2-config.liquid.)
  var OrderForm = factory(root);

  if (typeof module !== 'undefined' && module.exports) {
    // CommonJS (Jest / Node) : expose the modules for unit tests. Do NOT
    // bootstrap : there is no real form to wire and we must not assume a DOM.
    module.exports = OrderForm;
  } else {
    // Browser. Guard against double-injection : if a page renders this asset
    // more than once (two order forms -> two identical <script> tags, both of
    // which the browser executes), keep the FIRST module instance rather than
    // clobbering it, and let its idempotent bootstrap wire any forms. The
    // per-form marker inside bootstrap() prevents any form being wired twice
    // even if both instances reach this branch (PR A review item 2).
    if (!root.OrderForm) {
      root.OrderForm = OrderForm;
    }
    root.OrderForm._bootstrap(root);
  }
})(typeof window !== 'undefined' ? window : this, function (root) {
  'use strict';

  /* =========================================================================
   * Logging helpers (UPPERCASE message text, space-before-colon house style)
   * ========================================================================= */

  // Info logging is opt-in (window.ORDER_FORM_DEBUG truthy), mirroring the v1
  // `window.SSL_DEBUG` gate so production consoles stay quiet ; warn/error are
  // always emitted because they signal real misconfiguration / failure.
  var log = {
    info: function () {
      if (root && root.ORDER_FORM_DEBUG && typeof console !== 'undefined' && console.info) {
        console.info.apply(console, arguments);
      }
    },
    warn: function () {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn.apply(console, arguments);
      }
    },
    error: function () {
      if (typeof console !== 'undefined' && console.error) {
        console.error.apply(console, arguments);
      }
    }
  };

  /* =========================================================================
   * Constants (the cutover-critical vocabulary, frozen so it cannot drift)
   * ========================================================================= */

  // APPROVER_PREFIXES (the five DCV approver-email local-part prefixes) was REMOVED (2026-06-28) : it existed ONLY to
  // build the calculated approver set client-side (the prefixes x PSL-levels pairing in generateApproverEmails). That
  // set now arrives FINISHED from the middleware (GET /api/dcv/approver-emails-calculated), so the form holds no prefix
  // list and constructs nothing -- the middleware owns the calculation (prefixes included).

  /**
   * Canonical v2 DCV method vocabulary used in the form's ORDER DATA.
   *
   * The v2 middleware advertises this 5-symbol set via `/api/sku/:sku` (facts.dcvMethods), which is the ONLY
   * source the form reads, and it renders that array VERBATIM (server order, no form filtering or wildcard
   * exclusion -- owner 2026-06-29). These symbols are used only for BEHAVIOUR (EMAIL needs an approver ;
   * applyDcvPanels shows the matching detail panel), never to filter or order the server's list. Two owner
   * decisions (PR A) pin how the form uses it :
   *   - The form standardises its order-data token on DNS_CNAME for CNAME DCV
   *     (the v1 form wrote the literal 'DNS' ; the middleware webhook maps both,
   *     keeping 'DNS' for in-flight v1 orders, aligned middleware-side in PR C).
   *   - DNS_TXT is a NORMAL method here, gated ENTIRELY by the server : it appears in
   *     the picker only when GET /api/sku's facts.dcvMethods includes it, and not
   *     otherwise, exactly like every other method (owner 2026-06-28 : the form makes
   *     NO per-method decision -- the SKU parser decides which methods a product offers).
   *     The old form-side "always drop DNS_TXT" filter was REMOVED in that change. As of
   *     2026-07-05 (middleware-01 PR #172 : DNS TXT random-value DCV) the parser advertises
   *     DNS_TXT on every SSL Traditional SKU, WILDCARDS INCLUDED (DNS TXT validates
   *     wildcards, so it is NOT subject to the SC45 HTTP/HTTPS wildcard strip) ; the
   *     customer-facing DNS_TXT option label (dcv.method.label.dns_txt) + detail panel
   *     (data-order-form-dcv-details="dnstxt") now exist -- buildMethodSelect renders the
   *     label and applyDcvPanels reveals the panel, gated ENTIRELY by facts.dcvMethods.
   * @type {Readonly<Record<string,string>>}
   */
  var DCV_METHODS = Object.freeze({
    EMAIL: 'EMAIL',
    HTTP: 'HTTP',
    HTTPS: 'HTTPS',
    DNS_CNAME: 'DNS_CNAME',
    DNS_TXT: 'DNS_TXT'
  });

  /**
   * ApiClient retry / backoff / timeout tunables.
   *
   * The form's API calls are non-critical : every endpoint has a client-side
   * fallback (the approver default, the local domain-format check), so the
   * client retries transient failures a couple of times then degrades
   * gracefully rather than blocking the customer. 429 is deliberately NOT
   * retried here (see ApiClient._shouldRetry) : the UI surfaces a "try again"
   * state and the customer keeps the default options.
   *
   * WORST-CASE LATENCY (PR A round-5 note) : a fully-unresponsive endpoint can
   * keep a promise pending ~MAX_ATTEMPTS x TIMEOUT_MS (~45s) plus backoff. That
   * is fine here because every call is non-blocking with a client-side fallback.
   * When PR B/C wire these into INTERACTIVE flows (e.g. on-blur domain parse,
   * on-change SKU lookup), consider a shorter per-call TIMEOUT_MS for those
   * keystroke-driven lookups so a dead endpoint reads better.
   * @type {Readonly<Record<string,number>>}
   */
  var RETRY = Object.freeze({
    MAX_ATTEMPTS: 3, // total attempts including the first
    BASE_DELAY_MS: 250, // exponential backoff base
    MAX_DELAY_MS: 2000, // backoff ceiling
    TIMEOUT_MS: 15000 // per-attempt fetch timeout
  });

  /* =========================================================================
   * Small utilities
   * ========================================================================= */

  /**
   * Promise-returning delay. Extracted so ApiClient backoff can be made
   * instantaneous in unit tests by injecting `options.delay`.
   * @param {number} ms
   * @returns {Promise<void>}
   */
  function defaultDelay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /**
   * Exponential backoff delay for a given (1-based) attempt number, capped.
   * @param {number} attempt 1 for the first retry, 2 for the second, ...
   * @returns {number} milliseconds
   */
  function backoffDelay(attempt) {
    var ms = RETRY.BASE_DELAY_MS * Math.pow(2, attempt - 1);
    return Math.min(ms, RETRY.MAX_DELAY_MS);
  }

  /**
   * Email format validator. The regex is carried VERBATIM from the v1 form
   * (ValidationUtils.isValidEmail in snippets/ssl-unified-order-form.liquid (deprecated, removed 2026-07-05)) so
   * the v2 client-side accept/reject boundary matches v1 exactly.
   * @param {string} value
   * @returns {boolean}
   */
  function isValidEmail(value) {
    if (typeof value !== 'string') {
      return false;
    }
    // Preserved BYTE-FOR-BYTE from v1 (ValidationUtils.isValidEmail) so the v2
    // client-side accept/reject boundary is identical. The in-character-class
    // escapes (e.g. \.) are part of that verbatim copy ; do not "tidy" them, the
    // point is parity with the shipped v1 regex, not minimal escaping.
    //
    // KNOWN SHAPE (PR A review item 3, conscious carry-over) : the nested
    // quantifiers (([^...]+(\.[^...]+)*)) are a classic catastrophic-backtracking
    // (ReDoS) pattern. Accepted HERE because the input is a short, client-side,
    // self-supplied e-mail : a user can only stall their OWN browser, and the
    // middleware re-validates server-side. Do NOT reuse this pattern for any
    // server-side validation on attacker-controlled input.
    var emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(value.trim());
  }

  /* =========================================================================
   * ApiClient  (REAL)  -- fetch wrapper over the middleware /api/* surface
   * =========================================================================
   *
   * Net-new : replaces the v1 direct-from-browser fetches (the runtime PSL fetch
   * from github, the CDN crypto libraries). Every method resolves to a uniform
   * outcome object and NEVER rejects, so callers can branch on the result and
   * degrade gracefully (the form must never blow up because the API is slow or
   * down). Cross-origin GET reads + one POST, no credentials (the middleware
   * serves `Access-Control-Allow-Origin: *` with no credential support).
   */

  /**
   * @typedef {Object} ApiOutcome
   * @property {boolean} ok          true only for a 2xx response
   * @property {number}  status      HTTP status, or 0 for a network/timeout failure
   * @property {Object|null} data    parsed JSON body on success, else null. NOTE
   *                                 (PR A round-3 nit) : `ok:true` can STILL carry
   *                                 `data:null` when a 2xx response has an empty or
   *                                 non-JSON body (res.json() rejected). Consumers
   *                                 MUST treat `ok && data == null` as "no usable
   *                                 body", never assume `ok` implies `data`.
   * @property {Object|null} error   { code, message } on failure, else null
   * @property {number|null} retryAfter seconds from a 429 Retry-After header, else null
   */

  /**
   * @param {Object} options
   * @param {string} options.middlewareApiBase absolute middleware ORIGIN (no /api suffix)
   * @param {Function} [options.fetch] fetch implementation (injectable for tests)
   * @param {Function} [options.delay] delay(ms)->Promise (injectable for tests)
   * @constructor
   */
  function ApiClient(options) {
    options = options || {};

    // The base is the absolute middleware origin. There is intentionally NO
    // default here : a missing base must fail loudly, never silently fall back
    // to dev (the Phase-3 assembler owns the production-origin guarantee). When
    // absent, every request short-circuits to an API_NO_BASE outcome.
    //
    // Normalise to an origin : strip trailing slashes, then strip a trailing
    // "/api" (case-insensitive) defensively (PR A round-5 note). The contract is
    // origin-only and the endpoint methods append the "/api/..." path themselves,
    // so a base that mistakenly ends in /api would otherwise build /api/api/... and
    // 404 every call. A real origin never legitimately ends in /api, so this only
    // ever corrects a Phase-3 misconfiguration.
    var rawBase = typeof options.middlewareApiBase === 'string' ? options.middlewareApiBase : '';
    var trimmedBase = rawBase.replace(/\/+$/, '');
    this._base = trimmedBase.replace(/\/api$/i, '');

    // Warn when the /api strip ACTUALLY fired (PR B round-13 #5) : the strip is
    // silent on its own and the constructor otherwise only logs on a fully-empty
    // base, so a Phase-3 base that mistakenly ended in /api was corrected with no
    // signal at all. A loud warn surfaces the misconfig for triage. Gated on a
    // non-empty result (PR B round-1 review) : a pathological "/api" input strips
    // to empty, where the MISSING-base error below is the right (single) signal --
    // no need to also warn "STRIPPED TO ORIGIN " (empty).
    if (this._base && this._base !== trimmedBase) {
      log.warn('ORDER FORM API : MIDDLEWARE API BASE ENDED IN /api - STRIPPED TO ORIGIN ' + this._base + ' (PASS THE ORIGIN ONLY ; ApiClient APPENDS /api)');
    }

    // fetch is injectable so unit tests can run without a network or a polyfill.
    this._fetch = options.fetch || (root && typeof root.fetch === 'function' ? root.fetch.bind(root) : null);

    // delay is injectable so backoff is instantaneous under test.
    this._delay = options.delay || defaultDelay;

    if (!this._base) {
      log.error('ORDER FORM API : MIDDLEWARE API BASE MISSING - API CALLS DISABLED');
    }
  }

  /**
   * Whether an outcome is worth retrying. Retry transient failures only :
   * network/timeout (status 0) and 5xx. NEVER retry 4xx, including 429 (the UI
   * shows a graceful try-again state and the customer keeps the default
   * options ; auto-retrying 429 would only burn more of the rate-limit budget).
   * @param {ApiOutcome} outcome
   * @returns {boolean}
   */
  ApiClient.prototype._shouldRetry = function (outcome) {
    return outcome.status === 0 || (outcome.status >= 500 && outcome.status <= 599);
  };

  /**
   * Parse a Retry-After header value into a number of seconds.
   *
   * Handles the RFC 7231 delta-seconds form, which is what the middleware
   * `/api/*` surface emits : it sets `Retry-After` to `String(retryAfterSeconds)`
   * (see `res.setHeader('Retry-After', String(burst.retryAfterSeconds))` in
   * middleware-01 src/handlers/api/index.ts, with the explicit round-38 note that
   * it is delta-seconds not an HTTP-date). The alternative HTTP-date form would
   * parse to NaN here and degrade to null, which is acceptable : the UI then just
   * shows a generic try-again and the customer keeps the client-side default
   * options (PR A review item 4).
   * Uses `Number()` (not `parseInt`) for a STRICT parse : `parseInt('30abc')`
   * would wrongly yield 30, whereas `Number('30abc')` is NaN and degrades to null
   * (PR A round-2 nit). The empty / missing case is guarded above, so `Number('')`
   * (which would be 0) is never reached. The result is clamped to >= 0 (PR A
   * round-4 nit) : a negative Retry-After is nonsensical and would otherwise feed a
   * future "try again in N seconds" UI a negative countdown ; 0 means "retry now".
   * A whitespace-only header is treated as absent (PR A round-7 nit) : without the
   * trim, `Number('   ')` coerces to 0 ("retry now") instead of null. Unreachable
   * in practice (the middleware emits `String(retryAfterSeconds)`), defensive only.
   * @param {string|null} headerValue
   * @returns {number|null}
   */
  ApiClient.prototype._parseRetryAfter = function (headerValue) {
    if (!headerValue) {
      return null;
    }
    var trimmed = String(headerValue).trim();
    if (trimmed === '') {
      return null;
    }
    var seconds = Number(trimmed);
    if (!Number.isFinite(seconds)) {
      return null;
    }
    return Math.max(0, seconds);
  };

  /**
   * Map a failed response body to a provider-neutral { code, message }. The
   * middleware already returns `{ error: { code, message } }` for handled
   * errors ; for anything else we synthesise a generic code from the status.
   *
   * When an error envelope IS present, its `message` is preserved even if the
   * `code` is empty / missing : a falsy-but-present code (e.g. '') only causes the
   * CODE to be synthesised from the status, not the whole error to be discarded
   * (PR A round-6 nit). The middleware always emits a non-empty code from its
   * centralised dictionary, so the empty-code path is defensive against a
   * malformed envelope, not an expected case.
   *
   * The returned `message` MAY be an empty string (a malformed envelope omitting
   * message). The UI consumer in PR D MUST treat an empty `message` as "no
   * detail" and fall back to a generic string, rather than render a blank (PR A
   * round-7 forward-note).
   * @param {Object|null} body
   * @param {number} status
   * @returns {{code:string,message:string}}
   */
  ApiClient.prototype._extractError = function (body, status) {
    if (body && body.error) {
      var hasCode = body.error.code != null && String(body.error.code) !== '';
      var extracted = {
        code: hasCode ? String(body.error.code) : ('HTTP_' + status),
        message: String(body.error.message || '')
      };
      // `reason` : the stable per-rejection identifier POST /api/csr/validate carries beside CSR_INVALID
      // (middleware-01 docs/PUBLIC-API-REFERENCE.md section 6, 2026-09-01), the key the CSR arm maps to
      // per-reason copy (csrRejectionReasonKey). Carried ONLY when the wire sent a non-empty string, so every
      // other envelope keeps its exact { code, message } shape.
      if (typeof body.error.reason === 'string' && body.error.reason !== '') {
        extracted.reason = body.error.reason;
      }
      return extracted;
    }
    return { code: 'HTTP_' + status, message: 'REQUEST FAILED' };
  };

  /**
   * A single fetch attempt. Resolves to an ApiOutcome ; never rejects.
   * @param {string} method
   * @param {string} url
   * @param {Object|null} body
   * @returns {Promise<ApiOutcome>}
   */
  ApiClient.prototype._fetchOnce = function (method, url, body) {
    var self = this;

    if (!self._fetch) {
      // Distinct from the no-base case (API_NO_BASE in _request) so UI / log
      // triage can tell "no fetch in this environment" from "base not configured"
      // (PR A review nit : distinct codes aid debugging).
      return Promise.resolve({ ok: false, status: 0, data: null, error: { code: 'API_NO_FETCH', message: 'NO FETCH IMPLEMENTATION AVAILABLE' }, retryAfter: null });
    }

    // Per-attempt timeout via AbortController where available (Node 18+, jsdom,
    // modern browsers). If AbortController is missing we still issue the request
    // and rely on the platform default timeout.
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var timer = null;

    var init = {
      method: method,
      headers: { Accept: 'application/json' },
      mode: 'cors',
      credentials: 'omit' // the middleware /api/* surface supports no credentials
    };
    if (body != null) {
      // A JSON body makes this a NON-SIMPLE cross-origin request, so the browser
      // issues a CORS preflight (OPTIONS) before the actual POST (the POSTs here
      // are /api/csr/validate, the CaaS subscription check via checkCaasSubscription
      // and the Validation ID check via checkValidationId). The middleware answers that preflight :
      // src/handlers/api/index.ts sets Access-Control-Allow-Methods 'GET, POST,
      // OPTIONS' + Access-Control-Allow-Headers 'Content-Type' + Max-Age 3600
      // (verified PR A round-12). Unit tests inject a mock fetch and never
      // exercise CORS, so PR B should smoke-test the live preflight when it wires
      // CSR validation into the form.
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    if (controller) {
      init.signal = controller.signal;
      timer = setTimeout(function () {
        controller.abort();
      }, RETRY.TIMEOUT_MS);
    }

    return self._fetch(url, init).then(function (res) {
      if (timer) {
        // NOTE (PR A round-4 nit) : the timeout is cleared the moment the response
        // HEADERS arrive, so TIMEOUT_MS bounds connect-to-headers, NOT the
        // subsequent body read (res.json() below). A stalled body stream after
        // headers is therefore unbounded by TIMEOUT_MS. Negligible here (these
        // JSON bodies are tiny) ; do not assume TIMEOUT_MS bounds the whole
        // request. Moving the clear after res.json() was considered and rejected :
        // an abort mid-body makes res.json() reject, which our catch maps to
        // data:null + ok:true (a 2xx), i.e. a worse "empty success" outcome than a
        // clean TIMEOUT, so headers-time clear is the better trade-off.
        clearTimeout(timer);
      }
      var retryAfter = self._parseRetryAfter(res.headers && res.headers.get ? res.headers.get('Retry-After') : null);
      // res.json() can reject on an empty / non-JSON body ; treat that as null
      // rather than letting it surface as a network failure.
      return Promise.resolve()
        .then(function () {
          return res.json();
        })
        .catch(function () {
          return null;
        })
        .then(function (data) {
          return {
            ok: res.ok,
            status: res.status,
            data: res.ok ? data : null,
            error: res.ok ? null : self._extractError(data, res.status),
            retryAfter: retryAfter
          };
        });
    }).catch(function (err) {
      if (timer) {
        clearTimeout(timer);
      }
      // A timeout (AbortController) surfaces as an AbortError ; everything else
      // is a genuine network failure. Both get status 0 so they are retryable.
      var isTimeout = err && err.name === 'AbortError';
      return {
        ok: false,
        status: 0,
        data: null,
        error: { code: isTimeout ? 'TIMEOUT' : 'NETWORK', message: 'NETWORK REQUEST FAILED' },
        retryAfter: null
      };
    });
  };

  /**
   * Issue a request with retry + exponential backoff. Resolves to an ApiOutcome.
   *
   * IDEMPOTENCY ASSUMPTION (PR A round-4 nit) : transient failures (status 0 /
   * 5xx) are retried for EVERY verb, including POST /api/csr/validate and the
   * POST CaaS subscription check (checkCaasSubscription). That is
   * safe today because every endpoint this client calls is read-only or
   * validation-only (no server state change), so a retried POST is harmless. If a
   * future POST mutates state (e.g. an order-placement endpoint), it MUST opt out
   * of retry (or use an idempotency key) rather than inherit this policy.
   *
   * THE ONE OPT-OUT TODAY (`options.retry === false` : the Validation ID check, checkValidationId, 2026-10-18) : a
   * call whose server side meters a SMALL per-visitor ceiling and spends Certificate Authority budget on EVERY
   * attempt makes one attempt, the customer's next click being the retry (the paired middleware-01
   * src/services/prevalidation-lookup.ts contract : one attempt per call, a public button must not multiply CA
   * calls on an outage). Every other call keeps the shared policy.
   * @param {string} method
   * @param {string} path path starting with /api/...
   * @param {Object} [body]
   * @param {{retry?: boolean}} [options] `retry: false` makes exactly one attempt (no transport retry)
   * @returns {Promise<ApiOutcome>}
   */
  ApiClient.prototype._request = function (method, path, body, options) {
    var self = this;

    if (!self._base) {
      // No base configured : do not even attempt a request. Fail loud-but-soft.
      // Distinct code from API_NO_FETCH (PR A review nit) so a missing base is
      // unambiguous in logs / any future UI branch.
      return Promise.resolve({ ok: false, status: 0, data: null, error: { code: 'API_NO_BASE', message: 'MIDDLEWARE API BASE NOT CONFIGURED' }, retryAfter: null });
    }

    if (!self._fetch) {
      // No fetch implementation is a PERMANENT condition that can never succeed,
      // so short-circuit it HERE, before the retry loop, exactly as the no-base
      // case above does (PR A round-8 nit). Routing it through _fetchOnce would
      // surface a status-0 outcome that _shouldRetry retries MAX_ATTEMPTS times
      // with real backoff, burning ~750ms on a guaranteed failure. _fetchOnce
      // keeps its own no-fetch guard as defence in depth (e.g. if it is ever
      // called directly).
      return Promise.resolve({ ok: false, status: 0, data: null, error: { code: 'API_NO_FETCH', message: 'NO FETCH IMPLEMENTATION AVAILABLE' }, retryAfter: null });
    }

    var url = self._base + path;
    var attempt = 0;
    var maxAttempts = options && options.retry === false ? 1 : RETRY.MAX_ATTEMPTS;

    function run() {
      attempt += 1;
      return self._fetchOnce(method, url, body).then(function (outcome) {
        if (self._shouldRetry(outcome) && attempt < maxAttempts) {
          return self._delay(backoffDelay(attempt)).then(run);
        }
        return outcome;
      });
    }

    return run();
  };

  // ---- Endpoint methods (one per /api/* endpoint the forms consume) ----------
  // Paths and verbs match the middleware contract (canonical doc section 5.1).

  /** POST /api/csr/validate -- Sectigo DecodeCSR. @param {string} csr @returns {Promise<ApiOutcome>} */
  ApiClient.prototype.validateCsr = function (csr) {
    return this._request('POST', '/api/csr/validate', { csr: csr });
  };

  /** GET /api/domain/parse -- PSL normalise/parse. @param {string} domain @returns {Promise<ApiOutcome>} */
  ApiClient.prototype.parseDomain = function (domain) {
    return this._request('GET', '/api/domain/parse?domain=' + encodeURIComponent(domain));
  };

  /** GET /api/dcv/approver-emails -- the OFFICIAL set (opt-in additional addresses : levelEmails + whoisEmails +
   * dnsTxtEmails), appended into the one approver select on demand. @param {string} domain @returns {Promise<ApiOutcome>} */
  ApiClient.prototype.getApproverEmails = function (domain) {
    return this._request('GET', '/api/dcv/approver-emails?domain=' + encodeURIComponent(domain));
  };

  /** GET /api/dcv/approver-emails-calculated -- the CALCULATED set : the middleware returns the FINISHED
   * `calculatedEmails` array (+ `isValid`). The form sends the EXACT domain (wildcard "*." and all) and renders the
   * array directly -- it no longer constructs the list client-side (the prefix x levels pairing + /api/domain/auth
   * -domains were removed, the middleware owns this calculation). @param {string} domain @returns {Promise<ApiOutcome>} */
  ApiClient.prototype.getCalculatedApproverEmails = function (domain) {
    return this._request('GET', '/api/dcv/approver-emails-calculated?domain=' + encodeURIComponent(domain));
  };

  /** GET /api/sku/:sku -- canonical SKU breakdown + form config. @param {string} sku @returns {Promise<ApiOutcome>} */
  ApiClient.prototype.getSkuFacts = function (sku) {
    return this._request('GET', '/api/sku/' + encodeURIComponent(sku));
  };

  /** POST <subscriptionCheck.endpoint> -- the CaaS extension subscription check (middleware-01
   * docs/CAAS-SUBSCRIPTION-REGISTER-DESIGN.md section 9). The PATH is NOT hardcoded : it comes from the /api/sku
   * response's root `subscriptionCheck.endpoint` descriptor (currently /api/caas/check), read by the caller --
   * the form makes no endpoint decision. Read-only on the server (a decided verdict, no state change), so the
   * shared retry policy's idempotency assumption holds ; 429 is never retried (the per-visitor ceiling).
   * @param {string} endpoint the descriptor's path, starting /api/...
   * @param {string} acmeAccountId @param {string} sku @returns {Promise<ApiOutcome>} */
  ApiClient.prototype.checkCaasSubscription = function (endpoint, acmeAccountId, sku) {
    return this._request('POST', endpoint, { acmeAccountId: acmeAccountId, sku: sku });
  };

  /** POST <validationIdCheck.endpoint> -- the Validation ID check (operator decision 2026-10-18 ; middleware-01
   * docs/PUBLIC-API-REFERENCE.md section 10). The PATH is NOT hardcoded : it comes from the /api/sku response's root
   * `validationIdCheck.endpoint` descriptor, read by the caller -- the form makes no endpoint decision. ONE ATTEMPT,
   * NO TRANSPORT RETRY (`retry: false`, the one opt-out of the shared policy) : the server meters this call at TEN an
   * hour per visitor and charges every attempt BEFORE the handler answers, and every attempt that reaches the handler
   * spends Certificate Authority budget, so the shared three-attempt retry would charge one click three times against
   * that ceiling on an outage a sub-second retry cannot clear. The middleware's own contract for this endpoint is one
   * attempt per call with the customer's next click as the retry (middleware-01 src/services/prevalidation-lookup.ts).
   * 429 was never retried anyway (_shouldRetry).
   * @param {string} endpoint the descriptor's path, starting /api/...
   * @param {string} validationId @param {string} sku @returns {Promise<ApiOutcome>} */
  ApiClient.prototype.checkValidationId = function (endpoint, validationId, sku) {
    return this._request('POST', endpoint, { validationId: validationId, sku: sku }, { retry: false });
  };

  /** GET /api/reference/countries -- ISO 3166 list. @returns {Promise<ApiOutcome>} */
  ApiClient.prototype.getCountries = function () {
    return this._request('GET', '/api/reference/countries');
  };

  /* =========================================================================
   * FormValidator  (REAL skeleton)  -- the single submit gate
   * =========================================================================
   *
   * The v1 forms had ONE gate (FormValidator.validateSSLForm) that blocked
   * submission. v2 preserves that single-gate shape. PR A implements the parts
   * that are self-contained and pure (the required-field contract, email format,
   * and the SAN strict-equality count check) ; the primary-input / DCV / SAN
   * CONTENT checks are wired in once their modules land (PR B/C/D) -- see the
   * clearly-marked TODO hooks in `validate`.
   *
   * PARSER-DRIVEN, FAMILY-AGNOSTIC (owner 2026-06-28) : validate() applies NO
   * per-family contract and contains NO family literal. It runs ONE uniform loop
   * over the server's per-field `fields` states (from GET /api/sku) : a field is
   * enforced iff the parser marks it 'required', and the coverage guard (GUARD B)
   * rejects fail-CLOSED any 'required' field the gate has no validator for (e.g.
   * S/MIME's 'email' until its validator lands). So a not-yet-wired family cannot
   * silently pass, and the form makes NO decision the parser did not hand it.
   */

  /**
   * SSL always-required contact fields. The field ids AND the uppercase message
   * tokens are part of the cutover contract : they match the alwaysRequiredFields
   * array in FormValidator.validateSSLForm in snippets/ssl-unified-order-form.liquid (deprecated, removed 2026-07-05)
   * exactly, so the v2 "this field is required" UX cannot drift from v1.
   * @type {ReadonlyArray<{id:string,token:string}>}
   */
  var SSL_ALWAYS_REQUIRED_FIELDS = Object.freeze([
    // Each entry carries the field id (gate value lookup), tokenKey (the "is required" copy) and lengthKey (the
    // "too long" copy). The max-length VALUE is NOT here anymore (owner 2026-06-29) : it lives in the single source
    // snippets/order-form-v2-validation.liquid, reaches the engine via config.validation (context.validation at the
    // gate), and the per-field CONTENT check (fieldContentError) reads it by id. NO `charset` : the Sectigo contract
    // (AutoApplyOrder OpenAPI, confirmed 2026-06-23) imposes no character-set rule on identity/contact fields -- only
    // a max length -- and the real gate is the OV/EV registry match. E-mail additionally keeps its FORMAT check
    // (validateEmailFormat / isValidEmail), a universal standard ; the LENGTH comes from config.validation like the rest.
    Object.freeze({ id: 'contact-first-name', tokenKey: 'validation.contact.firstname.required', lengthKey: 'validation.length.firstname' }),
    Object.freeze({ id: 'contact-last-name', tokenKey: 'validation.contact.lastname.required', lengthKey: 'validation.length.lastname' }),
    Object.freeze({ id: 'contact-email', tokenKey: 'validation.contact.email', lengthKey: 'validation.length.email' })
  ]);

  /**
   * SSL organization fields, required only for OV / EV. Carried verbatim from the
   * orgRequiredFields array in FormValidator.validateSSLForm.
   * @type {ReadonlyArray<{id:string,token:string}>}
   */
  var SSL_ORG_REQUIRED_FIELDS = Object.freeze([
    // id + tokenKey + lengthKey only ; the max-length VALUE lives in the single source order-form-v2-validation.liquid
    // and reaches the gate via config.validation (owner 2026-06-29). NO charset (the CA imposes none on these
    // subject/identity fields -- see the SSL_ALWAYS_REQUIRED_FIELDS note). org-country is a <select> (a fixed
    // ISO-3166 list), so it has no length rule -- fieldContentError no-ops when config.validation has no entry for it.
    Object.freeze({ id: 'org-name', tokenKey: 'validation.org.name.required', lengthKey: 'validation.length.orgname' }),
    Object.freeze({ id: 'org-address', tokenKey: 'validation.org.address.required', lengthKey: 'validation.length.streetaddress' }),
    Object.freeze({ id: 'org-city', tokenKey: 'validation.org.city.required', lengthKey: 'validation.length.city' }),
    Object.freeze({ id: 'org-state', tokenKey: 'validation.org.state.required', lengthKey: 'validation.length.state' }),
    Object.freeze({ id: 'org-postal', tokenKey: 'validation.org.postal.required', lengthKey: 'validation.length.postal' }),
    Object.freeze({ id: 'org-country', tokenKey: 'validation.org.country.required' })
  ]);

  // (Removed 2026-06-23, owner decision : the SUBJECT_LETTERS range, the CHARSET_SPECS name/org patterns and
  // charsetErrorToken. The authoritative Sectigo / middleware contract (AutoApplyOrder OpenAPI) imposes NO
  // character-set rule on the identity / contact fields -- ONLY a max length -- and the real gate on them is the
  // OV/EV registry match, not a character filter. A client charset filter was therefore STRICTER than the CA and
  // turned away valid international customers (e.g. non-Latin-1 names), so it was removed. Identity fields are now
  // length-validated only ; DOMAINS keep their own ASCII / PSL rule (the CA does enforce ASCII on the CN + SANs).)

  // (lengthErrorToken removed 2026-06-27 : the "THE <field> IS TOO LONG" messages are now PRE-COMPOSED customer
  // copy in order-form-v2-copy.liquid (validation.length.*), referenced by each field rule's lengthKey and resolved
  // via config.copy at the validate sites. The old 'THE '+field.label+' IS TOO LONG' frame was itself a hardcoded
  // literal -- splicing the label in JS defeated single-source -- so the whole message moved to copy.)

  /**
   * UTF-8 BYTE length of a string. The Sectigo / CA subject-field caps are measured in BYTES, not characters, and
   * now that the form accepts multi-byte characters (the charset filter was removed) a name's CHARACTER count can
   * sit within the cap while its BYTE count is over -- so the client must measure bytes to match the CA exactly
   * (owner decision 2026-06-23). TextEncoder gives the true UTF-8 byte length and is present in every browser +
   * jsdom ; guarded so a (contracted-impossible) absence degrades to the classic encodeURIComponent byte-count
   * idiom rather than throwing. @param {string} str @returns {number}
   */
  function utf8ByteLength(str) {
    if (typeof TextEncoder === 'function') {
      return new TextEncoder().encode(str).length;
    }
    // Fallback : encodeURIComponent emits each non-ASCII byte as %XX (3 chars / byte) + ASCII verbatim ; collapse
    // every %XX back to a single char so the resulting length is the true byte count.
    return encodeURIComponent(str).replace(/%[0-9A-Fa-f]{2}/g, 'x').length;
  }

  /**
   * The SINGLE content-validation primitive (LENGTH only) for a free-text subject field, shared by the submit gate
   * and the card-status completeness check so they enforce the IDENTICAL rule. Returns an error TOKEN, or null when
   * the value is acceptable. There is NO character-set check : the CA imposes none on the identity/contact fields
   * (owner decision 2026-06-23 ; see the SSL_ALWAYS_REQUIRED_FIELDS note). PRESENCE is a SEPARATE concern
   * (the gate's _validateFieldList presence checks in validate()) : a blank value returns null here (an empty required field is caught by the presence
   * check ; an empty OPTIONAL field is genuinely fine). The native maxlength on each snippet caps TYPED input at the
   * same value, so this gate-side length check is the authority for PASTED / programmatic over-length values.
   * @param {string} value the (already trimmed) field value
   * @param {number|undefined} maxLength the byte cap, resolved from config.validation (owner 2026-06-29) ; undefined -> no length rule
   * @param {string} lengthKey the "too long" copy key returned when over the cap
   * @returns {string|null}
   */
  function fieldContentError(value, maxLength, lengthKey) {
    if (typeof value !== 'string') {
      return null;
    }
    var v = value.trim();
    if (v === '') {
      return null; // presence is enforced separately ; do not double-report a blank field as a content error
    }
    // BYTE length, not character count (PR #24 r6 #1, owner decision 2026-06-23) : the CA caps these fields in
    // BYTES, and since the charset filter was removed a multi-byte name (accents / CJK) can be within the character
    // count yet over the byte cap -- so the client must measure UTF-8 bytes to match the CA exactly. The maxLength
    // comes from config.validation (the single source order-form-v2-validation.liquid), NOT a hardcoded number here.
    if (typeof maxLength === 'number' && utf8ByteLength(v) > maxLength) {
      // Return the field's lengthKey (a copy KEY, non-null), NOT message text : callers resolve it via config.copy
      // (content-centralization 2026-06-27). The boolean-check callers (the tick, the card-status completeness
      // check) only test null-vs-non-null, so a key works.
      return lengthKey;
    }
    return null;
  }

  /**
   * Resolve a field's max-length byte cap from a validation map (config.validation / context.validation), by the
   * engine's validator field id. The single source is order-form-v2-validation.liquid -> config.validation (owner
   * 2026-06-29) ; undefined when the map has no entry for the id (e.g. a <select> like org-country, or a missing map),
   * which fieldContentError treats as "no length rule". @param {Object} validationMap @param {string} fieldId @returns {number|undefined}
   */
  function maxLengthOf(validationMap, fieldId) {
    var rule = validationMap && validationMap[fieldId];
    return (rule && typeof rule.maxLength === 'number') ? rule.maxLength : undefined;
  }

  /**
   * CaaS content rules : id + lengthKey only (the max-length VALUE lives in config.validation, owner
   * 2026-06-29). The Validation ID (ov-anchor-order) is a Sectigo order reference (caas-openapi-v1.0.yaml
   * ovAnchorOrderNumber) ; since the operator decision of 2026-07-09 it ALSO carries a digits-only charset
   * rule in config.validation, applied inside FIELD_VALIDATORS.ovAnchor (mirroring the middleware's
   * sanitizeValidationIdValue rail), while this constant keeps supplying the "too long" copy key. The ACME
   * Account ID is NOT here : its charset rule (base64url) is gated by isValidAcmeAccountId.
   * @type {ReadonlyArray<{id:string,lengthKey:string}>}
   */
  var OV_ANCHOR_FIELD_RULES = Object.freeze([
    Object.freeze({ id: 'ov-anchor-order', lengthKey: 'validation.length.ovanchor' })
  ]);

  /**
   * End User Telephone Number rule : id + lengthKey only, the EXACT shape of OV_ANCHOR_FIELD_RULES above (the
   * max-length VALUE and the composed-rail PATTERN live in config.validation under 'telephone', from the single
   * source order-form-v2-validation.liquid ; middleware-01 commit a5b3879, operator rulings 2026-07-24). The
   * REQUIRED-state presence token is resolved at the validator's use site (copyOf
   * 'validation.telephone.required' in FIELD_VALIDATORS.telephone), matching the ov-anchor-order shape : the
   * tokenKey field is consumed only by the presence-LIST walk (SSL_ALWAYS / SSL_ORG), which a standalone
   * validator never uses -- an unread tokenKey here was dead metadata (PR #87 round 3), dropped.
   * @type {ReadonlyArray<{id:string,lengthKey:string}>}
   */
  var TELEPHONE_FIELD_RULES = Object.freeze([
    Object.freeze({ id: 'telephone', lengthKey: 'validation.length.telephone' })
  ]);

  /**
   * Field rules keyed by VALIDATOR field id, built once from the same SSL_ALWAYS / SSL_ORG / CAAS constants the
   * gate uses -- so the card-status completeness check (wireOneCardStatus.allProvided) and the submit gate share
   * ONE source for maxLength and can never drift. The lookup maps a field's data-order-form-property -> validator
   * id (via PROPERTY_TO_VALIDATOR_FIELD) -> this rule. A field with no entry falls back to presence-only.
   * @type {Object<string,Object>}
   */
  var FIELD_RULES_BY_ID = (function () {
    var map = {};
    function add(list) {
      for (var i = 0; i < list.length; i++) {
        map[list[i].id] = list[i];
      }
    }
    add(SSL_ALWAYS_REQUIRED_FIELDS);
    add(SSL_ORG_REQUIRED_FIELDS);
    add(OV_ANCHOR_FIELD_RULES);
    add(TELEPHONE_FIELD_RULES);
    return map;
  })();

  /**
   * DOMAIN-VALIDATION TOKENS (Validation-Hardening A.1-A.4 ; owner content-pass PLACEHOLDERS, brief D).
   * Terse keys surfaced via the field error region + the aria-live summary, exactly like the existing
   * tokens ; the owner finalises the customer sentences in the content pass. UPPERCASE per the Trustico
   * message style. These mirror the failure modes middleware-01/src/utils/product-requirements.ts rejects,
   * so client and server agree (client = early feedback ; the middleware is the server-side authority).
   */
  // The validate-gate ERROR TOKENS (domain / wildcard / sellable / SAN / method ; the CaaS acme + anchor PRESENCE
  // tokens ; the fail-closed gate token ; the form-level summary line) are now customer copy in order-form-v2-copy.liquid
  // (validation.* / form.summary.*), resolved from the validate context's copy map (context.copy) at each gate site,
  // or via copyOf(state.config, ...) on the interactive CSR/FQDN wire arms (content-centralization 2026-06-27). No
  // token text lives in this file anymore.
  //
  // ROUTING NOTE (retained from the deleted GATE_NO_REQUIRED_FIELD_TOKEN block, PR #32 review r7) : the THREE pre-loop
  // fail-closed guards (org/address consistency, GATE_ENFORCED_FIELDS coverage, the no-required-field floor) all
  // return fieldId:'order-form', which resolveFieldEl CANNOT anchor to a field (the <form> carries class="order-form",
  // not id), so onSubmit surfaces their SPECIFIC token (validation.gate.notPrepared) in the form-level SUMMARY rather
  // than the generic "correct the highlighted fields" with nothing highlighted. They are INTENTIONALLY
  // indistinguishable to the shopper : each signals a malformed /api/sku (a build error that never happens in prod,
  // never user-fixable), so one message fits all three. primaryDomainErrorToken now returns a copy KEY (resolved at
  // its emission sites) ; the not-implemented token is a FRAME ('VALIDATION NOT IMPLEMENTED FOR CERT TYPE' with the
  // cert type appended at runtime, so the test /NOT IMPLEMENTED/ regex still matches).

  // Memoised compiled, whole-value-anchored RegExp for a charset pattern (PR #35 review nit) : isValidAcmeAccountId
  // runs on the live field tick (every keystroke), so compiling `new RegExp` per call is wasteful. Cache by the
  // pattern STRING (operator-controlled, from config.validation -- a tiny, bounded key space : one pattern in
  // practice). Returns null for an empty pattern so the caller fails closed.
  var _anchoredReCache = {};
  function anchoredRegExp(pattern) {
    if (typeof pattern !== 'string' || !pattern) {
      return null;
    }
    if (!Object.prototype.hasOwnProperty.call(_anchoredReCache, pattern)) {
      _anchoredReCache[pattern] = new RegExp('^(?:' + pattern + ')$');
    }
    return _anchoredReCache[pattern];
  }

  /**
   * Non-empty, within-cap, charset-matching ACME Account ID (CaaS extension). The max length + the charset PATTERN
   * come from config.validation['caas-acme-account'] (the single source order-form-v2-validation.liquid, owner
   * 2026-06-29) -- NOT hardcoded here. The SERVER is the authority (the middleware requires the ACME Account ID
   * property present at webhook time, then again before EXTENDDOMAINS) ; this is early client feedback. FAIL CLOSED
   * if no rule/pattern is supplied (a missing config.validation is a build error, never a pass-through).
   * @param {string} value @param {{maxLength?:number, pattern?:string}} rule @returns {boolean}
   */
  function isValidAcmeAccountId(value, rule) {
    if (typeof value !== 'string') {
      return false;
    }
    var v = value.trim();
    if (v === '') {
      return false;
    }
    var maxLength = rule && typeof rule.maxLength === 'number' ? rule.maxLength : undefined;
    if (typeof maxLength === 'number' && v.length > maxLength) { // base64url is ASCII, so char count == byte count
      return false;
    }
    var re = anchoredRegExp(rule && rule.pattern); // compiled once per pattern (cached) ; null when no charset rule
    if (!re) {
      return false; // no charset rule available -> fail closed (config.validation must supply it)
    }
    return re.test(v);
  }

  /**
   * THE ONE composition source for the End User Telephone Number (middleware-01 commit a5b3879 parity, operator
   * rulings 2026-07-24). Reads the row's two visible controls (the dial-code <select> + the national <input>)
   * and returns the COMPOSED cart value : dialCode + the national digits with separators stripped (whitespace /
   * dot / hyphen / parentheses -- the SAME class the middleware's sanitizeTelephoneValue strips, so client and
   * server measure the identical string). Returns '' while the national digits are blank (an optional empty
   * phone is simply absent : no dial-only fragment ever composes). A national value WITHOUT a chosen dial code
   * composes WITHOUT the leading '+' and therefore FAILS the rail -> the invalid feedback correctly steers the
   * customer to pick a country code (NEVER a client-side guess : the parity brief forbids deriving the dial
   * from the org country). Consumed by ALL FOUR value readers -- buildSubmitContext (the gate), writeThrough
   * (the cart twin), wireOneFieldCheck (the tick), wireOneCardStatus (the card chip) -- so one rule can never
   * drift four ways. @param {Element|null} rowEl the [data-order-form-field-state="telephone"] row @returns {string}
   */
  function composeTelephoneValue(rowEl) {
    if (!rowEl) {
      return '';
    }
    var dial = rowEl.querySelector('[data-order-form-telephone-dial]');
    var national = rowEl.querySelector('[data-order-form-validate="phone"]');
    var digits = national ? (national.value || '').replace(/[\s().\-]/g, '') : '';
    if (digits === '') {
      return '';
    }
    return ((dial && dial.value) || '') + digits;
  }

  /**
   * Composed-telephone rail check : within-cap + pattern-matching ('+' then 8-15 digits, non-zero first digit),
   * both from config.validation['telephone'] (the single source order-form-v2-validation.liquid ; the
   * middleware's sanitizeTelephoneValue rail is the AUTHORITY, this is UX parity -- lockstep note lives in that
   * file). FAIL CLOSED with no rule/pattern, the isValidAcmeAccountId stance : config.validation must supply the
   * rule, a form without it must not silently accept free text. Blank is NOT this function's business : blank
   * handling is the caller's STATE decision (optional-blank passes the gate, required-blank fails presence).
   * @param {string} value @param {{maxLength?:number, pattern?:string}} rule @returns {boolean}
   */
  function isValidTelephoneValue(value, rule) {
    if (typeof value !== 'string' || value === '') {
      return false;
    }
    var maxLength = rule && typeof rule.maxLength === 'number' ? rule.maxLength : undefined;
    if (typeof maxLength === 'number' && value.length > maxLength) { // '+' and digits are ASCII : char count == byte count
      return false;
    }
    var re = anchoredRegExp(rule && rule.pattern);
    if (!re) {
      return false;
    }
    return re.test(value);
  }

  /**
   * The telephone ROW (the [data-order-form-field-state="telephone"] wrapper inside the contact card) and its
   * hidden-check. Row-LEVEL granularity deliberately mirrors isInHiddenSection's card-level guard : the telephone
   * is the FIRST field whose parser state gates a ROW inside a visible card (operator placement ruling
   * 2026-07-24 : embedded in the End User Information card, not a separate card), so its hidden-check must read
   * the ROW's own computed display -- the enclosing contact SECTION being visible says nothing about the row.
   * Same computed-display-first posture, and the same INLINE-only show/hide contract, as isInHiddenSection.
   */
  function telephoneRowEl(form) {
    return form.querySelector('[data-order-form-field-state="telephone"]');
  }
  function isRowHidden(rowEl) {
    if (!rowEl) {
      return true; // no row rendered : treat as hidden (fail-closed, matching the visibility seam's absent-key stance)
    }
    var view = rowEl.ownerDocument && rowEl.ownerDocument.defaultView;
    if (view && view.getComputedStyle) {
      return view.getComputedStyle(rowEl).display === 'none';
    }
    return !!(rowEl.style && rowEl.style.display === 'none');
  }

  /**
   * Whether the SKU is a wildcard family (plain wildcard OR wildcard-multi). The SINGLE source for the
   * "this SKU expects a *. primary" decision, mirroring middleware-01 product-requirements getDomainType
   * returning WILDCARD || WILDCARDMULTI. @param {Object} facts @returns {boolean}
   */
  function skuRequiresWildcard(facts) {
    return !!(facts && (facts.isWildcard || facts.isWildcardMultiDomain));
  }

  /**
   * WILDCARD ENTRY (operator decision 2026-10-26) : the value to put back in a domain box before it is validated. On a
   * product that requires a wildcard (`required` : skuRequiresWildcard for the primary, the SKU's isWildcardMultiDomain
   * for a SAN row) a domain typed WITHOUT any "*" gets the "*." prefix, so a customer who types example.com on a
   * wildcard product orders *.example.com and sees it in the box, instead of the "requires a wildcard" error with no
   * hint of what to type. Anything else comes back as typed : a value already carrying a "*" anywhere is the
   * customer's own wildcard attempt and is validated as typed (the server parse rejects a misplaced asterisk, and a
   * leading "*." is never doubled), an empty box is never turned into a bare "*.", and a non-wildcard product never
   * gains a prefix (a typed "*." there still meets the wildcard.notAllowed rule). The prefix is the same "*." + host
   * construction as DomainNormalizer.applyWildcard, which serves the inverse case (the SERVER's wildcard verdict
   * re-applied to its stripped `normalized`) ; this one acts on the PRODUCT's requirement, before the parse. A leading
   * "www." is deliberately NOT stripped : the form never second-guesses the name the customer typed (operator decision
   * 2026-10-26 : the wildcard wording and the confirmation chip show the result, *.www.example.com is a valid nested
   * wildcard, and the customer corrects it if it is not what they meant).
   * @param {string} value the box's current value @param {boolean} required the product requires a wildcard
   * @returns {string} the value to validate, and to write back into the box when it differs
   */
  function wildcardEntryFor(value, required) {
    if (!required || typeof value !== 'string') {
      return value;
    }
    var trimmed = value.trim();
    if (!trimmed || trimmed.indexOf('*') !== -1) {
      return value;
    }
    return '*.' + trimmed;
  }

  /**
   * A.1 + A.3 PRIMITIVE -- the SINGLE wildcard/validity rule, shared by the interactive wirePrimaryInput
   * arms (A.5 : early field feedback) AND the submit gate (A.0/A.1/A.3 : authoritative), so client feedback
   * and the gate enforce the SAME rule -- which in turn MIRRORS middleware-01/src/utils/product-requirements.ts
   * (validateProductRequiredFields, traditional branch). Given the /api/domain/parse verdict for the primary
   * domain -- apiIsValid + apiIsWildcard, the SERVER's PSL truth, NOT a client check (the project does NO
   * client-side PSL) -- and the SKU facts, return an error TOKEN, or null when the primary is acceptable :
   *   - A.3 : reject an invalid domain (apiIsValid === false).
   *   - A.1 (both directions) : reject a `*.` primary on a NON-wildcard SKU, AND a non-`*.` primary on a
   *     WILDCARD SKU. The wildcard verdict is the API's, never a `startsWith('*.')` client check.
   * apiIsValid is read STRICTLY : only an explicit `false` rejects (a missing apiIsValid means the parse was
   * not done, which the primary-input `valid` gate already blocks -- this primitive does not re-reject it).
   * FACTS-SOURCE ASYMMETRY (PR#21 review) : the SUBMIT GATE passes the SNAPSHOTTED context.skuFacts (always
   * present -- onSubmit fail-closes on null facts before building the context), so it is authoritative. The
   * INTERACTIVE arms (A.5) pass LIVE state.facts -- best-effort EARLY FEEDBACK. When facts are not yet loaded
   * (null), the wildcard-symmetry check is DEFERRED (the `if (!facts) return null` below ; PR#21 r3) so the
   * arm never shows a PREMATURE wildcard verdict before /api/sku resolves -- only the apiIsValid check runs ;
   * the shopper re-validates once facts load, and the gate re-checks against the loaded skuFacts at submit.
   * @param {boolean} apiIsValid @param {boolean} apiIsWildcard @param {Object} facts @returns {string|null}
   */
  function primaryDomainErrorToken(apiIsValid, apiIsWildcard, facts) {
    // Returns a copy KEY (resolved to the customer message via config.copy at each emission site : the gate uses
    // context.copy, the interactive CSR/FQDN arms use copyOf(state.config, ...)), or null when the domain is OK
    // (content-centralization 2026-06-27). Tests assert the returned KEY, robust to a content-pass reword.
    if (apiIsValid === false) {
      return 'validation.domain.primary.invalid';
    }
    // PR#21 r3 : DEFER the wildcard-symmetry check when facts are not loaded. The interactive arms (A.5) pass
    // LIVE state.facts, which is null in the brief window before /api/sku resolves -- without this guard,
    // skuRequiresWildcard(null) returns false and a legitimate `*.` domain on a wildcard SKU would read as
    // WILDCARD_NOT_ALLOWED prematurely. Deferring (return null) leaves only the apiIsValid check until facts
    // exist ; the shopper re-validates once loaded, and the SUBMIT GATE -- which always passes the loaded
    // skuFacts (onSubmit fail-closes on null facts) -- is the authoritative wildcard check regardless.
    if (!facts) {
      return null;
    }
    var wild = skuRequiresWildcard(facts);
    if (apiIsWildcard && !wild) {
      return 'validation.domain.wildcard.notAllowed';
    }
    if (wild && !apiIsWildcard) {
      return 'validation.domain.wildcard.required';
    }
    return null;
  }

  /**
   * @param {Object} [options]
   * @param {Function} [options.isValidEmail] email validator (injectable for tests)
   * @param {Object} [options.validation] the field-validation map (max lengths + ACME pattern). Production passes it
   *   per-call via context.validation (from config.validation -- the single source order-form-v2-validation.liquid) ;
   *   this constructor option is a convenience so a unit test can inject the SAME map once instead of per validate().
   *   The validators read context.validation FIRST, then fall back to this. owner 2026-06-29.
   * @constructor
   */
  function FormValidator(options) {
    options = options || {};
    this._isValidEmail = options.isValidEmail || isValidEmail;
    this._validation = options.validation || null;
  }

  /**
   * REMOVED (tri-state `fields` migration) : FormValidator.prototype.requiredFieldsFor
   * and .validateRequiredFields derived the required-field list CLIENT-SIDE from the
   * V1 `validationLevel` ('DV' | 'OV' | 'EV'). They had no live caller in the form
   * (validate() checks the always-required and org-required lists directly via
   * _validateFieldList below) and existed only for that level-derivation. Field
   * requirements now come ENTIRELY from the server's per-field `fields` descriptor
   * (GET /api/sku/:sku), read at the org-required gate in validate() ; there is no
   * client-side level-to-fields derivation left anywhere in this engine.
   */

  /**
   * Validate that every field in a given list has a non-blank value, returning
   * the FIRST missing one (or null). Extracted so `validate()` can check the
   * always-required list and the org-required list SEPARATELY, in the v1 order
   * (always -> email format -> org), rather than as one combined pass.
   * @param {Object} values map of fieldId -> value
   * @param {ReadonlyArray<{id:string,token:string}>} fields
   * @returns {{ok:boolean, firstMissing:({id:string,token:string}|null)}}
   * @private
   */
  FormValidator.prototype._validateFieldList = function (values, fields) {
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      var value = values[field.id];
      if (typeof value !== 'string' || value.trim() === '') {
        return { ok: false, firstMissing: field };
      }
    }
    return { ok: true, firstMissing: null };
  };

  /**
   * Validate the CONTENT (LENGTH) of every field in a list that has a present value, returning
   * the FIRST violation as a gate error (or {ok:true}). Mirrors _validateFieldList's first-failure shape so the
   * caller can return it directly. PRESENCE is NOT this method's job (a blank value passes here ; the paired
   * _validateFieldList catches a blank REQUIRED field) -- so this can be called on the always / org lists right
   * after their presence check, enforcing the owner's 2026-06-23 character + length rules at the cart boundary.
   * @param {Object} values map of fieldId -> value @param {ReadonlyArray<Object>} fields
   * @returns {{ok:boolean, error:({fieldId:string,token:string}|null)}}
   * @private
   */
  FormValidator.prototype._validateContentList = function (values, fields, context) {
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      // fieldContentError returns the field's lengthKey (a copy KEY) on over-length, or null. Resolve it through the
      // SAME copyOf(context, key) resolver every other gate site uses (PR #33 review #3 : ONE resolution idiom, and
      // this length-message path now also gets copyOf's dropped-emit dev-warn). copyOf reads context.copy and is
      // fail-soft ('' on a missing key/map, never undefined). copy is always present in production anyway.
      // The max-length VALUE comes from the validation map (context.validation in production -- the single source
      // order-form-v2-validation.liquid -> config.validation ; this._validation when a unit test injected it at
      // construction), resolved by the field's id ; the lengthKey (the "too long" copy) stays on the field constant.
      var lengthKey = fieldContentError(values[field.id], maxLengthOf(context.validation || this._validation, field.id), field.lengthKey);
      if (lengthKey) {
        return { ok: false, error: { fieldId: field.id, token: copyOf(context, lengthKey) } };
      }
    }
    return { ok: true, error: null };
  };

  /**
   * Email format check (delegates to the verbatim v1 regex by default).
   * @param {string} value
   * @returns {boolean}
   */
  FormValidator.prototype.validateEmailFormat = function (value) {
    return this._isValidEmail(value);
  };

  /**
   * SAN count integrity check. v1 used a STRICT equality (it rejected both too-
   * few AND too-many visible SAN rows, logging a SECURITY WARNING on mismatch).
   * Preserving the strict check is load-bearing : a naive "too few" check would
   * weaken it. Returns true only when the visible row count exactly equals the
   * SKU-expected SAN count.
   * @param {number} visibleCount
   * @param {number} expectedCount
   * @returns {boolean}
   */
  FormValidator.prototype.validateSanCount = function (visibleCount, expectedCount) {
    return visibleCount === expectedCount;
  };

  // ── The uniform submit-gate field registry ──────────────────────────────────────────────────────────────
  // validate() is ONE generic loop over these keys (GATE_FIELD_ORDER) : for each, the server's per-field
  // `fields[key]` state (from GET /api/sku/:sku, middleware buildFieldStates) decides ENFORCEMENT -- 'required'
  // = presence + content, 'optional' = content only if a value is FILLED (never blocks an empty one), 'hidden'
  // (or absent) = skipped -- and the registered validator does the field-SPECIFIC check. This is the GATE half
  // of the same `fields` contract the VISIBILITY layer already uses (applyFieldVisibility),
  // so render and gate cannot drift, and SSL / CaaS / S-MIME differ ONLY in the `fields` map they arrive with
  // and which validators exist -- there are NO per-family arms. The validators legitimately differ in WHAT a
  // valid value is (a domain is not an e-mail is not an ACME ID) -- that is correct, not a wart -- but every
  // field is gated the SAME way, through this one loop, in one deterministic ORDER (which preserves the v1
  // first-error sequence the tests pin : primary -> SAN -> DCV -> contact -> org). Each validator is
  // `fn(self, context, state) -> {fieldId, token} | null` (self = the FormValidator instance, for the shared
  // _validateFieldList / _validateContentList / validateSanCount / validateEmailFormat helpers). The structural
  // SAN rules (count / ceiling / per-row) live in the `san` validator and run whenever the SAN section is SHOWN
  // (san is 'optional' when shown, never 'required'). `csr` / `email` keys are reserved for S-MIME (no validator
  // yet ; the SSL csr-or-fqdn primary is covered by `domain`). The org block reads fields.org and validates
  // org + address together (SSL_ORG_REQUIRED_FIELDS spans both ; fields.org === fields.address always).

  // The strongest of several field states (required > optional > hidden/absent). Used where ONE gate covers
  // multiple `fields` keys : the 'domain' validator gates the csr-OR-fqdn PRIMARY, which the middleware describes
  // with TWO keys (fields.domain + fields.csr). Gating on the stronger means the primary check runs whenever
  // EITHER is shown/required -- so a csr-only SKU (csr:'required', domain:'hidden') is NOT a fail-open.
  function strongestFieldState() {
    var best = 'hidden';
    for (var i = 0; i < arguments.length; i++) {
      if (arguments[i] === 'required') {
        return 'required';
      }
      if (arguments[i] === 'optional') {
        best = 'optional';
      }
    }
    return best;
  }

  // 'telephone' sits after 'contact', matching its DOM position (the row is EMBEDDED in the contact card ;
  // operator placement ruling 2026-07-24) -- the gate surfaces the FIRST failing field, so gate order must
  // track visual order (the v1 parity contract's check-order principle).
  var GATE_FIELD_ORDER = ['domain', 'san', 'dcv', 'contact', 'telephone', 'org', 'acmeAccount', 'ovAnchor'];

  // The field keys the gate ENFORCES. The eight in GATE_FIELD_ORDER each run their validator ; 'address' is
  // enforced BY the 'org' validator (SSL_ORG_REQUIRED_FIELDS spans org-name + the address fields, and the
  // middleware ships fields.org === fields.address) ; 'csr' is enforced BY the 'domain' validator (an SSL SKU
  // ships csr:'required' and the primary is csr-OR-fqdn, so a valid primaryInput satisfies it). 'email' is the
  // S-MIME cert-subject e-mail -- RESERVED, no validator yet. A field a SKU marks 'required' that is NOT in this
  // set is UN-ENFORCEABLE, so validate() fails CLOSED rather than cart it un-validated (a build / contract error,
  // never a silent fail-open). When S-MIME lands, give 'email' (and any S-MIME-specific 'csr' semantics) a validator.
  var GATE_ENFORCED_FIELDS = ['domain', 'csr', 'san', 'dcv', 'contact', 'telephone', 'org', 'address', 'acmeAccount', 'ovAnchor'];

  // ── SOURCE-OF-TRUTH PER FIELD (owner 2026-07-01 : documented so this is NOT re-raised as an inconsistency) ──
  // Every validator below is driven by the SAME two single sources : the SKU parser's `fields.*` states (WHICH
  // field is gated) + the ONE validation file (config.validation, via each validator's rule). The form makes NO
  // decision of its own here. What LOOKS different between primaries is only WHERE each field's truth lives :
  //   - the `domain` validator reads the SERVER's verdict (context.primaryInput.valid) because a CSR / domain's
  //     validity can ONLY be determined by the middleware (Sectigo) -- decode the CSR, PSL-confirm the domain ; the
  //     browser cannot compute it, so the gate reads the cached server verdict.
  //   - the `contact` / `org` validators read config.validation directly because their rule (charset + length ;
  //     presence) IS a local rule that lives in the one validation file.
  //   - the `ovAnchor` validator reads BOTH (the Validation ID check, operator decision 2026-10-18) :
  //     config.validation for the local length + digits rule, AND the SERVER's check verdict
  //     (context.validationIdCheck) for any TYPED value, in the required AND the optional state -- whether the
  //     pre-validation exists, is active and fits the product can ONLY be decided by the middleware against the
  //     Certificate Authority's record. It FAILS CLOSED on a missing / stale / value-mismatched verdict.
  //   - the `acmeAccount` validator reads BOTH : config.validation for the local charset + length rule, AND the
  //     SERVER's subscription-check verdict (context.subscriptionCheck, the CaaS Subscription Register go-live
  //     2026-08-29) -- eligibility can ONLY be decided by the middleware (the register), exactly the reason the
  //     `domain` validator reads the cached server verdict. Required-state ACME FAILS CLOSED on a missing /
  //     stale / value-mismatched verdict (the FLOOR SCOPE rule below : a required field's absent slice must
  //     fail closed, never skip).
  // All are single-source-driven ; NONE is a form decision. The uniform "the active primary must be VALID"
  // invariant still lives in ONE place for EVERY primary kind -- applyPrimaryGate (progressive disclosure reads
  // primaryInput.valid for csr / fqdn / acme / email alike ; the acme primary's valid is set ONLY on an
  // eligible:true check). Do NOT re-open this as a "primaries validated inconsistently" finding.
  var FIELD_VALIDATORS = {
    // PRIMARY domain (SSL csr-or-fqdn OR CaaS fqdn) : presence (primaryInput.valid) + wildcard-symmetry.
    // SKIP-WHEN-ABSENT : a unit test that exercises a LATER field passes no primaryInput, so an absent slice is
    // skipped ; the live submit handler ALWAYS supplies it. The "missing primary" wording reflects what the form
    // accepts (SSL takes a CSR or a domain ; CaaS takes a domain only).
    domain: function (self, context) {
      if (context.primaryInput && !context.primaryInput.valid) {
        // Message is PARSER-DRIVEN, not family-keyed (owner 2026-06-28) : the primary accepts a CSR exactly when
        // the SKU parser ships csr non-hidden in `fields` (SSL = csr 'required' -> "CSR or domain" ; CaaS = csr
        // 'hidden' -> "valid FQDN"). No certType / family literal -- the form reads what the parser allows.
        var acceptsCsr = !!(context.fields && context.fields.csr && context.fields.csr !== 'hidden');
        var tokenKey = acceptsCsr
          ? 'validation.domain.primary.csrOrDomain'
          : 'validation.domain.primary.invalid';
        return { fieldId: context.primaryInput.fieldId || 'order-form', token: copyOf(context, tokenKey) };
      }
      if (context.primaryInput && context.primaryInput.valid && context.skuFacts) {
        var pErrKey = primaryDomainErrorToken(context.primaryInput.apiIsValid, context.primaryInput.apiIsWildcard, context.skuFacts);
        if (pErrKey) {
          return { fieldId: context.primaryInput.fieldId || 'order-form', token: copyOf(context, pErrKey) };
        }
      }
      return null;
    },
    // ADDITIONAL-DOMAINS (SAN) structural rules, run when the SAN section is SHOWN : count must EQUAL the
    // SKU-expected count (strict, v1) ; 1 + visible SANs must not exceed totalDomains ; every visible row must be
    // a valid domain AND, when the SKU shows DCV, carry a chosen method/approver token (else the cart's
    // domain/token lists mis-align). Each sub-check is SKIP-WHEN-ABSENT (Number.isFinite / Array.isArray).
    san: function (self, context) {
      // Build 2 (the CaaS multi-domain sale, docs/CAAS-MULTI-DOMAIN-DESIGN.md section 8 site 1) : a per-row
      // Validation Token is required ONLY when the SKU shows DCV. A CaaS SKU ships fields.dcv 'hidden' (ACME
      // validates the names ; dcvMethods is empty), so its rows carry no method and no token by design, and
      // refusing them here would block every CaaS multi-domain sale. Keyed on the server's fields.dcv -- the same
      // fact the DCV card and the primary-token cart write already key on -- never on a family or a SKU string.
      var dcvHidden = !!(context.fields && context.fields.dcv === 'hidden');
      if (Number.isFinite(context.visibleSanCount) && Number.isFinite(context.expectedSanCount)) {
        if (!self.validateSanCount(context.visibleSanCount, context.expectedSanCount)) {
          return { fieldId: 'san-container', token: copyOf(context, 'validation.san.countMismatch') };
        }
      }
      var totalDomains = context.skuFacts ? context.skuFacts.totalDomains : undefined;
      if (Number.isFinite(totalDomains) && Number.isFinite(context.visibleSanCount)) {
        if (1 + context.visibleSanCount > totalDomains) {
          return { fieldId: 'san-container', token: copyOf(context, 'validation.san.tooManyDomains') };
        }
      }
      if (Array.isArray(context.sanRows)) {
        for (var s = 0; s < context.sanRows.length; s++) {
          var sanRow = context.sanRows[s];
          if (!sanRow || !sanRow.valid) {
            return { fieldId: (sanRow && sanRow.fieldId) || 'order-form', token: copyOf(context, 'validation.domain.primary.invalid') };
          }
          if (!sanRow.hasToken && !dcvHidden) {
            return { fieldId: sanRow.methodFieldId || 'order-form', token: copyOf(context, 'validation.dcv.methodRequired') };
          }
        }
      }
      return null;
    },
    // PRIMARY DCV method / approver : a chosen, tokenised method. SKIP-WHEN-ABSENT (a unit test that exercises a
    // later field passes no primaryDcv).
    dcv: function (self, context) {
      if (context.primaryDcv && !context.primaryDcv.hasToken) {
        return { fieldId: context.primaryDcv.fieldId || 'order-form', token: copyOf(context, 'validation.dcv.methodRequired') };
      }
      return null;
    },
    // END-USER CONTACT (first / last / e-mail). 'required' : presence + content + e-mail FORMAT. 'optional' :
    // content + format only on a FILLED e-mail (never blocks an empty one). LENGTH is checked before FORMAT so a
    // 300-char paste reports "too long" rather than "invalid" (the v1 order, defended PR #24 review #3).
    contact: function (self, context, state) {
      var values = context.values || {};
      if (state === 'required') {
        var presence = self._validateFieldList(values, SSL_ALWAYS_REQUIRED_FIELDS);
        if (!presence.ok) {
          return { fieldId: presence.firstMissing.id, token: copyOf(context, presence.firstMissing.tokenKey) };
        }
      }
      var content = self._validateContentList(values, SSL_ALWAYS_REQUIRED_FIELDS, context);
      if (!content.ok) {
        return content.error;
      }
      // EMAIL FORMAT (PR #32 review r6 #4) : this check lives INSIDE the contact validator now (it ran
      // UNCONDITIONALLY in v1). So contact-email format is verified only when the contact section is SHOWN
      // (fields.contact !== 'hidden', i.e. the loop ran this validator at all). Behaviour is IDENTICAL for every
      // real SSL/CaaS SKU (SSL : contact is always 'required' -> checked ; CaaS : there is no contact-email field).
      // A future S-MIME family that needs a subject e-mail format-checked must keep its contact / email field
      // non-hidden so this runs -- do NOT assume email is always format-checked just because v1 checked it.
      var email = values['contact-email'];
      if (typeof email === 'string' && email.trim() !== '' && !self.validateEmailFormat(email)) {
        return { fieldId: 'contact-email', token: copyOf(context, 'validation.contact.email') };
      }
      return null;
    },
    // ORGANISATION + ADDRESS block (one validator over SSL_ORG_REQUIRED_FIELDS = org-name + the 5 address
    // fields). 'required' (SSL / S-MIME OV-EV) : presence + content. 'optional' : content only on filled
    // fields, never blocks an empty block (no family uses it since 2026-10-18 : the CaaS-OV cards are hidden).
    org: function (self, context, state) {
      var values = context.values || {};
      if (state === 'required') {
        var presence = self._validateFieldList(values, SSL_ORG_REQUIRED_FIELDS);
        if (!presence.ok) {
          return { fieldId: presence.firstMissing.id, token: copyOf(context, presence.firstMissing.tokenKey) };
        }
      }
      var content = self._validateContentList(values, SSL_ORG_REQUIRED_FIELDS, context);
      if (!content.ok) {
        return content.error;
      }
      return null;
    },
    // ACME Account ID (CaaS extension) : non-empty + length + charset, all from config.validation['caas-acme-account']
    // (the single source). 'required' rejects an empty/invalid id ; an 'optional' value is checked only when filled
    // (future-proofing -- acmeAccount is required-or-hidden today).
    acmeAccount: function (self, context, state) {
      var values = context.values || {};
      var val = values['caas-acme-account'];
      var blank = typeof val !== 'string' || val.trim() === '';
      if (state !== 'required' && blank) {
        return null;
      }
      // A blank REQUIRED ACME reads "please provide" (consistent with ovAnchor), NOT "invalid"
      // (operator 2026-06-27) ; the INVALID token is reserved for a non-empty value that fails the rule.
      if (state === 'required' && blank) {
        return { fieldId: 'caas-acme-account', token: copyOf(context, 'validation.caas.acme.required') };
      }
      // The max length + charset pattern come from the validation map['caas-acme-account'] (context.validation in
      // production -- the single source -> config.validation ; self._validation when a test injected it), NOT a
      // hardcoded rule here.
      var acmeMap = context.validation || self._validation;
      if (!isValidAcmeAccountId(val, acmeMap && acmeMap['caas-acme-account'])) {
        return { fieldId: 'caas-acme-account', token: copyOf(context, 'validation.caas.acme.invalid') };
      }
      // SUBSCRIPTION CHECK (CaaS Subscription Register go-live, 2026-08-29 ; middleware-01
      // docs/CAAS-SUBSCRIPTION-REGISTER-DESIGN.md section 9). A required ACME may cart ONLY when the LATEST
      // middleware check answered eligible:true for the EXACT current value (context.subscriptionCheck, the
      // buildSubmitContext slice off state.subscriptionCheck). FAIL CLOSED on an absent slice : 'required' is
      // the descriptor-independent signal a check is mandatory (never the subscriptionCheck descriptor's own
      // presence -- the forbidden fail-open reading), so no verdict = no cart. The value equality is the
      // edit-invalidates rule's belt : resetPrimary clears the verdict on every edit event, and this compare
      // also covers a value changed with no event (programmatic set, bfcache restore). The check requirement
      // is keyed on state === 'required' exactly (acmeAccount is required-or-hidden today ; a hypothetical
      // 'optional' filled value keeps the local-rule-only path above, matching the section-9 detection rule).
      if (state === 'required') {
        var subCheck = context.subscriptionCheck;
        if (!subCheck || subCheck.eligible !== true || subCheck.value !== val.trim()) {
          return { fieldId: 'caas-acme-account', token: copyOf(context, 'validation.caas.acme.unchecked') };
        }
      }
      return null;
    },
    // Validation ID (ov-anchor-order, CaaS OV-new) : presence (when 'required') + the byte cap + the
    // digits-only charset rule, all from config.validation. The earlier "format deferred to Sectigo"
    // stance was SUPERSEDED by the operator decision of 2026-07-09 : this rule MIRRORS the middleware's
    // server-side rail (sanitizeValidationIdValue in middleware-01/src/utils/product-requirements.ts :
    // trim, then /^[0-9]{1,64}$/, warn-and-drop). The server stays authoritative ; this client mirror is
    // UX only (reject a typo at the field instead of "order accepted, then failed"). LOCKSTEP : if the
    // middleware rule widens (FLIP PRECONDITION block at PREVALIDATION_ADVERTISED_SKUS, same middleware
    // file), both sides change together.
    ovAnchor: function (self, context, state) {
      var values = context.values || {};
      var anchor = values['ov-anchor-order'];
      var blank = typeof anchor !== 'string' || anchor.trim() === '';
      if (state === 'required' && blank) {
        return { fieldId: 'ov-anchor-order', token: copyOf(context, 'validation.ovanchor.required') };
      }
      if (!blank) {
        // Max length from the validation map (context.validation in production -> config.validation ; self._validation
        // when a test injected it) ; the lengthKey (the "too long" copy) stays on the OV_ANCHOR_FIELD_RULES constant.
        // Length is checked BEFORE charset so an over-cap value keeps its specific "too long" message, and on the
        // TRIMMED value (PR #66 round 1) : the middleware rail trims before applying /^[0-9]{1,64}$/, so the exact
        // mirror measures what the server will measure (surrounding whitespace never tips a 64-digit value over).
        var anchorKey = fieldContentError(anchor.trim(), maxLengthOf(context.validation || self._validation, 'ov-anchor-order'), (FIELD_RULES_BY_ID['ov-anchor-order'] || {}).lengthKey);
        if (anchorKey) {
          // anchorKey is the ov-anchor lengthKey (a copy KEY) ; resolve to the message via context.copy (INC-4).
          return { fieldId: 'ov-anchor-order', token: copyOf(context, anchorKey) };
        }
        // Digits-only charset from the validation map's pattern, tested on the TRIMMED value exactly like
        // the middleware rail. Fail-closed when the map carries no pattern (the acmeAccount stance :
        // config.validation must supply the rule ; a form without it must not silently accept free text).
        var anchorRule = (context.validation || self._validation || {})['ov-anchor-order'];
        var anchorRe = anchoredRegExp(anchorRule && anchorRule.pattern);
        if (!anchorRe || !anchorRe.test(anchor.trim())) {
          return { fieldId: 'ov-anchor-order', token: copyOf(context, 'validation.ovanchor.invalid') };
        }
        // THE SERVER VERDICT (the Validation ID check, operator decision 2026-10-18) : a TYPED value, in EITHER
        // state, must hold the latest valid:true verdict for its exact trimmed value (context.validationIdCheck,
        // set only by wireValidationIdCheck's valid arm, cleared on every edit and every variant switch). Keyed
        // on non-blank rather than on 'required' (the acmeAccount rule above) because an optional typed value
        // must be checked too ; a blank optional value already passed above. FAIL CLOSED on an absent slice --
        // never on the descriptor's presence (the forbidden fail-open reading).
        var idCheck = context.validationIdCheck;
        if (!idCheck || idCheck.valid !== true || idCheck.value !== anchor.trim()) {
          return { fieldId: 'ov-anchor-order', token: copyOf(context, 'validation.ovanchor.unchecked') };
        }
      }
      return null;
    },
    // End User Telephone Number (middleware-01 commit a5b3879, operator rulings 2026-07-24) : the COMPOSED
    // value (buildSubmitContext injects values.telephone via composeTelephoneValue -- a DERIVED field, wiring
    // style (b) of order-form-v2-hidden-properties.liquid, so collectValues' direct-copy walk never sees it).
    // Registry semantics implemented IN FULL : 'required' = presence + content, 'optional' = content only when
    // filled (never blocks an empty one). fields.telephone is OPTIONAL-ONLY by design today (the parity brief),
    // and the complete implementation is the forward-posture that lets a future middleware 'required' flip ship
    // with ZERO theme edits. Content = length-then-pattern from config.validation['telephone'] (the single
    // source ; length FIRST so an over-cap value keeps its specific "too long" message : the ovAnchor order),
    // fail-closed when the map carries no pattern (the acmeAccount / ovAnchor stance). The server rail
    // (sanitizeTelephoneValue : malformed values railed out with a WARNING, the order proceeds phoneless) stays
    // authoritative ; this client mirror is UX only. LOCKSTEP note lives in order-form-v2-validation.liquid.
    telephone: function (self, context, state) {
      var values = context.values || {};
      var phone = values.telephone;
      var blank = typeof phone !== 'string' || phone.trim() === '';
      if (state === 'required' && blank) {
        return { fieldId: 'telephone', token: copyOf(context, 'validation.telephone.required') };
      }
      if (!blank) {
        var trimmedPhone = phone.trim();
        var phoneLengthKey = fieldContentError(trimmedPhone, maxLengthOf(context.validation || self._validation, 'telephone'), (FIELD_RULES_BY_ID['telephone'] || {}).lengthKey);
        if (phoneLengthKey) {
          return { fieldId: 'telephone', token: copyOf(context, phoneLengthKey) };
        }
        var phoneRule = (context.validation || self._validation || {}).telephone;
        if (!isValidTelephoneValue(trimmedPhone, phoneRule)) {
          return { fieldId: 'telephone', token: copyOf(context, 'validation.telephone.invalid') };
        }
      }
      return null;
    }
  };

  /**
   * The full submit gate (skeleton). Returns { ok, error } where error is
   * { fieldId, token } anchoring the failure to a field, matching the v1 "show
   * the message next to the FIRST failing field and stop" behaviour.
   *
   * CHECK ORDER MATCHES v1 (PR A round-10 finding) : the order in which checks
   * win is part of the parity contract, not just the message text, because v1
   * highlights the first failing field. Verified against
   * snippets/ssl-unified-order-form.liquid (deprecated, removed 2026-07-05):validateSSLForm, the v1 order is :
   *   primary-input -> SAN -> DCV method/approver -> always-required(presence)
   *   -> EMAIL FORMAT -> org-required(presence).
   * Note that v1 checks the EMAIL FORMAT BEFORE the org-required fields, so this
   * gate must too (a combined always+org pass would wrongly surface a missing org
   * field ahead of a malformed email on an OV/EV form).
   *
   * PR A implemented the self-contained subset (SAN-count, always-required, email
   * format, org-required) in that relative order. 3b NOW FILLS the reserved hooks
   * at their v1 positions : primary-input BEFORE the SAN check, per-SAN CONTENT
   * (the validateSan AND token-presence HARD requirement) right after the SAN-count
   * check, and the primary DCV method/approver AFTER the SAN section and BEFORE
   * always-required. Each is real (NOT faked) and SKIP-WHEN-ABSENT : the check fires
   * only when the assembler supplies its slice of the context, exactly as the
   * SAN-count check fires only when both counts are known. The assembler's submit
   * handler always supplies all three for a live SSL form ; the
   * gate-never-silently-weaker-than-v1 test pins that each fires at its v1 position.
   *
   * @param {Object} context
   * @param {Object} context.values            map of fieldId -> string value
   * @param {Object} [context.fields]          the server's per-field state map
   *   ('required' | 'optional' | 'hidden') ; the org-required gate reads context.fields.org
   * @param {string} context.validationLevel   'DV' | 'OV' | 'EV' (LABEL ONLY : no
   *   longer drives any field-requirement decision here ; see context.fields)
   * @param {number} [context.visibleSanCount] visible SAN rows
   * @param {number} [context.expectedSanCount] SKU-expected SAN count
   * @param {{valid:boolean, fieldId:string}} [context.primaryInput] active CSR/FQDN result
   * @param {Array<{valid:boolean, hasToken:boolean, fieldId:string, methodFieldId:string}>} [context.sanRows] per visible SAN row, in order
   * @param {{hasToken:boolean, fieldId:string}} [context.primaryDcv] primary DCV method/approver result
   * @returns {{ok:boolean, error:({fieldId:string, token:string}|null)}}
   */
  FormValidator.prototype.validate = function (context) {
    context = context || {};
    // The whole gate is the UNIFORM loop below, driven off the server's per-field `fields` states (NOT
    // context.validationLevel -- that is a human-readable label only). Each field's value is read by its
    // validator from context.values, so validate() keeps no local values copy.

    // NO per-family guard here (owner 2026-06-28 : the form is driven ENTIRELY by the SKU parser -- it makes NO
    // family decisions). An "unhandled" family is caught fail-CLOSED by GUARD B below, purely off the parser's
    // `fields` map : any field the parser marks 'required' that the gate has no validator for (e.g. S/MIME's
    // 'email' before its contract lands) rejects the submit. So a hardcoded family allow-list (the removed
    // VALIDATE_HANDLED_CERT_TYPES) is redundant -- the parser-driven coverage guard already refuses an ungated
    // required field, with no family literal in the form.

    // A.2 isSellable -- a SKU-level block. The assembler carries context.skuFacts (a slice of the live SKU
    // facts) ; only a SKU the middleware marks sellable may reach the cart.
    // FAIL CLOSED ON ABSENCE (owner 2026-07-01) : the middleware GUARANTEES isSellable is present as a boolean on
    // every /api/sku 200, so the gate now blocks unless it is EXPLICITLY true -- a missing / non-boolean isSellable
    // is a MALFORMED contract and must NOT sell. This replaces the earlier `=== false`-only read, which fail-OPENED
    // on absence : the one spot that contradicted this form's "a missing fact fails closed" rule. The `context
    // .skuFacts &&` guard is KEPT so a UNIT test exercising a LATER check without a skuFacts slice is still skipped
    // (SKIP-WHEN-ABSENT) ; in production the gate is only reached with facts present (onSubmit fail-closes on null
    // facts first). Fires EARLY (before the per-field checks) because it disqualifies the whole order regardless of
    // input ; the submit button is ALSO disabled in the wiring (applySellable, the SAME `!== true` rule) so the
    // shopper sees it before submit. Anchored to the primary field so the error has a visible home (no SKU field).
    if (context.skuFacts && context.skuFacts.isSellable !== true) {
      return { ok: false, error: { fieldId: (context.primaryInput && context.primaryInput.fieldId) || 'order-form', token: copyOf(context, 'validation.sku.notSellable') } };
    }

    // ── UNIFORM GATE ──────────────────────────────────────────────────────────────────────────────────────
    // ONE loop over GATE_FIELD_ORDER drives the WHOLE gate off the server's `fields` states -- no per-family
    // (SSL / CaaS) arms. See the FIELD_VALIDATORS docblock : 'hidden' (or absent) skips, otherwise the field's
    // validator runs with its state ('required' = presence + content ; 'optional' = content only if filled). The
    // order is deterministic + preserves the v1 first-error sequence (primary -> SAN -> DCV -> contact -> org) ;
    // the CaaS fields (acmeAccount / ovAnchor) sit at the end and are simply 'hidden' for an SSL form.
    var fields = context.fields || {};
    // GUARD A (org/address consistency) : org + address are ONE block -- the 'org' validator enforces
    // SSL_ORG_REQUIRED_FIELDS (org-name + the address fields) and the gate keys the block off fields.org alone.
    // The middleware ALWAYS ships fields.org === fields.address (ovBlockState) ; a divergence is a malformed
    // /api/sku that would let the loop silently DROP the un-read side (a fail-open on 'address'), so reject it.
    if ((fields.org || 'hidden') !== (fields.address || 'hidden')) {
      // BRANCH 2 (owner 2026-07-01) : org/address diverged in /api/sku fields = a malformed contract. WARN LOUD (same
      // family as the missing-fields / missing-subject warns) so it is diagnosable ; the customer still gets notPrepared.
      log.warn('ORDER FORM : /api/sku fields.org DOES NOT MATCH fields.address - MALFORMED CONTRACT, GATE FAILS CLOSED', { sku: context.skuFacts && context.skuFacts.sku, org: fields.org, address: fields.address });
      return { ok: false, error: { fieldId: 'order-form', token: copyOf(context, 'validation.gate.notPrepared') } };
    }
    // GUARD B (coverage) + FAIL-CLOSED FLOOR : every 'required' field must be one the gate actually ENFORCES
    // (GATE_ENFORCED_FIELDS) -- a 'required' field with no rule (e.g. csr / email before S-MIME wires them) would
    // cart UN-VALIDATED, a fail-open -- AND at least one field must be 'required' (an all-non-required map is a
    // malformed /api/sku). Both reject loudly : they are contract / build errors, not user-input ones. (Owner
    // 2026-06-28 : this floor was wrongly removed and is RESTORED -- every real product the parser describes
    // requires at least one value to identify it, so a zero-'required' map can only ever be malformed / degraded.)
    var hasRequired = false;
    for (var fk in fields) {
      if (Object.prototype.hasOwnProperty.call(fields, fk) && fields[fk] === 'required') {
        if (GATE_ENFORCED_FIELDS.indexOf(fk) === -1) {
          // BRANCH 3 (owner 2026-07-01) : /api/sku marks a field 'required' that the gate has NO validator for = an
          // un-enforceable contract (a fail-open the floor blocks). WARN LOUD (same family) ; customer gets notPrepared.
          log.warn('ORDER FORM : /api/sku MARKS A FIELD required THAT THE GATE CANNOT ENFORCE - MALFORMED CONTRACT, GATE FAILS CLOSED', { sku: context.skuFacts && context.skuFacts.sku, field: fk });
          return { ok: false, error: { fieldId: 'order-form', token: copyOf(context, 'validation.gate.notPrepared') } };
        }
        hasRequired = true;
      }
    }
    if (!hasRequired) {
      // BRANCH 4 (owner 2026-07-01) : an all-non-required /api/sku fields map = malformed/degraded (every real product
      // requires >= 1 value to identify it). WARN LOUD (same family) ; the customer still gets notPrepared.
      log.warn('ORDER FORM : /api/sku fields MAP HAS NO required FIELD - MALFORMED CONTRACT, GATE FAILS CLOSED', { sku: context.skuFacts && context.skuFacts.sku });
      return { ok: false, error: { fieldId: 'order-form', token: copyOf(context, 'validation.gate.notPrepared') } };
    }
    // FLOOR SCOPE NOTE (PR #32 review r6 #2) : the floor proves a field is MARKED 'required', NOT that it was
    // CHECKED. The per-field validators are skip-when-absent on their context slice (e.g. the domain validator
    // returns null when context.primaryInput is absent, by design, so unit tests can exercise later checks in
    // isolation). So the floor's safety DEPENDS on buildSubmitContext always supplying every required field's
    // slice -- which it does today (primaryInput is always set, values always collected). If a future required
    // field is ever added whose slice can be legitimately absent, gate its validator to fail CLOSED on the
    // missing slice rather than skip, or this floor would pass an unvalidated-but-required field.
    for (var gi = 0; gi < GATE_FIELD_ORDER.length; gi++) {
      var gateKey = GATE_FIELD_ORDER[gi];
      var gateState = fields[gateKey];
      // The PRIMARY (csr-or-fqdn) is described by TWO keys -- domain + csr -- but gated as ONE unit by the
      // 'domain' validator (primaryInput.valid covers either). Gate it on the STRONGER of the two so a SKU
      // shipping csr:'required' with domain:'hidden' STILL runs the primary check -- closing the fail-open where
      // csr's place in GATE_ENFORCED_FIELDS would otherwise be claimed coverage with no enforcement behind it.
      if (gateKey === 'domain') {
        gateState = strongestFieldState(fields.domain, fields.csr);
      }
      if (gateState === 'hidden' || !gateState) {
        continue; // not shown -> not gated (fail-closed default for an unknown / absent field)
      }
      var validator = FIELD_VALIDATORS[gateKey];
      if (!validator) {
        continue; // a reserved key (csr / email) with no validator yet
      }
      var gateErr = validator(this, context, gateState);
      if (gateErr) {
        return { ok: false, error: gateErr };
      }
    }
    return { ok: true, error: null };
  };

  /* =========================================================================
   * LATER-PR MODULES  -- filled IN PLACE as the build order reached each one
   * =========================================================================
   *
   * PR A defined each as a stub whose methods threw NOT_IMPLEMENTED (loud, not
   * silently wrong). PR B filled DomainNormalizer + CsrValidator, PR C filled
   * DcvManager + SanBuilder, and 3b filled the last two, SkuParser + UI -- all real
   * now, in the same ApiClient / DomainNormalizer style. NO module remains a stub,
   * so the NOT_IMPLEMENTED throw helper was retired with this fill (it had zero
   * callers once SkuParser + UI became real ; no-dead-code rule).
   */

  /* =========================================================================
   * DomainNormalizer  (REAL, PR B)
   * =========================================================================
   *
   * Cheap, PURE client-side helpers PLUS a thin async delegator to /api/domain/parse.
   * The heavy Public-Suffix-List work that v1 did in the browser (a runtime fetch of
   * the PSL from github + hand-rolled tldLists) is GONE : the authoritative parse is
   * /api/domain/parse. (The approver-email auth-domain levels used to come from
   * /api/domain/auth-domains via getAuthDomainLevels ; both were removed in the
   * 2026-06-28 migration -- the calculated approver set now arrives finished from the middleware.) The
   * pure helpers below are carried verbatim in behaviour from v1 :
   *   - normalize          <- v1 ValidationUtils.prepareDomain (trim + lowercase
   *                           ONLY ; NOT scheme/www/path stripping -- the API's
   *                           tldts.parse does scheme/path, and www / wildcards
   *                           are REAL parts of a cert domain and must be kept).
   *   - quickFormatCheck   <- v1 DomainUtils.validateDomain's local checks
   *                           (placeholder reject, wildcard-only-at-start, the RFC
   *                           format regex, >= 2 labels). It is a cheap PRE-check
   *                           so an obviously-bad input never fires an API call ;
   *                           the authoritative parse is parse().
   * Async methods DEGRADE GRACEFULLY (never throw, never block the customer) :
   * with no ApiClient (or on an API failure) parse() resolves to null, so a
   * caller treats "no parse" uniformly and the customer is never blocked.
   *
   * @param {ApiClient} [apiClient]
   * @constructor
   */
  function DomainNormalizer(apiClient) {
    this._api = apiClient || null;
  }

  /** Trim + lowercase, matching v1 prepareDomain. @param {string} input @returns {string} */
  DomainNormalizer.prototype.normalize = function (input) {
    if (typeof input !== 'string') {
      return '';
    }
    return input.trim().toLowerCase();
  };

  /**
   * Strip a leading "*." wildcard (the SUBMITTED domain keeps it). Used ONLY by quickFormatCheck's local
   * domain-format pre-check now : it strips the "*." so the RFC-ish label check runs on the base host. The
   * approver-email path STOPPED using this 2026-07-01 (PR #40) -- the middleware owns wildcard handling
   * per-endpoint, so the form sends the domain VERBATIM to BOTH approver endpoints (no client-side strip).
   * @param {string} domain @returns {string}
   */
  DomainNormalizer.prototype.stripWildcard = function (domain) {
    return (typeof domain === 'string' && domain.indexOf('*.') === 0) ? domain.substring(2) : domain;
  };

  /**
   * Re-apply a leading "*." when the server's verdict says the domain is a wildcard -- the INVERSE of
   * stripWildcard. The middleware /api/domain/parse builds its `normalized` field from the FULL input host
   * with ONLY the leading "*." removed -- it does NOT collapse to the registrable base (that is the SEPARATE
   * `baseDomain` field) -- while flagging `isWildcard:true`. So `normalized` for "*.sub.example.com" is
   * "sub.example.com" (every sub-label preserved), and re-applying "*." is exact for BOTH single-label
   * (*.example.com) AND nested (*.sub.example.com) wildcards -- no special-casing. (Owner-confirmed
   * 2026-06-25 against middleware-01 src/handlers/api/domain.ts, the /parse route's sanitisedNormalized
   * construction.) The SUBMITTED Primary FQDN and the on-card display must KEEP the wildcard (the certificate
   * secures "*.example.com" ; the approver-email path no longer strips it either -- owner 2026-07-01 (PR #40) :
   * both approver endpoints get the domain VERBATIM, the middleware owns wildcard handling per-endpoint, so
   * stripWildcard is now used ONLY by quickFormatCheck's local format pre-check).
   * Owner-reported 2026-06-24 : the secured-domain chip showed the base domain (e.g. trustico.com) for a "*."
   * order. Idempotent : never double-prefixes, so it is safe to call on a value that already carries the "*."
   * (a future server build, or a test double, that returns `normalized` WITH the wildcard).
   * @param {string} domain the server's `normalized` domain (the full host minus the leading "*.")
   * @param {boolean} isWildcard the server's wildcard verdict (parsed.data.isWildcard)
   * @returns {string} the wildcard-preserving display / submit form
   */
  DomainNormalizer.prototype.applyWildcard = function (domain, isWildcard) {
    // Guard empty / non-string too : never synthesize a bare "*." from nothing.
    if (!isWildcard || typeof domain !== 'string' || !domain) {
      return domain;
    }
    return domain.indexOf('*.') === 0 ? domain : ('*.' + domain);
  };

  /**
   * Cheap local format pre-check (mirrors v1 DomainUtils.validateDomain). The
   * AUTHORITATIVE PSL parse is parse() -> /api/domain/parse ; this only avoids
   * firing an API call on obvious garbage and rejects the known placeholders.
   *
   * SOFT HINT, NOT A HARD BLOCK (PR B round-2 note 3) : this is intentionally
   * strict like v1 -- a scheme-prefixed paste ("https://example.com") returns
   * FALSE here, even though normalize() deliberately leaves the scheme for the
   * server-side tldts.parse to strip. The Phase-3 wiring MUST therefore treat a
   * quickFormatCheck miss as a soft hint (still try parse() / the API, which
   * RECOVERS scheme/path/port pastes), not as a hard reject -- otherwise a
   * recoverable URL paste would never reach /api/domain/parse.
   * @param {string} domain
   * @returns {boolean}
   */
  DomainNormalizer.prototype.quickFormatCheck = function (domain) {
    if (typeof domain !== 'string') {
      return false;
    }
    var d = domain.trim().toLowerCase();
    if (!d) {
      return false;
    }
    // Reject the literal placeholders v1 rejected.
    if (d === 'example.com' || d === '*.example.com') {
      return false;
    }
    // A '*' is only valid as the leading "*." wildcard ; anywhere else is invalid.
    if (d.indexOf('*') !== -1 && d.indexOf('*.') !== 0) {
      return false;
    }
    var toCheck = this.stripWildcard(d);
    // RFC-ish label format. owner decision 2026-06-23 : the label classes ACCEPT non-ASCII (\u00A1-\uFFFF)
    // alongside a-z0-9-, so an INTERNATIONALISED (IDN / Unicode) SAN -- muenchen.de with an umlaut, etc. --
    // is no longer rejected here. This is a LENIENT local pre-check by design : the middleware is the
    // authority for IDN validation + the Unicode->punycode conversion at order submission (the form passes
    // the Unicode through ; the customer types the human form, the middleware converts). \u escapes keep
    // the source pure-ASCII (encoding-safe through deploy). The primary FQDN already accepts Unicode via the
    // server /api/domain/parse ; this brings the SAN local check into line so IDN works for SANs too.
    // SETTLED, do NOT tighten (PR #24 r6 #2, owner-confirmed 2026-06-23) : the reviewer flagged that this range
    // is WIDER than valid IDN U-labels (it admits BMP symbols / emoji). That is DELIBERATE and acknowledged as
    // NOT a defect -- the middleware is the authoritative Unicode->punycode validator (a nonsense SAN it cannot
    // convert is rejected there), and the value reaches the DOM only via textContent / the cart property, so it
    // is not an injection vector. Tightening here would duplicate the middleware AND risk wrongly rejecting a
    // legitimate IDN the middleware would accept ; the lenient-form / authoritative-middleware split is the design.
    // NOT A SPOOFING GUARD (PR #24 r12) : the range also admits bidi / zero-width / format chars (U+200E/202E,
    // U+FEFF, ...) that enable IDN HOMOGRAPH spoofing -- this local check does NOT defend against that, by design.
    // It is a shape pre-filter only ; the middleware's punycode conversion + Sectigo's IDN policy are the
    // authority that catches a deceptive / un-convertible label, and the value is textContent-only here (no sink).
    var domainRegex = /^(?!:\/\/)([a-zA-Z0-9\u00A1-\uFFFF]([a-zA-Z0-9\u00A1-\uFFFF-]{0,61}[a-zA-Z0-9\u00A1-\uFFFF])?\.)+[a-zA-Z0-9\u00A1-\uFFFF][a-zA-Z0-9\u00A1-\uFFFF-]{0,61}[a-zA-Z0-9\u00A1-\uFFFF]$/;
    if (!domainRegex.test(toCheck)) {
      return false;
    }
    // At least two labels (e.g. example.com), matching v1's parts-length check.
    if (toCheck.split('.').length < 2) {
      return false;
    }
    return true;
  };

  /** Authoritative PSL parse via /api/domain/parse. @param {string} domain @returns {Promise<ApiOutcome|null>} */
  DomainNormalizer.prototype.parse = function (domain) {
    if (!this._api) {
      return Promise.resolve(null);
    }
    // Defensive .catch (PR B round-2 note 2) : resolve to null on a rejection so
    // callers can treat "no parse" uniformly and never see an unhandled rejection.
    return this._api.parseDomain(this.normalize(domain)).catch(function () {
      return null;
    });
  };

  // getAuthDomainLevels was REMOVED (2026-06-28) : it fetched /api/domain/auth-domains SOLELY to feed the client-side
  // calculated-approver construction (the 5-prefixes x levels pairing). The calculated set now comes finished from the
  // middleware (GET /api/dcv/approver-emails-calculated), so the form no longer computes approver levels and no longer calls
  // /api/domain/auth-domains at all. (Verified before removal : that endpoint had no other consumer -- no DNS/HTTP DCV
  // record-placement use.)

  /* =========================================================================
   * CsrValidator  (REAL, PR B)
   * =========================================================================
   *
   * Replaces v1's ~250KB of browser crypto (node-forge + jsrsasign) and its
   * hand-rolled ASN.1 sniffing with one authoritative call to POST
   * /api/csr/validate (Sectigo DecodeCSR). quickFormatCheck is a cheap local gate
   * the CALLER runs first (validate() does not self-gate ; see its JSDoc) so
   * obvious garbage never fires the API call.
   *
   * NO-SANITISE (cutover-critical) : validate() sends the CSR text the customer
   * pasted (the caller is responsible only for trimming). The CSR is public-key +
   * metadata, NOT secret, but it MUST NOT be HTML-escaped or payload-mutated
   * (that would corrupt the PEM). The middleware decodes it ; the form writes the
   * raw CSR to properties[_CSR]. v1 origin : CSRUtils (validateCSR /
   * validateCSRFormat / extractCommonName) + the S/MIME CN-email extraction.
   *
   * @param {ApiClient} [apiClient]
   * @param {number} [maxBytes]  the CSR size cap (config.validation.csr.maxLength) ; the single source is
   *   order-form-v2-validation.liquid (owner 2026-06-29). The cap MATCHES the middleware's CSR byte limit so the form
   *   never rejects a CSR the backend would accept (or pass one it would reject on size). Real CSRs are 1-3 KB even
   *   at 4096-bit with long SANs, so this only sheds DoS-shaped payloads before the round-trip. The form value is
   *   ASCII (base64 + PEM armor), so string length is a faithful byte proxy. Undefined -> no size cap (the PEM-shape
   *   check + the Sectigo round-trip still apply).
   * @constructor
   */
  function CsrValidator(apiClient, maxBytes) {
    this._api = apiClient || null;
    this._maxBytes = typeof maxBytes === 'number' ? maxBytes : undefined;
  }

  /**
   * Cheap PEM-SHAPE gate, run BEFORE validate() so obvious junk never fires the Sectigo round-trip (owner
   * 2026-06-22 ; the middleware confirmed the form is the primary defence). MIRRORS the middleware's pre-Sectigo
   * shape check, NOT crypto : (1) non-empty after trim, (2) within the configured CSR byte cap, (3) '-----BEGIN' ANCHORED at
   * position 0 (so junk-prefixed blobs like '<html>...-----BEGIN...' are rejected) AND (4) '-----END' present.
   * Only the markers are checked, NOT the full label, so any armor variant (CERTIFICATE REQUEST / NEW
   * CERTIFICATE REQUEST / ...) passes -- lenient on purpose. No node-forge / jsrsasign : Sectigo (via validate())
   * stays the authoritative validator.
   * BEGIN-AT-0 CONFIRMED PARITY (PR #24 r10 #2, owner-confirmed 2026-06-23) : RFC 7468 technically permits
   * explanatory text BEFORE the boundary, but the middleware's pre-Sectigo check ALSO anchors at the start
   * (rejects junk-prefixed CSRs) -- so this client check MIRRORS it deliberately. Keep it strict : loosening to
   * allow a leading comment would let a CSR pass the form that the middleware then rejects (a worse, later fail).
   * @param {string} csr @returns {boolean}
   */
  CsrValidator.prototype.quickFormatCheck = function (csr) {
    if (typeof csr !== 'string') {
      return false;
    }
    var trimmed = csr.trim();
    if (!trimmed) {
      return false;
    }
    if (typeof this._maxBytes === 'number' && trimmed.length > this._maxBytes) { // cap from config.validation.csr (owner 2026-06-29)
      return false;
    }
    // indexOf(...) === 0 is the ES5-safe "starts with" (the UMD bundle avoids String.prototype.startsWith).
    return trimmed.indexOf('-----BEGIN') === 0 && trimmed.indexOf('-----END') !== -1;
  };

  /**
   * Authoritative CSR validation via POST /api/csr/validate (Sectigo DecodeCSR).
   * NO browser crypto. NO-SANITISE : the csr is forwarded as given. Resolves to an
   * ApiOutcome (never rejects) ; on success outcome.data carries the decoded
   * fields (commonName, organization, ..., keyLength, signatureAlgorithm).
   *
   * CALLER-GATED (round-7 review note 2) : validate() forwards UNCONDITIONALLY ; it
   * does NOT call quickFormatCheck itself. The cheap local "is this even a PEM"
   * pre-gate is the CALLER'S job (the Phase-3 wiring runs quickFormatCheck first and
   * only calls validate() when it passes), so obvious garbage never fires the API
   * call. Keeping the gate at the call site, not inside validate(), lets a caller
   * that has already checked the format avoid a redundant re-check.
   * @param {string} csr
   * @returns {Promise<ApiOutcome>}
   */
  CsrValidator.prototype.validate = function (csr) {
    if (!this._api) {
      // Distinct code from ApiClient's API_NO_BASE (PR B round-6 review note 2) :
      // the condition here is "the validator was constructed with NO ApiClient",
      // NOT "the ApiClient has no middleware base". A separate code lets log triage
      // tell a missing-wiring bug from a missing-base config, mirroring the
      // ApiClient API_NO_FETCH vs API_NO_BASE split (the message was already
      // accurate ; only the misnamed code changed).
      return Promise.resolve({ ok: false, status: 0, data: null, error: { code: 'CSR_NO_CLIENT', message: 'NO API CLIENT CONFIGURED' }, retryAfter: null });
    }
    // Defensive .catch (PR B round-2 note 2) : a rejection still resolves to a
    // uniform failure outcome, so the form can show a graceful error and the
    // customer is never left with an unhandled rejection.
    return this._api.validateCsr(csr).catch(function () {
      return { ok: false, status: 0, data: null, error: { code: 'NETWORK', message: 'CSR VALIDATION FAILED' }, retryAfter: null };
    });
  };

  /** Primary domain (CN) from a /api/csr/validate success body (SSL). @param {Object} data @returns {string} */
  CsrValidator.prototype.extractDomain = function (data) {
    return data && typeof data.commonName === 'string' ? data.commonName : '';
  };

  /**
   * The SIXTEEN CSR-rejection reasons POST /api/csr/validate can name in error.reason beside 400 CSR_INVALID
   * (middleware-01 docs/PUBLIC-API-REFERENCE.md section 6 ; the CSR_REASON_NAMES set in its src/handlers/api/csr.ts) :
   * every rejection a customer can act on by providing a different CSR. The middleware omits the field for a
   * rejection outside this family (its own request plumbing, an unknown upstream code), and a NEWER middleware may
   * name a reason this list does not know : both fall back to the generic line, so the allowlist fails closed to
   * the copy the form always had (owner decision 2026-09-01, after a live customer met the generic line on a
   * compromised-key rejection with no way to know a new key pair was the fix).
   */
  var CSR_REJECTION_REASONS = [
    'CSR_NO_WILDCARD_ALLOWED',
    'CSR_WILDCARD_REQUIRED',
    'INVALID_COUNTRY',
    'CSR_MISSING_FIELD',
    'CSR_INVALID_BASE64',
    'CSR_CANNOT_DECODE',
    'CSR_UNSUPPORTED_ALGORITHM',
    'CSR_INVALID_SIGNATURE',
    'CSR_UNSUPPORTED_KEY_SIZE',
    'INTRANET_NOT_FQDN',
    'DOMAIN_IS_IP',
    'CSR_COMMON_NAME_HAS_HTTP_PREFIX',
    'CSR_COMMON_NAME_HAS_HTTPS_PREFIX',
    'CSR_COMMON_NAME_INVALID_CHARS',
    'CSR_KEY_COMPROMISED',
    'CSR_INVALID_KEY'
  ];

  // Reason code -> customer copy KEY, the caas.check.reason.<code lowercased> idiom : one key per reason in
  // order-form-v2-copy.liquid (ssl.csr.error.reason.csr_key_compromised etc.), emitted to config.copy by
  // order-form-v2-config.liquid. Returns null for an unknown / absent code (see CSR_REJECTION_REASONS), and the
  // caller then shows ssl.csr.error.generic.
  function csrRejectionReasonKey(reason) {
    if (typeof reason !== 'string' || CSR_REJECTION_REASONS.indexOf(reason) === -1) {
      return null;
    }
    return 'ssl.csr.error.reason.' + reason.toLowerCase();
  }

  /* =========================================================================
   * SubscriptionChecker  (CaaS Subscription Register go-live, 2026-08-29)
   * =========================================================================
   *
   * The CaaS-extension eligibility check (middleware-01 docs/CAAS-SUBSCRIPTION-REGISTER-DESIGN.md section 9) :
   * POSTs { acmeAccountId, sku } to the endpoint the /api/sku descriptor names (root `subscriptionCheck.endpoint`)
   * and resolves the middleware's decided verdict. The THIRD instance of the button-triggered middleware-check
   * module shape (CsrValidator, DcvManager are the precedents). The form RENDERS the answer, never interprets it :
   * every eligibility decision (the eight wire reason codes, the window, the product match) is the middleware's.
   *
   * FAIL-CLOSED CONTRACT (the form is the ONLY gate on this path -- section 9) : the form knows a check is
   * required from fields.acmeAccount === 'required' ALONE, never from the descriptor's presence. A missing
   * endpoint therefore resolves a FAILURE outcome here (SUBSCRIPTION_NO_ENDPOINT) rather than skipping the
   * check : the caller renders the try-again state and submission stays blocked. Reachable, not theoretical --
   * a stale /api/sku body (cached descriptor-less config after the middleware deploy) or a middleware rollback.
   *
   * @param {ApiClient} [apiClient]
   * @constructor
   */
  function SubscriptionChecker(apiClient) {
    this._api = apiClient || null;
  }

  /**
   * Run the subscription check. Resolves an ApiOutcome (never rejects) ; on ok the middleware's decided body is
   * outcome.data : { eligible: true, productName?, expiresAt } or { eligible: false, reason, ... } (nullable keys
   * are OMITTED on the wire, never sent null). Every DECIDED outcome is HTTP 200 ; non-200s (400 / 429 / 5xx /
   * a bare 404 from an unmounted route) surface as ok:false outcomes the caller maps to its message states.
   * @param {string|null} endpoint the /api/sku descriptor's subscriptionCheck.endpoint (null/absent = fail closed)
   * @param {string} acmeAccountId the trimmed field value
   * @param {string} sku the SKU being ordered (state.facts.sku, the server-canonical echo)
   * @returns {Promise<ApiOutcome>}
   */
  SubscriptionChecker.prototype.check = function (endpoint, acmeAccountId, sku) {
    if (!this._api) {
      // Mirror of CSR_NO_CLIENT (CsrValidator.validate) : constructed with no ApiClient = a wiring bug,
      // distinct from API_NO_BASE (a missing origin config) for log triage.
      return Promise.resolve({ ok: false, status: 0, data: null, error: { code: 'SUBSCRIPTION_NO_CLIENT', message: 'NO API CLIENT CONFIGURED' }, retryAfter: null });
    }
    if (typeof endpoint !== 'string' || endpoint === '') {
      // The fail-closed descriptor case (section 9) : fields.acmeAccount said 'required' but /api/sku carried no
      // subscriptionCheck at root. Resolve a failure OUTCOME so the caller's non-200 arm renders the try-again
      // state -- never a silent skip (that is the forbidden fail-open reading).
      return Promise.resolve({ ok: false, status: 0, data: null, error: { code: 'SUBSCRIPTION_NO_ENDPOINT', message: 'NO SUBSCRIPTION CHECK ENDPOINT IN /api/sku DESCRIPTOR' }, retryAfter: null });
    }
    // Defensive .catch (the CsrValidator.validate posture) : a rejection still resolves to a uniform failure
    // outcome, so the form shows a graceful try-again and never an unhandled rejection.
    return this._api.checkCaasSubscription(endpoint, acmeAccountId, sku).catch(function () {
      return { ok: false, status: 0, data: null, error: { code: 'NETWORK', message: 'SUBSCRIPTION CHECK FAILED' }, retryAfter: null };
    });
  };

  // The COMPLETE wire reason set (middleware-01 src/services/caas-extension-decision.ts CaasCheckWireRefusalReason,
  // cross-checked against docs/CAAS-SUBSCRIPTION-REGISTER-DESIGN.md section 9 : eight codes, no drift). An answer
  // carrying any OTHER reason maps to null -> the caller renders the try-again state (fail closed on an unknown
  // code : the form renders the middleware's decided set, it never invents a message for a code it does not know).
  var CAAS_CHECK_REASONS = [
    'SUBSCRIPTION_NOT_FOUND',
    'WRONG_PRODUCT',
    'OUTSIDE_WINDOW',
    'SUBSCRIPTION_CANCELLED',
    'SUBSCRIPTION_INACTIVE',
    'EMPTY_SUBSCRIPTION',
    'MIXED_SUBSCRIPTION',
    'HOLDINGS_EXCEED_PRODUCT'
  ];

  // Reason code -> customer copy KEY, the dcv.method.label.<method lowercased> lookup idiom : one key per
  // outcome in order-form-v2-copy.liquid (caas.check.reason.subscription_not_found etc.), emitted to config.copy
  // by order-form-v2-config.liquid. Returns null for an unknown / absent code (see CAAS_CHECK_REASONS).
  function caasCheckReasonKey(reason) {
    if (typeof reason !== 'string' || CAAS_CHECK_REASONS.indexOf(reason) === -1) {
      return null;
    }
    return 'caas.check.reason.' + reason.toLowerCase();
  }

  // A check date for DISPLAY : "16 JUL 2026" (owner ruling 2026-08-29, replacing the first draft's bare ISO
  // "2026-07-16" -- "it is better for customers to see a date like 16 JUL 2026"). The middleware serialises
  // epoch seconds to full ISO-8601 UTC strings (expiresAt / windowOpensAt / windowClosesAt) ; the time-of-day
  // carries no customer meaning for a licence expiry, so only the DATE part renders. The month names are
  // customer-visible text, so they live in order-form-v2-copy (caas.check.date.months, a space-separated
  // twelve-entry set read via config.copy -- the every-string-in-the-copy-master rule), split here. FAIL
  // CLOSED on a missing / short month set : no invented month text (the no-content-fallback rule) -- the
  // emit-superset + copy-non-empty contract tests keep the key present in practice.
  function caasCheckDate(config, iso) {
    var m = typeof iso === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
    if (!m) {
      return '';
    }
    var months = copyOf(config, 'caas.check.date.months').split(' ');
    var monthName = months[Number(m[2]) - 1] || '';
    if (!monthName) {
      return '';
    }
    return Number(m[3]) + ' ' + monthName + ' ' + m[1];
  }

  // The product-being-refused / eligible detail, LABELED (owner-approved rendering 2026-08-29, superseding the
  // first draft's bare values -- the operator called the unlabeled date pair confusing) :
  //   "<productName> - EXPIRY : <16 JUL 2026>[ - EXTENSION WINDOW : <17 APR 2026> TO <15 AUG 2026>]
  //    [ - <held label> : <5>]"
  // Each part is omitted when its value is absent (the wire OMITS null keys, never sends null) ; the window
  // part renders on OUTSIDE_WINDOW alone (the only reason carrying the pair), and the held-count part on
  // HOLDINGS_EXCEED_PRODUCT alone (the only reason carrying heldDomains : Build 2, section 13 D2). Every WORD --
  // the EXPIRY / EXTENSION WINDOW / TO / held labels and the month names -- lives in order-form-v2-copy
  // (content-centralization) ; this function only joins labels and values (" : " is the house label-value pair
  // form ; " - " the part join).
  function caasCheckDetailText(config, data) {
    if (!data) {
      return '';
    }
    var parts = [];
    if (typeof data.productName === 'string' && data.productName) {
      parts.push(data.productName);
    }
    var expires = caasCheckDate(config, data.expiresAt);
    if (expires) {
      parts.push(copyOf(config, 'caas.check.detail.expiry') + ' : ' + expires);
    }
    if (data.reason === 'OUTSIDE_WINDOW') {
      var opens = caasCheckDate(config, data.windowOpensAt);
      var closes = caasCheckDate(config, data.windowClosesAt);
      if (opens && closes) {
        parts.push(copyOf(config, 'caas.check.detail.window') + ' : ' + opens + ' ' + copyOf(config, 'caas.check.detail.window.to') + ' ' + closes);
      }
    }
    // Build 2 (section 13 D2, operator decision 2026-08-30) : HOLDINGS_EXCEED_PRODUCT alone carries heldDomains,
    // the paid count the covering variant must reach, disclosed so the customer can pick that variant. One
    // integer, rendered as the same LABEL : VALUE pair as the expiry ; keyed on the reason (never on the key's
    // mere presence) so a count on any other reason is ignored, exactly as the window pair is.
    if (data.reason === 'HOLDINGS_EXCEED_PRODUCT' && Number.isInteger(data.heldDomains)) {
      parts.push(copyOf(config, 'caas.check.detail.held') + ' : ' + data.heldDomains);
    }
    return parts.join(' - ');
  }

  // One outcome -> ONE rendered message string for the acme error region, resolved ENTIRELY from config.copy :
  //   - 200 { eligible: false, reason } -> that reason's message ; refusals other than SUBSCRIPTION_NOT_FOUND
  //     also name the product being refused (the LABELED caasCheckDetailText appended after ' - ', the
  //     owner-approved 2026-08-29 rendering) per section 9. HOLDINGS_EXCEED_PRODUCT has TWO messages, chosen
  //     off the ordered product's isMultiDomain fact (`facts`, the third argument : see the note in the body).
  //   - an UNKNOWN reason -> try-again, warned loudly (fail closed ; see CAAS_CHECK_REASONS).
  //   - HTTP 400 with a VALUE code (FIELD_WRONG_TYPE / MISSING_FIELDS / FIELD_TOO_LONG on error.field acmeAccountId,
  //     middleware-01 docs/PUBLIC-API-REFERENCE.md section 8) -> the check-the-value message. Unreachable while the
  //     theme's field rule honours the 64-byte-bound invariant, implemented anyway (section 9 : defined so the form
  //     never proceeds unchecked).
  //   - HTTP 400 SKU_UNKNOWN (error.field sku) is NOT the value's fault : a parseable extension SKU off the sell
  //     list still gets its card and its check (/api/sku answers 200 with isSellable false and only the submit is
  //     disabled), so it renders try-again rather than send the customer back to a correct value (the Validation
  //     ID check's validationIdCheckFailureText rule, 2026-10-18 ; section 9 carves SKU_UNKNOWN out the same day).
  //   - EVERYTHING else -- 503 / 500 / 504 / 429, a bare 404 (unmounted route), network / timeout, the
  //     fail-closed SUBSCRIPTION_NO_ENDPOINT descriptor outcome -> the try-again message.
  function caasCheckFailureText(config, outcome, facts) {
    var data = outcome && outcome.ok ? outcome.data : null;
    if (data && data.eligible === false) {
      var reasonKey = caasCheckReasonKey(data.reason);
      if (reasonKey) {
        // Build 2 (docs/CAAS-MULTI-DOMAIN-DESIGN.md section 13 D11, operator decision 2026-08-30 ; wording
        // 2026-09-02) : HOLDINGS_EXCEED_PRODUCT means two things. On a MULTI-DOMAIN subscription the customer picks
        // the variant that covers the chargeable count (the CHARGEABLE DOMAINS : N detail names it), so the message
        // only reports ; on a SINGLE-SITE one, whose family has no bigger variant, the answer stays contact us. The
        // wire sends ONE reason for both and the form decides nothing : on this reason the ordered product's family
        // always equals the subscription's (WRONG_PRODUCT is refused first, middleware-01 section 5.3 row 7), so the
        // split reads the ORDERED product's own isMultiDomain fact from /api/sku. Absent facts fall to the
        // single-site message : a contact line is never a wrong instruction, and the refusal is never skipped.
        if (data.reason === 'HOLDINGS_EXCEED_PRODUCT' && facts && facts.isMultiDomain === true) {
          reasonKey = 'caas.check.reason.holdings_exceed_product.multidomain';
        }
        var detail = caasCheckDetailText(config, data);
        var message = copyOf(config, reasonKey);
        return detail ? message + ' - ' + detail : message;
      }
      log.warn('ORDER FORM : SUBSCRIPTION CHECK ANSWERED AN UNKNOWN REASON CODE - RENDERING TRY-AGAIN, SUBMIT STAYS BLOCKED', { reason: data && data.reason });
      return copyOf(config, 'caas.check.tryagain');
    }
    if (outcome && outcome.status === 400 && !(outcome.error && outcome.error.code === 'SKU_UNKNOWN')) {
      return copyOf(config, 'caas.check.badvalue');
    }
    return copyOf(config, 'caas.check.tryagain');
  }

  /**
   * =========================================================================
   * ValidationIdChecker  (the Validation ID check, operator decision 2026-10-18)
   * =========================================================================
   *
   * The pre-submission check of a Validation ID (middleware-01 docs/PUBLIC-API-REFERENCE.md section 10) : POSTs
   * { validationId, sku } to the endpoint the /api/sku descriptor names (root `validationIdCheck.endpoint`) and
   * resolves the middleware's decided verdict. The FOURTH instance of the button-triggered middleware-check module
   * shape (CsrValidator, DcvManager, SubscriptionChecker are the precedents). The form RENDERS the answer, never
   * interprets it : whether the record exists, is active, is unexpired and is the right kind for the product is the
   * middleware's decision, made against the Certificate Authority's record.
   *
   * FAIL-CLOSED CONTRACT (the SubscriptionChecker's, one card over) : the form knows a typed value must be checked
   * from the ovAnchor gate validator ALONE, never from the descriptor's presence. A missing endpoint resolves a
   * FAILURE outcome here (VALIDATION_ID_NO_ENDPOINT) rather than skipping the check : the caller renders the
   * try-again state and submission stays blocked.
   *
   * @param {ApiClient} [apiClient]
   * @constructor
   */
  function ValidationIdChecker(apiClient) {
    this._api = apiClient || null;
  }

  /**
   * Run the Validation ID check. Resolves an ApiOutcome (never rejects) ; on ok the middleware's decided body is
   * outcome.data : { valid: true, type, activeFrom?, expiresAt? } or { valid: false, reason, type?, expiresAt? }
   * (a date the CA has not set is OMITTED, never sent null). Every DECIDED outcome is HTTP 200 ; non-200s (400 /
   * 429 / 503 / a bare 404 from an unmounted route) surface as ok:false outcomes the caller maps to its messages.
   * @param {string|null} endpoint the /api/sku descriptor's validationIdCheck.endpoint (null/absent = fail closed)
   * @param {string} validationId the trimmed field value
   * @param {string} sku the SKU being ordered (state.facts.sku, the server-canonical echo)
   * @returns {Promise<ApiOutcome>}
   */
  ValidationIdChecker.prototype.check = function (endpoint, validationId, sku) {
    if (!this._api) {
      return Promise.resolve({ ok: false, status: 0, data: null, error: { code: 'VALIDATION_ID_NO_CLIENT', message: 'NO API CLIENT CONFIGURED' }, retryAfter: null });
    }
    if (typeof endpoint !== 'string' || endpoint === '') {
      // The fail-closed descriptor case : the card is shown but /api/sku carried no validationIdCheck at root (a
      // stale cached body from before the middleware deploy, or a rollback). A failure OUTCOME, never a silent skip.
      return Promise.resolve({ ok: false, status: 0, data: null, error: { code: 'VALIDATION_ID_NO_ENDPOINT', message: 'NO VALIDATION ID CHECK ENDPOINT IN /api/sku DESCRIPTOR' }, retryAfter: null });
    }
    return this._api.checkValidationId(endpoint, validationId, sku).catch(function () {
      return { ok: false, status: 0, data: null, error: { code: 'NETWORK', message: 'VALIDATION ID CHECK FAILED' }, retryAfter: null };
    });
  };

  // The COMPLETE wire reason set (middleware-01 src/services/prevalidation-decision.ts PrevalidationRefusalReason :
  // four codes). An answer carrying any OTHER reason maps to null -> the caller renders the try-again state (fail
  // closed on an unknown code, the CAAS_CHECK_REASONS posture).
  var VALIDATION_ID_CHECK_REASONS = ['NOT_FOUND', 'NOT_ACTIVE', 'EXPIRED', 'WRONG_TYPE'];

  // Reason code -> customer copy KEY ('ovanchor.check.reason.' + the code lowercased, the caasCheckReasonKey idiom :
  // one key per outcome in order-form-v2-copy.liquid, emitted to config.copy by order-form-v2-config.liquid).
  function validationIdCheckReasonKey(reason) {
    if (typeof reason !== 'string' || VALIDATION_ID_CHECK_REASONS.indexOf(reason) === -1) {
      return null;
    }
    return 'ovanchor.check.reason.' + reason.toLowerCase();
  }

  // The LABELED detail appended to a verdict : "VALID UNTIL : 11 APR 2027" when the answer carries expiresAt
  // (caasCheckDate renders the date from the same month set the ACME check uses). The organisation is never on the
  // wire, so there is nothing else to show : the label lives in order-form-v2-copy (ovanchor.check.detail.expiry).
  function validationIdCheckDetailText(config, data) {
    if (!data) {
      return '';
    }
    var expires = caasCheckDate(config, data.expiresAt);
    return expires ? copyOf(config, 'ovanchor.check.detail.expiry') + ' : ' + expires : '';
  }

  // One outcome -> ONE rendered message for the card's error region, resolved ENTIRELY from config.copy (the
  // caasCheckFailureText shape) : a 200 { valid: false, reason } renders that reason's message plus the expiry
  // detail when the answer carries one ; an unknown reason warns and renders try-again ; HTTP 400 with a VALUE code
  // (VALIDATION_ID_INVALID and the body-shape codes of section 10 : unreachable while the card's local rule mirrors
  // the 64-digit rail) renders check-the-value ; HTTP 400 SKU_UNKNOWN is NOT the value's fault (a parseable SKU off
  // the sell list still gets its card and its button : /api/sku answers 200 with isSellable false and only the submit
  // is disabled), so it renders try-again rather than send the customer back to a correct value ; everything else
  // (503 / 500 / 504 / 429, a bare 404, network, the fail-closed no-descriptor outcome) try-again.
  function validationIdCheckFailureText(config, outcome) {
    var data = outcome && outcome.ok ? outcome.data : null;
    if (data && data.valid === false) {
      var reasonKey = validationIdCheckReasonKey(data.reason);
      if (reasonKey) {
        var detail = validationIdCheckDetailText(config, data);
        var message = copyOf(config, reasonKey);
        return detail ? message + ' - ' + detail : message;
      }
      log.warn('ORDER FORM : VALIDATION ID CHECK ANSWERED AN UNKNOWN REASON CODE - RENDERING TRY-AGAIN, SUBMIT STAYS BLOCKED', { reason: data && data.reason });
      return copyOf(config, 'ovanchor.check.tryagain');
    }
    if (outcome && outcome.status === 400 && !(outcome.error && outcome.error.code === 'SKU_UNKNOWN')) {
      return copyOf(config, 'ovanchor.check.badvalue');
    }
    return copyOf(config, 'ovanchor.check.tryagain');
  }

  /**
   * SkuParser (3b, real). Resolves a SKU's facts from GET /api/sku/:sku via
   * ApiClient.getSkuFacts and caches them BY SKU STRING so a variant switch back to
   * a previously-seen SKU is instant (no re-fetch). The returned facts object is the
   * single source of truth that drives form rendering. The server's per-field `fields`
   * descriptor ('required' | 'optional' | 'hidden' per section) drives WHICH sections
   * render and which the submit gate requires (org / address via fields.org / .address ;
   * the SAN section via fields.san). The remaining facts are data / labels : validationLevel
   * (DV/OV/EV, LABEL only now -- no longer derives showOrg/showAddress), isWildcard /
   * isMultiDomain / isWildcardMultiDomain, totalDomains / sanCount (-> SAN row count),
   * isSellable (-> submit gate), requiresOrgInfo / requiresCSR, dcvMethods.
   *
   * v1 origin : the whole skuCache (computeValidationLevel / computeIsWildcard /
   * computeIsMultiDomain / computeDomainCounts -- the brittle positional SKU split)
   * PLUS the duplicated VariantHandler + init re-parse all collapse into this one
   * server-authoritative lookup. The form never parses the SKU string itself.
   *
   * The cache is module-scoped (one OrderForm instance per page) ; a SINGLETON like
   * the other stub-replaced modules. Tests call clearCache() in beforeEach so the
   * shared cache never leaks between cases (Jest clearMocks does not touch it).
   */
  var SkuParser = (function () {
    // sku string -> RESOLVED (deep-frozen) facts object. Reassigned (not mutated) by
    // clearCache so the closure keeps referencing the same _cache binding. NOTE
    // (PR#16 r4 obs 3) : the cache is keyed by SKU ALONE, not by apiClient. That is
    // correct under the one-OrderForm-per-page assumption (a single config / API base ;
    // see snippets/order-form-v2-config.liquid) but it is NOT safe to share across two
    // clients with different middleware bases -- a hypothetical multi-form page would
    // need a per-client cache, the same scoping caveat the single-global config carries.
    var _cache = {};
    // sku string -> the IN-FLIGHT getFacts promise (PR#16 review obs 3). A second
    // getFacts for the same uncached SKU joins the pending request instead of firing
    // a duplicate API call (the collapse v1's VariantHandler did ; apiBurst-friendly).
    // Cleared per-entry as each request settles, and wholesale by clearCache.
    var _inflight = {};

    // Recursively freeze the facts object + its nested arrays/objects so the cached,
    // shared-by-reference facts are FULLY read-only (PR#16 r4 obs 1 : a shallow
    // Object.freeze leaves facts.dcvMethods / requiredFields mutable, so a consumer
    // doing facts.dcvMethods.push(...) would corrupt the shared cache for everyone).
    // SKU facts are plain JSON (no cycles), so the recursion terminates.
    function deepFreeze(obj) {
      Object.freeze(obj);
      Object.keys(obj).forEach(function (key) {
        var val = obj[key];
        if (val !== null && typeof val === 'object' && !Object.isFrozen(val)) {
          deepFreeze(val);
        }
      });
      return obj;
    }

    /**
     * @param {string} sku
     * @param {ApiClient} apiClient
     * @returns {Promise<Object|null>} the facts object, or null when the SKU is
     *   blank, the apiClient is missing, or the lookup fails. The assembler degrades :
     *   a failure is NOT cached, so a later retry can still succeed.
     */
    function getFacts(sku, apiClient) {
      // GUARD (PR#16 review obs 1) : a blank SKU or a missing apiClient degrades to
      // null rather than throwing -- matches the never-blow-up-the-form posture of
      // DomainNormalizer / CsrValidator (which guard a missing _api the same way).
      if (!sku || !apiClient) {
        return Promise.resolve(null);
      }
      if (Object.prototype.hasOwnProperty.call(_cache, sku)) {
        return Promise.resolve(_cache[sku]);
      }
      // Join an in-flight request for the same SKU instead of starting a second.
      if (Object.prototype.hasOwnProperty.call(_inflight, sku)) {
        return _inflight[sku];
      }
      var pending = apiClient.getSkuFacts(sku).then(function (outcome) {
        // EPOCH GUARD (PR#16 round-2 obs 2) : `_inflight[sku] === pending` is the
        // per-request token. It is true only if THIS request is still the current one
        // for `sku` -- a clearCache() (which empties _inflight) followed by a new
        // getFacts() / refresh() would have replaced _inflight[sku] with a newer
        // pending. We use it to gate BOTH the in-flight clear AND the cache write, so
        // a superseded OLD request that settles late cannot overwrite the newer
        // request's cache entry with stale data. Either way this call's own awaiters
        // still receive the fetched data (they asked for it) ; only the shared cache
        // is protected.
        var isCurrent = _inflight[sku] === pending;
        if (isCurrent) {
          delete _inflight[sku];
        }
        // Cache ONLY a successful lookup with data, AND only while still current. A
        // failed outcome returns null and is NOT cached, so a retry re-attempts rather
        // than serving a poisoned null forever.
        if (outcome && outcome.ok && outcome.data) {
          // READ-ONLY single source of truth (PR#16 r3 point 2 + r4 obs 1). The facts
          // are cached BY REFERENCE and shared by every awaiter, so a consumer mutating
          // them would corrupt the cache for everyone. deepFreeze enforces it for the
          // top-level AND the nested arrays (dcvMethods / requiredFields) -- a plain
          // Object.freeze is shallow and would leave those arrays mutable. A stray write
          // throws under 'use strict'.
          //
          // PLACEMENT (PR#16 r5 obs 1/2) : freezing IN PLACE is safe because
          // ApiClient._request returns a freshly-parsed object per call, so SkuParser
          // owns outcome.data and nothing else retains the reference (if _request ever
          // memoised a response, freeze a copy instead). And the freeze runs BEFORE the
          // isCurrent check ON PURPOSE : every awaiter -- including a superseded,
          // late-settling request that does NOT populate the cache -- still receives a
          // read-only object. Freezing a superseded throwaway is negligible work.
          // CONSISTENCY INVARIANT (PR#17 r10 nit b ; re-keyed 2026-09-02, operator ruling, for Build 2 of
          // docs/CAAS-MULTI-DOMAIN-DESIGN.md section 5.1) : the form shows the Additional Domains card off
          // fields.san (applyFieldVisibility) and builds its rows off sanCount (setupSan -> rebuildToCount), so
          // those two server facts must agree -- a card with no rows, or rows built inside a hidden card (their
          // empty values would still be written to the cart), is a malformed /api/sku. Warn LOUD so an upstream
          // mis-configuration is diagnosable (the gate still fails closed). The check used to pair isMultiDomain
          // with sanCount : since Build 2 a CaaS multi-domain EXTENSION SKU is isMultiDomain:true with sanCount 0
          // BY DESIGN (its count segment is the number of domains the extension covers, never a SAN allowance),
          // the form's only other read of isMultiDomain is the HOLDINGS_EXCEED_PRODUCT message split in
          // caasCheckFailureText (a render fact of the ordered product, no coupling to sanCount), and
          // card-versus-rows is the coupling the form actually has. ONE universal rule over every product shape ;
          // no family branch, no SKU string.
          var _sanShown = !!(outcome.data.fields && outcome.data.fields.san && outcome.data.fields.san !== 'hidden');
          var _sc = outcome.data.sanCount;
          if (_sanShown !== (Number.isFinite(_sc) && _sc > 0)) {
            log.warn('ORDER FORM : SKU FACTS INCONSISTENT - fields.san DOES NOT MATCH sanCount', { sku: sku, sanShown: _sanShown, sanCount: _sc });
          }
          var facts = deepFreeze(outcome.data);
          if (isCurrent) {
            _cache[sku] = facts;
          }
          return facts;
        }
        // BRANCH 5 (owner 2026-07-01) : the GET /api/sku CALL itself FAILED (non-200 / no data) -> facts null -> the
        // form fails closed at submit. A DISTINCT diagnosis from a missing/malformed FACT (a 200 with a contract gap) :
        // the API is unreachable or errored, NOT misconfigured, so it gets its OWN message (an operator reads "the
        // endpoint is down", not "the contract is wrong") emitted HERE at the fetch site where the status + error are in
        // hand -- never conflated with the /api/sku-contract-gap warns.
        log.warn('ORDER FORM : GET /api/sku FETCH FAILED (non-200 / no data) - SKU FACTS UNAVAILABLE, SUBMIT FAILS CLOSED', { sku: sku, status: outcome && outcome.status, error: outcome && outcome.error });
        return null;
      }, function () {
        // ApiClient.getSkuFacts never rejects (it degrades to a failure OUTCOME), so
        // this arm is defensive. Clear the in-flight slot (epoch-guarded as above) and
        // DEGRADE TO NULL -- never throw, consistent with the blank-sku / missing-client
        // / failed-outcome paths above (a retry re-fires since nothing was cached).
        if (_inflight[sku] === pending) {
          delete _inflight[sku];
        }
        return null;
      });
      _inflight[sku] = pending;
      return pending;
    }

    /**
     * Force a fresh lookup for `sku`, bypassing + replacing any cached entry.
     * @param {string} sku
     * @param {ApiClient} apiClient
     * @returns {Promise<Object|null>}
     */
    function refresh(sku, apiClient) {
      if (sku) {
        delete _cache[sku];
        // Also drop any IN-FLIGHT request (PR#16 round-2 obs 1) so refresh truly forces
        // a NEW fetch rather than coalescing with an outstanding one -- otherwise
        // getFacts() below would join the pending request and refresh would neither
        // bypass nor replace, contradicting this JSDoc. The epoch guard in getFacts's
        // settle means the dropped old request cannot clobber the new one's slot/cache.
        delete _inflight[sku];
      }
      return getFacts(sku, apiClient);
    }

    /** Empty the cache AND drop any in-flight requests (test isolation + a hard
     *  variant-catalog refresh hook). */
    function clearCache() {
      _cache = {};
      _inflight = {};
    }

    return { getFacts: getFacts, refresh: refresh, clearCache: clearCache };
  })();

  /**
   * DcvManager (PR C, real). Owns the 5-method set, builds the method select
   * (primary + per-SAN) from the server's facts.dcvMethods VERBATIM (the wildcard
   * exclusion was removed 2026-06-29 ; the server owns the per-SKU set), and writes the validation token
   * (the method id, standardised on DNS_CNAME, or the chosen approver email ;
   * EMAIL stays invalid until an approver is chosen, preserving the v1 asymmetry).
   * v1 origin : the validation-method / approver-email change handlers and the
   * per-SAN DCV state machine.
   */
  // (DCV_METHOD_ORDER removed 2026-06-29, owner) : the form no longer imposes a canonical order or an allow-list on
  // the DCV methods. GET /api/sku returns facts.dcvMethods as an ORDERED array and owns the per-SKU set (including
  // which methods a wildcard SKU may use), so the picker renders that array VERBATIM, in the server's order, dropping
  // and excluding nothing. The form ordering + the intersection filter were form-side decisions duplicating the
  // server -- removed. (DCV_METHODS, the symbol vocabulary, stays : it is used for behaviour, e.g. EMAIL needs an
  // approver and applyDcvPanels shows the matching detail panel -- not to filter or order the server's list.)

  // (DCV_METHOD_LABELS removed 2026-06-27 : the per-method option labels + the picker placeholder are now customer
  // copy in order-form-v2-copy.liquid (dcv.method.label.* / dcv.method.placeholder), delivered to buildMethodSelect
  // via config.copy. No method-label wording lives in this file anymore : order-form-v2-copy is the single source.)

  /**
   * @param {Object} [options]
   * @param {string[]} [options.dcvMethods]       the SKU's methods : GET /api/sku's facts.dcvMethods, rendered VERBATIM in the server's order ; NO fallback, NO form ordering/filtering ; absent/empty -> empty picker -> fail closed
   * @param {Function} [options.isValidEmail]     injectable ; defaults to the module isValidEmail
   * @param {Object}   [options.copy]             config.copy map (key->text) ; the method labels + placeholder render from it
   * @constructor
   */
  function DcvManager(options) {
    options = options || {};
    // The SKU's methods = EXACTLY what the server returned, in the server's order (owner 2026-06-29). The form
    // imposes NO order, drops NOTHING, and applies NO wildcard exclusion -- GET /api/sku owns the per-SKU set
    // (including which methods a wildcard SKU may use). A non-array / missing / EMPTY dcvMethods yields an empty
    // set -> an empty picker (placeholder only) -> FAIL CLOSED : no method is selectable, so the submit gate's dcv
    // check blocks. There is NO fallback and NO DCV_METHOD_ORDER intersection (both were forbidden form-side
    // decisions -- one invented a list when the server was silent, the other re-ordered/filtered the server's list).
    this._dcvMethods = Array.isArray(options.dcvMethods) ? options.dcvMethods.slice() : [];
    this._isValidEmail = options.isValidEmail || isValidEmail;
    // config.copy map (key -> customer text). buildMethodSelect renders the method labels + placeholder from it,
    // so that wording lives ONLY in order-form-v2-copy.liquid (content-centralization 2026-06-27). {} when absent
    // (e.g. a unit test asserting option VALUES, not label text) ; labels then fall back to the bare method id.
    this._copy = options.copy || {};
    // Per-instance DCV selection state. ONE DcvManager per picker : the primary
    // domain gets one ; SanBuilder creates one per SAN row.
    this._method = '';
    this._approver = '';
    // MEMBERSHIP set : the lowercased UNION of the calculated set + the official set. onApproverChange / getToken
    // accept ONLY a member of this set (round-4 review), so a value from either source is valid. Empty until loaded.
    this._approverEmails = [];
    // TWO SOURCES, ONE LIST (owner 2026-06-28 two-list model -> owner 2026-06-30 single merged list : the customer
    // found two dropdowns confusing, so the additional addresses now JOIN the one select instead of a second one).
    // CALCULATED : the FINISHED set the middleware returns from GET /api/dcv/approver-emails-calculated, shown
    // automatically when the domain validates (the form no longer constructs it client-side -- 2026-06-28 migration).
    // OFFICIAL : the ADDITIONAL addresses sourced from the FULL official set GET /api/dcv/approver-emails (levelEmails
    // + whoisEmails + dnsTxtEmails), fetched ONLY when the shopper clicks the additional-addresses button
    // (data-order-form-action="approver-more") and then APPENDED (de-duped) into the single calculated select. These
    // hold the RAW (display-case) addresses for each source ; _approverEmails above is the lowercased union for
    // membership ; the rendered single select shows mergeExtraApproverEmails(calculated, official).
    this._calculatedApproverEmails = [];
    this._officialApproverEmails = [];
  }

  /**
   * Populate the given <select> with the SKU's methods IN THE SERVER'S ORDER (facts.dcvMethods, verbatim),
   * placeholder first (empty value => the picker is INVALID until the customer chooses, preserving the v1
   * "stays invalid until chosen" gate). The form applies NO wildcard exclusion : the server decides the per-SKU
   * set (owner 2026-06-29). Idempotent (clears existing options first). Operates on an EXISTING element from the
   * snippet markup (the v2 markup-in-Liquid convention) rather than creating one. @param {HTMLSelectElement} selectEl @returns {HTMLSelectElement}
   */
  DcvManager.prototype.buildMethodSelect = function (selectEl) {
    var doc = selectEl && selectEl.ownerDocument;
    if (!doc) {
      throw new Error('DCV MANAGER : buildMethodSelect REQUIRES A <select> ELEMENT WITH AN ownerDocument');
    }
    while (selectEl.firstChild) {
      selectEl.removeChild(selectEl.firstChild);
    }
    var placeholder = doc.createElement('option');
    placeholder.value = '';
    // Placeholder wording lives in order-form-v2-copy (dcv.method.placeholder), delivered via config.copy
    // (content-centralization 2026-06-27) : "- Select Validation Method -", consistent with every other dropdown.
    // '' when copy is absent (a degenerate empty option, NOT a hardcoded literal).
    placeholder.textContent = this._copy['dcv.method.placeholder'] || '';
    // DROPDOWN-PLACEHOLDER PARITY (owner 2026-07-02) : mirror the static country <option> idiom
    // (order-form-v2-address-section.liquid : address.placeholder.country carries `disabled selected`) so EVERY
    // dropdown's placeholder renders greyed/lighter AND cannot be re-chosen by the shopper. Both flags are required :
    // `disabled` alone (with real options below) makes the browser auto-select the first ENABLED option, wrongly
    // pre-picking a method ; `selected` forces the placeholder to stay shown. The submit gate + applySelectTick read
    // select.value === '' (a disabled+selected placeholder still returns '') so invalid-until-chosen is preserved, and
    // the engine's programmatic resets (selectEl.value = '') can still select a disabled option from script.
    placeholder.disabled = true;
    placeholder.selected = true;
    selectEl.appendChild(placeholder);
    // this._dcvMethods is the server's facts.dcvMethods VERBATIM, in the server's order (owner 2026-06-29) :
    // iterate it directly. The form imposes no order and drops nothing.
    for (var i = 0; i < this._dcvMethods.length; i++) {
      var m = this._dcvMethods[i];
      var opt = doc.createElement('option');
      opt.value = m;
      // Label from order-form-v2-copy via config.copy (dcv.method.label.<method>) ; falls back to the bare method id
      // (a degenerate identifier, NOT customer copy) only when copy is absent.
      opt.textContent = this._copy['dcv.method.label.' + m.toLowerCase()] || m;
      selectEl.appendChild(opt);
    }
    // NO applyWildcardExclusions (removed 2026-06-29) : the server's facts.dcvMethods already reflects what the SKU
    // (wildcard or not) may use ; the form excludes nothing of its own.
    // Rebuilt to the placeholder (value '') -> clear any stale validation tick (owner 2026-06-22). Idempotent.
    applySelectTick(selectEl);
    return selectEl;
  };

  /**
   * Record the chosen method and return a descriptor for the assembler to act on.
   * Resets any prior approver (v1 clears the approver when the method changes). The
   * assembler owns the DOM (show/hide the approver region, error visibility) ; this
   * only mutates DcvManager state. @param {string} method @returns {{method:string, showApprover:boolean, token:(string|null)}}
   */
  DcvManager.prototype.onMethodChange = function (method) {
    // Gate on the INSTANCE's selectable set (round-6 review nit) : a method not
    // allowed for this SKU (i.e. NOT in the server's facts.dcvMethods) is rejected HERE too, so
    // onMethodChange matches the self-enforcing posture of getToken / onApproverChange (the SC45
    // wildcard exclusion + the global DCV_METHOD_ORDER allow-list were both removed 2026-06-29).
    this._method = this._isMethodSelectable(method) ? method : '';
    this._approver = '';
    return { method: this._method, showApprover: this._method === DCV_METHODS.EMAIL, token: this.getToken() };
  };

  /**
   * The valid approver-e-mail set onApproverChange enforces membership against : the calc+official UNION.
   * setCalculatedApproverEmails sets it to the middleware's calculated set ; setOfficialApproverEmails re-sets it to
   * the calc+official union after the additional-addresses fetch. Stored lowercase so the membership check is
   * case-insensitive. @param {string[]} list @returns {void}
   */
  DcvManager.prototype.setApproverEmails = function (list) {
    // FILTER non-string / empty entries, THEN lowercase (round-6 review nit) : the
    // set carries no empty strings, consistent with the don't-trust-the-API posture.
    this._approverEmails = Array.isArray(list) ? list.filter(function (e) {
      return typeof e === 'string' && e;
    }).map(function (e) {
      return e.toLowerCase();
    }) : [];
    // CART-CORRECTNESS (adversarial review) : if the STANDING chosen approver is no longer a MEMBER of the
    // new set, DROP it. The set is rebuilt on every approver reload : a DIFFERENT-domain re-validate yields a
    // set WITHOUT the old domain's approver, but onApproverChange does NOT re-fire on a reload, so without
    // this getToken() would keep returning the OLD domain's approver -- and the submit gate (which only
    // checks hasToken, i.e. getToken() !== null) would ship a stale approver e-mail for the NEW domain's
    // certificate (a fail-OPEN ; the CA cannot then deliver approval and the order stalls). The same-domain /
    // "Show More Options" SUPERSET case keeps the approver (still a member). Self-enforcing, mirroring
    // onApproverChange's own membership check + getToken's fail-closed posture.
    if (this._approver && this._approverEmails.indexOf(this._approver) === -1) {
      this._approver = '';
    }
  };

  /**
   * Flatten a GET /api/dcv/approver-emails payload into the FULL official approver set, EXACTLY as the
   * server sends it : every levelEmails group in the server's key order, THEN whoisEmails, THEN dnsTxtEmails,
   * concatenated. NO re-ordering and NO de-duplication HERE (owner 2026-06-29 : the form shows what the server returns
   * and decides nothing -- the prior case-insensitive de-dupe was a form-side decision, removed ; the calc+official
   * merge that feeds the one select de-dupes downstream, in setOfficialApproverEmails). The only filtering
   * is skipping non-string / empty entries, so the dropdown never renders a blank option (hygiene, not a content
   * decision). Shape verified middleware-01 src/services/dcv-emails.ts (whoisEmails:string[], dnsTxtEmails:string[],
   * levelEmails:{level:string[]}). @param {{whoisEmails?:string[], dnsTxtEmails?:string[], levelEmails?:Object}} payload @returns {string[]}
   */
  DcvManager.prototype.buildOfficialApproverEmails = function (payload) {
    var out = [];
    var push = function (arr) {
      if (!Array.isArray(arr)) { return; }
      for (var k = 0; k < arr.length; k++) {
        if (typeof arr[k] === 'string' && arr[k]) { out.push(arr[k]); } // server order, NO de-dupe
      }
    };
    var levelEmails = (payload && payload.levelEmails && typeof payload.levelEmails === 'object') ? payload.levelEmails : {};
    var keys = Object.keys(levelEmails); // server's group order (e.g. level4, level3 = most-specific -> apex)
    for (var i = 0; i < keys.length; i++) {
      push(levelEmails[keys[i]]);
    }
    push(payload && payload.whoisEmails);
    push(payload && payload.dnsTxtEmails);
    return out;
  };

  /**
   * Record the OFFICIAL set (the additional full set, RAW display-case for rendering) AND fold it into the membership
   * set so an appended official address tokenises. setApproverEmails is re-called with the UNION (calculated first,
   * official deduped after) -- it lowercases for membership and drops a standing approver only if it is in NEITHER
   * source, so a calculated address the shopper already picked survives the official fetch. RETURNS that calc+official
   * UNION (display-case) so the caller (wireApproverMore) can render it into the SINGLE approver select (owner
   * 2026-06-30 : the additional addresses join the one list rather than a second dropdown). @param {string[]} list @returns {string[]}
   */
  DcvManager.prototype.setOfficialApproverEmails = function (list) {
    this._officialApproverEmails = Array.isArray(list) ? list.filter(function (e) {
      return typeof e === 'string' && e;
    }) : [];
    // ONE merged display list (owner 2026-06-30) : calculated first, then the official addresses not already present
    // (case-insensitive de-dupe). Drive BOTH the membership set and the caller's single-select render off this union.
    var merged = this.mergeExtraApproverEmails(this._calculatedApproverEmails, this._officialApproverEmails);
    this.setApproverEmails(merged);
    return merged;
  };

  /**
   * Record the chosen approver address (EMAIL method only). SELF-ENFORCING (round-4
   * review) : the address is lowercased (the generated set + the CA's accepted
   * addresses are lowercase, matching collect()'s domain normalisation) and accepted
   * ONLY when it is a valid e-mail AND a MEMBER of the generated approver set -- so a
   * free-text / mixed-case / unauthorised address can never tokenise (the same
   * fail-closed posture getToken applies to the method). An empty / invalid /
   * non-member address leaves EMAIL invalid (the v1 asymmetry : EMAIL is the only
   * "method chosen but still invalid" state). @param {string} approverEmail @returns {{valid:boolean, token:(string|null)}}
   */
  DcvManager.prototype.onApproverChange = function (approverEmail) {
    var addr = (typeof approverEmail === 'string') ? approverEmail.toLowerCase() : '';
    if (this._method === DCV_METHODS.EMAIL && this._isValidEmail(addr) && this._approverEmails.indexOf(addr) !== -1) {
      this._approver = addr;
    } else {
      this._approver = '';
    }
    var token = this.getToken();
    return { valid: token !== null, token: token };
  };

  /**
   * Whether `method` is currently SELECTABLE for this instance : it must be in the SKU's server-returned set
   * (this._dcvMethods = facts.dcvMethods, verbatim). This makes the token contract SELF-ENFORCING (PR C round-2
   * review #2) : getToken trusts the INSTANCE'S server set, so a method the server did NOT return can never yield
   * a non-null token even if set programmatically -- a fail-closed posture, not reliant on the wiring to gate it.
   * No wildcard exclusion (removed 2026-06-29) : the server's facts.dcvMethods already reflects what a wildcard SKU
   * may use, so the form excludes nothing of its own. @param {string} method @returns {boolean}
   */
  DcvManager.prototype._isMethodSelectable = function (method) {
    return this._dcvMethods.indexOf(method) !== -1;
  };

  /**
   * The exact value to write into the validation-token property, or null when the
   * picker is not yet complete OR the chosen method is not selectable for this SKU.
   * EMAIL => the chosen approver address (null until chosen) ; HTTP/HTTPS/DNS_CNAME
   * => the literal method id. The form ALWAYS writes DNS_CNAME (never DNS) ; the
   * webhook's normalizeDcvMethod maps DNS_CNAME -> DNS server-side (PR A owner
   * decision, frozen in DCV_METHODS). @returns {string|null}
   */
  DcvManager.prototype.getToken = function () {
    // Self-enforcing : null unless the chosen method is selectable for THIS instance (round-2 review #2), so a
    // method the server did not return never tokenises.
    if (!this._method || !this._isMethodSelectable(this._method)) {
      return null;
    }
    if (this._method === DCV_METHODS.EMAIL) {
      return this._approver || null;
    }
    return this._method;
  };

  // (applyWildcardExclusions removed 2026-06-29, owner) : the form no longer disables HTTP/HTTPS for wildcard SKUs.
  // GET /api/sku owns the per-SKU method set, so if a wildcard SKU may not use file-based DCV the server simply does
  // not return HTTP/HTTPS in facts.dcvMethods. The form renders what the server returns and excludes nothing of its own.

  /**
   * Generic ORDER-PRESERVING, CASE-INSENSITIVE de-dupe of two address lists : `base` first, then any `extra`
   * not already present. The de-dupe key is LOWERCASED (round-5 review item 3) so it matches setApproverEmails /
   * the lowercased token : a mixed-case duplicate (Admin@x.com vs admin@x.com) collapses to one option rather than
   * two visually-distinct entries. Reused for both the calculated+official UNION (setOfficialApproverEmails) and the
   * official-set build itself (buildOfficialApproverEmails chains it across level/whois/dnsTxt). The RAW (first-seen)
   * casing is preserved in the output for display. @param {string[]} base @param {string[]} extra @returns {string[]}
   */
  DcvManager.prototype.mergeExtraApproverEmails = function (base, extra) {
    var seen = {};
    var out = [];
    var add = function (list) {
      if (!Array.isArray(list)) {
        return;
      }
      for (var i = 0; i < list.length; i++) {
        var v = list[i];
        if (typeof v === 'string' && v && !Object.prototype.hasOwnProperty.call(seen, v.toLowerCase())) {
          seen[v.toLowerCase()] = true;
          out.push(v);
        }
      }
    };
    add(base);
    add(extra);
    return out;
  };

  /**
   * Record the CALCULATED set the middleware returns from GET /api/dcv/approver-emails-calculated. The form
   * fetches the FINISHED `calculatedEmails` array (loadApprovers, in the wiring) and hands it here ; this is a pure
   * SETTER (no API call, no client-side construction -- the 2026-06-28 migration moved the calculation to the
   * middleware). Records the raw display-case list AND sets the membership set so onApproverChange can enforce
   * membership (round-4 review). A later official-list fetch calls setOfficialApproverEmails, which re-sets membership
   * to the calc+official UNION. RESETS the official set : a new domain load makes the previous domain's official
   * addresses stale, so the shopper must re-click the additional-addresses button (which re-appends them). @param {string[]} list @returns {void}
   */
  DcvManager.prototype.setCalculatedApproverEmails = function (list) {
    var clean = Array.isArray(list) ? list.filter(function (e) { return typeof e === 'string' && e; }) : [];
    this._calculatedApproverEmails = clean.slice();
    this.setApproverEmails(clean);
    this._officialApproverEmails = [];
  };

  /**
   * SanBuilder (PR C, real). Builds / destroys per-SAN rows to match sanCount,
   * persists typed values across rebuilds, validates each SAN (not == primary,
   * not duplicate, wildcard rule, format) on input, and collects the index-
   * aligned domains + tokens. Fixes the v1 array-misalignment bug (an EMAIL SAN
   * with no approver must still push a token slot). v1 origin : SANModule.
   */
  // NO form-side SAN ceiling (owner 2026-06-29) : the per-product domain count comes ENTIRELY from GET /api/sku
  // (sanCount / totalDomains / maxDomains). The form renders exactly facts.sanCount rows and decides no cap of its
  // own -- a hardcoded overall cap (the removed SAN_MAX=50) was a form-side decision that ignored the per-product
  // server limit (e.g. it would have allowed 50 rows on a SKU the server caps at 2). The server is the single source.
  // The FROZEN per-row id convention. The FQDN input REUSES
  // snippets/order-form-v2-fqdn-input.liquid with a per-row id (that snippet's JSDoc
  // already names exactly 'order-form-fqdn-input-1'). renderRow MUST produce a row
  // whose elements carry these ids for index N.
  function sanRowIds(index) {
    return {
      fqdn: 'order-form-fqdn-input-' + index,
      method: 'order-form-san-dcv-method-' + index,
      approver: 'order-form-san-approver-' + index
    };
  }

  /**
   * @param {Object} options
   * @param {HTMLElement} options.container          the SAN-rows container element
   * @param {function(number):HTMLElement} options.renderRow  builds one row's markup for index N
   *        (the order-form-v2-san-builder.liquid <template> clone, re-indexed) ; SanBuilder does NOT
   *        hardcode markup
   * @param {function():string} [options.primaryDomainRef]  current primary domain (for "SAN != primary")
   * @param {boolean} [options.isWildcardMd]         product is Wildcard-MD (every SAN must be "*.")
   * @param {DomainNormalizer} options.domainNormalizer
   * @param {Object} [options.dcvOptions]            forwarded to each row's DcvManager
   * @param {boolean} [options.dcvHidden]           the SKU ships fields.dcv 'hidden' (CaaS) : its rows carry no
   *        method, so writeHiddenFields leaves the tokens twin BLANK (Build 2, section 8 site 3)
   * @param {Function} [options.DcvManager]          injectable DcvManager ctor (defaults to module's)
   * @constructor
   */
  function SanBuilder(options) {
    options = options || {};
    this._container = options.container || null;
    this._renderRow = options.renderRow || null;
    this._primaryDomainRef = options.primaryDomainRef || function () { return ''; };
    this._isWildcardMd = !!options.isWildcardMd;
    this._domainNormalizer = options.domainNormalizer || null;
    this._dcvOptions = options.dcvOptions || {};
    this._dcvHidden = !!options.dcvHidden;
    this._DcvManager = options.DcvManager || DcvManager;
    // Per-row records, index-aligned : { index, dcvManager, fqdnInput, methodSelect, approverSelect }.
    this._rows = [];
  }

  /**
   * Rebuild the SAN rows to `sanCount` (the server's per-product count, no form cap), preserving typed FQDN
   * values POSITIONALLY across the rebuild (so a variant change does not lose what
   * the customer typed, and an empty middle row does not shift later values up). Each
   * row gets its own DcvManager with its method <select> populated. The assembler
   * wires the row events + error-region display (FORWARD-NOTE-2) ; this only builds
   * the DOM + per-row state. @param {number} sanCount @returns {void}
   */
  SanBuilder.prototype.rebuildToCount = function (sanCount) {
    if (!this._container || !this._renderRow) {
      log.warn('SAN BUILDER : rebuildToCount WITH NO CONTAINER / renderRow - SKIPPING');
      return;
    }
    var count = (typeof sanCount === 'number' && sanCount > 0) ? Math.floor(sanCount) : 0; // server's per-product count ; NO form cap (owner 2026-06-29)
    // 1. Preserve the RAW typed values positionally (no blank-filter).
    var preserved = [];
    for (var p = 0; p < this._rows.length; p++) {
      preserved.push(this._rows[p].fqdnInput ? this._rows[p].fqdnInput.value : '');
    }
    // 2. Tear down.
    while (this._container.firstChild) {
      this._container.removeChild(this._container.firstChild);
    }
    this._rows = [];
    // 3. Build `count` rows, each with a DcvManager + populated method select.
    for (var i = 1; i <= count; i++) {
      var row = this._renderRow(i);
      if (!row) {
        continue;
      }
      this._container.appendChild(row);
      var ids = sanRowIds(i);
      // Scope the element lookup to THIS freshly-returned row (round-4 review nit) :
      // a container-wide querySelector('#id') returns the FIRST document-order match,
      // a footgun if a future renderRow ever produced a non-unique id. Scoping to
      // `row` keeps each row's wiring tied to its own element.
      var methodSelect = row.querySelector('#' + ids.method);
      // Each SAN row's DcvManager gets the SAME server-returned method set as the primary (this._dcvOptions =
      // facts.dcvMethods + copy). NO wildcard override (removed 2026-06-29) : the server's facts.dcvMethods already
      // reflects what a wildcard / wildcard-MD SKU may use, so the row picker excludes nothing of its own. (A shallow
      // copy so each row's DcvManager is independent ; the constructor slices dcvMethods, so the array is not shared.)
      var dm = new this._DcvManager(Object.assign({}, this._dcvOptions));
      if (methodSelect) {
        dm.buildMethodSelect(methodSelect);
      }
      this._rows.push({
        index: i,
        dcvManager: dm,
        fqdnInput: row.querySelector('#' + ids.fqdn),
        methodSelect: methodSelect,
        // The row's single approver <select> (owner 2026-06-30 : ONE list -- the additional/official addresses are
        // appended into THIS select by the row's "retrieve additional" button, no second dropdown). Null on a fixture
        // that omits it (every use guards).
        approverSelect: row.querySelector('#' + ids.approver)
      });
    }
    // 4. Restore preserved values into the new inputs in index order.
    for (var k = 0; k < this._rows.length; k++) {
      if (typeof preserved[k] === 'string' && this._rows[k].fqdnInput) {
        this._rows[k].fqdnInput.value = preserved[k];
      }
    }
  };

  /**
   * Whether the SAN at `index` is valid : non-empty, NOT equal to the primary domain
   * (case-insensitive), NOT a duplicate of another visible SAN, passes the soft local
   * format gate, and obeys the wildcard rule (Wildcard-MD requires "*." ; other
   * products forbid it). PURE : reads the DOM, mutates nothing (the assembler toggles
   * the method-select enabled state + the error region). @param {number} index @returns {boolean}
   *
   * VALIDATION-DEPTH ASYMMETRY vs the PRIMARY (owner decision 2026-06-22 : "keep it as it is for now ... we
   * will likely also add content later to reflect this for SANs"). The PRIMARY FQDN runs the AUTHORITATIVE
   * server-side parse (DomainNormalizer.parse -> /api/domain/parse, real public-suffix / TLD validation). Each
   * SAN here runs only the SYNCHRONOUS LOCAL checks below : DomainNormalizer.quickFormatCheck (an RFC-ish label
   * regex) + the duplicate + wildcard rules. It does NOT call the server, so a SAN that merely LOOKS like a
   * domain (e.g. a bogus or dormant TLD) passes the client gate and shows the success tick, whereas the same
   * string as the primary could be rejected. This is intentional for now : (a) it keeps the submit gate
   * synchronous (no per-row /api round-trip, no per-row epoch-race machinery), and (b) the middleware
   * (trustico-2016) is the AUTHORITATIVE validator at order-processing time and will reject a genuinely invalid
   * SAN, so a bad SAN cannot actually be issued. FUTURE (owner) : tighten this to the server parse for parity,
   * and/or add SAN-specific copy explaining the lighter client check. Until then the tick means "format OK",
   * not "server-validated". See DomainNormalizer.parse (the primary path) for the deeper check.
   */
  SanBuilder.prototype.validateSan = function (index) {
    if (!this._container || !this._domainNormalizer) {
      return false;
    }
    var dn = this._domainNormalizer;
    var input = this._container.querySelector('#' + sanRowIds(index).fqdn);
    if (!input) {
      return false;
    }
    var domain = dn.normalize(input.value);
    if (!domain) {
      return false;
    }
    var primary = dn.normalize(this._primaryDomainRef() || '');
    if (primary && domain === primary) {
      return false;
    }
    var others = this._container.querySelectorAll('input[id^="order-form-fqdn-input-"]');
    for (var i = 0; i < others.length; i++) {
      if (others[i] !== input && dn.normalize(others[i].value) === domain) {
        return false;
      }
    }
    if (!dn.quickFormatCheck(domain)) {
      return false;
    }
    var isWild = domain.indexOf('*.') === 0;
    if (this._isWildcardMd && !isWild) {
      return false;
    }
    if (!this._isWildcardMd && isWild) {
      return false;
    }
    return true;
  };

  /**
   * The index-aligned SAN domains + per-row tokens (same length = number of rows). An
   * EMAIL row with no approver chosen STILL occupies a slot (token '') so the two
   * cart lists never misalign -- the v1 array-misalignment bug fix. The assembler's
   * pre-submit gate ensures every row is valid before writeHiddenFields runs.
   * @returns {{domains:string[], tokens:string[]}}
   */
  SanBuilder.prototype.collect = function () {
    var domains = [];
    var tokens = [];
    var dn = this._domainNormalizer;
    for (var i = 0; i < this._rows.length; i++) {
      var row = this._rows[i];
      var raw = row.fqdnInput ? row.fqdnInput.value : '';
      domains.push(dn ? dn.normalize(raw) : (raw || '').trim());
      var token = row.dcvManager ? row.dcvManager.getToken() : null;
      tokens.push(token || '');
    }
    return { domains: domains, tokens: tokens };
  };

  /**
   * Single-audit-point write-through. The visible SAN inputs/selects have NO `name` ;
   * the cart values flow ONLY through these hidden properties[...] inputs (created by
   * order-form-v2-hidden-properties.liquid, a PR-D deliverable). SAN domains -> the
   * SAN-ONLY properties[Additional FQDN] (the PRIMARY FQDN is a SEPARATE property,
   * never included here -- owner ruling) ; per-SAN tokens -> properties[_Validation
   * Tokens] (plural), index-aligned -- or BLANK when the SKU hides DCV (the Build 2 note in the
   * body). Degrades (warn, no throw) if a hidden reference
   * is missing -- the never-block posture. @param {{additionalDomainsHidden?:HTMLInputElement, validationTokensHidden?:HTMLInputElement}} elements @returns {void}
   */
  SanBuilder.prototype.writeHiddenFields = function (elements) {
    elements = elements || {};
    var data = this.collect();
    if (elements.additionalDomainsHidden) {
      elements.additionalDomainsHidden.value = data.domains.join(', ');
    } else {
      log.warn('SAN BUILDER : NO additionalDomainsHidden REFERENCE - SAN DOMAINS NOT WRITTEN');
    }
    if (elements.validationTokensHidden) {
      // Build 2 (section 8 site 3) : with DCV hidden every row's token is '' by design, and joining N of them
      // yields ", " repeated -- NOT blank, so it survives the submit-time blank strip and lands as "," in
      // orders.validation_tokens. Write the twin BLANK instead, so the strip drops the property for every sanCount.
      elements.validationTokensHidden.value = this._dcvHidden ? '' : data.tokens.join(', ');
    } else {
      log.warn('SAN BUILDER : NO validationTokensHidden REFERENCE - SAN TOKENS NOT WRITTEN');
    }
  };

  /**
   * The per-row record for `index` (1-based), or null. Exposed so the Phase-3
   * assembler can wire the row's events + the tests can drive its DcvManager directly.
   * @param {number} index @returns {Object|null}
   */
  SanBuilder.prototype.getRow = function (index) {
    for (var i = 0; i < this._rows.length; i++) {
      if (this._rows[i].index === index) {
        return this._rows[i];
      }
    }
    return null;
  };

  /**
   * UI (3b, real). Centralised, side-effect-only DOM helpers : the message / icon /
   * field-state writers and the single audit point for hidden-input writes. Every
   * helper GUARDS a missing element (log.warn, never throw) so a markup drift in one
   * field can never abort the whole submit handler.
   *
   * THE ARIA SEQUENCING CONTRACT (round-8 review note 4) : a role="alert" region is
   * display:none until it has a message. Some screen readers do NOT announce content
   * that was already present in a hidden region when it becomes visible. So a message
   * is shown by UN-HIDING the region FIRST and setting textContent SECOND
   * (showMessage / announce) ; it is cleared by emptying textContent and THEN hiding
   * (hideMessage), so the region is never visible-but-empty. aria-invalid on the
   * field is toggled in lockstep (setFieldState) so assistive tech sees the field's
   * invalid state alongside the described-by error text.
   *
   * v1 origin : UIUtils + ValidationUI + the FormHelpers display handlers, minus the
   * v1 inline-style height juggling (that is CSS now).
   */
  var UI = (function () {
    /**
     * Show a field-level OR form-level message : UN-HIDE the region, THEN set the
     * text (the round-8 order, so the role="alert" announces on becoming visible).
     *
     * MARKUP CONTRACT (PR#16 r3 point 1) : un-hiding is `style.display = ''`, which
     * clears an INLINE `display:none`. Every v2 error/summary region is hidden with an
     * inline `style="display: none;"` in its snippet (order-form-*-section /
     * -fqdn-input / -dcv-section / -submit-button etc.), so this is correct for them.
     * It deliberately does NOT strip a hiding CSS CLASS : if a future snippet ever hides
     * a region via a class instead of the inline style, the region must keep using the
     * inline-style convention (or this helper must learn the class) -- the hidden-via-
     * inline-display contract is what makes the un-hide a one-liner. hideMessage mirrors
     * it (sets the inline `display:none` back).
     * @param {Element} regionEl the role="alert" message container (hidden via inline display)
     * @param {string} text UPPERCASE customer-facing text (house style)
     */
    function showMessage(regionEl, text) {
      if (!regionEl) {
        log.warn('ORDER FORM UI : showMessage CALLED WITH NO REGION');
        return;
      }
      regionEl.style.display = '';
      regionEl.textContent = text == null ? '' : String(text);
    }

    /**
     * Clear + hide a message region : EMPTY the text FIRST, then hide, so the region
     * is never left visible-but-empty.
     * @param {Element} regionEl
     */
    function hideMessage(regionEl) {
      if (!regionEl) {
        log.warn('ORDER FORM UI : hideMessage CALLED WITH NO REGION');
        return;
      }
      regionEl.textContent = '';
      regionEl.style.display = 'none';
    }

    /**
     * The form-level summary variant of showMessage : the ONE designated
     * aria-live="assertive" region (order-form-submit-button-summary). Same
     * un-hide-then-text order ; kept as a named helper so the assembler's intent
     * (announce a form-WIDE blocking message, not a field error) reads clearly.
     * @param {Element} summaryEl @param {string} text
     */
    function announce(summaryEl, text) {
      showMessage(summaryEl, text);
    }

    /**
     * Reflect a field's validation state visually + for assistive tech : toggle
     * aria-invalid on the control and show the matching decorative icon. The icon
     * spans live in the field's .field__validation-icon container (a sibling of the
     * input / .select within the .field) ; they are aria-hidden (3a) so toggling
     * their display never changes what a screen reader announces -- aria-invalid is
     * the signal that does.
     * @param {Element} inputEl the input / select
     * @param {('valid'|'error'|'neutral')} state
     */
    function setFieldState(inputEl, state) {
      if (!inputEl) {
        log.warn('ORDER FORM UI : setFieldState CALLED WITH NO INPUT');
        return;
      }
      inputEl.setAttribute('aria-invalid', state === 'error' ? 'true' : 'false');
      // Find the field wrapper, then its icon spans. closest() is supported by
      // jsdom + every browser the theme targets.
      var field = inputEl.closest ? inputEl.closest('.field') : null;
      if (!field) {
        return;
      }
      var validIcon = field.querySelector('.icon-valid');
      var errorIcon = field.querySelector('.icon-error');
      if (validIcon) {
        validIcon.style.display = state === 'valid' ? '' : 'none';
      }
      if (errorIcon) {
        errorIcon.style.display = state === 'error' ? '' : 'none';
      }
    }

    /**
     * Write a value through to a hidden cart-property input (the single audit point).
     * @param {Element} hiddenEl @param {string} value
     */
    function writeHidden(hiddenEl, value) {
      if (!hiddenEl) {
        log.warn('ORDER FORM UI : writeHidden CALLED WITH NO INPUT');
        return;
      }
      hiddenEl.value = value == null ? '' : String(value);
    }

    /**
     * Clear a hidden cart-property input (clear-on-failure / clear-on-non-MD so
     * invalid or stale data never reaches the cart -- e.g. the SAN hidden fields on
     * a switch to a non-Multi-Domain variant, Phase-3 obligation b).
     * @param {Element} hiddenEl
     */
    function clearHidden(hiddenEl) {
      if (!hiddenEl) {
        log.warn('ORDER FORM UI : clearHidden CALLED WITH NO INPUT');
        return;
      }
      hiddenEl.value = '';
    }

    return {
      showMessage: showMessage,
      hideMessage: hideMessage,
      setFieldState: setFieldState,
      writeHidden: writeHidden,
      clearHidden: clearHidden,
      announce: announce
    };
  })();

  /* =========================================================================
   * bootstrap  -- browser entry point
   * =========================================================================
   *
   * bootstrap() finds every `.order-form` on the page and wires it (wireOneForm ->
   * wireForm), deriving everything from GET /api/sku. The product.unified-product-form-v2
   * template renders the universal assembler order-form-v2-unified, so real
   * forms render + wire on the live product page.
   *
   * SINGLE-GLOBAL CONFIG LIMITATION (PR A round-3 finding 3) : config currently
   * lives in ONE global, window.ORDER_FORM_CONFIG, and _config.liquid reassigns
   * it per render. With ONE order form per page (the reality for product pages)
   * this is fine. But on a hypothetical page with TWO different order forms
   * (different SKU), the last render would win and bootstrap would
   * wire every .order-form from that single config. The double-injection guard
   * protects the asset, NOT config divergence. If a multi-form page is ever
   * needed, the Phase-3 assembler MUST scope config per form (e.g. a data-*
   * attribute or a per-form config map) instead of one global.
   */

  /* =========================================================================
   * ASSEMBLER WIRING (3b part 2)  -- wireForm + the submit-gate orchestration
   * =========================================================================
   *
   * bootstrap() hands each .order-form to wireForm(), which instantiates the modules,
   * resolves the SKU facts + country list from the API, renders the SKU-driven sections
   * (SAN row count, org/address visibility, the DCV method options, the country list),
   * wires the field events, and -- the part-2 HARD REQUIREMENT -- attaches the submit
   * handler so that on EVERY submit it BUILDS the full gate context (primaryInput +
   * sanRows + primaryDcv) from the live modules and runs it through FormValidator.
   * validate. That is what makes the skip-when-absent gate ACTIVE on a real form rather
   * than inert ; tests/wireform-integration.test.js asserts all three slices are supplied.
   */

  // The visible direct-copy inputs carry data-order-form-property tokens ; FormValidator's
  // required-field contract keys by ITS OWN field ids. This bridges the two so
  // collectValues() builds a `values` map validate() understands (the derived fields --
  // csr / primary-fqdn / primary-token / the SAN shells -- are NOT direct-copy and are
  // written by their modules, so they are intentionally absent from this map).
  var PROPERTY_TO_VALIDATOR_FIELD = {
    'first-name': 'contact-first-name',
    'last-name': 'contact-last-name',
    'email': 'contact-email',
    'org-name': 'org-name',
    'address': 'org-address',
    'city': 'org-city',
    'state': 'org-state',
    'postal': 'org-postal',
    'country': 'org-country',
    // ACME / OV-Anchor direct-copy fields : the visible ACME / OV-Anchor inputs carry these property tokens, so
    // collectValues keys the gate values by the validator id and the acmeAccount / ovAnchor field validators read
    // values['caas-acme-account'] / values['ov-anchor-order']. These are FIELD ids (parser-gated off
    // fields.acmeAccount / fields.ovAnchor), not a family branch. (The domain primary is DERIVED, not direct-copy.)
    'acme-account-id': 'caas-acme-account',
    'ov-anchor-order': 'ov-anchor-order'
  };

  // True when the element lives inside a [data-order-form-section] that is currently hidden.
  // A hidden section's values MUST NOT reach the gate or the cart : on an OV/EV -> DV variant
  // switch applyFieldVisibility hides the organisation + address sections but their inputs
  // keep any typed values, so without this guard a DV cart would carry stale organisation/
  // address data (the same leak HARD-REQUIREMENT #2 closes for the SAN fields). PR#17 #3.
  // closest() walks ancestors, so a field reports hidden whenever its enclosing section is
  // hidden. Robustness (PR#17 round-2 #3) : prefer the COMPUTED display so the guard also
  // catches a section hidden by a CSS class, not only the inline style.display that
  // applyFieldVisibility uses ; fall back to the inline value when getComputedStyle is
  // unavailable.
  //
  // SHOW/HIDE CONTRACT (PR#17 r7 #1, pinned next to applyFieldVisibility too) : org/address
  // sections are toggled ONLY via INLINE style.display -- the markup ships them inline
  // display:none and applyFieldVisibility flips inline display none<->''. They MUST NOT be
  // hidden-by-default via a CSS CLASS : an inline display:'' (the "reveal") does NOT override a
  // class's display:none, so a class-hidden section would stay COMPUTED-hidden even after
  // applyFieldVisibility "revealed" it, and this guard would then wrongly CLEAR a required OV/EV
  // value from the cart. Reading COMPUTED display is correct ONLY while that inline-only contract
  // holds (it catches the variant-switch leak AND reports the reveal as visible). The 3c assembler
  // must keep org/address hidden via inline display, not a class.
  //
  // PROGRESSIVE-DISCLOSURE INVARIANT (2026-06-24 second-eyes audit) : this checks the SECTION's OWN
  // computed display -- closest('[data-order-form-section]') then getComputedStyle on THAT element --
  // NOT an ancestor wrapper's. getComputedStyle(section).display returns the section's own display value
  // even when an ANCESTOR is display:none (display:none on an ancestor stops box generation but does not
  // change a descendant's own computed display), so when the whole [data-order-form-post-primary] gate
  // wrapper is hidden (the primary is not yet validated) an OV/EV org/address section still reports
  // display '' here -> isInHiddenSection returns false -> collectValues WOULD collect those values. That
  // is SAFE ONLY because the submit gate refuses to submit while the primary is invalid, and a hidden
  // [data-order-form-post-primary] wrapper is the ONE state where the primary is invalid (applyPrimaryGate
  // sets the wrapper's display from state.primaryInput.valid). So a value collected behind the closed gate
  // never reaches a cart : onSubmit is blocked first. INVARIANT to preserve : do NOT let a submit through
  // while [data-order-form-post-primary] is hidden, and do NOT move org/address OUTSIDE that wrapper without
  // re-checking this guard -- either change would let an inside-a-hidden-gate value leak to the cart, which
  // this section-OWN-display check does not catch on its own.
  function isInHiddenSection(el) {
    var section = el.closest ? el.closest('[data-order-form-section]') : null;
    if (!section) {
      return false;
    }
    var view = section.ownerDocument && section.ownerDocument.defaultView;
    if (view && view.getComputedStyle) {
      var computedHidden = view.getComputedStyle(section).display === 'none';
      // CONTRACT CROSS-CHECK (PR#17 r10 #2) : a comment cannot fail a build. If the section computes
      // to hidden but its INLINE display is NOT 'none', something OTHER than inline style (a CSS
      // class) is hiding it -- the exact contract violation that makes applyFieldVisibility's
      // inline-'' reveal ineffective and would silently CLEAR a required OV/EV value. Warn LOUD so
      // the divergence is diagnosable at runtime instead of waiting for the 3c contract test.
      if (computedHidden && section.style && section.style.display !== 'none') {
        log.warn('ORDER FORM : SECTION HIDDEN BY A NON-INLINE RULE - SHOW/HIDE CONTRACT VIOLATED', { section: section.getAttribute('data-order-form-section') });
      }
      return computedHidden;
    }
    return !!(section.style && section.style.display === 'none');
  }

  // Collect the validate() `values` map (validatorFieldId -> trimmed value) from the
  // VISIBLE direct-copy inputs. The hidden-properties twins also carry
  // data-order-form-property, so type=hidden is skipped (only the visible inputs feed it) ;
  // inputs inside a hidden section are skipped too so a hidden OV/EV org/address value is not
  // validated against a DV cart (PR#17 #3).
  function collectValues(form) {
    var values = {};
    var els = form.querySelectorAll('[data-order-form-property]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.type === 'hidden') {
        continue;
      }
      if (isInHiddenSection(el)) {
        continue;
      }
      var fieldId = PROPERTY_TO_VALIDATOR_FIELD[el.getAttribute('data-order-form-property')];
      if (fieldId) {
        values[fieldId] = (el.value || '').trim();
      }
    }
    return values;
  }

  // sanRows slice : one { valid, hasToken, fieldId, methodFieldId } per visible SAN row,
  // from SanBuilder.validateSan(i) AND getRow(i).dcvManager.getToken() !== null -- the
  // per-row token-presence gate the part-1 hard requirement enforces. Rows are 1-based and
  // contiguous (rebuildToCount), so iterate getRow until it returns null.
  //
  // CONTRACT (PR#17 r11 #2) : the per-row fieldId / methodFieldId are FIXED LITERAL ids
  // (order-form-fqdn-input-<i> / order-form-san-dcv-method-<i>), NOT of_id-prefixed -- unlike the
  // primary/contact/org anchors, SAN rows are NOT of_id-configurable. This matches SanBuilder's
  // frozen sanRowIds + order-form-v2-san-builder.liquid, which renders the rows with these exact ids ;
  // resolveFieldEl then resolves them FORM-SCOPED via [id="..."]. The 3c assembler MUST keep the SAN
  // row template ids non-of_id-prefixed (the 3c contract test should assert this).
  function buildSanRows(sanBuilder) {
    var rows = [];
    if (!sanBuilder) {
      return rows;
    }
    var i = 1;
    var row = sanBuilder.getRow(i);
    while (row) {
      rows.push({
        valid: sanBuilder.validateSan(i),
        hasToken: row.dcvManager.getToken() !== null,
        fieldId: 'order-form-fqdn-input-' + i,
        methodFieldId: 'order-form-san-dcv-method-' + i
      });
      i += 1;
      row = sanBuilder.getRow(i);
    }
    return rows;
  }

  // Build the FULL gate context from the live form + modules. This is the function whose
  // output the part-2 integration test inspects : it MUST carry primaryInput + sanRows +
  // primaryDcv so the gate is active.
  function buildSubmitContext(form, state) {
    var facts = state.facts;
    var sanRows = buildSanRows(state.sanBuilder);
    // The primary DCV select's REAL id (of_id-configurable), so a missing-method gate failure
    // anchors the error/focus to the actual element. When the select is absent there is NO element
    // to anchor to, so the fallback is the non-anchoring 'order-form' SENTINEL (resolveFieldEl
    // cannot resolve it, so the error routes to the form SUMMARY), NEVER a hardcoded component id
    // that would duplicate the markup id and could point at a phantom element (owner 2026-06-29 A+
    // sweep : every anchor default is the sentinel ; no 'order-form-<component>' id literal remains).
    var dcvSelect = findPrimaryDcvSelect(form);
    // The expected SAN count is the server's per-product facts.sanCount, used verbatim (owner 2026-06-29 : NO
    // form-side cap). rebuildToCount renders exactly this many rows, so visibleSanCount and expectedSanCount agree.
    var expectedSan = facts && Number.isFinite(facts.sanCount) ? facts.sanCount : undefined;
    // DIAGNOSTIC (PR #32 review r4) : the submit gate hard-depends on the server's `fields` descriptor (there is
    // NO client-side fallback -- the v1 validationLevel-derived logic was removed). If /api/sku returned facts
    // but OMITTED `fields` (a middleware version-skew, or a SKU not yet migrated to buildFieldStates), validate()
    // sees an empty map -> the no-required-field floor rejects EVERY submit with the generic token : a SILENT
    // "nothing carts". Warn LOUD so an operator can diagnose it from the console instead of a mystery (logs-only ;
    // the gate still fails closed, so the cart is never corrupted -- this only makes the failure VISIBLE).
    if (facts && (!facts.fields || Object.keys(facts.fields).length === 0) && !state.noFieldsWarned) {
      // Latch once per form (PR #32 review r5 #4) : a shopper re-clicking submit on a misconfigured SKU should not
      // spam the console -- one diagnostic line per wired form is enough (the gate still blocks every attempt).
      log.warn('ORDER FORM : /api/sku RETURNED NO FIELDS DESCRIPTOR - SUBMIT WILL FAIL CLOSED', { sku: facts.sku });
      state.noFieldsWarned = true;
    }
    // End User Telephone Number : a DERIVED value (two controls -> one composed string, wiring style (b)), so
    // the direct-copy collectValues walk never sees it. Inject the composition here, mirroring collectValues'
    // hidden-section skip at ROW granularity : a parser-hidden row contributes nothing to the gate (the same
    // PR#17 #3 stale-value posture ; isRowHidden documents why the row needs its own check).
    var gateValues = collectValues(form);
    var phoneRow = telephoneRowEl(form);
    if (phoneRow && !isRowHidden(phoneRow)) {
      gateValues.telephone = composeTelephoneValue(phoneRow);
    }
    return {
      // The customer-copy map (config.copy) so the gate's FIELD_VALIDATORS resolve their error tokens from
      // order-form-v2-copy at submit time (content-centralization 2026-06-27 ; no token literal lives in this file).
      copy: state.config.copy,
      // The field-validation values (max lengths + the ACME charset pattern) from the SINGLE source
      // order-form-v2-validation.liquid -> config.validation (owner 2026-06-29). The gate's length / ACME checks read
      // these by field id (maxLengthOf / isValidAcmeAccountId) ; no validation VALUE is hardcoded in this file.
      validation: state.config.validation,
      validationLevel: facts ? facts.validationLevel : undefined,
      // The server's per-field state map ('required' | 'optional' | 'hidden'), read by
      // validate()'s org-required gate (context.fields.org === 'required'). Replaces the
      // V1 validationLevel-derived org check ; carried alongside validationLevel (now
      // label-only) so the gate drives entirely off server facts.
      fields: facts ? facts.fields : undefined,
      values: gateValues,
      visibleSanCount: sanRows.length,
      expectedSanCount: expectedSan,
      // primaryInput : the active CSR/FQDN result, tracked by the field events. fieldId is the
      // anchor wirePrimaryInput resolved to the REAL primary field (csr OR fqdn). Where no primary
      // element exists (a non-CSR/non-FQDN primary), the anchor is the non-anchoring 'order-form'
      // SENTINEL, so the error routes to the form SUMMARY rather than a phantom hardcoded id (owner
      // 2026-06-29 A+ sweep : the hardcoded 'order-form-<component>' anchor defaults are GONE at
      // every anchor site -- the FormValidator anchor fallbacks all use the sentinel now).
      // A.0 WIDENED : also carry the primary DOMAIN string + the /api/domain/parse verdict
      // (apiIsValid + apiIsWildcard) the arm stamped on it, so the gate's A.1/A.3 domain rules can run.
      // For a typed FQDN these come straight off the parse result ; for a CSR the CN is routed through the
      // SAME /api/domain/parse (A.3), so both paths carry server-derived (not client-PSL) verdicts.
      primaryInput: {
        valid: !!state.primaryInput.valid,
        fieldId: state.primaryInput.fieldId,
        domain: state.primaryInput.domain || '',
        apiIsValid: state.primaryInput.apiIsValid,
        apiIsWildcard: !!state.primaryInput.apiIsWildcard
      },
      sanRows: sanRows,
      // primaryDcv : null token until a selectable method (and, for EMAIL, an approver).
      primaryDcv: { hasToken: state.primaryDcv ? state.primaryDcv.getToken() !== null : false, fieldId: dcvSelect ? dcvSelect.id : 'order-form' },
      // subscriptionCheck : the CaaS-extension eligibility verdict (state.subscriptionCheck, set ONLY by an
      // eligible:true answer in checkAcmeNow, cleared by resetPrimary on every edit / mode / variant reset).
      // The acmeAccount validator FAILS CLOSED on a null slice when fields.acmeAccount === 'required'
      // (section 9 : the form is the only gate on this path), and re-checks the value equality as the
      // no-event-edit belt. null (not undefined) when no verdict is held, so the fail-closed branch reads it.
      subscriptionCheck: state.subscriptionCheck ? { eligible: state.subscriptionCheck.eligible === true, value: state.subscriptionCheck.value } : null,
      // validationIdCheck : the Validation ID verdict (state.validationIdCheck, set ONLY by a valid:true answer in
      // wireValidationIdCheck, cleared on every edit and variant switch). The ovAnchor validator FAILS CLOSED on a
      // null slice for any typed value, and re-checks the value equality as the no-event-edit belt.
      validationIdCheck: state.validationIdCheck ? { valid: state.validationIdCheck.valid === true, value: state.validationIdCheck.value } : null,
      // A.0 SKU-FACTS SLICE for the domain rules (A.1-A.4). onSubmit fail-closes on null facts BEFORE
      // calling buildSubmitContext, so facts is present here ; guard defensively anyway (undefined ->
      // the gate's skuFacts-gated checks SKIP, never throw). Only the fields the gate reads are carried.
      skuFacts: facts ? {
        isWildcard: !!facts.isWildcard,
        isWildcardMultiDomain: !!facts.isWildcardMultiDomain,
        totalDomains: facts.totalDomains,
        sanCount: facts.sanCount,
        isSellable: facts.isSellable
      } : undefined
    };
  }

  // Map a token-collection from the hidden-properties block : token -> hidden <input>.
  function hiddenInputsByToken(form) {
    var map = {};
    var els = form.querySelectorAll('[data-order-form-hidden-properties] [data-order-form-property]');
    for (var i = 0; i < els.length; i++) {
      map[els[i].getAttribute('data-order-form-property')] = els[i];
    }
    return map;
  }

  // On a PASSING submit : write every cart value through to its hidden twin (the single
  // audit point). Direct-copy fields copy the visible value ; the derived fields (csr,
  // primary-fqdn, primary-token, the SAN shells) are written from their modules.
  function writeThrough(form, state) {
    var hidden = hiddenInputsByToken(form);
    // The ONE selector for the cart-property TWINS (hidden inputs carrying a properties[...] name), defined ONCE so the
    // step-(0) name snapshot/restore and the step-(5) skip-empty strip ALWAYS target the SAME set (owner 2026-07-02 ;
    // PR #41 review) -- if the scope is ever WIDENED (see the step-(5) SCOPE note), change it HERE, in one place, and
    // both sites stay in lock-step (a divergence would let a twin be stripped but never restored -> a permanent strip).
    var CART_PROPERTY_TWIN_SELECTOR = 'input[type="hidden"][name^="properties["]';
    // (0) SELF-CONTAINED NAME RESTORE (owner 2026-07-02) : a prior submit -- or a bfcache Back-restore -- may have
    // STRIPPED the name off an empty property twin (the skip-empty pass (5) below). Restore EVERY hidden property's
    // name BEFORE writing values + re-stripping, so a now-populated field POSTs again regardless of page lifecycle
    // (a re-submit after a strip is correct ; nothing is ever permanently un-submittable). Two name sources :
    //   (a) cart-key twins -> re-apply from facts.cartProperties (applyCartPropertyNames, the render-time mechanism).
    //   (b) STATIC-named twins (_Validation Token / _Validation Tokens : a FIXED liquid name, NOT in cartProperties) ->
    //       restore from their ORIGINAL name, snapshotted (ONCE, while still intact) into data-order-form-original-name
    //       so a later strip can be undone. SNAPSHOT here on the first submit (names intact) : any property twin that
    //       carries a `properties[...]` name but NO data-order-form-cart-key is a static-named one. `id` (the variant)
    //       is not a `properties[` name, so it is never touched.
    var namedNow = form.querySelectorAll(CART_PROPERTY_TWIN_SELECTOR); // hidden TWINS only (see step (5) SCOPE note)
    for (var cap = 0; cap < namedNow.length; cap++) {
      if (!namedNow[cap].hasAttribute('data-order-form-cart-key') && !namedNow[cap].hasAttribute('data-order-form-original-name')) {
        namedNow[cap].setAttribute('data-order-form-original-name', namedNow[cap].getAttribute('name').replace(/^properties\[/, '').replace(/\]$/, ''));
      }
    }
    applyCartPropertyNames(form, state.facts); // (a) restore cart-key twin names from the contract
    var staticNamed = form.querySelectorAll('[data-order-form-original-name]');
    for (var st = 0; st < staticNamed.length; st++) {
      staticNamed[st].setAttribute('name', 'properties[' + staticNamed[st].getAttribute('data-order-form-original-name') + ']'); // (b) restore static-named twins
    }
    // (1) Direct-copy : every VISIBLE input that carries a data-order-form-property.
    var visible = form.querySelectorAll('[data-order-form-property]');
    for (var i = 0; i < visible.length; i++) {
      var el = visible[i];
      if (el.type === 'hidden') {
        continue;
      }
      var token = el.getAttribute('data-order-form-property');
      if (!hidden[token]) {
        continue;
      }
      // A field inside a hidden section (a DV cart's org/address inputs after an OV/EV
      // switch) must NOT carry its typed value to the cart : CLEAR the hidden twin instead
      // of copying it, mirroring HARD-REQUIREMENT #2's active SAN clear (PR#17 #3).
      if (isInHiddenSection(el)) {
        UI.clearHidden(hidden[token]);
        continue;
      }
      // Write the TRIMMED value so the cart matches what was validated : collectValues() gates
      // on (el.value || '').trim(), so a field entered as "  ada@example.com  " passes the
      // email-format check but must reach the middleware WITHOUT the padding (PR#17 round-2 #1 :
      // _End User E-Mail Address is the field the middleware is most sensitive to). Trim every
      // direct-copy field for the same write-equals-validated guarantee.
      UI.writeHidden(hidden[token], (el.value || '').trim());
    }
    // (1b) DERIVED : End User Telephone Number (middleware-01 commit a5b3879, operator rulings 2026-07-24) --
    // COMPOSED (dial + separator-stripped digits) via the ONE composition source, wiring style (b), so the
    // direct-copy loop above never sees it. A parser-hidden row CLEARS the twin (the same stale-value posture
    // as the org/address clear above) ; a blank composition clears too, and the step-(5) skip-empty strip then
    // removes the twin's name, so an optional un-filled phone never posts a blank property.
    if (hidden.telephone) {
      var phoneRowWt = telephoneRowEl(form);
      var composedPhone = (phoneRowWt && !isRowHidden(phoneRowWt)) ? composeTelephoneValue(phoneRowWt) : '';
      if (composedPhone === '') {
        UI.clearHidden(hidden.telephone);
      } else {
        UI.writeHidden(hidden.telephone, composedPhone);
      }
    }
    // (2) Derived primary input : CSR or FQDN (single-active). The normalised primary
    // domain is whatever the active input validated to (state.primaryInput.domain). The CSR
    // element is the one wirePrimaryInput resolved FORM-SCOPED (PR#17 r3 #1), not a hardcoded
    // document.getElementById of the default of_id. The CSR is written UNTRIMMED on purpose --
    // it is a PEM block whose surrounding whitespace the middleware parser tolerates, unlike the
    // direct-copy fields above which are trimmed to match collectValues' validation (PR#17 r11 #5).
    var csrEl = state.primaryCsrEl;
    if (state.primaryInput.kind === 'csr' && csrEl && hidden.csr) {
      UI.writeHidden(hidden.csr, csrEl.value);
    } else if (hidden.csr) {
      // Active primary is FQDN (or unvalidated) : CLEAR the _CSR twin so a stale CSR from an
      // earlier CSR-mode submit cannot reach the cart alongside an FQDN primary on an AJAX
      // add-to-cart page (the same stale-property leak HARD-REQUIREMENT #2 closes for the SAN
      // fields + PR#17 #3 closes for the OV/EV org fields). PR#17 r11 #1.
      UI.clearHidden(hidden.csr);
    }
    if (hidden['primary-fqdn']) {
      // PRIMARY FQDN is written ONLY for a DOMAIN-BEARING primary : a validated CSR (kind 'csr', Primary FQDN = the
      // extracted Common Name) OR a validated FQDN / AutoCSR / CaaS-domain (kind 'fqdn', Primary FQDN = the parsed
      // domain). For ANY other active primary -- a CaaS-extension ACME `text` primary, or an un-validated primary --
      // the twin is CLEARED, so a domain typed under a previous variant can never leak into the cart (the same
      // stale-property posture as the org / address / CSR clears above).
      //
      // PROVENANCE-KEYED (universal-form rebuild, 2026-06-28) : before the primary became a DATA-DRIVEN slot, this
      // keyed off the CaaS [data-order-form-field-state="domain"] CARD's visibility (the domain was a CaaS-only
      // field-state card). The rebuild made the domain a primary SLOT (data-order-form-primary-input-type="domain")
      // SHARED by SSL (autocsr) + CaaS (domain), with NO field-state attr -- so the discriminator is now the value's
      // OWN provenance, state.primaryInput.kind, not a CaaS-specific card lookup. This is STRICTLY correct for SSL CSR
      // mode too : kind 'csr' writes the CN even though the domain SLOT is hidden (the csr slot is shown), which a
      // slot-visibility check would have wrongly cleared. refreshForVariant resets primaryInput to {kind:null} on every
      // variant switch, so a switch to the ACME extension (kind null) clears any domain carried by the prior variant.
      var primaryKind = state.primaryInput.kind;
      if (primaryKind === 'csr' || primaryKind === 'fqdn') {
        UI.writeHidden(hidden['primary-fqdn'], state.primaryInput.domain || '');
      } else {
        UI.clearHidden(hidden['primary-fqdn']);
      }
    }
    // (3) Derived primary token : the primary DcvManager's chosen DCV token. DCV LEAK GUARD (universal-form rebuild,
    // 2026-06-28) : the DCV card is now parser-gated and HIDDEN for any SKU whose fields.dcv === 'hidden' (every
    // normal CaaS SKU). A hidden DCV card must NOT carry a token to the cart -- CLEAR the twin, the SAME stale-
    // property posture as the primary-fqdn / org / address / CSR clears above (defense-in-depth : getToken() is
    // also null when no method is chosen, and setupPrimaryDcv rebuilds a fresh manager on a variant switch, but
    // the explicit clear matches every other hidden card and survives a future refactor). The DCV card is FOUND by
    // its data-order-form-field-state="dcv" (the query below) ; isInHiddenSection then reads its COMPUTED display via
    // closest('[data-order-form-section]') -- the card carries data-order-form-section="dcv" too. SSL keeps DCV shown
    // (fields.dcv='required'), so the query resolves a visible section and the token is written normally.
    if (hidden['primary-token']) {
      var dcvSection = form.querySelector('[data-order-form-field-state="dcv"]');
      if (dcvSection && isInHiddenSection(dcvSection)) {
        UI.clearHidden(hidden['primary-token']);
      } else if (state.primaryDcv) {
        UI.writeHidden(hidden['primary-token'], state.primaryDcv.getToken() || '');
      }
    }
    // (4) SAN shells : SanBuilder owns these (positional, index-aligned). LEAK-SAFETY NOTE (owner 2026-07-01) : this
    // is the ONE cart write NOT guarded by isInHiddenSection -- ON PURPOSE. The SAN card DELIBERATELY omits
    // data-order-form-section (see order-form-v2-san-builder.liquid "DELIBERATE ATTRIBUTE OMISSION", PR #34 review #2)
    // because it has NO direct-copy [data-order-form-property] twins -- SanBuilder owns each row's value POSITIONALLY
    // -- so the section-based clear does not apply. A hidden / stale SAN list is instead kept out of the cart by THREE
    // OTHER layers : (a) rebuildToCount(sanCount) tears the rows down to the server's count (0 for a single-domain SKU
    // -> empty twins) ; (b) refreshForVariant explicitly clears BOTH SAN twins on an MD->non-MD switch (HARD-REQ #2) ;
    // (c) applyCartPropertyNames leaves the Additional FQDN / tokens twins UNNAMED unless the SKU's cartProperties
    // lists them, so an unexpected value would not POST anyway. If a future change ever lets the SAN card hide
    // MID-SESSION without a rebuild, re-establish one of those layers (or add data-order-form-section + the
    // isInHiddenSection guard) rather than relying on this write being reached only while the card is shown.
    if (state.sanBuilder) {
      state.sanBuilder.writeHiddenFields({ additionalDomainsHidden: hidden['additional-fqdn'], validationTokensHidden: hidden['validation-tokens'] });
    }
    // (5) SKIP EMPTY -- EMIT ONLY NON-EMPTY CART PROPERTIES (owner 2026-07-02) : a cart line-item property is submitted
    // IFF its trimmed value is non-empty. Runs LAST, after every value above is written (direct-copy, derived primary,
    // DCV token, SAN shells + the hidden-section clears), so it sees each twin's FINAL submit-time value. Strip the
    // name from ANY property twin whose value is blank -- BOTH the cart-key twins AND the static-named DCV tokens.
    // SCOPE (owner 2026-07-02 ; PR #41 review) : this selector -- AND the step-(0) snapshot/restore -- is scoped to
    // HIDDEN <input> twins (input[type="hidden"][name^="properties["]). That makes the "cart TWINS only" invariant
    // EXPLICIT : a VISIBLE property-named input, were one ever added to the form, is NOT stripped (a shopper-facing
    // field is not a twin) ; and the variant `id` (not a properties[...] name) is untouched. 100% coverage TODAY
    // because EVERY v2 cart-property twin (the cart-key twins AND the static _Validation Token / _Validation Tokens)
    // is a hidden <input>. FUTURE-AWARENESS : a static-named property added as a <select> / <textarea> (any non-hidden
    // -<input> element carrying a properties[...] name) would be MISSED by BOTH the strip and the snapshot -- widen the
    // shared CART_PROPERTY_TWIN_SELECTOR const (one place) to include it then. A nameless <input> is not POSTed, so the blank property is dropped
    // from the cart. Restored at the next writeThrough start (step (0)), so a re-submit of a now-populated field emits
    // it again. SAFE + a NON-decision (verified in middleware-01, owner 2026-07-02) : the webhook's findPropertyValue
    // returns undefined for an ABSENT property and treats it identically to an empty string (sanitizeInput(... || '') ;
    // validateProductRequiredFields gates on non-empty), so omitting a blank property is behaviourally identical to
    // sending it empty -- zero middleware change, zero /api/sku contract change. It decides NOTHING about what a
    // product requires : a REQUIRED field is already guaranteed non-empty at submit (the gate blocks an empty required
    // field), so it is ALWAYS emitted -- this sweep can only ever drop a genuinely-blank OPTIONAL / inapplicable
    // property. Names + visibility + required/optional/hidden still come entirely from the parser ; the form applies.
    var named = form.querySelectorAll(CART_PROPERTY_TWIN_SELECTOR); // hidden TWINS only (see SCOPE note above)
    for (var n = 0; n < named.length; n++) {
      var nv = named[n].value;
      if (typeof nv !== 'string' || nv.trim() === '') {
        named[n].removeAttribute('name'); // blank -> not POSTed ; name restored at the next writeThrough start (step 0)
      }
    }
  }

  // FormValidator returns LOGICAL field ids for the value-based checks (the contact / org
  // required-field ids + 'contact-email' + 'san-container'), but the rendered DOM uses prefixed
  // ids (order-form-contact-section-first-name, ...). This maps each logical id to its
  // data-order-form-property token so onSubmit can find the REAL element by querying the visible
  // input that carries it -- derived from the DOM so it survives a configurable section of_id.
  var VALIDATOR_FIELD_TO_PROPERTY = {
    'contact-first-name': 'first-name',
    'contact-last-name': 'last-name',
    'contact-email': 'email',
    'org-name': 'org-name',
    'org-address': 'address',
    'org-city': 'city',
    'org-state': 'state',
    'org-postal': 'postal',
    'org-country': 'country',
    // CaaS direct-copy fields : resolveFieldEl maps a CaaS gate error back to its visible input
    // (the ACME Account ID / Validation ID field) for the error region + focus.
    'caas-acme-account': 'acme-account-id',
    'ov-anchor-order': 'ov-anchor-order'
  };

  // Resolve a validator field id to the real, FORM-SCOPED rendered DOM element (PR review : the
  // block path used the logical id directly, so contact/org/SAN error regions + aria-invalid +
  // focus silently no-op'd). Resolution is FORM-SCOPED throughout (PR#17 r3 #1) : it survives a
  // configurable of_id AND a multi-form page (document.getElementById would return ANOTHER form's
  // element with the same default id). Logical contact/org ids map via their data-order-form-property
  // token ; san-container maps to the SAN container ; ids the validator already emits as real DOM
  // ids (the primary input, the SAN row ids, the dcv-section default) are looked up form-scoped by id.
  function resolveFieldEl(form, validatorFieldId) {
    var token = VALIDATOR_FIELD_TO_PROPERTY[validatorFieldId];
    if (token) {
      var el = form.querySelector('[data-order-form-property="' + token + '"]:not([type="hidden"])');
      if (el) {
        return el;
      }
    }
    if (validatorFieldId === 'san-container') {
      var container = form.querySelector('[data-order-form-san-container]');
      if (container) {
        return container;
      }
    }
    // The telephone is a DERIVED field (no data-order-form-property on its visible controls), so the token map
    // above cannot resolve it : anchor its gate error to the national input (which carries the shared
    // aria-describedby error region for both controls). Same named-hook precedent as 'san-container' above.
    if (validatorFieldId === 'telephone') {
      var nationalEl = form.querySelector('[data-order-form-validate="phone"]');
      if (nationalEl) {
        return nationalEl;
      }
    }
    // A real DOM id the validator already emits : resolve it FORM-SCOPED (an exact [id="..."] match
    // within this form), never document-wide.
    return form.querySelector('[id="' + validatorFieldId + '"]');
  }

  // Clear the per-field error state a PRIOR submit attempt surfaced, so it does not accumulate
  // across attempts (PR#17 r3 #2 : a fixed-then-refailed sequence otherwise left the earlier field's
  // region visible + aria-invalid still set). FORM-SCOPED. Two distinct scopes, deliberately :
  //  - error REGIONS (<id>-error) are un-hidden ONLY by a submit (UI.showMessage) -- blur validation
  //    never touches them -- so hiding ALL of them each submit is safe and clears the stale one.
  //  - aria-invalid is set by BOTH the submit path AND blur validation (wirePrimaryInput / wireSanRow
  //    setFieldState on a failed parse). A blanket reset of every [aria-invalid="true"] would clobber
  //    a genuinely-invalid BLUR-set field the submit never reached (e.g. an invalid SAN row when the
  //    gate blocks earlier on the primary), silently dropping its invalid signal until re-touched
  //    (PR#17 r3 round-2 regression). So reset aria-invalid ONLY on the field the LAST submit itself
  //    flagged (state.lastSubmitErrorEl), and only if it is still flagged -- a field the shopper has
  //    since blur-VALIDATED keeps its valid state. The form-level summary (id ends -summary, not
  //    -error) is managed separately by onSubmit.
  function resetFieldErrors(form, state) {
    var regions = form.querySelectorAll('[id$="-error"]');
    for (var i = 0; i < regions.length; i++) {
      UI.hideMessage(regions[i]);
    }
    if (state.lastSubmitErrorEl && state.lastSubmitErrorEl.getAttribute('aria-invalid') === 'true') {
      UI.setFieldState(state.lastSubmitErrorEl, 'neutral');
    }
    state.lastSubmitErrorEl = null;
  }

  // Mark the submit button in-flight on a VALID submit (owner 2026-06-30). The form is a NATIVE POST to /cart/add that
  // NAVIGATES to /cart, so a double-click in the brief window before navigation could fire a SECOND cart add. Setting
  // `aria-disabled="true"` (a) makes isSubmitInFlight block that second submit and (b) gives a gentle visual via the
  // theme's `.button[aria-disabled='true'] { opacity: 0.5 }` -- a SLIGHT dim, the label stays fully readable. (ACCEPTED
  // EDGE, PR #37 review : in Windows High-Contrast / forced-colors mode the theme's own
  // `.product-form__submit[aria-disabled='true'] { color: Window }` blanks the LABEL during this window -- HCM-only +
  // transient (navigation follows) + the HCM-forced button border still shows ; we do NOT override theme forced-colors
  // CSS to fix it, per the no-custom-override rule.)
  //
  // We deliberately do NOT use the theme's `.loading` class / reveal the `loading-spinner` here (owner 2026-06-30 :
  // "drop the spinner, keep it from vanishing"). That loading state is built for an AJAX add-to-cart -- the page stays
  // and the spinner spins during the fetch. On a NAVIGATING native POST it cannot paint cleanly : `.button.loading`
  // makes the label `color:transparent` and the page tears down before the spinner shows, so the button READ AS
  // COMPLETELY VANISHED in place (owner-reported). A plain dim renders fine and never vanishes.
  //
  // `aria-disabled` NOT the native `disabled` property : applySellable is the SOLE writer of `submitBtn.disabled` (the
  // not-sellable gate), so this uses a separate attribute. It ALSO sets `aria-busy="true"` so assistive tech announces
  // the pending state (PR #37 review #2). Idempotent ; no re-enable path (a valid submit navigates away, a failed POST
  // navigates to an error page), so this lives only for the in-flight window -- bfcache restore is handled separately.
  function setSubmitInFlight(state) {
    var btn = state.submitBtn;
    if (!btn) { return; }
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('aria-busy', 'true');             // announce the pending state to assistive tech (a11y)
  }

  // The re-entrancy guard for onSubmit : is a valid submit's /cart/add POST already in flight? aria-disabled does NOT
  // natively suppress the submit event (unlike the native `disabled` property), so this read is what makes the in-flight flag effective
  // against a double-click. Set ONLY by setSubmitInFlight (the valid path) ; a BLOCKED submit never flags it, so the
  // shopper can still retry after fixing a field.
  //
  // OWNERSHIP (PR #37 review) : the in-flight path (setSubmitInFlight / clearSubmitInFlight / the bfcache handler) is the
  // SOLE owner of `aria-disabled` on the submit button -- the mirror of applySellable owning the native `disabled`
  // property -- so this guard can trust `aria-disabled="true"` to mean "submit in flight". Do NOT set `aria-disabled` on
  // the submit button from anywhere else, or this guard would wrongly block a legitimate submit.
  function isSubmitInFlight(state) {
    return !!(state.submitBtn && state.submitBtn.getAttribute('aria-disabled') === 'true');
  }

  // The inverse of setSubmitInFlight : drop the in-flight flags so the button is submittable again. Takes the BUTTON
  // (not state) so the ONE global bfcache handler below can clear EVERY order-form on a restore. Idempotent.
  function clearSubmitInFlight(btn) {
    if (!btn) { return; }
    btn.removeAttribute('aria-disabled');
    btn.removeAttribute('aria-busy');
  }

  // BFCACHE RECOVERY, registered ONCE for the whole document (PR #37 review #1 : the earlier per-wireForm listener
  // leaked one global handler per wired form, and would duplicate if a form were ever re-wired). setSubmitInFlight
  // relies on the native /cart/add POST NAVIGATING to /cart to discard the in-flight flags ; but Safari/Firefox/Chrome
  // restore this page from the back-forward cache on a Back click with the DOM as left (button still `aria-disabled`),
  // and the re-entrancy guard would then BLOCK a legitimate re-submit -- a stuck, un-submittable button. `pageshow`
  // with e.persisted fires on a bfcache restore ; clear the in-flight flags on EVERY order-form submit button (the
  // clear is pure DOM, so it needs no per-form state). A hung POST that never navigates (offline / dropped connection)
  // is recovered by a normal page RELOAD ; NO re-enable TIMEOUT on purpose (owner-confirmed 2026-06-30) -- a timeout
  // would re-arm the button mid-submit on a slow-but-valid POST and reintroduce the very double-submit the guard
  // exists to prevent (and the browser's own network timeout navigates to an error page on a truly dropped connection).
  var bfcacheRecoveryBound = false;
  function bindBfcacheRecovery() {
    if (bfcacheRecoveryBound) { return; }            // idempotent : ONE global listener, no per-form leak / no re-wire dup
    bfcacheRecoveryBound = true;
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) { return; }                  // only a bfcache restore leaves a stale loading button behind
      var btns = document.querySelectorAll('.order-form [data-order-form-action="submit"]');
      for (var i = 0; i < btns.length; i++) { clearSubmitInFlight(btns[i]); }
    });
  }

  // The submit handler. Builds the gate context, validates, and either BLOCKS (preventDefault
  // + field error + the form-level live-region summary + focus) or writes the cart through and
  // lets the POST proceed.
  //
  // FAIL CLOSED on ANY unexpected error (PR#17 r5 minor 1 + r6 #4) : the gate evaluation
  // (buildSubmitContext -> validate) AND the cart write-through both run here, at submit time,
  // INSIDE this event listener. An exception anywhere before e.preventDefault() -- e.g. a shifted
  // SanBuilder row-shape contract dereferenced in buildSanRows, or a write-through failure -- would
  // let the native POST proceed with an UNVALIDATED or PARTIALLY-WRITTEN cart (a fail-OPEN, and one
  // that bootstrap's wireForm .catch does NOT cover, since that only guards the async setup, not
  // this listener). So the whole handler is wrapped : on any throw, block the submit + surface the
  // form-level error, matching the gate's fail-closed posture for a flow that drives certificate
  // issuance.
  function onSubmit(e, form, state) {
    try {
      // RE-ENTRANCY GUARD (owner 2026-06-30) : if a prior VALID submit already put the button in flight (its /cart/add
      // POST is round-tripping before the page navigates to /cart), a double-click would fire a SECOND submit -> a
      // duplicate cart add. Block it. This MUST be first : aria-disabled does not natively stop the submit event, so
      // only this explicit check makes the in-flight flag effective. A blocked (invalid) submit never sets the flag.
      if (isSubmitInFlight(state)) {
        e.preventDefault();
        return;
      }
      // FAIL CLOSED on missing SKU facts (PR#17 r17 #1 ; owner decision 2026-06-19 : "block + retry,
      // fail-closed"). A facts-load failure (GET /api/sku/:sku !ok -> SkuParser returns null) -- or a
      // submit before facts resolve -- leaves state.facts null, which blinds the ENTIRE SKU contract
      // (validationLevel, isWildcard, sanCount, dcvMethods, requiresCSR), not just org. The gate then
      // cannot validate anything reliably : without the server's fields.* the gate cannot know org/address are
      // required, so a form that still showed them (with no facts) would let an empty-org order through (a
      // fail-OPEN inconsistent with the rest of the gate). So block + surface a "cannot validate, please retry" summary (the
      // same posture as the catch below) rather than degrade to an unenforced contract. SkuParser does
      // NOT cache failures, so a retry re-fetches.
      if (!state.facts) {
        e.preventDefault();
        resetFieldErrors(form, state);
        if (state.summaryEl) {
          // Terse token ; the final customer sentence is the owner's Phase-3 content pass.
          UI.announce(state.summaryEl, copyOf(state.config, 'form.summary.unableToValidate'));
        }
        return;
      }
      var ctx = buildSubmitContext(form, state);
      var result = state.formValidator.validate(ctx);
      // Reset the stale per-field error state THIS handler surfaced on a PRIOR attempt BEFORE
      // surfacing this attempt's result (PR#17 r3 #2). The current failing field, if any, is
      // re-surfaced immediately below ; a PASS leaves the prior submit error cleared.
      resetFieldErrors(form, state);
      if (!result.ok) {
        e.preventDefault();
        var err = result.error || { fieldId: 'order-form', token: copyOf(state.config, 'form.summary.correctForm') };
        // Per-field : un-hide the field's error region (round-8 order) + set aria-invalid + focus.
        // resolveFieldEl returns the FORM-SCOPED element ; the region is this form's `<id>-error`.
        var fieldEl = resolveFieldEl(form, err.fieldId);
        var regionEl = (fieldEl && fieldEl.id) ? form.querySelector('[id="' + fieldEl.id + '-error"]') : null;
        if (regionEl) {
          UI.showMessage(regionEl, err.token);
        }
        if (fieldEl) {
          UI.setFieldState(fieldEl, 'error');
          // Remember the field THIS submit flagged so the next submit can clear exactly it (and not
          // a blur-set invalid field the gate never reached) -- see resetFieldErrors (PR#17 r3 #2).
          state.lastSubmitErrorEl = fieldEl;
          if (typeof fieldEl.focus === 'function') {
            fieldEl.focus();
          }
        }
        // Form-level (brief part 2) : the ONE aria-live="assertive" summary shows a SINGLE GENERIC line, NOT
        // the raw field token. err.token is a terse field-specific key (e.g. "ADDITIONAL FQDN") that reads as
        // a cryptic fragment when surfaced in the summary, far below its field ; the specific token stays in
        // the field's own -error region (above). The summary just points the shopper back to the highlighted
        // field. (The facts-null / catch / not-sellable paths above/below already use their own specific lines.)
        if (state.summaryEl) {
          // FIELD error (regionEl set above) : the specific token already shows in the field's own -error region,
          // so the summary shows a GENERIC line that points back to the highlighted field (err.token can be a terse
          // field fragment that reads cryptically far below its field). FORM-LEVEL error (fieldId:'order-form' : the
          // three fail-closed guards + the P1 unhandled-family guard + the null-error fallback, none of which
          // resolveFieldEl can anchor to a field, so regionEl is null) : there is NO field region, so surface the
          // SPECIFIC err.token here instead -- every order-form token is a full sentence (never a cryptic fragment),
          // and an accurate "refresh / contact us" beats a generic "correct the highlighted fields" with nothing
          // highlighted (PR #32 r7, owner-approved 2026-06-26).
          UI.announce(state.summaryEl, regionEl ? copyOf(state.config, 'form.summary.correctFields') : err.token);
        }
        return;
      }
      // PASS : hide the summary, write the cart, show the in-flight feedback, let the native POST proceed.
      if (state.summaryEl) {
        UI.hideMessage(state.summaryEl);
      }
      writeThrough(form, state);
      // Flag in-flight (aria-disabled + the theme opacity:0.5 dim, NO spinner) so a double-click is blocked while the
      // native /cart/add POST round-trips. Runs AFTER writeThrough (so a write-through throw lands in the catch with NO
      // stuck in-flight state) and does NOT preventDefault, so the native POST proceeds + navigates to /cart (owner 2026-06-30).
      setSubmitInFlight(state);
    } catch (submitErr) {
      // Any unexpected error in gate-evaluation or write-through -> block the POST (no unvalidated /
      // partial cart) + surface the form-level error. Terse token ; final copy is the owner's 3c pass.
      e.preventDefault();
      // Clear any prior-attempt per-field error too (PR#17 r16 #1) : the throw may have happened in
      // buildSubmitContext/validate BEFORE resetFieldErrors ran in the try, so without this a stale
      // field region + aria-invalid would linger next to the generic summary. Consistent with the
      // blocked/pass paths, which both reset.
      resetFieldErrors(form, state);
      log.error('ORDER FORM : SUBMIT HANDLER FAILED', submitErr && submitErr.message ? submitErr.message : submitErr);
      if (state.summaryEl) {
        UI.announce(state.summaryEl, copyOf(state.config, 'form.summary.unableToSubmit'));
      }
    }
  }

  // FAIL-LOUD on a DEGRADED /api/sku (PR #34 review). SHARED by the initial render AND refreshForVariant so BOTH
  // paths behave identically (the variant-switch path previously lacked this -- review #1). SkuParser.getFacts
  // degrades a failed lookup to null WITHOUT rejecting, and a 200 with NO primaryMethods (e.g. the middleware not
  // yet shipping the list) would render a BLANK, un-orderable primary with only a console warn. Reveal the EXISTING
  // [data-order-form-init-error] "could not be loaded" message so the shopper sees an explanation. TOGGLE (not
  // one-way) : a later GOOD variant switch HIDES it again, so a transient bad variant recovers cleanly. Submit ALSO
  // fails closed at the gate ; this is the VISIBLE half. Every real product the parser describes has >= 1 primary
  // method, so an empty list can only be a degraded / not-yet-deployed response. Returns true when degraded.
  function revealInitErrorIfDegraded(form, facts) {
    var degraded = !facts || !Array.isArray(facts.primaryMethods) || facts.primaryMethods.length === 0;
    var initErrEl = form.querySelector('[data-order-form-init-error]');
    if (initErrEl) { initErrEl.style.display = degraded ? '' : 'none'; }
    if (degraded) {
      log.warn('ORDER FORM : /api/sku HAD NO USABLE PRIMARY METHODS - INIT ERROR SHOWN, SUBMIT FAILS CLOSED', { sku: facts && facts.sku });
    }
    return degraded;
  }

  // ONE VISIBILITY SEAM (Option A universal-form rebuild, 2026-06-28). applyFieldVisibility is the SINGLE function
  // that shows/hides EVERY gated card. Each card -- org / address / contact / dcv / san / the CaaS field sections /
  // every future card -- carries data-order-form-field-state="<fieldsKey>" and ships INLINE display:none ; this
  // reveals each iff facts.fields[key] !== 'hidden'. The old split is GONE : there is no applySectionVisibility, no
  // resolveShow, and no config.showOrg/showAddress/showContact flags (with their 'sku-driven' sentinel). The
  // `fields` map from GET /api/sku is the single source of truth, so the form can never carry a show* flag that
  // drifts from it. The old consistency-warn (fields.X='required' but a hardcoded show* resolved hidden) is now
  // STRUCTURALLY IMPOSSIBLE (no show* flag exists to drift) and is dropped : a required field whose card is absent
  // is caught fail-closed by the gate's GATE_ENFORCED_FIELDS coverage guard at submit, and a rendered card whose
  // key is absent from `fields` warns below.
  //
  // CONTRACT (PR#17 r7 #1, pinned next to isInHiddenSection too) : visibility is toggled ONLY via INLINE
  // style.display. Reveal sets inline display='' (defer to the card's natural CSS layout) ; hide sets inline
  // display:none. The leak guard isInHiddenSection reads COMPUTED display, which stays consistent with this ONLY
  // while cards are never hidden-by-default by a CSS class -- an inline '' cannot override a class's display:none.
  // Every card root must therefore ship INLINE display:none, not a class. A key with no `fields` entry defaults to
  // HIDDEN (fail-closed : an unknown field stays out of the cart, never silently shown).
  function applyFieldVisibility(form, facts) {
    var fields = (facts && facts.fields) || null;
    var sections = form.querySelectorAll('[data-order-form-field-state]');
    for (var i = 0; i < sections.length; i++) {
      var key = sections[i].getAttribute('data-order-form-field-state');
      // DIAGNOSTIC (PR #32 review r5 #2) : a rendered CaaS section whose key is ABSENT from the server's `fields`
      // map (a snippet key typo like "acme" for "acmeAccount", or a SKU not yet migrated to that key) would stay
      // HIDDEN FOREVER -- fail-closed, but that mode is un-orderable. Warn LOUD so the drift is diagnosable from
      // the console. Gated on `fields` PRESENT but this key missing : a wholly-absent `fields` is already reported
      // once by buildSubmitContext's submit-time warn, so this does not double-report. (Real /api/sku always ships
      // all field keys -- acmeAccount:'hidden' etc. -- so this fires only on genuine drift, not a legit hidden mode.)
      if (fields && !Object.prototype.hasOwnProperty.call(fields, key)) {
        // Wording GENERALIZED 2026-07-24 (was "CaaS SECTION KEY ..." from its PR #32 origin) : the seam is
        // universal and the telephone row is its first non-CaaS firer (it fires on every load until the
        // middleware PR shipping fields.telephone deploys -- console-only, harmless, and by design : absent
        // key = hidden, fail-closed).
        log.warn('ORDER FORM : SECTION KEY NOT IN /api/sku FIELDS - SECTION STAYS HIDDEN', { key: key });
      }
      var show = !!(fields && fields[key] && fields[key] !== 'hidden');
      sections[i].style.display = show ? '' : 'none';
      // PARSER-STATE STAMP (operator + middleware-agent ruling 2026-07-24) : project this element's tri-state
      // onto the DOM so DOM-side readers -- the card chip's completeness walk (wireOneCardStatus.allProvided) --
      // consume the PARSER's state, never a form-side rule. Stamped from the SAME fields map that decided the
      // display above (absent key = 'hidden'), refreshed on every variant switch : a pure projection of
      // /api/sku, so the form still decides NOTHING.
      sections[i].setAttribute('data-order-form-field-state-value', (fields && fields[key]) || 'hidden');
      // STATE-KEYED CARD COPY (operator 2026-10-17, the OV CaaS launch) : a card may carry two wordings of its
      // intro and guidance, marked data-order-form-copy-state="optional" / "required" (the required ones ship
      // inline display:none, like the card roots) ; show the one matching the state just stamped. Here, in the
      // ONE visibility seam, and never in the stylesheet : the no-display:none contract on order-form-v2.css
      // (tests/snippet-contract.test.js) exists so no CSS can hide form markup. The Validation ID card is the
      // user : optional on the traditional OV / EV products, required on a new CaaS OV order.
      var requiredState = (fields && fields[key]) === 'required';
      var copies = sections[i].querySelectorAll('[data-order-form-copy-state]');
      for (var c = 0; c < copies.length; c++) {
        var wanted = copies[c].getAttribute('data-order-form-copy-state') === (requiredState ? 'required' : 'optional');
        copies[c].style.display = wanted ? '' : 'none';
      }
      // The stamp can change which PROMPT a card's chip must show (the state-keyed prompt in showCardStatus),
      // and the chip renders only on its fields' input / blur / change events : tell the card's status wiring
      // (wireOneCardStatus) that the state moved, so the chip re-reads it now rather than at the next keystroke.
      // A non-bubbling event on the stamped element itself, so a stamped ROW (the telephone) never reaches
      // its card's listener.
      sections[i].dispatchEvent(new Event('order-form-field-state'));
    }
  }

  // CART-PROPERTY NAMES are CONTRACT-OWNED (owner 2026-06-30). GET /api/sku returns facts.cartProperties : a map
  // { logical-key -> the EXACT Shopify line-item property name to emit, with the leading underscore ALREADY baked in
  // (`_` = hidden in the cart/checkout UI ; no `_` = VISIBLE to the customer) }. The form emits each cart property
  // under the EXACT name the parser gives and makes NO cart-visibility decision + hardcodes NO property name (owner
  // ruling : the parser owns this, not the form -- e.g. S/MIME's e-mail is VISIBLE `End User E-Mail Address`, SSL/CaaS
  // hide it `_End User E-Mail Address`, with NO productFamily branch here). Each cartProperties-owned hidden input in
  // order-form-v2-hidden-properties.liquid declares its contract KEY via data-order-form-cart-key and ships with NO
  // `name`. This sets name="properties[<facts.cartProperties[key]>]" at runtime, on the INITIAL render AND every
  // variant switch (the map can differ per SKU). A key ABSENT from the map leaves the input WITHOUT a name (so it does
  // not submit -- fail-closed, never a guessed name) + warns. The two always-hidden DCV tokens (_Validation Token /
  // _Validation Tokens) have NO visibility choice, so they are NOT in cartProperties : they keep their own `name` in
  // the snippet and carry no data-order-form-cart-key, so this leaves them untouched.
  function applyCartPropertyNames(form, facts) {
    var map = (facts && facts.cartProperties) || null;
    var els = form.querySelectorAll('[data-order-form-cart-key]');
    for (var i = 0; i < els.length; i++) {
      var key = els[i].getAttribute('data-order-form-cart-key');
      var name = (map && Object.prototype.hasOwnProperty.call(map, key)) ? map[key] : null;
      if (name) {
        els[i].setAttribute('name', 'properties[' + name + ']');
      } else {
        // No contract name -> do NOT submit this property (fail-closed) ; drop any stale name from a prior variant.
        els[i].removeAttribute('name');
        log.warn('ORDER FORM : CART PROPERTY KEY NOT IN /api/sku cartProperties - PROPERTY NOT EMITTED', { key: key });
      }
    }
  }

  // Read the SUBJECT the SKU parser put on the CSR primary method (facts.primaryMethods, the key:'csr' entry). It is a
  // PARSER FACT the form READS and APPLIES -- it never decides the type itself. 'domain' = the decoded CSR Common Name
  // is a domain (SSL) ; 'email' = the CN is an e-mail (S/MIME) ; undefined = the parser did not describe it, so the
  // caller FAILS CLOSED (no default, no guess). @param {Object} facts @returns {string|undefined}
  function csrMethodSubject(facts) {
    var methods = (facts && Array.isArray(facts.primaryMethods)) ? facts.primaryMethods : [];
    for (var i = 0; i < methods.length; i++) {
      if (methods[i] && methods[i].key === 'csr') {
        return methods[i].subject;
      }
    }
    return undefined;
  }

  // Configure the End User E-Mail Address field from the CSR method's `subject` fact (the parser decides the routing,
  // the form applies it). subject:'email' (S/MIME) -> the certified e-mail comes from the CSR's CN, so the field is a
  // READ-ONLY DERIVED display : `readonly` (NOT `disabled` -- readonly keeps the value focusable + screen-reader-
  // announced so the shopper can READ their certified e-mail, a11y ; the cart value flows through the hidden twin via
  // writeThrough either way, PR #38 review) + cleared on render, then JS-filled from the validated CN (the CSR-validate
  // path), so there is NO typed e-mail box. subject:'domain'/absent -> a normal typed contact e-mail (SSL/CaaS).
  //
  // KEYED OFF THE SKU's csr-subject, NOT the ACTIVELY-selected method : csrMethodSubject scans primaryMethods for the
  // csr entry, so this derives the e-mail whenever the SKU HAS a subject:'email' csr method. That is correct TODAY
  // because the only subject:'email' family (S/MIME) is CSR-ONLY (a single primary method, always active). A future SKU
  // that offered a subject:'email' csr method ALONGSIDE another primary method would need this gated on the ACTIVE
  // method instead (selecting the non-csr method would otherwise leave the e-mail read-only + underivable) -- no such
  // SKU exists in the contract, so no guard is built (PR #38 review : documented assumption, not speculative code).
  //
  // Run on the initial render AND every variant switch, so the field's nature always traces to the CURRENT SKU fact,
  // never to an earlier interaction. @param {HTMLElement} form @param {Object} facts @returns {void}
  function applyContactEmailMode(form, facts) {
    var emailEl = form.querySelector('[data-order-form-property="email"]:not([type="hidden"])');
    if (!emailEl) {
      return;
    }
    if (csrMethodSubject(facts) === 'email') {
      emailEl.readOnly = true;                                       // read-only mechanism (a11y : focusable + announced, unlike `disabled`)
      emailEl.setAttribute('data-order-form-csr-derived', '');       // PROVENANCE flag : this value is machine-filled from the CSR CN
      emailEl.value = '';                                            // cleared until the CSR is validated (v1 "extracted from CSR" shape)
      UI.setFieldState(emailEl, 'neutral');
      // A11Y (owner 2026-07-01) : reference the visually-hidden derived-state note so a SCREEN READER announces WHY the
      // field is pre-filled + locked (sighted users get that from the muted styling). aria-describedby is a SPACE-
      // separated id list : the error region FIRST (so a live error still announces), then the note. The note element
      // (<id>-derived-note) is rendered always-present + visually-hidden by order-form-v2-contact-section.liquid ;
      // adding its id HERE is what makes AT announce it (an unreferenced describedby target is silent). The <id>-error
      // / <id>-derived-note convention matches exactly what that snippet renders for this input.
      emailEl.setAttribute('aria-describedby', emailEl.id + '-error ' + emailEl.id + '-derived-note');
    } else {
      // Leaving (or never in) e-mail-derived mode. Use the explicit PROVENANCE marker (not `readonly`) to decide whether
      // the value was machine-derived : a previously CSR-derived address must NOT carry over into a typed-e-mail variant
      // as if the shopper typed it (PR #38 review), but a user-TYPED contact e-mail (no marker) is preserved across a
      // variant switch (the existing convention). The marker decouples provenance from the read-only mechanism, so a
      // future change that toggles readonly for another reason cannot misfire this clear.
      if (emailEl.hasAttribute('data-order-form-csr-derived')) {
        emailEl.removeAttribute('data-order-form-csr-derived');
        emailEl.value = '';
        UI.setFieldState(emailEl, 'neutral');
      }
      emailEl.readOnly = false; // typed contact e-mail
      // A11Y : a typed contact e-mail has NO derived-state note -- restore aria-describedby to the error region ONLY
      // (dropping the derived-note id the derived branch adds), so the note is never announced for an editable field.
      emailEl.setAttribute('aria-describedby', emailEl.id + '-error');
    }
  }

  // BRANCH 1 (owner 2026-07-01) : warn when the SKU SHOWS the DCV section (fields.dcv is not hidden) but ships NO
  // dcvMethods -- a malformed /api/sku where the customer would see an EMPTY validation-method picker and the gate
  // fails closed. CONDITIONED on fields.dcv being shown : an empty dcvMethods list is CORRECT for a DCV-hidden SKU
  // (CaaS / S-MIME), so those never false-warn. Same warn family as the other /api/sku-contract-gap warns ; run from
  // the render path (initial + every variant switch). @param {Object} facts @returns {void}
  function warnDcvContractGap(facts) {
    var dcvState = facts && facts.fields && facts.fields.dcv;
    var methods = facts && facts.dcvMethods;
    if (dcvState && dcvState !== 'hidden' && (!Array.isArray(methods) || methods.length === 0)) {
      log.warn('ORDER FORM : /api/sku SHOWS DCV (fields.dcv=' + dcvState + ') BUT HAS NO dcvMethods - VALIDATION-METHOD PICKER EMPTY, SUBMIT FAILS CLOSED', { sku: facts && facts.sku });
    }
  }

  // Fill the country <select> from GET /api/reference/countries (iso2 value, name label).
  // Degrades quietly (warn) on a failed lookup -- the placeholder option remains.
  function fillCountries(form, outcome) {
    var select = form.querySelector('[data-order-form-validate="country"]');
    if (!select) {
      return;
    }
    if (!outcome || !outcome.ok || !outcome.data || !Array.isArray(outcome.data.countries)) {
      log.warn('ORDER FORM : COUNTRY LIST UNAVAILABLE - SELECT LEFT AS PLACEHOLDER');
      return;
    }
    // Defensive clear : drop any previously-appended country options (every option with a
    // non-empty value -- the dynamically-added iso2 entries) before refilling, so a
    // re-invocation cannot duplicate the list. The static placeholder the markup ships
    // (the `<option value="" disabled selected>` in order-form-v2-address-section.liquid, whose label
    // is rendered from order-form-v2-copy key address.placeholder.country) has an empty value and is
    // preserved. PR#17 #5.
    var existing = select.querySelectorAll('option');
    for (var c = existing.length - 1; c >= 0; c--) {
      if (existing[c].value !== '') {
        select.removeChild(existing[c]);
      }
    }
    var list = outcome.data.countries;
    for (var i = 0; i < list.length; i++) {
      var opt = select.ownerDocument.createElement('option');
      opt.value = list[i].iso2;
      opt.textContent = list[i].name;
      select.appendChild(opt);
    }
  }

  // Fill the telephone dial-code <select> from the SAME GET /api/reference/countries response the country
  // select uses (middleware-01 commit a5b3879 : every entry carries dialCode, '+'-prefixed). Entries whose
  // dialCode is null are SKIPPED -- the five uninhabited territories (BV/GS/HM/TF/UM) have no subscriber
  // numbers (operator-approved 2026-07-24) ; the COUNTRY select keeps all entries, untouched. Same
  // degrade-quietly + defensive-clear posture as fillCountries (pattern sweep). The option label is
  // "<name> (<dialCode>)" so the customer picks by COUNTRY and shared dial codes (e.g. +1) stay
  // distinguishable ; the option VALUE is the dialCode itself, which composeTelephoneValue prepends verbatim.
  function fillDialCodes(form, outcome) {
    var select = form.querySelector('[data-order-form-telephone-dial]');
    if (!select) {
      return;
    }
    if (!outcome || !outcome.ok || !outcome.data || !Array.isArray(outcome.data.countries)) {
      log.warn('ORDER FORM : DIAL CODE LIST UNAVAILABLE - SELECT LEFT AS PLACEHOLDER');
      return;
    }
    // Defensive clear, mirroring fillCountries : drop previously-appended dial options (non-empty value) so a
    // re-invocation cannot duplicate the list ; the static placeholder (empty value) is preserved.
    var existing = select.querySelectorAll('option');
    for (var c = existing.length - 1; c >= 0; c--) {
      if (existing[c].value !== '') {
        select.removeChild(existing[c]);
      }
    }
    var list = outcome.data.countries;
    for (var i = 0; i < list.length; i++) {
      if (typeof list[i].dialCode !== 'string' || list[i].dialCode === '') {
        continue; // dialCode null (uninhabited territory) : skipped, see the header comment
      }
      var opt = select.ownerDocument.createElement('option');
      opt.value = list[i].dialCode;
      opt.textContent = list[i].name + ' (' + list[i].dialCode + ')';
      select.appendChild(opt);
    }
  }

  // Build the per-row renderer for SanBuilder : clone the row <template> (NO innerHTML --
  // template.content.cloneNode) and rewrite the ROWINDEX token in every attribute to the
  // 1-based row index, producing the frozen sanRowIds. SCOPE the lookup to the SAN field
  // (PR#17 round-2 #2) : an unscoped form.querySelector('template') would clone the FIRST
  // template in the form, and the 3c assembler is free to ship another template earlier in
  // the markup. The row template always lives inside [data-order-form-field="san"] (see
  // order-form-v2-san-builder.liquid : `<template id="{{ of_id }}-row-template">` under the SAN
  // field div), so scoping there is robust against both extra templates and of_id prefixing
  // (the id is configurable, the data-attr seam is not).
  function makeSanRowRenderer(form, dcvHidden) {
    var tmpl = form.querySelector('[data-order-form-field="san"] template');
    return function (index) {
      if (!tmpl || !tmpl.content) {
        return form.ownerDocument.createElement('div');
      }
      var clone = tmpl.content.cloneNode(true);
      // Build 2 (section 8 site 2) : a CaaS SKU shows no DCV (fields.dcv 'hidden'), so the row's validation-method
      // block -- the <select>, its error region and the per-method panels, wrapped by the template's
      // [data-order-form-san-dcv] seam -- is REMOVED from the clone rather than rendered locked. With no method
      // select on the row, wireSanRow's existing null-guards skip the method wiring and the approver wiring on their
      // own. What the row KEEPS (operator finding 2026-09-02) : its status chip, which sits AFTER the seam in the
      // template so it survives the removal, and which wireSanRow's state.sanDcvHidden branch fills with
      // san.status.prompt / san.status.provided on such a row (the method hint on an SSL row). Null-guarded : a
      // custom template without the seam simply keeps its block.
      if (dcvHidden) {
        var dcvBlock = clone.querySelector('[data-order-form-san-dcv]');
        if (dcvBlock && dcvBlock.parentNode) {
          dcvBlock.parentNode.removeChild(dcvBlock);
        }
      }
      var els = clone.querySelectorAll('*');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        // CONTRACT (PR#17 r9 #2) : ROWINDEX is substituted only in ATTRIBUTE values (id / name / for /
        // aria-* / data-*), never in TEXT nodes. This is intentional + sufficient -- the row template
        // uses ROWINDEX only inside ids/attrs, never in visible copy. The 3c row template MUST keep
        // ROWINDEX out of text nodes (a literal "Domain ROWINDEX" label would NOT be substituted).
        for (var a = 0; a < el.attributes.length; a++) {
          var attr = el.attributes[a];
          if (attr.value.indexOf('ROWINDEX') !== -1) {
            attr.value = attr.value.replace(/ROWINDEX/g, index);
          }
        }
      }
      // owner 2026-06-22 : NUMBER each SAN row's FQDN label ("Additional FQDN #1", "#2", ... since the owner
      // shortened the base wording on 2026-10-17 for small screens) so a multi-SAN certificate reads clearly. The
      // base wording stays in the copy file (order-form-v2-san-builder.liquid renders the fqdn.label.additional
      // key) ; only the NUMBER is added
      // here because Liquid renders ONE template that this renderer clones per SKU SAN count -- so the renderer's
      // 1-based index is the single source of the row number. This is deliberately NOT a ROWINDEX text-token
      // substitution (ROWINDEX stays attribute-only, the r9 #2 contract above) : it targets the row's FQDN
      // floating label by its now-substituted frozen id (label[for="order-form-fqdn-input-N"]). Null-guarded :
      // a custom template without that label (or the S/MIME reuse) simply gets no number.
      var fqdnLabel = clone.querySelector('label[for="order-form-fqdn-input-' + index + '"]');
      if (fqdnLabel) {
        fqdnLabel.textContent = fqdnLabel.textContent + ' #' + index;
      }
      return clone.firstElementChild || clone.querySelector('*');
    };
  }

  // Instantiate SanBuilder for a multi-domain SKU + render facts.sanCount rows. For a
  // non-MD SKU (sanCount 0) the builder still exists but renders no rows.
  function setupSan(form, state, facts) {
    var container = form.querySelector('[data-order-form-san-container]');
    if (!container) {
      return;
    }
    // Build 2 (section 8 sites 2 + 3) : the SKU's fields.dcv 'hidden' (every CaaS SKU) is read ONCE here, from the
    // same facts the rest of the builder derives from, and handed to the row renderer (no validation-method block
    // on the row) and to the builder (a blank tokens twin). setupSan rebuilds the builder on every variant switch,
    // so the flag always describes the current SKU.
    var dcvHidden = !!(facts && facts.fields && facts.fields.dcv === 'hidden');
    // The card's intro + guidance paragraphs are written for an SSL multi-domain SKU (every row carries a
    // validation-method picker : "each SAN needs its own validation", "DNS methods are recommended"). A CaaS
    // multi-domain SALE shows the same card with no picker on any row, so both paragraphs are re-filled from
    // config.copy off the SAME fact : the SSL pair when DCV is shown, the operator's CaaS pair when it is hidden
    // (promotion PR #97 round 1, 2026-09-02). Re-run on every variant switch with the rows, so the wording always
    // describes the current SKU. Null-guarded : a custom card without the seams keeps its Liquid-rendered text.
    var sanIntroEl = form.querySelector('[data-order-form-san-intro]');
    var sanGuidanceEl = form.querySelector('[data-order-form-san-guidance]');
    if (sanIntroEl) {
      sanIntroEl.textContent = dcvHidden ? copyOf(state.config, 'san.intro.nodcv') : copyOf(state.config, 'san.intro');
    }
    if (sanGuidanceEl) {
      sanGuidanceEl.textContent = dcvHidden ? copyOf(state.config, 'san.guidance.nodcv') : copyOf(state.config, 'san.guidance');
    }
    // Operator 2026-09-02 : on a DCV-hidden SKU the guidance sits BELOW the last domain box (the other cards'
    // control-then-guidance format) ; on an SSL SKU it stays above the rows (the 2026-06-22 ruling : every row ends
    // in its own chip). Moved in the DOM off the same fact, and moved back on a variant switch the other way.
    var sanField = form.querySelector('[data-order-form-field="san"]');
    if (sanGuidanceEl && sanField && sanField.parentNode) {
      sanField.parentNode.insertBefore(sanGuidanceEl, dcvHidden ? sanField.nextSibling : sanField);
    }
    // wireSanRow reads the fact for the row chips (prompt / provided on a DCV-hidden row, the method hint otherwise).
    state.sanDcvHidden = dcvHidden;
    state.sanBuilder = new SanBuilder({
      container: container,
      renderRow: makeSanRowRenderer(form, dcvHidden),
      primaryDomainRef: function () { return state.primaryInput.domain || ''; },
      isWildcardMd: !!(facts && facts.isWildcardMultiDomain),
      dcvHidden: dcvHidden,
      domainNormalizer: state.domainNormalizer,
      dcvOptions: { dcvMethods: facts && facts.dcvMethods, copy: state.config.copy } // dcvMethods ONLY from /api/sku, verbatim ; NO fallback, NO isWildcard exclusion (owner 2026-06-29) -- absent -> empty -> fail closed
    });
    var count = facts && Number.isFinite(facts.sanCount) ? facts.sanCount : 0;
    state.sanBuilder.rebuildToCount(count);
  }

  // Instantiate the primary DcvManager + populate the method <select> from facts.dcvMethods, VERBATIM. The method
  // list comes ONLY from GET /api/sku's facts.dcvMethods -- there is NO fallback and NO form-side ordering/exclusion
  // (owner 2026-06-29 : the form holds no method list of its own and makes NO decision ; the SKU parser is the single
  // source, including which methods a wildcard SKU may use). If facts is absent, the manager receives undefined -> an
  // empty picker -> FAIL CLOSED (no method selectable -> the submit gate's dcv check blocks).
  function setupPrimaryDcv(form, state, facts) {
    var select = findPrimaryDcvSelect(form);
    state.primaryDcv = new DcvManager({
      dcvMethods: facts && facts.dcvMethods,
      copy: state.config.copy
    });
    if (select) {
      state.primaryDcv.buildMethodSelect(select);
    }
  }

  /**
   * Wire one .order-form : instantiate the modules, resolve SKU facts + the country list,
   * render the SKU-driven sections, wire the field events + the submit handler. Returns a
   * Promise that resolves when the async setup is complete (so tests can await it).
   *
   * @param {Element} form
   * @param {{config:Object, apiClient:ApiClient}} deps  (element lookups are form-scoped via
   *   form.querySelector, so no document handle is needed -- see PR#17 r3 #1)
   * @returns {Promise<void>}
   */

  /**
   * FIX 5 : is `url` safe to hand to window.open ? TRUE only for an absolute http:// or https:// URL.
   * The CSR-tool button carries data-order-form-url, which Liquid `| escape`'s (HTML-escaping) but does
   * NOT scheme-check -- a `javascript:` / `data:` / `vbscript:` URL would still reach the handler, so this
   * gate is LOAD-BEARING (the pre-written SECURITY contract in order-form-v2-csr-textarea.liquid's header).
   * Parsed via the URL constructor (handles case, leading control chars, malformed input) ; anything that
   * is not an http/https absolute URL -- including a schemeless / relative value -- is rejected fail-soft.
   * @param {string} url @returns {boolean}
   */
  function isValidPopupUrl(url) {
    if (typeof url !== 'string' || !url) {
      return false;
    }
    try {
      var proto = new URL(url).protocol.toLowerCase();
      return proto === 'http:' || proto === 'https:';
    } catch (e) {
      // URL throws on a schemeless / malformed value -> not a safe absolute URL.
      return false;
    }
  }

  /**
   * FIX 5 : wire the "Generate Certificate Signing Request (CSR) Tool" popup button. Restores v1's
   * external CSR tool, but CSP-safe (v1 used an inline onclick) and scheme-guarded : read the button's
   * data-order-form-url, validate it is http/https (reject javascript: etc.), then window.open with
   * noopener,noreferrer (v1 lacked these -- the snippet header REQUIRES them) reusing the named 'csr-popup'
   * window + v1's dimensions. OPTIONAL (fail-soft) : an FQDN-only form ships no button, an invalid URL
   * warns + no-ops, neither throws nor blocks the form.
   * @param {Element} form
   */
  function wireCsrPopup(form) {
    var popupBtn = form.querySelector('[data-order-form-action="csr-popup"]');
    if (!popupBtn) {
      return;
    }
    popupBtn.addEventListener('click', function () {
      var url = popupBtn.getAttribute('data-order-form-url');
      if (!isValidPopupUrl(url)) {
        log.warn('ORDER FORM : CSR POPUP BLOCKED - INVALID URL SCHEME', { url: String(url) });
        return;
      }
      // window name 'csr-popup' + dimensions are v1-faithful. NOTE : with `noopener` set, the HTML spec has
      // the browser treat the target name as '_blank' (the name is NOT used to locate an existing context),
      // so a repeat click opens a FRESH popup rather than reusing a named one. That is acceptable here :
      // security (sealing the opened window off from window.opener) takes priority over window reuse, and the
      // name is kept only for v1-faithfulness (harmless, since it is ignored). noopener,noreferrer = the
      // snippet's security contract (data-order-form-url is Liquid-escaped but the scheme guard above is what
      // blocks javascript:/data:).
      window.open(url, 'csr-popup', 'noopener,noreferrer,width=1000,height=800,toolbar=no,menubar=no');
    });
  }

  // STANDARD CARD STATUS (owner spec 2026-06-22, extended 2026-06-23) : every direct-input card (contact /
  // organisation / address) ENDS with a status chip that is ALWAYS shown -- a neutral PROMPT while the card is
  // incomplete ("PLEASE PROVIDE THE END USER INFORMATION") and the --success confirmation once EVERY validatable
  // field is filled (and any e-mail field is a valid address, any <select> has a chosen value -- "END USER
  // INFORMATION HAS BEEN PROVIDED"). Owner 2026-06-23 : "the End User card is missing the status text/chip when
  // the data is not filled in". The two messages are CARD-SPECIFIC, carried in the chip's data-* (rendered from
  // order-form-v2-copy : contact/org/address.status.prompt/provided) so each card words its own. UX FEEDBACK ONLY --
  // the submit gate (FormValidator) remains the sole authority on what reaches the cart, so a hidden DV org/address
  // card (whose fields stay empty + carry no events) simply shows its (unseen) prompt. Each slot's card + fields are
  // resolved once ; the listeners read live values, so no re-wire is needed on a variant switch (the same fields
  // persist). (CARD_STATUS_*_TEXT constants removed 2026-06-27 : every shipped card supplies its prompt/provided
  // text via the data-* attributes from order-form-v2-copy, so the in-JS fallback is now '' -- no hardcoded literal.)

  // SHARED CARD-STATUS CHIP CONTROLLER (owner 2026-06-23 : "every card always requires a message ... it should
  // be using shared coding"). EVERY card ends with ONE status chip that follows the SAME pattern : always shown,
  // a neutral PROMPT until the card's requirement is met, the --success confirmation once it is. This is the ONE
  // place the modifier class + text + reveal are set, so the Issuance / CSR / AutoCSR / Validation-Method / each
  // SAN row / End User / Organization / Address cards all read identically. Each card supplies only its own
  // `complete` boolean ; the PROMPT text comes from the chip's `data-order-form-card-status-prompt` (so the wording
  // lives in the markup, owner-editable) and the CONFIRMATION from `providedOverride` (a DYNAMIC confirmation, e.g.
  // the Issuance card's method-specific text, or the CSR/FQDN "...PROVIDED") falling back to a
  // `data-order-form-card-status-provided` attribute. Null-guarded. (The DCV/SAN method picker keeps its 3-state
  // hint -- locked / METHOD_REQUIRED / chosen -- which is a superset of this prompt->confirmation toggle.)
  function showCardStatus(chipEl, complete, providedOverride) {
    if (!chipEl) {
      return;
    }
    chipEl.classList.remove('order-form-status--neutral', 'order-form-status--success', 'order-form-status--error');
    chipEl.classList.add(complete ? 'order-form-status--success' : 'order-form-status--neutral');
    var prompt = chipEl.getAttribute('data-order-form-card-status-prompt') || '';
    // STATE-KEYED PROMPT (operator 2026-10-17, the OV CaaS launch) : a chip may carry a second prompt for the
    // 'required' parser state (data-order-form-card-status-prompt-required). It is read when the chip's card
    // root is stamped 'required' (data-order-form-field-state-value, the applyFieldVisibility projection of
    // /api/sku's fields.*) ; every other state, and a chip outside a stamped card (the primary slots), keeps
    // the one prompt. The Validation ID card is the user : its optional-state prompt says "only if
    // pre-validated", which a required anchor must not say. The matching paragraphs are toggled by
    // applyFieldVisibility off the same stamp.
    var stateRoot = chipEl.closest ? chipEl.closest('[data-order-form-field-state-value]') : null;
    if (stateRoot && stateRoot.getAttribute('data-order-form-field-state-value') === 'required') {
      prompt = chipEl.getAttribute('data-order-form-card-status-prompt-required') || prompt;
    }
    var provided = providedOverride || chipEl.getAttribute('data-order-form-card-status-provided') || '';
    UI.showMessage(chipEl, complete ? provided : prompt);
  }

  function wireCardStatus(form, config) {
    var slots = form.querySelectorAll('[data-order-form-card-status]');
    for (var i = 0; i < slots.length; i++) {
      wireOneCardStatus(slots[i], config);
    }
  }
  function wireOneCardStatus(statusEl, config) {
    // The card this status belongs to + its validatable inputs. closest('.order-form-section') resolves THIS
    // card, the querySelectorAll then scopes to its own fields. INVARIANT (pinned by snippet-contract.test.js) :
    // a [data-order-form-card-status] card must NOT nest another .order-form-section -- the assembler renders
    // the cards as FLAT siblings, and contact / organisation / address each have a single section root, so this
    // descendant query never folds a nested card's fields into the completeness check. A slot with no card / no
    // fields never confirms (the two guards below) : correct for a hidden DV org/address card whose fields stay empty.
    // SETTLED leave-as-is (PR #24 r6 #3, owner-confirmed 2026-06-23) : on a DV order the hidden org/address cards
    // keep this chip on its "PLEASE PROVIDE ..." prompt (their fields are empty), but the CARD is display:none, so
    // the prompt is never SEEN and the submit gate ignores hidden-section values regardless. It is invisible
    // cosmetic state, NOT a defect. UPDATE (2026-07-24, the parser-state-aware generalization below) : hidden
    // scopes are now skipped as a natural consequence of reading the parser's states -- NOT a special case --
    // so a hidden card's chip stays on its prompt even if its fields carry stale values. The outcome is still
    // invisible cosmetic state ; only the (equally invisible) filled-hidden corner changed, and the tests that
    // exercised chip text on parser-hidden cards moved to OV fixtures where the chip is customer-visible.
    var card = statusEl.closest ? statusEl.closest('.order-form-section') : null;
    if (!card) {
      return;
    }
    var inputs = card.querySelectorAll('[data-order-form-validate]');
    if (!inputs.length) {
      return;
    }
    // The card-specific PROMPT / PROVIDED copy lives in the chip's data-* and is resolved by showCardStatus.
    //
    // PARSER-STATE-AWARE completeness (operator + middleware-agent ruling 2026-07-24 : the approved
    // generalization). The chip previously HARDCODED a form-side assumption -- "every field in a card is
    // required" -- harmless only while the parser happened to mark every card's fields required together. The
    // telephone row (optional-by-parser, EMBEDDED in the contact card) broke that uniformity, so the chip now
    // reads the SAME parser tri-state the gate and the visibility seam read, via the
    // data-order-form-field-state-value stamp applyFieldVisibility projects : 'required' counts always,
    // 'optional' counts only when FILLED (an empty optional never withholds the confirmation -- the gate's
    // exact optional semantics), and a parser-HIDDEN scope's inputs are not counted at all. A field with no
    // stamped scope keeps the required-counting behavior (every pre-telephone card, unchanged). GUARD : a card
    // whose visible fields are ALL optional-and-empty must NOT vacuously confirm -- the Pre-Validation card
    // (one optional input) keeps its neutral PROMPT until filled -- so completeness also requires at least ONE
    // counted-filled field. Every FILLED field (required or optional) still runs the full content mirror
    // below : the chip must never confirm what the gate would block (explicit per the middleware agent's
    // ruling note (i)).
    function allProvided() {
      var sawFilled = false;
      for (var j = 0; j < inputs.length; j++) {
        var el = inputs[j];
        var isPhone = el.getAttribute('data-order-form-validate') === 'phone';
        // The element's parser state, from the nearest stamped field-state scope : the ROW for the telephone,
        // the card root for every direct card field (both carry data-order-form-field-state).
        var stateEl = el.closest ? el.closest('[data-order-form-field-state]') : null;
        if (stateEl && isRowHidden(stateEl)) {
          continue; // parser-hidden scope : not part of this card's completeness (the chip-side mirror of PR#17 #3)
        }
        var fieldState = stateEl ? stateEl.getAttribute('data-order-form-field-state-value') : null;
        // The telephone's chip value is the COMPOSED one (the same single composition source as the gate /
        // twin / tick) ; direct fields keep their own trimmed value.
        var val = isPhone ? composeTelephoneValue(stateEl) : (el.value || '').trim();
        if (!val) {
          if (fieldState === 'optional') {
            continue; // the PARSER says optional : an empty optional never withholds the card confirmation
          }
          return false;
        }
        // An e-mail field must be a VALID address, not merely non-blank (other types only need a value ; an
        // empty <select> placeholder is value="" so it is caught by the blank check above).
        if (el.getAttribute('data-order-form-validate') === 'email' && !isValidEmail(val)) {
          return false;
        }
        // PR #24 review #1 : the card confirmation must MIRROR the submit gate. An OVER-LENGTH value blocks the gate,
        // so the card must NOT flip to "...HAS BEEN PROVIDED" while it does. Run the SAME fieldContentError primitive
        // the gate (_validateContentList) uses, with the max-length VALUE from config.validation (the single source,
        // owner 2026-06-29) resolved by the field's validator id, and the lengthKey from FIELD_RULES_BY_ID.
        // The telephone resolves its id DIRECTLY ('telephone') : a derived field carries no property token.
        var fieldId = isPhone ? 'telephone' : PROPERTY_TO_VALIDATOR_FIELD[el.getAttribute('data-order-form-property')];
        var rule = FIELD_RULES_BY_ID[fieldId];
        if (fieldContentError(val, maxLengthOf(config && config.validation, fieldId), rule && rule.lengthKey) !== null) {
          return false;
        }
        // PR #66 round 1 : the gate ALSO enforces a charset pattern where config.validation carries one
        // (the Validation ID digits-only rule, operator 2026-07-09 ; the telephone composed rail, operator
        // 2026-07-24). The chip must mirror that too, or a non-digit under-cap value flips the card to
        // "...HAS BEEN PROVIDED" while the gate blocks : the exact mirror invariant above. Data-driven off the
        // SAME map entry the gate reads, so any future pattern-carrying card field is covered automatically ;
        // `val` is already trimmed/composed, matching the gate + the middleware rail. (A pattern-less entry
        // imposes no charset here : the gate's fail-closed stance for a REQUIRED-pattern field missing its rule
        // stays the gate's job.)
        var mapRule = config && config.validation && config.validation[fieldId];
        if (mapRule && mapRule.pattern) {
          var chipRe = anchoredRegExp(mapRule.pattern);
          if (!chipRe || !chipRe.test(val)) {
            return false;
          }
        }
        sawFilled = true;
      }
      return sawFilled;
    }
    // EDGE-TRIGGERED : the status slot is a role="status" POLITE live region, so writing it on every keystroke
    // (the field `input` events) would make a screen reader RE-ANNOUNCE "INFORMATION HAS BEEN PROVIDED" on each
    // keypress once the card is complete. Track the last shown-state and only show/hide on a TRANSITION (the
    // same edge-trigger discipline the other one-shot writers -- lockDcv, the validate-result success shells --
    // already use), so the confirmation is announced exactly once when the card becomes complete.
    // null (not false) so the FIRST evaluate() always renders the initial state (the prompt, or the confirmation
    // if the card is browser-autofilled), then only transitions thereafter.
    var shown = null;
    function evaluate() {
      var complete = allProvided();
      if (complete === shown) {
        return;
      }
      shown = complete;
      // The SHARED controller : always-shown chip, neutral PROMPT -> --success confirmation (the messages come
      // from this chip's data-order-form-card-status-prompt / -provided). Same call every other card now uses.
      showCardStatus(statusEl, complete);
    }
    // Re-evaluate on every edit / blur / change of the card's fields (selects fire `change`, not `input`), so
    // the confirmation appears once the card is complete and clears if a field is later emptied/invalidated.
    // The parser-state stamp moved (applyFieldVisibility, on facts load and on every variant switch) : the chip's
    // prompt may now be the other one (showCardStatus reads the stamp). Re-render ONLY when the stamped value
    // actually changed (a term switch re-stamps the same value), keeping the edge-trigger discipline above : one
    // write to the live region per state transition, never one per variant switch.
    var lastState = null;
    card.addEventListener('order-form-field-state', function () {
      var state = card.getAttribute('data-order-form-field-state-value');
      if (state === lastState) {
        return;
      }
      lastState = state;
      shown = null;
      evaluate();
    });
    for (var k = 0; k < inputs.length; k++) {
      inputs[k].addEventListener('input', evaluate);
      inputs[k].addEventListener('blur', evaluate);
      inputs[k].addEventListener('change', evaluate);
    }
    // The dial-code select is HALF of the composed telephone value but carries no data-order-form-validate of
    // its own (one value, one tick : the national input owns the pair's feedback) : re-evaluate the card on its
    // change too, or picking the dial AFTER typing the number would leave the chip stale.
    var chipDial = card.querySelector('[data-order-form-telephone-dial]');
    if (chipDial) {
      chipDial.addEventListener('change', evaluate);
    }
    // RESYNC HOOK (universal-form rebuild, 2026-06-28) : a FORCED re-render from the field's CURRENT content, bypassing
    // the edge-trigger guard. The PRIMARY text/email slots' status chip carries BOTH data-order-form-card-status (this
    // wiring) AND data-order-form-primary-status (activatePrimaryMethod) -- two writers on one chip. When the engine
    // (re)activates that slot on a variant switch it sets fresh prompt/provided attrs and must re-render the chip to
    // MATCH the field (e.g. a preserved-and-still-valid ACME must show "...PROVIDED", an empty one the prompt) WITHOUT
    // the stale `shown` edge-guard early-returning. resetting `shown = null` makes the next evaluate() always render.
    // Exposed on the element so activatePrimaryMethod can call it after filling the attrs (closure-scoped `shown`).
    statusEl.resyncCardStatus = function () { shown = null; evaluate(); };
    // Evaluate once at wire-up so a browser-autofilled card confirms without waiting for a manual edit.
    evaluate();
  }

  /* =========================================================================
   * PRE-FILL BY LINK (owner request 2026-10-03)
   * =========================================================================
   * A product-page address may end in `#order-form?<name>=<value>&...` and the form places each named value into its
   * field, so a customer sent by a renewal e-mail, a dashboard, a thank-you page or a support agent does not type it
   * all again. Every value is OPTIONAL : a link may carry one field or all of them (owner 2026-10-03 : "we may want to
   * provide some but not all data"). The link is built by whoever sends it ; the form reads NOTHING from any other
   * system.
   *
   * WHAT THIS DOES AND DOES NOT DO (owner 2026-10-03 : keep it simple, no "magical" coding) :
   *  - It PLACES values. The link itself validates nothing, opens nothing and adds nothing to the cart. A placed domain
   *    or ACME Account ID is then checked exactly as a typed one is : when the customer clicks Validate, leaves the
   *    field, or changes the product term (refreshForVariant re-validates whatever the primary holds, placed or typed).
   *    The submit gate still runs every check, so a wrong value in a link is caught as a wrong typed value is.
   *  - A value that does not fit the product is DROPPED by the guards that already exist : a card the parser hides
   *    (organisation / address on a DV SKU) never reaches the cart (writeThrough's hidden-section clear) ; additional
   *    domains beyond the SKU's SAN count fall off the end of the rows (restoreTypedSans) ; an issuance method or
   *    country code the form does not offer matches no <option> and is skipped ; unknown link names are ignored.
   *  - Left out ON PURPOSE : the CSR (kilobytes of PEM, too large for a link, and each validate spends the metered
   *    Sectigo decode budget), the validation method and the approver e-mail (both exist only AFTER the primary has
   *    validated, and sequencing that is the extra wiring the owner asked to avoid ; they stay one click).
   *
   * A FRAGMENT, the same shape as the assistant's open-by-link (assets/smart-assistant.js OPEN_LINK_FRAGMENT +
   * parseOpenLink), so the theme has ONE way of doing links. The part after `#` is never sent to Shopify's servers,
   * so the customer's details stay in the browser and in the message that carried the link. The `?variant=` that
   * picks the product term stays in the QUERY : Liquid reads it server-side (product.selected_or_first_available_variant).
   * NOTE : URLSearchParams decodes `+` as a space, so a `+` inside a value (an e-mail local part, a dial code) must be
   * written `%2B` by the link builder ; the dial code accepts digits alone for exactly that reason (parsePrefillLink).
   */
  var PREFILL_FRAGMENT = 'order-form';

  /**
   * Link name -> data-order-form-property token of the visible DIRECT-COPY input (order-form-v2-hidden-properties.liquid
   * wiring style (a)). Together with PREFILL_SEAM_NAMES this is the whole list of what a link may name. Frozen : adding
   * a name here is the ONLY change needed to let a link fill a new direct-copy field.
   */
  var PREFILL_DIRECT_COPY = Object.freeze({
    first: 'first-name',
    last: 'last-name',
    email: 'email',
    org: 'org-name',
    street: 'address',
    city: 'city',
    state: 'state',
    postal: 'postal',
    country: 'country',             // ISO-3166 alpha-2 : the country <option> values GET /api/reference/countries supplies
    validationid: 'ov-anchor-order' // the Validation ID box (the ov-anchor card) : required on CaaS OV-new, optional on the organisation-validated SSL and S/MIME bases the middleware advertises (PREVALIDATION_ADVERTISED_SKUS) ; digits, the gate's pattern rule still applies at submit
  });
  /**
   * The link names that land on an ENGINE SEAM rather than a direct-copy input : the issuance-method <select>, the
   * primary domain slot, the ACME Account ID slot, the SAN rows, and the two halves of the telephone row.
   */
  var PREFILL_SEAM_NAMES = Object.freeze(['method', 'domain', 'acme', 'san', 'dial', 'phone']);

  /**
   * Parse a pre-fill link fragment. PURE (no DOM), the parseOpenLink shape : a non-string, or any fragment that does not
   * start with `order-form?`, is null ; each known name is read with URLSearchParams and trimmed ; blank values and
   * unknown names are ignored ; `san` splits on commas into row order ; `country` is upper-cased to the iso2 form the
   * country <option> values use ; `dial` carries a leading `+` whether or not the link wrote one (a literal `+` decodes
   * as a space, so `dial=61` and `dial=%2B61` both mean +61). Null when nothing usable was named : nothing to do.
   * @param {string} hash  window.location.hash, with or without the leading `#`
   * @returns {Object|null} the present names only : { method, domain, acme, san: [...], dial, phone, first, last, email,
   *   org, street, city, state, postal, country, validationid }
   */
  function parsePrefillLink(hash) {
    if (typeof hash !== 'string') { return null; }
    var fragment = hash.charAt(0) === '#' ? hash.slice(1) : hash;
    var prefix = PREFILL_FRAGMENT + '?';
    if (fragment.indexOf(prefix) !== 0) { return null; }
    var params = new URLSearchParams(fragment.slice(prefix.length));
    var names = PREFILL_SEAM_NAMES.concat(Object.keys(PREFILL_DIRECT_COPY));
    var prefill = {};
    var found = false;
    for (var i = 0; i < names.length; i++) {
      var name = names[i];
      var raw = (params.get(name) || '').trim();
      if (!raw) { continue; }
      if (name === 'san') {
        var list = raw.split(',').map(function (s) { return s.trim(); }).filter(function (s) { return s !== ''; });
        if (!list.length) { continue; }
        prefill.san = list;
      } else if (name === 'country') {
        prefill.country = raw.toUpperCase();
      } else if (name === 'dial') {
        var digits = raw.replace(/^\+/, '');
        if (!digits) { continue; }
        prefill.dial = '+' + digits;
      } else {
        prefill[name] = raw;
      }
      found = true;
    }
    return found ? prefill : null;
  }

  // Whether a <select> carries an <option> with this value. Assigning a value no option has silently reads '', so a
  // method the SKU does not offer is skipped outright (the form only ever offers what GET /api/sku returned).
  function selectHasOption(select, value) {
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value === value) { return true; }
    }
    return false;
  }

  /**
   * Place the parsed link values into the wired form. Runs ONCE, from wireForm, after BOTH the SKU-facts render and the
   * countries fill have settled (so the method options, the SAN rows and the country / dial options all exist). Every
   * lookup is null-guarded : a slot or card the assembler did not render is skipped. The issuance method goes FIRST so
   * that its slot is showing and its change handler's primary reset (wirePrimaryMode -> resetPrimaryInput) has already
   * run before anything is placed ; the order is for clarity, not survival (that reset keeps an enabled, typed domain).
   *
   * Each direct-copy value, and the ACME Account ID, is followed by the SAME event a keystroke fires (`input`, or `change`
   * for a <select>), so the per-field tick (wireOneFieldCheck) and the card chip (wireOneCardStatus) show the placed value
   * exactly as they would a typed one (the ACME `input` also runs resetPrimary, a no-op before anything has validated).
   * The primary DOMAIN is placed with NO event : it carries no live tick, and its only listeners (`input` -> resetPrimary +
   * the single-active CSR toggle, `blur` -> a middleware validate) are the customer's ; validating is left to them (header).
   * The SAN rows reuse the variant-switch idiom verbatim : restoreTypedSans then revalidateSanRows, which blurs each
   * populated row so its local check runs and its method <select> unlocks (a row's method stays locked until its domain
   * has validated once : lockRowDcv / unlockRowDcv in wireSanRow).
   * @param {HTMLFormElement} form @param {Object} state  wireForm's state @param {Object|null} prefill  parsePrefillLink's result
   */
  function applyPrefill(form, state, prefill) {
    if (!prefill) { return; }
    if (!state.facts) {
      // A variant switch is mid-flight (refreshForVariant nulls facts until its fetch lands) : the rows and options are
      // being rebuilt under us. Skip rather than place values on a half-rendered form ; the customer fills in by hand.
      log.warn('ORDER FORM : PREFILL SKIPPED - SKU FACTS NOT READY');
      return;
    }
    // (1) Issuance method, FIRST (see above). Only a 2+-method SKU has options here : a single-method SKU shows its slot
    // directly and setupPrimaryMethods leaves this <select> empty, so the name is simply ignored there.
    if (prefill.method) {
      var modeSelect = form.querySelector('[data-order-form-primary-mode]');
      if (modeSelect && selectHasOption(modeSelect, prefill.method)) {
        modeSelect.value = prefill.method;
        modeSelect.dispatchEvent(new Event('change'));
      }
    }
    // (2) Primary inputs : placed, not validated. The FQDN slot is shared by the SSL autocsr and the CaaS domain methods ;
    // a DISABLED FQDN is CSR-derived (the single-active toggle) and is left alone.
    if (prefill.domain) {
      var fqdn = findPrimaryFqdn(form);
      if (fqdn && !fqdn.disabled) { fqdn.value = prefill.domain; }
    }
    if (prefill.acme) {
      var acme = form.querySelector('[data-order-form-primary-input-type="text"] [data-order-form-validate="acme"]');
      if (acme) {
        acme.value = prefill.acme;
        acme.dispatchEvent(new Event('input')); // the live evaluate (neutral for a well-formed id, the error icon for a malformed one : the tick is the check's) + the slot's card chip, as for a typed id ; no blur, so no check
      }
    }
    // (3) Additional domains : positional into the rows setupSan built to the SKU's SAN count ; extras drop off the end.
    if (prefill.san && state.sanBuilder) {
      restoreTypedSans(state.sanBuilder, prefill.san);
      revalidateSanRows(state);
    }
    // (4) Direct-copy fields : the VISIBLE input carrying the property token (never the hidden twin, which writeThrough
    // owns). A read-only or disabled input is the engine's (a CSR-derived S/MIME e-mail) and is left alone. A <select>
    // (country) fires `change`, everything else `input`, matching wireOneFieldCheck.
    var linkNames = Object.keys(PREFILL_DIRECT_COPY);
    for (var i = 0; i < linkNames.length; i++) {
      var value = prefill[linkNames[i]];
      if (!value) { continue; }
      var el = form.querySelector('[data-order-form-property="' + PREFILL_DIRECT_COPY[linkNames[i]] + '"]:not([type="hidden"])');
      if (!el || el.disabled || el.readOnly) { continue; }
      // A <select> (country) with no <option> for the value is SKIPPED, the same guard the method and dial selects use :
      // assigning it would deselect the placeholder (selectedIndex -1, a blank box where the field name stood) and read ''.
      if (el.tagName === 'SELECT' && !selectHasOption(el, value)) { continue; }
      el.value = value;
      el.dispatchEvent(new Event(el.tagName === 'SELECT' ? 'change' : 'input'));
    }
    // (5) Telephone : two controls, one composed value (composeTelephoneValue). The dial <option> values are the
    // '+'-prefixed codes fillDialCodes appended ; the national number is placed raw (separators are stripped at composition).
    var phoneRow = telephoneRowEl(form);
    if (phoneRow) {
      if (prefill.dial) {
        var dial = phoneRow.querySelector('[data-order-form-telephone-dial]');
        if (dial && selectHasOption(dial, prefill.dial)) {
          dial.value = prefill.dial;
          dial.dispatchEvent(new Event('change'));
        }
      }
      if (prefill.phone) {
        var national = phoneRow.querySelector('[data-order-form-validate="phone"]');
        if (national) {
          national.value = prefill.phone;
          national.dispatchEvent(new Event('input'));
        }
      }
    }
  }

  /* =========================================================================
   * CLEAR FORM (owner request 2026-10-04)
   * =========================================================================
   * The second button of the two-button row at the end of the form (order-form-v2-submit-button.liquid) : a visitor
   * who has started filling the form can start over. Until now the only way to wipe everything was a reload, and the
   * per-card Clear Options buttons wipe only their own box. This runs NO reset logic of its own : every control is
   * emptied through the SAME path a keystroke or the existing per-card Clear takes, so the ticks, the chips, the row
   * locks, the DCV token and the progressive-disclosure gate all return to their starting state by the engine's
   * existing rules. Anything a pre-fill link placed goes too (the link fills once, at load ; a cleared form stays
   * empty). The hidden cart twins are not touched : writeThrough rewrites every twin from the live controls at the
   * next submit, and a cleared primary keeps the gate closed until the customer validates again. No confirmation
   * dialog, matching the per-card Clear Options buttons.
   */
  function clearForm(form, state) {
    // (1) Every control outside the primary slots : the SAN rows, the DCV method + approver selects, the contact /
    // organisation / address / Validation ID fields, the telephone pair. '' plus the control's own keystroke event
    // (`change` for a <select>, `input` otherwise) : a SAN row re-locks its method (wireSanRow), the DCV manager drops
    // its method + approver (wireDcv / wireApproverSelect), the ticks and card chips return to their prompts
    // (wireOneFieldCheck / wireOneCardStatus). The primary slots and the method <select> follow in (2) and (3).
    var controls = form.querySelectorAll('input:not([type="hidden"]), textarea, select');
    for (var i = 0; i < controls.length; i++) {
      var el = controls[i];
      if (el.closest('[data-order-form-primary-input-type]') || el.hasAttribute('data-order-form-primary-mode')) { continue; }
      el.value = '';
      el.dispatchEvent(new Event(el.tagName === 'SELECT' ? 'change' : 'input'));
    }
    // (2) The primary slots, through the per-card Clear paths (wirePrimaryInput's clearCsrField / clearFqdnField /
    // clearAcmeField) : the field is wiped, the primary reset, the DCV re-locked and the gate re-hidden.
    if (state.clearPrimaryInputs) { state.clearPrimaryInputs(); }
    // (3) The issuance method back to its placeholder, ONLY when the SKU offers a choice (a 2+-method SKU populates
    // the <select>) : its change handler (wirePrimaryMode) hides every slot and shows the selector prompt. A
    // single-method SKU has no options here and keeps its only slot showing, now empty.
    var modeSelect = form.querySelector('[data-order-form-primary-mode]');
    var hasChoice = !!(modeSelect && modeSelect.options.length);
    if (hasChoice) {
      modeSelect.value = '';
      modeSelect.dispatchEvent(new Event('change'));
    }
    // (4) Any submit-time error left standing (the same reset the next submit would run) + the form-level summary.
    resetFieldErrors(form, state);
    if (state.summaryEl) { UI.hideMessage(state.summaryEl); }
    // (5) Back to the first step : focus the method <select>, or the shown slot's control on a single-method SKU.
    // Focusing scrolls the browser there, so the customer starts over from the top of the form.
    var firstStep = hasChoice ? modeSelect : firstShownPrimaryControl(form);
    if (firstStep && typeof firstStep.focus === 'function') { firstStep.focus(); }
  }
  // The control inside the first primary slot currently SHOWN (inline display is activatePrimaryMethod's seam).
  function firstShownPrimaryControl(form) {
    var slots = form.querySelectorAll('[data-order-form-primary-input-type]');
    for (var i = 0; i < slots.length; i++) {
      if (slots[i].style.display !== 'none') {
        return slots[i].querySelector('[data-order-form-validate]');
      }
    }
    return null;
  }
  // Wire the Clear Form button ONCE (wireForm, with the other one-time wirings). Null-guarded : an assembler that
  // renders no such button simply has no form-level clear.
  function wireClearButton(form, state) {
    var btn = form.querySelector('[data-order-form-action="clear"]');
    if (!btn) { return; }
    btn.addEventListener('click', function () { clearForm(form, state); });
  }

  function wireForm(form, deps) {
    deps = deps || {};
    var config = deps.config || {};
    var apiClient = deps.apiClient;
    // PRE-FILL BY LINK : the parsed link values (bootstrap's wireOneForm, or a test's deps.prefill) ; null = none.
    var prefill = deps.prefill || null;

    var state = {
      config: config,
      apiClient: apiClient,
      facts: null,
      formValidator: new FormValidator(),
      domainNormalizer: new DomainNormalizer(apiClient),
      // CSR size cap from the single source order-form-v2-validation.liquid -> config.validation.csr.maxLength
      // (owner 2026-06-29) ; no hardcoded byte cap in the engine.
      csrValidator: new CsrValidator(apiClient, config.validation && config.validation.csr && config.validation.csr.maxLength),
      // The CaaS-extension eligibility checker (SubscriptionChecker docblock ; the CsrValidator idiom).
      subscriptionChecker: new SubscriptionChecker(apiClient),
      // The LATEST eligible:true verdict { value, eligible } for the acme primary, or null. Set ONLY by
      // checkAcmeNow's eligible arm ; cleared by resetPrimary (every edit / mode / variant reset). Consumed by
      // buildSubmitContext -> the acmeAccount gate validator (fail-closed when 'required' and null).
      subscriptionCheck: null,
      // The Validation ID checker + its LATEST valid:true verdict { value, valid } (operator decision 2026-10-18) :
      // set ONLY by wireValidationIdCheck's valid arm, cleared on every edit and every variant switch. Consumed by
      // buildSubmitContext -> the ovAnchor gate validator (fail-closed on a typed value without it).
      validationIdChecker: new ValidationIdChecker(apiClient),
      validationIdCheck: null,
      primaryDcv: null,
      sanBuilder: null,
      // The active primary input + whether it has validated. kind tracks csr|fqdn for the
      // single-active toggle + the cart write-through. fieldId starts as the non-anchoring
      // 'order-form' SENTINEL (no phantom hardcoded id) and is corrected to the real primary
      // field (csr OR fqdn, whichever the markup renders) by wirePrimaryInput (owner 2026-06-29 A+
      // sweep ; before, the placeholder hardcoded order-form-csr-textarea, which an FQDN-only form
      // does not render, so a pre-wire anchor would have no-op'd against a phantom element).
      primaryInput: { valid: false, kind: null, fieldId: 'order-form', domain: '' },
      // Monotonic token guarding refreshForVariant against out-of-order async resolution : a
      // newer variant switch supersedes an older in-flight one so the LAST switch wins (PR#17
      // round-2 nit : rapid order-form:variant events fired overlapping refreshes).
      variantEpoch: 0,
      // The field the LAST submit attempt flagged invalid -- resetFieldErrors clears exactly this
      // one on the next submit, never a blur-set invalid field the gate did not reach (PR#17 r3 #2).
      lastSubmitErrorEl: null
    };

    // Wire the submit handler as the FIRST side-effect after state construction (PR#17 r8 #5c) :
    // nothing between `var state` and here can throw (object-literal + form.querySelector), but making
    // the gate the very first attachment closes any theoretical window where a setup throw would leave
    // the form GATE-LESS + submittable (fail-open). onSubmit reads state.summaryEl LAZILY at submit
    // time, so resolving the summary just below (after the listener) is fine. A submit before facts
    // load still builds + runs the gate (an unrendered form fails the primary-input check, never
    // silently passing).
    form.addEventListener('submit', function (e) {
      onSubmit(e, form, state);
    });

    // owner 2026-06-23 : pressing ENTER inside a single-line text field must NOT submit the form (an implicit
    // submit would run the WHOLE submit gate mid-entry and surface "you have not filled X" errors before the
    // shopper is done). Instead it does the SAME as clicking out of the box : blur the field, which fires its
    // own wired validation (the FQDN / SAN domain parse, the field tick, etc.). A <textarea> (the CSR) is left
    // alone so Enter stays a newline ; a real submit still happens via the Add-to-Cart button. Form-scoped, so
    // every present + future text input is covered with no per-input wiring.
    // SCOPE (PR #24 review, defended) : this suppresses Enter-to-submit even on a FULLY-VALID form -- that is
    // INTENTIONAL, not an oversight. The form's flow is gated by the per-step "Validate" buttons + the
    // progressive-disclosure reveal (owner design), so the deliberate single submit affordance is the Add-to-Cart
    // button ; a stray Enter in any of the many text fields firing the whole gate is exactly the premature-error
    // footgun the owner asked to remove. Add-to-Cart stays keyboard-reachable (it is a normal focusable button).
    // OWNER RE-CONFIRMED 2026-06-23 : this handler exists precisely BECAUSE pressing Enter used to report the field
    // as invalid ; keep it AS-IS -- the blur (validate-the-field) behaviour is wanted, no focus-retention change.
    form.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.keyCode !== 13) {
        return;
      }
      var t = e.target;
      if (t && t.tagName === 'INPUT' && t.type !== 'submit' && t.type !== 'button') {
        e.preventDefault();                       // stop the implicit form submission
        // Run the field's OWN blur-validation, but WITHOUT moving focus (PR #24 r12, owner 2026-06-23 : keep the
        // cursor in the field). A SYNTHETIC 'blur' event fires the same blur listeners a real blur triggers (so
        // Enter still validates exactly like clicking out), but a dispatched event does NOT change
        // document.activeElement -- unlike t.blur(), which dropped the keyboard user's focus to <body> and lost
        // their place. The caret stays put ; the field still validates + ticks / errors.
        t.dispatchEvent(new Event('blur'));
      }
    });

    // The ONE form-level live-region summary (id = <submit-button-id>-summary). Resolve it
    // FORM-SCOPED (PR#17 r3 #1) : doc.getElementById would return ANOTHER form's summary with the
    // same default id on a multi-form page. An exact [id="..."] match within this form is safe.
    var submitBtn = form.querySelector('[data-order-form-action="submit"]');
    // Cache the submit button so onSubmit can flag it in-flight on a valid submit (aria-disabled = the theme opacity:0.5
    // dim + the re-entrancy guard against a double-click while the native /cart/add POST is in flight ; owner 2026-06-30).
    state.submitBtn = submitBtn;
    // Register the document-wide bfcache recovery (idempotent ; see bindBfcacheRecovery) so a Back-button restore
    // clears the in-flight flags instead of leaving a stuck aria-disabled button the re-entrancy guard would honour.
    bindBfcacheRecovery();
    state.summaryEl = submitBtn ? form.querySelector('[id="' + submitBtn.id + '-summary"]') : null;
    // a11y (PR #24 r7 #1) : the visually-hidden POLITE announcer applyPrimaryGate speaks into when the gate reveals
    // the rest of the form. Form-scoped ; may be null (a custom assembler omitting it) -- applyPrimaryGate guards.
    state.gateAnnounceEl = form.querySelector('[data-order-form-gate-announce]');
    if (!state.summaryEl) {
      // Warn LOUD when the form-level live-region summary can't be resolved (PR#17 r18 #2) : the gate
      // still blocks fail-closed, but UI.announce would silently no-op, so a blocked submit would show
      // NO message. Consistent with the loud-warn posture (fields.san/sanCount consistency, the
      // show/hide-contract cross-check). Cause : the assembler shipped no submit button, or no
      // <submitId>-summary region.
      log.warn('ORDER FORM : NO FORM-LEVEL SUMMARY REGION - GATE MESSAGES WILL NOT ANNOUNCE', { submitBtnId: submitBtn ? submitBtn.id : null });
    }

    // The primary-input events (CSR validate button + FQDN blur) update state.primaryInput.
    wirePrimaryInput(form, state);
    // B : the primary-input MODE selector (CSR vs AutoCSR) -- shows only the chosen mode's controls.
    wirePrimaryMode(form, state);
    // Wire the DCV method <select> + the CSR auto-resize SYNCHRONOUSLY, up front -- NOT inside the
    // initial render's .then (PR#17 r4 #1). These wirings are one-time and must NOT depend on which
    // render wins the variant epoch race : when a variant switch supersedes the initial load, the
    // initial .then bails, so anything wired only there (previously wireDcv / wireAutoResize) would
    // never attach -- leaving the primary DCV change listener absent, getToken() forever null, and
    // the gate's primaryDcv.hasToken requirement unsatisfiable (the form could never submit). Their
    // listeners read state.primaryDcv / the cap lazily, so attaching before the first render is safe.
    wireDcv(form, state);
    wireAutoResize(form);
    // FIX 5 : wire the "Generate CSR Tool" popup button ONCE here, alongside the other one-time wirings.
    // wirePrimaryInput / wireDcv are each likewise called exactly once in wireForm ; refreshForVariant
    // re-runs setupPrimaryDcv / resetDcvPanels but NOT the wiring, so no path re-enters or stacks duplicate
    // listeners. Placing it here just keeps all the once-only wirings together. Scheme-validated window.open.
    wireCsrPopup(form);
    // STANDARD CARD STATUS (owner 2026-06-22) : the direct-input cards (contact / organisation / address) each
    // confirm completion in their own status slot, matching the per-step confirmations the CSR / FQDN / DCV
    // cards already show. One-time wiring (the slots are static markup) ; UX feedback only, never a gate.
    wireCardStatus(form, config);
    // PER-FIELD TICKS (owner 2026-06-22) : the contact / organisation / address text + e-mail + country fields
    // show the same green tick as the primary domain, for consistency. One-time wiring ; UX feedback only.
    // config carries config.validation (the single source) so the tick's length/ACME checks match the gate.
    wireFieldChecks(form, config);
    // THE VALIDATION ID CHECK (operator decision 2026-10-18) : the Validation ID card's Check button, wired once like
    // the other one-time wirings (it is not a primary, so it lives outside wirePrimaryInput).
    wireValidationIdCheck(form, state);
    // CLEAR FORM (owner 2026-10-04) : the second button of the submit row, wired once like the other actions.
    wireClearButton(form, state);

    if (!apiClient) {
      log.warn('ORDER FORM : NO API CLIENT - FORM RENDERED WITHOUT SKU/COUNTRY DATA');
      // Resolve a HANDLE-SHAPED object for a uniform contract (PR#17 r18 #4) : the normal path resolves
      // to { state, refreshForVariant }, so this branch does too. refreshForVariant is a no-op here
      // (no apiClient -> no facts to fetch).
      return Promise.resolve({ state: state, refreshForVariant: function () { return Promise.resolve(); } });
    }

    // VARIANT SWITCHING. This bootstrap LISTENS for an `order-form:variant` CustomEvent carrying the
    // new SKU and re-derives from it (refreshForVariant). What DISPATCHES that event is wireVariantBridge
    // (called just below) : it translates the Trade theme's pub-sub `variant-change` event into this
    // per-form CustomEvent (Liquid `{% render %}` is sandboxed + the v2 design ships no inline script, so
    // the bridge lives here in the shared module, not in the assembler). Exposing refreshForVariant on the
    // resolved handle ALSO lets a caller drive a refresh -- and keeps the SAN-clear hard requirement
    // directly testable -- without simulating the theme's variant selector OR the pub-sub bridge.
    var handle = {
      state: state,
      refreshForVariant: function (newSku) {
        return refreshForVariant(form, state, newSku);
      }
    };
    form.addEventListener('order-form:variant', function (e) {
      var newSku = e && e.detail ? e.detail.sku : null;
      if (newSku) {
        // This is a FIRE-AND-FORGET path (no awaiter), so guard it with a .catch to match the rest
        // of the file's posture (bootstrap .catch's the initial wireForm ; onSubmit is fully
        // fail-closed) -- PR#17 r8 #1. SkuParser.getFacts is contracted never to reject, but the
        // resolve callback does DOM mutation (applyFieldVisibility / setupPrimaryMethods / setupPrimaryDcv /
        // setupSan / wireSanRows) that could throw on markup drift ; without this the rejection is unhandled and
        // the form is silently left rendered for the prior variant. (The handle.refreshForVariant
        // above is intentionally NOT caught -- its caller awaits + handles the promise.)
        refreshForVariant(form, state, newSku).catch(function (err) {
          log.error('ORDER FORM : VARIANT REFRESH FAILED', err && err.message ? err.message : err);
        });
      }
    });

    // Bridge the Trade theme's variant picker into the listener above (pub-sub `variant-change` ->
    // this form's `order-form:variant` CustomEvent). See wireVariantBridge for the full rationale.
    wireVariantBridge(form, state);

    // The country list is variant-INDEPENDENT (the same set for every SKU), so fill it off its OWN
    // promise -- NOT gated by the SKU-facts epoch (PR#17 r4 #1) : a variant switch that wins the
    // initial-load race must NOT also drop the country fill. The returned promise still awaits it,
    // so tests + callers observe a filled <select>.
    var countriesPromise = apiClient.getCountries().then(function (outcome) {
      fillCountries(form, outcome);
      // The telephone dial-code picker rides the SAME response and promise (variant-independent, like the
      // country list ; middleware-01 commit a5b3879 made dialCode an additive field of this endpoint).
      fillDialCodes(form, outcome);
    }).catch(function (err) {
      // getCountries is contracted never to reject (ApiClient._request degrades to a failure
      // OUTCOME), but .catch it anyway so an unexpected rejection on the OPTIONAL country fill cannot
      // make wireForm's returned Promise.all reject -- a caller awaiting wireForm must always get a
      // resolved (if degraded) form, never a rejection (PR#17 r14 #3 ; uniform with the
      // order-form:variant listener's fire-and-forget .catch). The country select stays its placeholder.
      log.warn('ORDER FORM : COUNTRY FILL FAILED', err && err.message ? err.message : err);
    });

    // The SKU-FACTS render claims a variant epoch (PR#17 round-2 #5 + r3 #4) : the order-form:variant
    // listener is attached ABOVE, so a fast variant switch during a SLOW initial load would otherwise
    // let refreshForVariant apply the new facts first and then THIS resolution clobber them with the
    // original SKU's facts/rows. Epoching the initial render makes the LAST claim win either way.
    var renderEpoch = claimVariantEpoch(state);
    var factsPromise = SkuParser.getFacts(config.sku, apiClient).then(function (facts) {
      if (renderEpoch !== state.variantEpoch) {
        // A variant switch superseded the initial load before it resolved : skip the initial SKU
        // render (refreshForVariant owns the winning render) so it cannot clobber the newer one.
        return;
      }
      state.facts = facts;
      // FAIL-LOUD on a degraded initial load (PR #34 review #1 ; owner-approved 2026-06-28) -- shared with the
      // variant-switch path. The fail-closed render below still runs (it hides everything) ; this adds the visible half.
      revealInitErrorIfDegraded(form, facts);
      applyFieldVisibility(form, facts);
      applyCartPropertyNames(form, facts); // CONTRACT-OWNED cart property NAMES (facts.cartProperties) ; owner 2026-06-30
      applyContactEmailMode(form, facts); // CSR `subject` fact routes the e-mail field : read-only-derived (S/MIME) vs typed (owner 2026-07-01)
      warnDcvContractGap(facts); // diagnose a DCV-shown-but-no-dcvMethods /api/sku gap (owner 2026-07-01)
      // DATA-DRIVEN PRIMARY (universal-form rebuild, 2026-06-28) : build the primary input from facts.primaryMethods
      // (populate the issuance-method dropdown for a 2+-method SKU, or show the single method's slot directly), the
      // SAME way setupPrimaryDcv builds the DCV select. Runs here (after facts) because primaryMethods is a SKU fact,
      // not known at wireForm-time ; refreshForVariant re-runs it on a variant switch.
      setupPrimaryMethods(form, state, facts);
      setupPrimaryDcv(form, state, facts);
      setupSan(form, state, facts);
      applySellable(form, state, facts); // A.2 : disable submit + announce for a non-sellable SKU
      wireSanRows(form, state);
    });

    return Promise.all([factsPromise, countriesPromise]).then(function () {
      // PRE-FILL BY LINK runs LAST, after BOTH promises : the facts render built the method options, the SAN rows and
      // every card's visibility, and the countries fill gave the country + dial selects their options. Before either,
      // a value would land on nothing (an option-less <select> reads '' ; no SAN rows exist yet).
      applyPrefill(form, state, prefill);
      return handle;
    });
  }

  // VARIANT BRIDGE (3c). The merged wiring above only LISTENS for an `order-form:variant` CustomEvent ;
  // nothing dispatched it, because the Trade (Dawn-based) theme signals a variant change through its
  // pub-sub `variant-change` event, NOT a DOM event on the form. Verified against the theme source :
  //   - assets/product-info.js handleUpdateProductInfo publishes
  //     `publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId, html, variant } })` (the variant is
  //     the full Shopify variant object : .id, .sku, ...), and it EARLY-RETURNS before publishing when no
  //     variant matches the chosen options (the unavailable-combo case fires NO event).
  //   - PUB_SUB_EVENTS.variantChange === 'variant-change' (assets/constants.js) ; `subscribe` + publish
  //     live in assets/pubsub.js. Both constants.js + pubsub.js load as plain `<script defer>` in
  //     layout/theme.liquid <head> (before order-form-v2.js, which loads at content_for_layout via
  //     order-form-v2-config), so their top-level declarations are GLOBAL : a `function subscribe` (reachable
  //     bare or as window.subscribe) and a `const PUB_SUB_EVENTS` (reachable BARE only -- a top-level const
  //     is NOT a window property). This IIFE therefore references them as BARE identifiers ; `typeof` is
  //     the one strict-mode-safe probe for an undeclared global, so the bridge feature-detects and no-ops
  //     when the pub-sub is absent (jest jsdom, or a theme that failed to load pubsub.js) -- never throwing.
  //   - product-info.js updateVariantInputs only rewrites the `input[name="id"]` of `#product-form-*`
  //     forms, NEVER a `.order-form` -- so the bridge MUST write the new variant id into THIS form's hidden
  //     name="id" itself, or the native cart POST would carry the PREVIOUS variant id while the SKU-driven
  //     UI shows the new one (a silent variant/cart mismatch). v1 (ssl-unified-order-form.liquid (deprecated, removed 2026-07-05)) did the
  //     same (els.variantId.value = parsed.id).
  // This is the highest-level theme-sanctioned hook (price-per-item.js / pickup-availability.js subscribe
  // to the same event). No inline-script + no v1-style [data-selected-variant] MutationObserver fallback :
  // pubsub.js is a core theme asset always present in the browser, so a fallback would be dead speculative
  // code (the no-op path already covers the only real absence, which is the test environment).
  function wireVariantBridge(form, state) {
    // Feature-detect the theme pub-sub via BARE globals (window.PUB_SUB_EVENTS is undefined -- it is a
    // const, not a window property). `typeof <undeclared>` does not throw under 'use strict'.
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS || !PUB_SUB_EVENTS.variantChange) {
      // No theme pub-sub (jest, or pubsub.js absent) : the form still works for its INITIAL variant
      // (config.sku) ; live switching is simply unavailable. Fail-soft, never throw.
      return;
    }
    var unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, function (payload) {
      try {
        // A cross-PRODUCT swap removes the old form and re-bootstraps a fresh one (which re-subscribes) ;
        // this stale subscription self-detaches the first time it fires after its form left the DOM.
        if (form.isConnected === false) {
          if (typeof unsubscribe === 'function') { unsubscribe(); }
          state.variantBridgeUnsubscribe = null; // the live subscription is gone : keep the handle honest
          return;
        }
        var data = payload && payload.data;
        var variant = data && data.variant;
        // The theme does not publish on an unavailable combo (product-info.js !variant early-return), so a
        // received event always carries a variant ; guard anyway. A SKU-less variant cannot drive the
        // SKU-keyed contract (refreshForVariant also no-ops on a falsy sku), so bail.
        if (!variant || !variant.sku) {
          return;
        }
        // SCOPE to this form's product section : the pub-sub event is page-global, but a product page can
        // host OTHER <product-info> instances (related products / quick-add) that publish their own
        // variant-change. React only when the firing sectionId matches THIS form's <product-info> ancestor.
        // The theme publishes sectionId = (dataset.originalSection || dataset.section) -- product-info.js's
        // `get sectionId()`, where originalSection is set for a quick-add / featured-product context
        // (quick-add.js). MIRROR that getter EXACTLY (PR#18 review) : comparing to dataset.section alone
        // would silently ignore the form's OWN events if it ever renders inside such a context (where
        // originalSection differs from section). If the form is rendered OUTSIDE any product-info (a custom
        // placement), fall through to the documented single-order-form-per-page assumption and accept it.
        var productInfo = form.closest ? form.closest('product-info') : null;
        var formSection = productInfo ? (productInfo.dataset.originalSection || productInfo.dataset.section) : null;
        if (productInfo && data.sectionId != null && String(formSection) !== String(data.sectionId)) {
          return;
        }
        // Write the new variant id to the cart input (the theme will not, for a non-product-form form).
        var idInput = form.querySelector('[name="id"]');
        if (idInput && variant.id != null) {
          idInput.value = variant.id;
        }
        // Re-dispatch the per-form contract the listener consumes (detail.sku -> refreshForVariant).
        form.dispatchEvent(new CustomEvent('order-form:variant', { detail: { sku: variant.sku } }));
      } catch (err) {
        // A bridge failure must never break the page ; the form keeps its current (initial) variant.
        log.warn('ORDER FORM : VARIANT BRIDGE FAILED', err && err.message ? err.message : err);
      }
    });
    // Retain the unsubscribe handle on state : the cross-product-swap self-detach above drops the
    // subscription via the closure's `unsubscribe` (and nulls this), but keeping it here is the conventional
    // teardown handle for any future explicit caller (this no-SPA theme has none today) and lets a test
    // confirm whether a subscription was attached (PR#18 review note : it is a managed handle, not dead).
    state.variantBridgeUnsubscribe = unsubscribe;
  }

  // Claim the next monotonic variant-render epoch. BOTH the initial render and each
  // refreshForVariant claim one ; a resolution only applies if its epoch is still the latest, so
  // overlapping variant switches -- and a switch during a slow initial load -- leave the LAST
  // claim's render in place (PR#17 round-2 #5 + r3 #4 : the explicit last-write-wins guard, since
  // SkuParser caching mitigates but does not serialize).
  function claimVariantEpoch(state) {
    state.variantEpoch += 1;
    return state.variantEpoch;
  }

  // Reset the visible DCV state on a variant switch (PR#17 r3 #3) : setupPrimaryDcv rebuilds the
  // method <select> back to its placeholder, so any panel shown for the PREVIOUS variant's method
  // -- and the primaryShowApprover flag -- must be cleared too. Otherwise a stale EMAIL panel stays
  // visible and a later domain-validate would loadPrimaryApproversIfEmail for a method no longer
  // selected.
  function resetDcvPanels(form, state) {
    // Scope to the PRIMARY DCV container for consistency with wireDcv's panel toggling (PR#17 r13
    // #2 : wireDcv was scoped in r12 #3 but this stayed form-wide). Harmless today -- only the
    // primary renders dcv-details panels -- but scoping keeps the same order-independence the rest
    // of the PR applies, so a future SAN-row approver wrapped in data-order-form-dcv-details could
    // not be hidden by a primary variant switch. Fall back to the whole form if the container is absent.
    var select = findPrimaryDcvSelect(form);
    var scope = (select && select.closest('[data-order-form-field="dcv"]')) || form;
    var panels = scope.querySelectorAll('[data-order-form-dcv-details]');
    for (var i = 0; i < panels.length; i++) {
      panels[i].style.display = 'none';
    }
    // FIX 8 : the EMAIL confirmation is NOT cleared here. refreshForVariant -- the SOLE caller of
    // resetDcvPanels -- calls state.resetPrimaryInput right after this, which hides the confirmation via the
    // shared hideDcvConfirmation hook (resetPrimaryInput -> resetPrimary -> state.hideDcvConfirmation, set in
    // wireDcv). A separate clear here would be redundant. NOTE for a future maintainer : if you add a caller
    // of resetDcvPanels that does NOT also reset the primary input, call state.hideDcvConfirmation() yourself.
    state.primaryShowApprover = false;
    // FIX 3 : re-LOCK the method <select> on a variant switch. setupPrimaryDcv rebuilds its options and
    // may leave it enabled, but the new variant's primary is un-validated (refreshForVariant calls
    // state.resetPrimaryInput), so the method must follow the domain and stay disabled until the shopper
    // re-validates. (resetPrimaryInput also re-locks via the wirePrimaryInput closure ; doing it here too
    // keeps resetDcvPanels self-consistent and ordered AFTER the rebuild.)
    if (select) {
      select.disabled = true;
    }
    // Clear + re-disable the approver <select> too (PR#17 r14 #2 ; single-list 2026-06-30) : setupPrimaryDcv rebuilds
    // the method select to its placeholder, but the approver select would otherwise keep the PREVIOUS variant's
    // options (calculated + any appended official addresses) + enabled state. Not a cart-correctness issue (the new
    // DcvManager's token is null until a method is re-chosen, so the gate still blocks), but a shopper re-opening EMAIL
    // before re-validating would briefly see the stale list. Reset it to placeholder-only + disabled ; loadApprovers
    // re-populates + re-enables it once EMAIL is re-chosen with a validated domain, and the additional-addresses button
    // re-appends the official set for the new domain. The new DcvManager (reassigned by setupPrimaryDcv) starts with an
    // empty official set, so no stale official addresses survive the switch.
    if (state.primaryApproverSelect) {
      populateApproverSelect(state.primaryApproverSelect, [], 'approver.placeholder', state.config.copy);
      state.primaryApproverSelect.disabled = true;
    }
  }

  // Capture the RAW typed FQDN value of every SAN row, in 1-based row order (no blank-filter, so
  // positions are stable). Used to carry typed SANs across an MD -> MD variant switch that rebuilds
  // the SanBuilder (PR#17 r5 substantive ; owner: preserve). Mirrors rebuildToCount's own positional
  // preservation, but across two builder instances.
  function captureTypedSans(sanBuilder) {
    var values = [];
    if (!sanBuilder) {
      return values;
    }
    var i = 1;
    var row = sanBuilder.getRow(i);
    while (row) {
      values.push(row.fqdnInput ? row.fqdnInput.value : '');
      i += 1;
      row = sanBuilder.getRow(i);
    }
    return values;
  }

  // Restore captured typed FQDN values into a freshly-rebuilt SanBuilder's rows, positionally (the
  // new variant may have FEWER rows, so values past the new count are dropped -- the customer kept
  // their first N). Companion to captureTypedSans (PR#17 r5 substantive).
  function restoreTypedSans(sanBuilder, values) {
    if (!sanBuilder || !values || !values.length) {
      return;
    }
    for (var i = 0; i < values.length; i++) {
      var row = sanBuilder.getRow(i + 1);
      if (row && row.fqdnInput && values[i]) {
        row.fqdnInput.value = values[i];
      }
    }
  }

  // Re-derive the SKU-driven render on a variant switch + enforce HARD-REQUIREMENT #2 :
  // when the new variant is NON-Multi-Domain, CLEAR both SAN hidden fields (not just hide
  // the section) so a stale SAN list from the previous MD variant can never reach the cart
  // (v1 updateSanHiddenFields). For an MD variant, re-render the SAN rows to the new count.
  function refreshForVariant(form, state, newSku) {
    // Claim the latest variant epoch (shared with the initial render). If a NEWER switch is
    // dispatched while this fetch is in flight, this resolution is stale and must not apply its
    // (now-superseded) facts/rows -- otherwise rapid order-form:variant events whose fetches resolve
    // out of order could leave the form rendered for an older variant (PR#17 round-2 #5).
    var epoch = claimVariantEpoch(state);
    // RACE CLOSE (3c variant bridge). NULL the facts for the duration of the async re-fetch. The bridge
    // (wireVariantBridge) updates the cart input[name="id"] to the NEW variant SYNCHRONOUSLY, but the new
    // variant's SKU facts (validationLevel + sanCount -- what drive the org-required and SAN-count gate)
    // only arrive when getFacts resolves. Without this, a submit in that window would validate the NEW
    // variant id against the OLD variant's facts : e.g. a DV->OV switch would pass the DV (no-org) gate yet
    // POST an OV variant with an EMPTY organisation -- the silent-cart-corruption class this whole form
    // guards. Nulling facts routes a submit-during-refresh into onSubmit's facts-null fail-closed block
    // ("UNABLE TO VALIDATE - PLEASE TRY AGAIN") until the new facts land, exactly like a submit before the
    // INITIAL load resolves (owner fail-closed ruling, PR#17 r17). The epoch guard below still ensures only
    // the LATEST switch's facts are applied, so overlapping switches leave facts null (submit fail-closed)
    // until the winning fetch resolves. Before the bridge existed nothing dispatched order-form:variant, so
    // this window was unreachable ; wiring live switching is what surfaces it.
    state.facts = null;
    // Bump the primary-validate epoch SYNCHRONOUSLY at the start of the switch (before getFacts resolves), so a
    // CSR/FQDN validate dispatched JUST BEFORE this switch bails on resolution instead of applying stale primary
    // state against the now-null facts (Finding 3, owner 2026-07-01). The FULL primary reset still runs post-render
    // (resetPrimaryInput below) ; this only closes the pre-render race window. Fail-closed either way (onSubmit
    // blocks on null facts) -- this removes a transient stale-chip FLASH, not a cart hole. Null-guarded : the hook
    // is set by wirePrimaryInput, which always runs before any variant switch can fire.
    if (state.invalidatePrimaryEpoch) { state.invalidatePrimaryEpoch(); }
    // The Validation ID verdict is per SKU (the account and the product level both follow it), so the switch drops it
    // HERE, synchronously, for the same reason as the primary epoch bump above : a Check still in flight when the
    // switch starts must bail on resolution instead of painting the OLD SKU's CONFIRMED chip during the facts-null
    // window (the checker keeps its own epoch ; this reset bumps it). The typed value is kept and the customer checks
    // it again under the new variant (operator decision 2026-10-18). Null-guarded : the hook is set by
    // wireValidationIdCheck, which only exists when the card's input is in the markup.
    if (state.resetValidationIdCheck) { state.resetValidationIdCheck(); }
    return SkuParser.getFacts(newSku, state.apiClient).then(function (facts) {
      if (epoch !== state.variantEpoch) {
        // Superseded by a later switch : drop this stale render, leaving the newer one intact.
        // Return undefined (not the stale facts) to match the initial-load bail (PR#17 r8 #5a).
        return;
      }
      state.facts = facts;
      // FAIL-LOUD on a degraded variant switch (PR #34 review #1) : a switch whose new SKU's /api/sku fails (null) or
      // returns no primaryMethods reveals the same [data-order-form-init-error] the initial load does (and HIDES it on
      // a recovery), so a degraded switch is never a silent blank form. Same shared helper, identical behaviour.
      revealInitErrorIfDegraded(form, facts);
      applyFieldVisibility(form, facts);
      applyCartPropertyNames(form, facts); // re-apply the NEW variant's contract-owned cart property NAMES (owner 2026-06-30)
      applyContactEmailMode(form, facts); // re-apply the NEW variant's CSR `subject` e-mail-field routing (owner 2026-07-01)
      warnDcvContractGap(facts); // re-diagnose the DCV-shown-but-no-dcvMethods gap for the new variant (owner 2026-07-01)
      // DATA-DRIVEN PRIMARY (universal-form rebuild, 2026-06-28) : re-derive the primary input from the NEW variant's
      // primaryMethods FIRST, so the active slot is shown BEFORE the resets below. A switch within one method list
      // (SSL DV->OV) PRESERVES the chosen method (setupPrimaryMethods restores the prior dropdown value) ; a switch
      // that CHANGES the list (CaaS new domain <-> extension acme) swaps the slot. resetPrimaryInput then clears the
      // prior method's primaryInput, and revalidatePrimary (active-slot-guarded) re-checks ONLY the now-shown slot.
      setupPrimaryMethods(form, state, facts);
      // Re-derive the PRIMARY DCV from the new variant : the server's facts.dcvMethods can DIFFER per
      // variant, so the method select must rebuild from the new variant's set (PR review : the switch left
      // a stale method list ; the SC45 wildcard exclusion this once re-applied was removed 2026-06-29).
      // Reassigning state.primaryDcv is enough -- the existing wireDcv change listener reads it,
      // so no re-wire and no double-listener ; buildMethodSelect is idempotent (clears first).
      setupPrimaryDcv(form, state, facts);
      // The rebuilt method select resets to its placeholder : clear any DCV detail panel left
      // visible for the previous variant's method + the EMAIL approver flag (PR#17 r3 #3).
      resetDcvPanels(form, state);
      // Reset the PRIMARY input to un-validated (PR#17 r9 #1) : the primaryDcv is rebuilt (token ->
      // null) and the SAN rows are re-validated for the new variant, but state.primaryInput would
      // otherwise keep its variant-A valid:true -- so a primary validated under variant A (e.g. a
      // non-wildcard domain) would survive a switch to a wildcard variant unchecked. The typed value
      // is kept ; the shopper re-validates it (re-blur / re-Validate) under the new variant's rules,
      // matching the re-derivation the rest of the form gets. resetPrimaryInput is exposed by
      // wirePrimaryInput (it owns the csr/fqdn refs + the default anchor).
      if (state.resetPrimaryInput) {
        state.resetPrimaryInput();
      }
      // AUTO re-validate the preserved primary value under the new variant (owner UX fix 2026-06-21) : the reset
      // above kept the typed value but reset it to un-validated, so without this the shopper sees a valid domain
      // looking un-ticked + the DCV re-locked until they manually re-edit. revalidatePrimary re-runs the real
      // validation under state.facts (just assigned) -- a still-valid value regains its tick + unlocked DCV, a
      // now-invalid value errors. setupPrimaryDcv ran above, so unlockDcv enables the NEW variant's method select.
      if (state.revalidatePrimary) {
        state.revalidatePrimary();
      }
      applySellable(form, state, facts); // A.2 : re-evaluate sellability on a variant switch
      // SAN section is live whenever the server marks the SAN field non-hidden
      // (fields.san === 'optional' for an SSL multi-domain SKU ; 'hidden' otherwise).
      // Was facts.isMultiDomain ; the server's fields.san is now the single source.
      var hasSan = !!(facts && facts.fields && facts.fields.san !== 'hidden');
      if (hasSan) {
        // PRESERVE the customer's typed SANs across an MD -> MD switch (owner decision 2026-06-19 :
        // "Preserve typed SANs" ; rebuildToCount's JSDoc promises a variant change does not lose what
        // was typed). rebuildToCount preserves positionally WITHIN a builder, but setupSan must build
        // a NEW SanBuilder so its isWildcardMd (the SAN "*." domain rule) + the new variant's dcvMethods
        // match the new variant (a stale builder would mis-apply the wildcard SAN rule) -- a fresh instance has empty _rows, so the
        // preservation has nothing to copy. Carry the prior rows' raw typed values across explicitly :
        // capture BEFORE setupSan replaces state.sanBuilder, restore positionally AFTER (the submit
        // gate / next blur re-validates them, so a value now invalid for the new variant, a wildcard SAN
        // under a plain multi-domain variant, is flagged rather than silently shipped ; a plain SAN under a
        // wildcard-MD variant is prefixed "*." by the row's blur seam instead, the WILDCARD ENTRY rule of
        // 2026-10-26). PR#17 r5 substantive.
        var preservedSans = state.sanBuilder ? captureTypedSans(state.sanBuilder) : [];
        setupSan(form, state, facts);
        restoreTypedSans(state.sanBuilder, preservedSans);
        wireSanRows(form, state);
        // AUTO re-validate the restored SAN rows under the new variant (owner UX fix 2026-06-21) : mirror the
        // primary so each preserved SAN regains its tick + unlocked method without a manual per-row re-edit.
        revalidateSanRows(state);
      } else if (state.sanBuilder) {
        // HARD-REQ#2 : NON-MD -> drop the rows AND CLEAR both SAN hidden fields, so a stale SAN
        // list from the previous Multi-Domain variant can never reach the cart.
        state.sanBuilder.rebuildToCount(0);
        var hidden = hiddenInputsByToken(form);
        UI.clearHidden(hidden['additional-fqdn']);
        UI.clearHidden(hidden['validation-tokens']);
      }
      return facts;
    });
  }

  // Show/hide the SAN section per the variant's multi-domain status. Called on BOTH the initial
  // render AND a variant switch (PR review : the hide-on-non-MD only fired on a switch, so a
  // single-domain FIRST load left the empty SAN section visible).
  // applySanVisibility was DELETED in the 2026-06-28 universal-form rebuild : the SAN card now carries
  // data-order-form-field-state="san" and is revealed by the ONE visibility seam (applyFieldVisibility) off
  // fields.san, exactly like every other card. The old function read the same fields.san but via a separate
  // toggle that located the card by `[data-order-form-field="san"].closest('.order-form-section')`.

  // A.2 : disable the submit button + surface a message when the SKU is NOT sellable. The submit gate ALSO
  // blocks an unsellable SKU (defense-in-depth, in case the button is bypassed by a programmatic submit), but
  // disabling the button is the visible, can't-even-try signal. Re-enables (and clears ONLY the lingering
  // not-sellable message, never a live submit error) when a variant switch lands on a sellable SKU.
  // FAIL CLOSED ON ABSENCE (owner 2026-07-01) : the middleware guarantees isSellable is present as a boolean on
  // every /api/sku 200, so this disables unless it is EXPLICITLY true -- a missing / non-boolean isSellable is a
  // malformed contract and must NOT sell. It MIRRORS the submit gate's `isSellable !== true` block exactly, so the
  // visible button state and the gate agree. The `facts &&` guard leaves the NULL-facts case untouched : a failed
  // /api/sku fetch is a DIFFERENT diagnosis, owned by revealInitErrorIfDegraded + the onSubmit facts-null block
  // (the whole post-primary form is hidden then anyway). Uses the SAME summary region + announce pattern as onSubmit.
  function applySellable(form, state, facts) {
    var submitBtn = form.querySelector('[data-order-form-action="submit"]');
    var notSellable = !!(facts && facts.isSellable !== true);
    if (submitBtn) {
      // OWNERSHIP NOTE (PR#21 review obs 1) : applySellable is the SOLE writer of submitBtn.disabled --
      // nothing else in this form toggles it (there is no in-flight-submission disable). So this
      // unconditional set is safe : it cannot stomp another owner's state. If a future change adds an
      // in-flight-submit disable, that path and applySellable must coordinate (e.g. OR the two conditions)
      // rather than each writing .disabled blindly.
      submitBtn.disabled = notSellable;
    }
    // Resolve once into a local so the announce + the "did WE write this?" equality compare use the SAME string
    // (content-centralization 2026-06-27 ; the message lives in order-form-v2-copy as validation.sku.notSellable).
    var notSellableMsg = copyOf(state.config, 'validation.sku.notSellable');
    if (notSellable) {
      if (state.summaryEl) { UI.announce(state.summaryEl, notSellableMsg); }
    } else if (state.summaryEl && state.summaryEl.textContent === notSellableMsg) {
      UI.hideMessage(state.summaryEl);
    }
  }

  // Resolve the PRIMARY FQDN input form-scoped AND order-independently. The real snippets render
  // EVERY fqdn -- the primary AND each SAN row -- via order-form-v2-fqdn-input.liquid, which wraps each
  // in `[data-order-form-field="fqdn"]` and carries `data-order-form-validate="domain"` (the SAN
  // builder renders rows through the same snippet). So `[data-order-form-field="fqdn"]
  // [data-order-form-validate="domain"]` matches N+1 elements on a Multi-Domain form, and a bare
  // querySelector would pick the primary ONLY because it precedes the SAN section in document order
  // (PR#17 r3 round-2 : the prior comment wrongly claimed the wrapper excludes SAN rows). The
  // invariant that actually holds : the SAN rows live inside `[data-order-form-san-row]` and the
  // primary does not, so pick the first domain input NOT inside a SAN row -- robust even if a future
  // assembler renders the SAN section before the primary.
  function findPrimaryFqdn(form) {
    var candidates = form.querySelectorAll('[data-order-form-field="fqdn"] [data-order-form-validate="domain"]');
    for (var i = 0; i < candidates.length; i++) {
      if (!candidates[i].closest('[data-order-form-san-row]')) {
        return candidates[i];
      }
    }
    return null;
  }

  // Resolve the PRIMARY DCV method <select> with the SAME order-independent invariant as
  // findPrimaryFqdn (PR#17 r3 round-2 pattern sweep). Each SAN row also ships a
  // `data-order-form-validate="dcv-method"` <select> (order-form-v2-san-builder.liquid, inside
  // `[data-order-form-san-row]`), so a bare first-match query would pick the primary ONLY because
  // it precedes the SAN section in document order -- the exact fragility findPrimaryFqdn removed.
  // The primary DCV select lives in `[data-order-form-field="dcv"]` and never inside a SAN row, so
  // return the first dcv-method select NOT inside `[data-order-form-san-row]`.
  function findPrimaryDcvSelect(form) {
    var candidates = form.querySelectorAll('[data-order-form-validate="dcv-method"]');
    for (var i = 0; i < candidates.length; i++) {
      if (!candidates[i].closest('[data-order-form-san-row]')) {
        return candidates[i];
      }
    }
    return null;
  }

  // ============================================================================
  // DATA-DRIVEN PRIMARY METHODS (universal-form rebuild, 2026-06-28 ; PRIMARY-METHODS-SPEC.md)
  // ----------------------------------------------------------------------------
  // The middleware GET /api/sku returns facts.primaryMethods : an ORDERED list of { key, inputType } (NO labels --
  // labels are the form's job). The engine renders the SLOT by inputType (csr / domain / text / email) and the LABEL +
  // status by method KEY (csr / autocsr / domain / acme / email), off the per-key copy in config.copy (primary.method.
  // <key>.label / .status.* / .selected / .validate). This GENERALISES the old fixed csr-or-fqdn `{% case of_mode %}`
  // dropdown : the <option>s + the shown slot come from the list, not hardcoded csr/autocsr. setupPrimaryMethods runs on
  // the INITIAL render AND every variant switch (refreshForVariant) ; wirePrimaryMode wires the one-time <select> change
  // listener. The gate + cart stay field-state-driven (see order-form-v2-primary-input.liquid header) -- this is RENDER only.
  // ============================================================================

  // Resolve the method descriptor for a given key from the LIVE facts (re-read each call so a variant switch's new
  // list is honoured). Returns null for an unknown / empty key.
  function findPrimaryMethod(state, key) {
    var methods = (state.facts && Array.isArray(state.facts.primaryMethods)) ? state.facts.primaryMethods : [];
    for (var i = 0; i < methods.length; i++) {
      if (methods[i] && methods[i].key === key) {
        return methods[i];
      }
    }
    return null;
  }

  // Fill a per-method prose paragraph (intro / guidance) from config.copy by method key, shown only when the copy is
  // non-empty ; a slot without the seam (the email slot today) querySelects null and is a clean no-op. DRYs the two
  // identical intro/guidance fills in activatePrimaryMethod (PR #37 review nit 3).
  function fillPrimaryProse(active, attr, config, copyKey) {
    var el = active.querySelector('[' + attr + ']');
    if (!el) { return; }
    var text = copyOf(config, copyKey);
    el.textContent = text;
    el.style.display = text ? '' : 'none';
  }

  // Reveal the SLOT for `method` (resolved by its inputType) and JS-fill its heading + status chip + (domain slot)
  // Validate/Clear buttons from the per-key copy ; hide every other slot. A null method (the placeholder '' option, or
  // inputType 'none') hides ALL slots -- 'none' collects nothing (the gate's field-state coverage is the authority).
  function activatePrimaryMethod(form, state, method) {
    var slots = form.querySelectorAll('[data-order-form-primary-input-type]');
    var inputType = method ? method.inputType : null;
    var active = null;
    for (var i = 0; i < slots.length; i++) {
      var match = !!method && slots[i].getAttribute('data-order-form-primary-input-type') === inputType;
      slots[i].style.display = match ? '' : 'none';
      if (match) { active = slots[i]; }
    }
    if (!active) {
      return; // placeholder / 'none' / an inputType with no slot : nothing to fill
    }
    var key = method.key;
    var config = state.config;
    // Heading : the method's label (the SAME copy the dropdown <option> carries). textContent (never HTML).
    var heading = active.querySelector('[data-order-form-primary-heading]');
    if (heading) {
      heading.textContent = copyOf(config, 'primary.method.' + key + '.label');
    }
    // Intro + guidance prose : per-method copy (primary.method.<key>.intro / .guidance), JS-filled because the inputType
    // slots are SHARED across keys (the domain slot serves the autocsr + domain methods with DISTINCT per-key prose from
    // order-form-v2-copy). Restores every other card's heading -> intro -> control -> guidance -> status structure (owner
    // 2026-06-29). fillPrimaryProse shows each ONLY when the active method declares the key (a slot without the seam --
    // the email slot today -- querySelects null, so no copyOf + no empty paragraph).
    // WILDCARD WORDING (operator decision 2026-10-26) : the two methods that share the domain slot each carry ONE
    // sentence that is wrong on a wildcard product. The autocsr INTRO tells the customer to include the www or another
    // sub-domain (right for a single-site FQDN, wrong for *.example.com) and the domain method's GUIDANCE speaks of the
    // free www (the single-site pairing rule ; a wildcard's free name is its own apex). So each has a .wildcard variant,
    // keyed on the ORDERED product's wildcard family (skuRequiresWildcard : isWildcard OR isWildcardMultiDomain, the
    // same fact the entry rule in validateFqdnNow and the symmetry rule read), telling the customer to enter
    // *.example.com or just example.com and that the form adds the wildcard. The wildcard variant wins over the
    // multi-domain one on a wildcard multi-domain product, whose multi-domain sentence carries the same www instruction.
    var wild = skuRequiresWildcard(state.facts);
    var introKey = 'primary.method.' + key + '.intro';
    if (key === 'autocsr' && wild) {
      introKey = 'primary.method.autocsr.intro.wildcard';
    }
    fillPrimaryProse(active, 'data-order-form-primary-intro', config, introKey);
    // INTRO, SECOND PARAGRAPH (operator ruling 2026-10-26 : the wildcard intro's instruction is its own paragraph, one
    // paragraph was too long). Filled only for the method and product that declare one ; every other combination
    // passes the '' sentinel, which copyOf resolves silently and fillPrimaryProse hides (a slot without the seam is a
    // querySelector miss, also silent). The key list is here, not a concatenation, so the snippet-contract harvest and
    // copyOf's dev warning stay exact.
    var introMoreKey = (key === 'autocsr' && wild) ? 'primary.method.autocsr.intro.wildcard.more' : '';
    fillPrimaryProse(active, 'data-order-form-primary-intro-more', config, introMoreKey);
    // Build 2 (promotion PR #97 round 1, operator ruling + wording 2026-09-02) : the domain method's guidance promises a
    // free www, which is the SINGLE-SITE pairing rule. A multi-domain CaaS product carries no free www (an MD apex is
    // submitted WITHOUT its associated name ; a wildcard keeps its free apex), so the domain method has a SECOND
    // guidance wording keyed on the ORDERED product's isMultiDomain fact from /api/sku. state.facts is assigned before
    // every setupPrimaryMethods call (initial load and refreshForVariant), so the fact is the current SKU's ; absent
    // facts keep the single-site sentence. Method-scoped (the domain key alone, plus the autocsr intro's wildcard
    // variant above) so no other method gains a variant.
    var guidanceKey = 'primary.method.' + key + '.guidance';
    if (key === 'domain' && wild) {
      guidanceKey = 'primary.method.domain.guidance.wildcard';
    } else if (key === 'domain' && state.facts && state.facts.isMultiDomain === true) {
      guidanceKey = 'primary.method.domain.guidance.multidomain';
    }
    fillPrimaryProse(active, 'data-order-form-primary-guidance', config, guidanceKey);
    // Status chip : set the neutral PROMPT / --success PROVIDED wording from the per-key copy, then render the prompt.
    // This is the SAME chip wirePrimaryInput (csr/domain `<id>-success`) or wireCardStatus (text/email card-status)
    // drives later -- BOTH read these attrs LAZILY (showCardStatus at call time), so filling them here makes a validated
    // CSR/domain show the per-method "...PROVIDED" and a text/email presence its prompt->provided, with NO cross-surface
    // leak (each surface's method key carries its OWN wording : autocsr "...FQDN..." vs domain "DOMAIN NAME...").
    var statusChip = active.querySelector('[data-order-form-primary-status]');
    if (statusChip) {
      statusChip.setAttribute('data-order-form-card-status-prompt', copyOf(config, 'primary.method.' + key + '.status.prompt'));
      statusChip.setAttribute('data-order-form-card-status-provided', copyOf(config, 'primary.method.' + key + '.status.provided'));
      // TWO-WRITER RESYNC (review fix, 2026-06-28) : the text/email slots' chip is ALSO wireCardStatus-driven (it
      // carries data-order-form-card-status). Forcing the neutral prompt here would DIVERGE from wireOneCardStatus's
      // edge-trigger `shown` : after a variant round-trip that preserves a valid value (e.g. ACME ext->new->ext), the
      // chip would wrongly read "please provide" for a filled field AND the edge-guard would never re-render it. So for
      // a card-status chip, RESYNC from the field's current content (renders prompt for empty, "...PROVIDED" for valid)
      // ; the csr/domain chips (NOT card-status) keep the simple prompt -- wirePrimaryInput drives their validated state.
      if (statusChip.hasAttribute('data-order-form-card-status') && typeof statusChip.resyncCardStatus === 'function') {
        statusChip.resyncCardStatus();
      } else {
        showCardStatus(statusChip, false);
      }
    }
    // Explicit Validate/Check + Clear buttons : SHOWN when the active method carries a `.validate` key.
    // Both methods that share the FQDN/domain slot carry one -- SSL `autocsr` AND CaaS `domain` (owner 2026-06-29 :
    // consistency -- every primary must behave the same ; the CaaS Domain previously validated silently on blur, which
    // was confusing next to the SSL form's button) -- and the TEXT slot's `acme` method carries one too (the
    // subscription-check Check button, CaaS Subscription Register go-live 2026-08-29 : the click runs the SAME
    // checkAcmeNow as blur, the validateFqdnNow model). The button LABEL is the method's `.validate` copy. The csr
    // slot has no such wrapper (its Validate/Generate buttons are leaf-rendered and always present for the single
    // csr method), so this is a no-op there.
    var validateWrap = active.querySelector('[data-order-form-primary-validate]');
    if (validateWrap) {
      var validateLabel = copyOf(config, 'primary.method.' + key + '.validate');
      if (validateLabel) {
        var labelEl = validateWrap.querySelector('.order-form-action__label');
        if (labelEl) { labelEl.textContent = validateLabel; }
        validateWrap.style.display = '';
      } else {
        validateWrap.style.display = 'none';
      }
      var clearWrap = active.querySelector('[data-order-form-primary-clear]');
      if (clearWrap) { clearWrap.style.display = validateLabel ? '' : 'none'; }
    }
  }

  // Build the primary input from facts.primaryMethods. Called on the INITIAL render AND every variant switch. 0 methods
  // -> hide everything (fail-quiet) ; 1 method -> NO dropdown, show that slot directly ; 2+ -> populate + show the
  // selector (the slot is revealed on `change`). Idempotent (clears the <select> + hides every slot first), so
  // refreshForVariant can re-run it cleanly. The OPTIONS carry value=method.key, text=primary.method.<key>.label.
  function setupPrimaryMethods(form, state, facts) {
    var methods = (facts && Array.isArray(facts.primaryMethods)) ? facts.primaryMethods : [];
    var selectorCard = form.querySelector('[data-order-form-primary-selector]');
    var modeSelect = form.querySelector('[data-order-form-primary-mode]');
    var modeStatusEl = form.querySelector('[data-order-form-primary-mode-status]');
    // PRESERVE the shopper's chosen method across a re-run (variant switch). A validity-period / DV->OV switch keeps
    // the SAME method list (SSL : csr + autocsr), so resetting the dropdown to its placeholder would wrongly DISCARD
    // the chosen issuance method + its typed value on every switch. Capture the prior value BEFORE clearing ; restore
    // it below IF that method still exists in the new variant's list. If the new variant does NOT offer that method,
    // the dropdown shows its placeholder (the form only ever offers the methods the parser returned for THIS variant
    // -- it never carries a method the parser did not return). In practice variants of one product share the same
    // method list, so the restore succeeds ; the placeholder case is the correct behaviour if a list ever differs.
    // Initial render : '' .
    var prevKey = modeSelect ? modeSelect.value : '';
    // Reset : hide every slot + the selector, clear the dropdown options. A re-run starts clean before re-deriving.
    var slots = form.querySelectorAll('[data-order-form-primary-input-type]');
    for (var i = 0; i < slots.length; i++) {
      slots[i].style.display = 'none';
    }
    if (selectorCard) { selectorCard.style.display = 'none'; }
    if (modeSelect) {
      while (modeSelect.firstChild) { modeSelect.removeChild(modeSelect.firstChild); }
      modeSelect.value = '';
    }
    if (!methods.length) {
      // Fail-quiet : a SKU advertising NO primary method renders no input. The submit gate's field-state coverage
      // guard (FAIL-CLOSED FLOOR) is the loud signal -- a required field elsewhere still gates, and an all-hidden
      // primary contributes nothing to cart. Warn so the (likely middleware) misconfiguration is diagnosable.
      log.warn('ORDER FORM : NO PRIMARY METHODS IN /api/sku FACTS - PRIMARY INPUT NOT RENDERED', { sku: facts && facts.sku });
      return;
    }
    if (methods.length === 1) {
      // Single method : no choice to make, so no dropdown -- show the slot directly. (A switch BETWEEN single-method
      // variants -- e.g. CaaS new domain <-> extension acme -- swaps the slot here ; refreshForVariant's
      // resetPrimaryInput then clears the prior method's primaryInput so nothing leaks.)
      activatePrimaryMethod(form, state, methods[0]);
      return;
    }
    // 2+ methods : populate the dropdown (placeholder + one option per method) and show the selector. Element-scoped
    // createElement (ownerDocument) -- the SAME pattern DcvManager.buildMethodSelect / populateApproverSelect use.
    var restore = '';
    if (modeSelect && modeSelect.ownerDocument) {
      var doc = modeSelect.ownerDocument;
      var placeholder = doc.createElement('option');
      placeholder.value = '';
      placeholder.textContent = copyOf(state.config, 'primary.selector.option.placeholder');
      // DROPDOWN-PLACEHOLDER PARITY (owner 2026-07-02) : greyed/lighter + non-selectable, matching the country
      // <option> idiom -- see the same-dated note in DcvManager.buildMethodSelect for why BOTH flags are required and
      // why it is gate-safe. The explicit `modeSelect.value = restore` below still governs when a prior method key
      // survives ; the placeholder case (restore === '') is left displayed by `selected`.
      placeholder.disabled = true;
      placeholder.selected = true;
      modeSelect.appendChild(placeholder);
      for (var m = 0; m < methods.length; m++) {
        var opt = doc.createElement('option');
        opt.value = methods[m].key;
        opt.textContent = copyOf(state.config, 'primary.method.' + methods[m].key + '.label');
        modeSelect.appendChild(opt);
        if (methods[m].key === prevKey) { restore = prevKey; } // the prior choice survives if still offered
      }
      modeSelect.value = restore;
    }
    if (selectorCard) { selectorCard.style.display = ''; }
    if (restore) {
      // The prior method is still offered : re-show its slot + re-render the "...METHOD SELECTED" confirmation, so a
      // variant switch keeps the shopper's chosen method (refreshForVariant re-validates the preserved value after).
      activatePrimaryMethod(form, state, findPrimaryMethod(state, restore));
      if (modeStatusEl) { showCardStatus(modeStatusEl, true, copyOf(state.config, 'primary.method.' + restore + '.selected')); }
    } else if (modeStatusEl) {
      // No (surviving) selection : the selector status chip shows its neutral prompt until a method is chosen.
      showCardStatus(modeStatusEl, false);
    }
    applySelectTick(modeSelect);
  }

  // B : wire the primary-input METHOD selector (the data-order-form-primary-mode <select>) ONCE. The OPTIONS + the
  // initial slot are built by setupPrimaryMethods (after facts load + on every variant switch) ; this attaches only the
  // change listener, which reveals the chosen method's inputType slot, writes the "...METHOD SELECTED" confirmation, and
  // resets the primary + DCV (switching method invalidates the prior method's input). The <select> element always
  // exists in the markup (a single-method SKU just renders no OPTIONS + keeps the selector card hidden, so the listener
  // never fires) ; every lookup is guarded.
  function wirePrimaryMode(form, state) {
    var modeSelect = form.querySelector('[data-order-form-primary-mode]');
    if (!modeSelect) {
      return;
    }
    var modeStatusEl = form.querySelector('[data-order-form-primary-mode-status]');
    modeSelect.addEventListener('change', function () {
      var key = modeSelect.value;
      var method = findPrimaryMethod(state, key);
      // Reveal + fill the chosen method's slot (or hide all on the placeholder '') BEFORE the reset below, so
      // resetPrimaryInput re-renders the new slot's status PROMPT off the attrs activatePrimaryMethod just set.
      activatePrimaryMethod(form, state, method);
      // Selector confirmation : the per-method "...METHOD SELECTED" (primary.method.<key>.selected) as the
      // providedOverride ; an empty / unknown key resolves to '' -> showCardStatus shows the neutral prompt. The
      // tick follows the <select> value. (Only methods that can appear in a 2+ dropdown carry a .selected key.)
      showCardStatus(modeStatusEl, !!key, copyOf(state.config, key ? 'primary.method.' + key + '.selected' : ''));
      applySelectTick(modeSelect);
      // A method switch FULLY resets the DCV section, MATCHING the variant-switch path (refreshForVariant). resetDcvPanels
      // ALONE is NOT cart-safe here : on a VARIANT switch the cart-safety comes from setupPrimaryDcv rebuilding a FRESH
      // DcvManager (token -> null), NOT from resetDcvPanels ; a method switch has no setupPrimaryDcv of its own, so
      // resetDcvPanels alone would leave the OLD DcvManager (stale _method/_approver -> getToken() non-null) AND set
      // primaryShowApprover=false, which DISABLES the loadPrimaryApproversIfEmail reload the stale-approver-token clear
      // depends on -- re-opening the exact stale-approver fail-open. So rebuild the DcvManager FRESH first (token null +
      // the method <select> reset to its placeholder), THEN collapse the panels, exactly as the variant path does.
      // state.facts is loaded (the SKU is unchanged across a method switch ; setupPrimaryDcv reads facts.dcvMethods
      // ONLY -- no fallback -- and renders an empty picker if absent). resetPrimaryInput then re-sets the primary to un-validated.
      setupPrimaryDcv(form, state, state.facts);
      resetDcvPanels(form, state);
      if (state.resetPrimaryInput) {
        state.resetPrimaryInput();
      }
      // PATTERN-SWEEP NOTE (owner UX 2026-06-21) : refreshForVariant calls state.revalidatePrimary right after
      // resetPrimaryInput, but a METHOD switch deliberately does NOT -- it starts the new method's field fresh/empty, so
      // there is nothing to auto-re-validate (different context, deliberately left out, not an omission).
    });
  }

  // Wire the primary CSR / FQDN inputs : single-active toggle + validate-on-action. Filling
  // one disables + clears the other (v1 behaviour) ; a validated CSR / parsed FQDN sets
  // state.primaryInput.{valid, kind, fieldId, domain}.
  function wirePrimaryInput(form, state) {
    // Resolve the primary inputs FORM-SCOPED, by their data-attr seams -- NOT document.getElementById
    // with the default of_id (PR#17 r3 #1). The hardcoded-id lookup broke two ways : (a) a 3c
    // assembler that renders the primary inputs with a custom of_id would never wire them, so the
    // gate could never pass ; (b) on a multi-form page both forms resolved to the FIRST form's
    // inputs. The CSR textarea carries data-order-form-validate="csr" (unique to the primary, no SAN
    // row has a CSR) ; the primary FQDN is resolved order-independently by findPrimaryFqdn (the SAN
    // rows share the same fqdn markup, so it filters them by their [data-order-form-san-row] ancestor).
    var csr = form.querySelector('[data-order-form-field="csr"] [data-order-form-validate="csr"]');
    var fqdn = findPrimaryFqdn(form);
    // SIMPLE PRIMARIES (consistency fix, owner 2026-06-28 : "every form is the SAME form") : the CaaS-extension ACME
    // (inputType 'text') and the future S/MIME e-mail (inputType 'email') are PRIMARIES too -- they must drive the
    // progressive-disclosure gate exactly as a CSR/domain does. Resolved SCOPED to their primary SLOT so the email
    // primary is never confused with the CONTACT e-mail (both carry data-order-form-validate="email"), the same
    // SAN-row-exclusion discipline findPrimaryFqdn uses. The wiring + the gate-driver live after the FQDN block below.
    // Target the text slot's input by its SPECIFIC validate type ("acme") -- the text slot renders the acme-account
    // leaf (data-order-form-validate="acme"), so this matches emailPrimary's precise [data-order-form-validate="email"]
    // pattern rather than a broad first-[data-order-form-validate] grab (PR #34 review : less fragile). A future text
    // inputType method that is NOT acme would update this selector, exactly as the email slot's future S/MIME wiring will.
    var textPrimary = form.querySelector('[data-order-form-primary-input-type="text"] [data-order-form-validate="acme"]');
    var emailPrimary = form.querySelector('[data-order-form-primary-input-type="email"] [data-order-form-validate="email"]');
    var validateBtn = form.querySelector('[data-order-form-action="csr-validate"]');
    // owner 2026-06-22 : the AutoCSR "Validate Fully Qualified Domain Name (FQDN)" button (mirrors the CSR
    // Validate button) + the Clear buttons on both cards. All may be null (a non-csr-or-fqdn layout) -- guarded.
    var fqdnValidateBtn = form.querySelector('[data-order-form-action="fqdn-validate"]');
    var csrClearBtn = form.querySelector('[data-order-form-action="csr-clear"]');
    var fqdnClearBtn = form.querySelector('[data-order-form-action="fqdn-clear"]');
    // CaaS Subscription Register go-live (2026-08-29) : the acme text slot's Check + Clear buttons -- the SAME
    // primary-validate/-clear wrapper pair the FQDN slot carries, shown by activatePrimaryMethod off the acme
    // method's `.validate` copy key. Both may be null (a non-acme layout) -- guarded.
    var acmeCheckBtn = form.querySelector('[data-order-form-action="acme-check"]');
    var acmeClearBtn = form.querySelector('[data-order-form-action="acme-clear"]');
    // Remember the resolved CSR element so writeThrough copies the cart value without a second
    // (hardcoded, document-scoped) lookup (PR#17 r3 #1).
    state.primaryCsrEl = csr;

    // Feedback regions (the success/error message twins each leaf renders, resolved form-scoped by the
    // <fieldId>-success / <fieldId>-error convention) + the primary DCV method <select>. These wire the
    // v1 feedback layer the bare-icon v2 dropped : a validated CSR/FQDN must SHOW its success message +
    // HIDE its error, a failure must SHOW its error region with the API token, and the DCV method stays
    // LOCKED until the primary validates (v1 toggled validationMethodSelect.disabled). All resolved ONCE
    // here, never re-queried per event. Any may be null (a custom assembler that omits a region) -- every
    // use is guarded, so the form still validates + submits, just with degraded feedback.
    var csrSuccessEl = csr ? form.querySelector('[id="' + csr.id + '-success"]') : null;
    var csrErrorEl = csr ? form.querySelector('[id="' + csr.id + '-error"]') : null;
    var fqdnSuccessEl = fqdn ? form.querySelector('[id="' + fqdn.id + '-success"]') : null;
    var fqdnErrorEl = fqdn ? form.querySelector('[id="' + fqdn.id + '-error"]') : null;
    // SECURED-DOMAIN CHIP (owner 2026-06-24) : the CSR card's <csrId>-domain status chip, shown on a successful
    // CSR validate with the EXTRACTED domain so a CSR-mode shopper sees WHICH domain their CSR secures. This
    // REINSTATES the feedback v1 had as its "Securing : <CN>" echo : the 2026-06-21 standard-card redesign had
    // removed that echo, leaving the CN only filling the hidden AutoCSR FQDN twin (invisible in CSR mode) -- a
    // real oversight in the NEW form (v1 was not to be copied, so v2 owns it). Resolved form-scoped like the
    // other shells ; may be null (a custom assembler omitting it) -- every use is guarded. The cart Primary
    // FQDN is UNCHANGED : it still comes from state.primaryInput.domain (writeThrough), never this chip.
    var csrDomainEl = csr ? form.querySelector('[id="' + csr.id + '-domain"]') : null;
    // The acme text slot's error shell (<id>-error, the slot-owned region the input's aria-describedby targets)
    // + its ELIGIBLE-DETAIL chip (<id>-detail : the subscription the check confirmed -- product name + expiry --
    // the CSR secured-domain <id>-domain chip pattern). Both form-scoped, both may be null -- guarded.
    var textErrorEl = textPrimary ? form.querySelector('[id="' + textPrimary.id + '-error"]') : null;
    var textDetailEl = textPrimary ? form.querySelector('[id="' + textPrimary.id + '-detail"]') : null;
    var dcvSelect = findPrimaryDcvSelect(form);
    // C.2 : the hint shown WHILE the DCV method <select> is LOCKED (the <dcvId>-hint status chip the
    // dcv-section renders). Resolved once ; may be null (a custom assembler omitting it) -- every use guarded.
    var dcvHintEl = dcvSelect ? form.querySelector('[id="' + dcvSelect.id + '-hint"]') : null;

    // STANDARD CARD STATUS wording is per-METHOD customer copy in order-form-v2-copy.liquid (primary.method.<key>.status.
    // prompt / .provided), JS-FILLED onto each slot's -success chip by activatePrimaryMethod (universal-form rebuild,
    // 2026-06-28) ; showCardStatus below reads those attrs off the element. The CSR slot confirms "...PROVIDED" on a
    // valid CSR ; the domain slot the same (per the active method's copy -- SSL autocsr vs CaaS domain, no leak).
    // The SECURED-DOMAIN chip text = CSR_DOMAIN_LABEL + the extracted domain. EMPTY for now (owner 2026-06-24 :
    // show the domain BARE -- "example.com" -- "I want to see how it looks bare firstly because the second chip
    // legitimises it anyway"). The owner can add a label later (e.g. 'SECURING : ') by setting THIS ONE constant ;
    // nothing else changes. UPPERCASE-with-space-before-colon house style applies if/when a label word is added.
    var CSR_DOMAIN_LABEL = '';
    // owner 2026-06-22 : shown when the local format gate (CsrValidator.quickFormatCheck) rejects the pasted CSR
    // before any API call -- obvious junk that lacks the PEM armor / is empty / is over the size cap. House style
    // (UPPERCASE, " - " statement/instruction separator, "Certificate Signing Request (CSR)" spelled out). Owner
    // content-pass placeholder -- the wording is the owner's to finalise.
    // (CSR_FORMAT_INVALID_TEXT removed 2026-06-27 : now customer copy ssl.csr.error.format, read via copyOf below.)
    // The transient "working" button label (the brief's wording ; v2-net-new, since the server-side
    // Validate button itself is net-new -- v1 decoded the CSR in-browser with no button).
    // (VALIDATING_TEXT removed 2026-06-27 : now customer copy ssl.validate.working, read via copyOf below.)
    // (DCV_LOCKED_HINT removed 2026-06-27 : the locked-DCV hint is now customer copy dcv.method.locked, read via
    // copyOf(state.config, 'dcv.method.locked') in lockDcv below.)

    // ADVERSARIAL REVIEW : a monotonic generation token guarding the ASYNC primary-validate callbacks against
    // a reset that lands MID-FLIGHT. The CSR validate (validate -> parse, two hops) and the FQDN blur (parse)
    // are async ; a mode switch (wirePrimaryMode), a variant switch (refreshForVariant), or a field re-edit
    // all call resetPrimary SYNCHRONOUSLY in the window before a late callback resolves. Without this guard
    // the stale callback would re-set state.primaryInput = {valid:true} + unlockDcv for a now-hidden / wrong
    // primary, and the gate would ship it to the cart (the exact silent-cart-corruption the redesign exists
    // to prevent). Each handler CAPTURES primaryEpoch at start ; resetPrimary BUMPS it ; the .then arms BAIL
    // if it advanced -- the same epoch discipline refreshForVariant already applies via claimVariantEpoch.
    var primaryEpoch = 0;

    // FIX 3 : the DCV method <select> is LOCKED until the primary validates. The markup ships it enabled,
    // so lock it explicitly on wire-up ; the success branches unlock it, the reset paths re-lock it.
    // C.2 : the lock/unlock also toggles the hint that explains WHY the method picker is unavailable.
    // STALE-APPROVER FAIL-CLOSE (PR #39 review, owner 2026-07-01) : a re-locked primary must ALSO reset the method
    // <select> to its placeholder + reset the primary DcvManager (onMethodChange('') -> _method='' + _approver=''
    // -> getToken() null) + drop the tick + the showApprover flag -- EXACTLY as the SAN sibling lockRowDcv already
    // does per row. Without this, editing the primary DOMAIN (which fires resetPrimary -> lockDcv) left the OLD
    // method (e.g. EMAIL) selected and state.primaryDcv still holding the PREVIOUS domain's approver, so getToken()
    // returned that stale approver for the ~1 RTT between re-validating the NEW domain and the async approver reload
    // dropping it -- a gate fail-OPEN (an approver for the WRONG domain could reach the cart). The mode switch +
    // variant switch already close this by rebuilding the DcvManager FRESH (setupPrimaryDcv) ; the primary DOMAIN
    // re-edit was the one path without that, so null the token SYNCHRONOUSLY here. The shopper re-picks the method
    // after re-validating (identical to how an edited SAN row behaves). ALL null-guarded : lockDcv also runs at
    // wire-up (before state.primaryDcv exists) and state.primaryDcv is reassigned per variant / method switch.
    function lockDcv() {
      if (dcvSelect) {
        dcvSelect.disabled = true;
        dcvSelect.value = '';
        if (state.primaryDcv) { state.primaryDcv.onMethodChange(''); } // _method='' + _approver='' -> getToken() null
        applySelectTick(dcvSelect); // reset to placeholder -> clear the method tick (parity with lockRowDcv)
      }
      state.primaryShowApprover = false; // an invalidated primary shows no approver ; the token was just nulled above
      if (dcvHintEl) { UI.showMessage(dcvHintEl, copyOf(state.config, 'dcv.method.locked')); }
    }
    function unlockDcv() {
      if (dcvSelect) { dcvSelect.disabled = false; }
      // owner 2026-06-23 : the picker is now UNLOCKED -- if no method is chosen yet, the hint becomes the
      // "VALIDATION METHOD MUST BE SELECTED" prompt (was hidden) ; if a method is already chosen (a re-validate
      // that preserved it), hide it. The wireDcv change handler keeps it in sync as the method changes.
      if (dcvHintEl) {
        if (dcvSelect && dcvSelect.value) { UI.hideMessage(dcvHintEl); }
        else { UI.showMessage(dcvHintEl, copyOf(state.config, 'dcv.method.required')); }
      }
    }
    lockDcv();

    // The default anchor for an un-validated primary : whichever primary field the markup
    // actually renders (CSR-or-FQDN mode ships both ; an FQDN-only form ships no csr-textarea).
    // Keying on csr.id unconditionally meant a gate failure on an FQDN-only form -- BEFORE the
    // shopper touched the field -- anchored the error/focus to a non-existent element and
    // silently no-op'd (PR#17 round-2 nit). Fall through csr -> fqdn -> the non-anchoring
    // 'order-form' SENTINEL (owner 2026-06-29 A+ sweep : a primary with NEITHER a CSR nor an FQDN
    // element routes the error to the form SUMMARY, never a hardcoded phantom id).
    var defaultPrimaryFieldId = (csr && csr.id) || (fqdn && fqdn.id) || 'order-form';
    // Correct the initial state's anchor (set generically in wireForm before the fields were
    // known) so a pre-interaction gate failure highlights a REAL field.
    state.primaryInput.fieldId = defaultPrimaryFieldId;

    // resetPrimary anchors the (now-invalid) primary to whichever field the shopper last
    // touched, so a gate failure highlights + focuses THAT input (PR review : an invalid FQDN
    // wrongly anchored to the CSR field).
    function resetPrimary(fieldId) {
      // ADVERSARIAL REVIEW : BUMP the generation token first -- every reset (re-edit, mode switch, variant
      // switch via resetPrimaryInput) invalidates any in-flight CSR/FQDN validate callback, so a late
      // resolution cannot re-set a stale valid primary after the reset.
      primaryEpoch += 1;
      state.primaryInput = { valid: false, kind: null, fieldId: fieldId || defaultPrimaryFieldId, domain: '' };
      // FIX 3 : an un-validated primary re-LOCKS the DCV method (the method follows the domain).
      lockDcv();
      // owner 2026-06-23 : a reset returns the CSR / AutoCSR card status chip to its neutral PROMPT (it is the
      // shared status chip now -- prompt when not validated, --success confirmation when validated -- so a reset
      // shows "...MUST BE PROVIDED AND VALIDATED TO CONTINUE", never goes blank). The error region is driven
      // separately (an error path hides this chip + shows the -error ; a re-edit re-shows the prompt here).
      showCardStatus(csrSuccessEl, false);
      showCardStatus(fqdnSuccessEl, false);
      // SECURED-DOMAIN CHIP (owner 2026-06-24) : an un-validated primary clears the secured-domain chip too, so a
      // re-edited / cleared CSR can never show a stale domain next to a primary that no longer validates. HIDE it
      // (not a prompt revert : the chip is blank until a CSR validates, unlike the always-shown status chip above).
      if (csrDomainEl) { UI.hideMessage(csrDomainEl); }
      // SUBSCRIPTION-CHECK VERDICT (CaaS Subscription Register go-live, 2026-08-29) : EVERY reset drops the
      // eligible verdict -- an edit / mode switch / variant switch invalidates the check for the exact-value
      // rule (section 9 clause 3), so the gate re-blocks until the shopper re-checks. The eligible-detail chip
      // follows the secured-domain chip's rule above : blank until a check succeeds, hidden on any reset.
      state.subscriptionCheck = null;
      if (textDetailEl) { UI.hideMessage(textDetailEl); }
      // FIX 8 (pre-push review) : the EMAIL confirmation is the THIRD success surface and follows the same
      // rule -- an invalidated primary must clear it too. It lives in wireDcv's closure, reached via the
      // exposed hook (null-guarded : the hook is set when wireDcv runs, and resetPrimary only fires on field
      // events that occur after all wiring). Without this, a validated-EMAIL shopper who re-edits the domain
      // would keep seeing "...SUCCESSFULLY SELECTED" next to an un-validated primary.
      if (state.hideDcvConfirmation) { state.hideDcvConfirmation(); }
      // PROGRESSIVE DISCLOSURE (owner 2026-06-22) : an un-validated primary RE-HIDES the gated rest of the form,
      // and the Validate buttons drop their "validated" tick. So switching issuance method, or editing the CSR
      // or domain, collapses the form again -- signalling there is something to do before continuing.
      applyPrimaryGate(form, state);
      setButtonValidated(validateBtn, false);
      setButtonValidated(fqdnValidateBtn, false);
      setButtonValidated(acmeCheckBtn, false);
    }

    // Exposed so refreshForVariant can re-set the primary to un-validated on a variant switch
    // (PR#17 r9 #1) : the primaryDcv + SAN rows are re-derived/re-validated for the new variant, so
    // a primary validated under the OLD variant must not stay valid:true. The typed value is KEPT
    // (the inputs are not cleared) ; only the validation state + the field's valid/error icon reset,
    // so the shopper re-validates the same domain under the new variant's rules.
    state.resetPrimaryInput = function () {
      resetPrimary(defaultPrimaryFieldId);
      if (csr) { UI.setFieldState(csr, 'neutral'); }
      if (fqdn) {
        UI.setFieldState(fqdn, 'neutral');
        // FIX 2 : a CSR-DERIVED fqdn (auto-filled with the CN, hence disabled) is not shopper-typed, so a
        // variant reset drops it ; the KEPT CSR re-derives it when the shopper re-validates under the new
        // variant. A shopper-typed fqdn (not disabled) is preserved, matching this reset's keep-typed rule.
        if (fqdn.value && fqdn.disabled) { fqdn.value = ''; }
        // PR#21 r3 BUG FIX : RE-ENABLE the FQDN field. The single-active CSR<->FQDN toggle (csr.input
        // disables fqdn while the CSR has content) predates the B mode selector. Without this, switching from
        // CSR mode to AutoCSR mode -- after typing/validating a CSR -- left the now-VISIBLE AutoCSR domain
        // input DISABLED + empty, with no path back to enabled (re-enable previously fired only on a CSR
        // input event, and the CSR is now hidden). resetPrimaryInput runs on every mode + variant switch, so
        // restoring the field here makes the incoming mode usable. Cart-safe : primaryInput is reset to
        // invalid, so the gate fail-closes regardless of which inputs are enabled ; the single-active toggle
        // re-applies on the next csr/fqdn input in the (non-mode-selector) combined layout.
        fqdn.disabled = false;
      }
      // Clear any error message left from a prior attempt so the re-derived primary starts clean.
      if (csrErrorEl) { UI.hideMessage(csrErrorEl); }
      if (fqdnErrorEl) { UI.hideMessage(fqdnErrorEl); }
      if (textErrorEl) { UI.hideMessage(textErrorEl); }
      // The acme primary follows the csr/fqdn neutralize rule above (go-live 2026-08-29) : a refused check writes
      // 'error' onto the field, and a variant switch hides the refusal message (textErrorEl above), so leaving the
      // red icon standing would show an unexplained error state. Neutral until revalidatePrimary's auto re-check
      // settles -- the same transient the fqdn shows during its re-validate round-trip.
      if (textPrimary) { UI.setFieldState(textPrimary, 'neutral'); }
    };

    // Exposed so refreshForVariant can INVALIDATE any in-flight primary validate SYNCHRONOUSLY at the very START of
    // a switch -- BEFORE the async getFacts resolves (Finding 3, owner 2026-07-01). resetPrimaryInput also bumps the
    // epoch, but it only runs AFTER getFacts resolves ; in the window between a switch starting (facts nulled) and
    // getFacts landing, a CSR/FQDN validate dispatched JUST BEFORE the switch would otherwise resolve with the
    // not-yet-bumped epoch (gen === primaryEpoch) and transiently apply stale primary state (a flashed success /
    // generic-CSR-error chip) against the now-null facts. Bumping the epoch up-front makes those late callbacks bail
    // immediately. This is COSMETIC-only (a submit in that window is already fail-closed by onSubmit's null-facts
    // guard) -- it removes the stale FLASH, it is not a cart hole. Kept lightweight (epoch only) : the FULL primary
    // reset still runs post-render via resetPrimaryInput, so this does not double the DOM reset.
    state.invalidatePrimaryEpoch = function () { primaryEpoch += 1; };

    // Exposed so refreshForVariant can AUTO re-validate the PRESERVED primary value under the NEW variant's
    // rules (owner UX fix 2026-06-21 : a variant switch must not force the shopper to manually re-edit the
    // field to get the tick back + unlock the DCV method -- the prior behaviour left a typed-and-valid domain
    // looking un-validated after every switch). resetPrimaryInput has ALREADY run just before this : the field
    // is neutral, the DCV is re-locked, primaryEpoch is bumped. This re-runs the SAME validation path the
    // shopper's blur / Validate-click would, reading the freshly-assigned state.facts -- so the new SKU's A.1
    // wildcard-symmetry + A.3 CN-is-a-domain rules apply. A value still valid for the new SKU silently regains
    // its tick + unlocked method ; a value now INVALID (a wildcard CN on a single-site variant, a "*." typed on a
    // non-wildcard variant) correctly shows the error instead of a stale tick. The typed value is KEPT, with ONE
    // rewrite since the WILDCARD ENTRY rule of 2026-10-26 : a bare domain kept across a switch to a wildcard variant
    // goes through validateFqdnNow's entry rule like any blur, so it is prefixed "*." in the box and validates for
    // the new variant instead of showing the wildcard.required error (the CSR path never rewrites a CN). Async
    // (the parse / CSR validate runs on a microtask) ; the epoch guard in each handler bails if a newer switch
    // supersedes it.
    state.revalidatePrimary = function () {
      // ACTIVE-SLOT GUARD (universal-form rebuild, 2026-06-28) : only re-validate the input whose SLOT is currently
      // SHOWN (the active primary method). A HIDDEN csr/fqdn slot holds a value left over from a DIFFERENT method --
      // e.g. a domain typed on a CaaS NEW variant, preserved (un-cleared) when the shopper switches to the CaaS
      // EXTENSION (ACME) variant. Without this guard, re-validating that hidden domain would set state.primaryInput =
      // {valid:true, kind:'fqdn'} for a method that is NOT active, and writeThrough (kind-keyed) would LEAK its domain
      // into the ACME cart's Primary FQDN. setupPrimaryMethods runs FIRST in refreshForVariant, so the active slot is
      // already shown by the time this fires ; isInHiddenSection reads the slot's ([data-order-form-section]) COMPUTED
      // display. (For a 2+-method SKU with no method chosen yet, BOTH slots are hidden -> nothing re-validates, correct.)
      // CSR mode takes precedence (single-active : a populated, enabled CSR means the FQDN box was the derived one
      // resetPrimaryInput cleared). Re-run the Validate-CSR click path : it re-derives the CN and re-checks it against
      // the new SKU's wildcard rule. Guard on the button + the CSR being the active, populated, SHOWN input.
      if (validateBtn && csr && !csr.disabled && csr.value && !isInHiddenSection(csr)) {
        validateBtn.dispatchEvent(new Event('click'));
        return;
      }
      // FQDN mode : re-run the blur parse (server PSL truth + the new SKU's wildcard symmetry). Skip a disabled,
      // empty, or HIDDEN-slot field (an empty primary stays neutral ; a hidden one belongs to another method).
      if (fqdn && !fqdn.disabled && fqdn.value && !isInHiddenSection(fqdn)) {
        fqdn.dispatchEvent(new Event('blur'));
        return;
      }
      // ACME primary (CaaS Subscription Register go-live, 2026-08-29) : re-run the REAL subscription check for a
      // preserved value, exactly as the CSR / FQDN arms re-run their middleware validation above -- the check
      // POSTs { acmeAccountId, sku } and the SKU JUST CHANGED, so the old verdict cannot carry over (resetPrimary
      // already dropped it ; checkAcmeNow's already-eligible dedup is keyed on primaryInput.valid, which the reset
      // cleared, so the re-check always fires). Same active-slot guard : a hidden slot holds another method's value.
      if (textPrimary && textPrimary.value && !isInHiddenSection(textPrimary)) {
        checkAcmeNow();
        return;
      }
      // SIMPLE PRIMARY (email) : re-open the gate for an S/MIME-e-mail primary preserved across the switch -- the
      // local-validation analogue of the re-validates above (no manual re-edit). Same active-slot guard.
      if (emailPrimary && emailPrimary.value && !isInHiddenSection(emailPrimary)) {
        evaluateSimplePrimary(emailPrimary, 'email');
      }
    };

    // owner 2026-06-24 (PR #28 review R5 #1) : a field's blur fires when focus moves to ITS OWN Clear / Generate
    // -CSR-Tool button -- i.e. the shopper is about to DISCARD or REPLACE the value. Auto-validating in that window
    // would burn a metered Sectigo DecodeCSR / domain-parse quota call on a value that is about to vanish (the
    // in-flight result then no-ops on the primaryEpoch guard, so state stays correct -- but the quota call is
    // already spent). relatedTarget is the element GAINING focus ; it is null in browsers that do not focus a
    // button on mousedown (Firefox / Safari), where we fall through to the prior behaviour (one harmless, epoch
    // -guarded call) -- so this is a strict improvement, never a regression. closest() so a focus landing on a
    // child node of the button still resolves to the action. Bound to the BLUR path only ; an explicit button
    // click always validates (it carries no relatedTarget and IS the shopper's deliberate "validate now").
    function blurMovesToDiscard(ev, actions) {
      var to = ev && ev.relatedTarget;                                  // the element receiving focus (may be null)
      var actionEl = (to && to.closest) ? to.closest('[data-order-form-action]') : null;
      if (!actionEl) { return false; }
      return actions.indexOf(actionEl.getAttribute('data-order-form-action')) !== -1;
    }

    if (csr) {
      csr.addEventListener('input', function () {
        if (csr.value && fqdn) {
          // FIX 2 : a CSR edit drops the now-stale DERIVED fqdn value + its 'valid' icon ; the toggle
          // disables the FQDN field (single-active).
          fqdn.value = '';
          fqdn.disabled = true;
          UI.setFieldState(fqdn, 'neutral');
        } else if (fqdn) {
          fqdn.disabled = false;
        }
        // ANY edit in the CSR field invalidates the current primary validation, SELF-CONTAINED :
        // it resets unconditionally rather than only when the prior validation was a CSR one
        // (kind === 'csr') or the field is empty. The old guard left a validated FQDN's result
        // live when the shopper started typing a CSR -- safe ONLY because the toggle disables the
        // FQDN input, so a single broken toggle would have leaked a stale FQDN to the cart
        // (fail-open). A disabled field cannot fire 'input', so resetting on every editable edit
        // is correct in every case (re-edited CSR, switch-from-FQDN, clear). PR#17 #2 (+ the
        // earlier stale-CSR fix it subsumes).
        resetPrimary(csr.id);
        UI.setFieldState(csr, 'neutral');
        // FIX 1 : re-editing the CSR clears its prior error (resetPrimary already reverted the status chip to its
        // neutral PROMPT), so the slot shows exactly the prompt -- not the error and not a stale confirmation.
        if (csrErrorEl) { UI.hideMessage(csrErrorEl); }
      });
    }
    // owner 2026-06-24 : guards for the CSR auto-check (blur) + the button, which BOTH run the same click handler :
    //  - lastAttemptedCsr : the value last SENT to validate ; a re-blur of an UNCHANGED CSR (validated OR failed)
    //    skips a redundant /api/csr/validate call. Cleared on a Clear so a re-pasted CSR re-validates. KNOWN EDGE
    //    (PR #28 review #2, accepted) : reverting an edited CSR to the EXACT previously-attempted bytes (Ctrl-Z)
    //    then blurring is skipped here (value === lastAttemptedCsr) and stays gated ; recoverable via a manual
    //    Validate click. NOT cleared on resetPrimary on purpose -- the FAILURE path also resets and must keep the
    //    value to avoid re-spamming a failed CSR, so the rare edit-undo gap is the deliberate trade-off.
    //  - csrValidating : an IN-FLIGHT flag that dedups the mouse blur+click double-fire (this blur dispatches the
    //    button's click, then the real mouseup-click fires too) WITHOUT disabling the button. We deliberately do NOT
    //    use the disabled attribute to suppress the second call : disabling the button mid focus-transition (a
    //    keyboard Tab out of the CSR) steals focus from the button being tabbed to (A+ keyboard regression, PR #28
    //    review #1). The green validated tick (setButtonValidated) is the "done" signal instead of a disabled state.
    //    Held across BOTH async hops (validate + CN parse) and cleared only when the whole chain settles (the
    //    appended .then below), so a button press landing in the parse-hop window cannot start a second validate
    //    either (PR #28 review R3 #2). COUPLING (PR #28 review R6 #3) : this flag's safety depends on the chain
    //    ALWAYS settling -- a never-settling validate()/parse() would latch it true and hard-break ALL future
    //    validation (the guard blocks every later attempt, with no disabled state to signal it). Safe by
    //    construction : ApiClient wraps every fetch in a 15s AbortController + .catch, so the chain always resolves
    //    and the settle arm runs. The same coupling applies to the FQDN card's fqdnValidating flag.
    // KNOWN EDGE (PR #28 review R4 #1, accepted) : if a shopper EDITS the CSR while a prior validate is still in
    // flight (csrValidating true) and blurs the new value, that blur's dispatched click early-returns on the
    // in-flight flag ; when the prior validate settles it bails (its epoch was bumped by the edit) and clears the
    // flag, leaving the NEW value un-auto-validated until a manual Validate click. Recoverable, same class as the
    // edit-undo gap ; NOT re-armed on settle (a re-arm would add self-trigger-loop risk for little gain).
    // KNOWN EDGE (PR #28 review R8 #3, Info, accepted) : a VARIANT switch landing mid-validate is the same class --
    // refreshForVariant dispatches a re-validate click, but the in-flight flag early-returns it, so the primary is
    // left un-revalidated until the next interaction. This MATCHES the prior disabled-button behaviour (a disabled
    // button also swallowed the dispatched click), so it is NOT a regression ; and it fails CLOSED -- refreshForVariant
    // nulls state.facts first, so a submit landing in that window hits the facts-null fail-closed block (PR#17 r17).
    var lastAttemptedCsr = null;
    var csrValidating = false;
    if (validateBtn && csr) {
      validateBtn.addEventListener('click', function () {
        // owner 2026-06-24 : in-flight dedup -- if a validate is already running (e.g. the blur dispatched this
        // click and the real mouseup-click is now firing too), do nothing. Replaces the old disable-based
        // suppression, which stole keyboard focus on a Tab out of the CSR box (PR #28 review #1).
        if (csrValidating) { return; }
        // owner 2026-06-24 (PR #28 review R4 #2) : skip ONLY when THIS exact CSR is ALREADY validated (valid
        // primary + unchanged value) -- re-hitting Sectigo for an already-validated CSR is pure waste, and it is
        // exactly what a keyboard "Tab onto the button (which auto-validates on blur) then Enter" would otherwise
        // do (a doubled DecodeCSR quota call on a common flow). An UNVALIDATED CSR with an unchanged value STILL
        // validates : that is the deliberate recovery path (R3 #1) for the edit-undo gap AND a failed-CSR retry --
        // both leave an INVALID primary, so they fall through this guard. (NOT the blur's `value !== lastAttemptedCsr`
        // guard, which would wrongly block recovery -- this one is keyed on the primary being already VALID.)
        if (state.primaryInput && state.primaryInput.valid && state.primaryInput.kind === 'csr'
            && (csr.value || '').trim() === lastAttemptedCsr) { return; }
        // owner 2026-06-22 : CHEAP LOCAL FORMAT GATE before the API round-trip. The middleware confirmed the form
        // is the PRIMARY defence (rejects malformed CSRs at submit) ; /api/csr/validate is backend defence-in
        // -depth. Mirror its pre-Sectigo shape check via CsrValidator.quickFormatCheck (non-empty + size cap +
        // '-----BEGIN' anchored at 0 + '-----END' present ; NO crypto -- Sectigo stays the authority). Obvious
        // junk is rejected here so it never fires a Sectigo quota call. A failed check resets the primary (hides
        // the gate + re-locks DCV, like a CN-invalid CSR) and shows the format error WITHOUT calling the API.
        if (!state.csrValidator.quickFormatCheck(csr.value)) {
          resetPrimary(csr.id);
          UI.setFieldState(csr, 'error');
          if (csrSuccessEl) { UI.hideMessage(csrSuccessEl); } // an error owns the status slot : hide the prompt/confirmation chip
          if (csrErrorEl) { UI.showMessage(csrErrorEl, copyOf(state.config, 'ssl.csr.error.format')); }
          return;
        }
        // owner 2026-06-24 : record the value we are about to validate, so a later re-blur of this EXACT CSR does
        // not re-call the API (the blur auto-check below compares against it). Set AFTER the format gate, so a junk
        // value that never reaches the API is never recorded as "attempted".
        lastAttemptedCsr = (csr.value || '').trim();
        csrValidating = true;
        // owner 2026-06-24 : transient WORKING state = the "Validating…" LABEL only ; the button is NOT disabled
        // (disabling it mid focus-transition steals keyboard focus -- PR #28 review #1). The label swap targets the
        // dedicated label span (buttonLabelEl) so it never wipes the validated-tick icon span ; restored on settle.
        var validateLabelEl = buttonLabelEl(validateBtn);
        var restoreLabel = validateLabelEl.textContent;
        validateLabelEl.textContent = copyOf(state.config, 'ssl.validate.working');
        // ADVERSARIAL REVIEW : capture the generation at validate-start ; a reset that lands while the
        // validate/parse is in flight bumps primaryEpoch, and the arms below BAIL so a stale callback cannot
        // re-set a now-superseded primary (mode-switch / variant-switch / re-edit race).
        var gen = primaryEpoch;
        // owner 2026-06-24 (PR #28 review R8 #1) : guard the SYNCHRONOUS leg too. csrValidating is already true ; if
        // validate() ever threw synchronously (a future refactor making it not a clean promise factory) the settle
        // .then(ok,err) below would never attach and the flag would latch true -> hard-break ALL future validation.
        // This is the SAME flag-stranding invariant the reject arm guards (review R5 #2), now closed for the sync
        // path : clear the flag, restore the working label, SURFACE the error (log, never swallowed), and bail.
        var csrValidatePromise;
        try {
          csrValidatePromise = state.csrValidator.validate(csr.value);
        } catch (csrSyncErr) {
          csrValidating = false;
          validateLabelEl.textContent = restoreLabel;
          log.error('ORDER FORM : CSR VALIDATE THREW SYNCHRONOUSLY - IN-FLIGHT FLAG CLEARED', csrSyncErr);
          return;
        }
        csrValidatePromise.then(function (outcome) {
          validateLabelEl.textContent = restoreLabel;
          // The label restore above belongs to THIS click ; but bail before touching ANY primary/DCV state
          // if a reset superseded this validate (do not resurrect a stale primary into the cart path).
          if (gen !== primaryEpoch) {
            return;
          }
          if (!(outcome && outcome.ok && outcome.data)) {
            // The CSR itself is invalid (validateCsr failed -- a Certificate Authority (CA) DecodeCSR rejection).
            // resetPrimary hid the success + re-locked DCV ; SHOW the error region with a CENTRALISED copy key, never
            // the raw server message (owner 2026-07-01 : the form surfaces curated copy, not uncontrolled server
            // text). The per-reason copy pass that ruling deferred landed 2026-09-01 (owner decision) : the
            // middleware's error.reason selects the per-reason key (csrRejectionReasonKey), and a rejection with no
            // known reason keeps the generic line.
            resetPrimary(csr.id);
            UI.setFieldState(csr, 'error');
            if (csrSuccessEl) { UI.hideMessage(csrSuccessEl); } // an error owns the status slot : hide the prompt/confirmation chip
            var wireReason = outcome && outcome.error ? outcome.error.reason : undefined;
            var csrReasonKey = csrRejectionReasonKey(wireReason);
            if (!csrReasonKey && typeof wireReason === 'string' && wireReason) {
              // The wire named a reason this allowlist does not know (the theme and the middleware deploy
              // independently, so a seventeenth reason can ship middleware-side first). The customer still gets
              // the generic line ; WARN LOUD (the caasCheckFailureText posture) so the fall-back is diagnosable from
              // the console instead of silently reproducing the blind spot this mapping exists to close.
              log.warn('ORDER FORM : CSR VALIDATE ANSWERED AN UNKNOWN REJECTION REASON - SHOWING THE GENERIC LINE', { reason: wireReason });
            }
            if (csrErrorEl) {
              // The per-reason key falls back to the generic line when its copy is EMPTY as well as when the reason is
              // unknown : a reason added to the allowlist and the copy master but not to config.liquid's capture + emit
              // pair would otherwise paint a BLANK chip on a real rejection (copyOf resolves an unemitted key to ''),
              // strictly worse than the line this arm promises to fail closed to.
              var csrReasonText = csrReasonKey ? copyOf(state.config, csrReasonKey) : '';
              UI.showMessage(csrErrorEl, csrReasonText || copyOf(state.config, 'ssl.csr.error.generic'));
            }
            return;
          }
          // The CSR decoded ; its Common Name's SUBJECT is described by the SKU parser (facts.primaryMethods, the
          // key:'csr' entry's `subject`). The form READS that fact and APPLIES it -- it no longer hardcodes "the CN
          // must be a domain" (owner 2026-07-01 : the parser decides the type, the form validates off it).
          var csrCn = state.csrValidator.extractDomain(outcome.data) || '';
          var subject = csrMethodSubject(state.facts);
          if (subject === 'email') {
            // The parser said the CN is an E-MAIL (S/MIME) : validate with the existing isValidEmail primitive and
            // route the validated CN into the End User E-Mail Address field (CSR-derived, read-only ; writeThrough then
            // carries it to the cart twin under the cartProperties name). NO Primary FQDN field / secured-domain chip /
            // Primary FQDN property -- primaryInput.domain stays '' so writeThrough writes no Primary FQDN. There is no
            // second async hop here (isValidEmail is synchronous), so no extra epoch re-check is needed.
            // DEFENSIVE trim only (PR #38 review) : the middleware decoder already trims the CN (value.trim() in its
            // DecodeCSR text parse) and writeThrough trims the visible value again, so this is belt-and-suspenders, NOT
            // a fix for a real leak -- it just keeps the local validate + the displayed value consistent with those.
            var emailCn = csrCn.trim();
            // LENGTH CONSISTENCY (PR #38 review) : the derived e-mail lands in the READ-ONLY End User E-Mail Address
            // field, and the submit gate + the contact-card status + the live tick all apply the field's BYTE cap via
            // fieldContentError. A valid-FORMAT but over-cap CN would otherwise pass this CSR arm ("valid") only to be
            // blocked by the gate on a field the shopper cannot edit. So run the SAME config-driven length check the
            // gate uses for the contact e-mail : resolve the cap + lengthKey exactly as wireOneFieldCheck does (the
            // 'email' property -> the 'contact-email' validator id -> config.validation['contact-email'].maxLength +
            // its lengthKey). NO hardcoded 254 -- the value is the single source (order-form-v2-validation.liquid) ;
            // the form makes no length decision, it mirrors the gate. fieldContentError returns the lengthKey (a copy
            // KEY) when over-cap, or null when within it.
            var emailFieldId = PROPERTY_TO_VALIDATOR_FIELD['email'];
            var emailMaxLength = maxLengthOf(state.config && state.config.validation, emailFieldId);
            var emailLengthErr = fieldContentError(emailCn, emailMaxLength, (FIELD_RULES_BY_ID[emailFieldId] || {}).lengthKey);
            if (isValidEmail(emailCn) && emailLengthErr === null) {
              state.primaryInput = { valid: true, kind: 'csr', fieldId: csr.id, domain: '', apiIsValid: true, apiIsWildcard: false };
              UI.setFieldState(csr, 'valid');
              if (csrSuccessEl) { showCardStatus(csrSuccessEl, true); } // chip -> --success confirmation (per-card copy)
              if (csrErrorEl) { UI.hideMessage(csrErrorEl); }
              var emailEl = form.querySelector('[data-order-form-property="email"]:not([type="hidden"])');
              if (emailEl) {
                emailEl.value = emailCn; // read-only derived display ; writeThrough copies the visible value to the cart twin
                UI.setFieldState(emailEl, 'valid');
                emailEl.dispatchEvent(new Event('input')); // refresh the contact-card status + tick for the auto-filled value
              }
              unlockDcv();
              loadPrimaryApproversIfEmail(state);
              applyPrimaryGate(form, state);
              setButtonValidated(validateBtn, true);
              revalidateSanRows(state);
            } else {
              // Reject with centralised copy. TWO failure modes : (1) the CN is not an e-mail at all (a domain CSR on
              // an S/MIME SKU) -> validation.csr.cn.notEmail ; (2) the CN is a valid e-mail but busts the contact
              // e-mail byte cap -> show the SAME over-length copy the gate would show for that field (emailLengthErr
              // IS the field's lengthKey copy key), so the shopper sees WHY rather than a misleading "must be an
              // e-mail" on an address that already is one.
              resetPrimary(csr.id);
              UI.setFieldState(csr, 'error');
              if (csrSuccessEl) { UI.hideMessage(csrSuccessEl); } // an error owns the status slot : hide the prompt/confirmation chip
              var csrEmailRejectKey = (isValidEmail(emailCn) && emailLengthErr !== null) ? emailLengthErr : 'validation.csr.cn.notEmail';
              if (csrErrorEl) { UI.showMessage(csrErrorEl, copyOf(state.config, csrEmailRejectKey)); }
            }
            return;
          }
          if (subject !== 'domain') {
            // The parser did NOT describe the CSR's subject (or named an unknown one) : FAIL CLOSED (reject). No
            // default, no guess (owner 2026-07-01) -- the same fail-closed posture as missing /api/sku data. WARN LOUD
            // (mirroring the applyCartPropertyNames / degraded-facts warns) so a MISCONFIGURED production -- /api/sku not
            // yet emitting `subject` on the csr method -- is DIAGNOSABLE from the console, instead of presenting as a
            // silent generic "invalid CSR" to every customer (PR #38 review). Deploy ordering : /api/sku must emit
            // `subject` on every csr method before this reaches main (documented in the PR body).
            log.warn('ORDER FORM : CSR METHOD HAS NO subject FACT (expected domain|email) - CANNOT VALIDATE THE COMMON NAME, FAILING CLOSED', { subject: subject, sku: state.facts && state.facts.sku });
            resetPrimary(csr.id);
            UI.setFieldState(csr, 'error');
            if (csrSuccessEl) { UI.hideMessage(csrSuccessEl); }
            if (csrErrorEl) { UI.showMessage(csrErrorEl, copyOf(state.config, 'ssl.csr.error.generic')); }
            return;
          }
          // subject === 'domain' (SSL) : apply the SERVER verdict via /api/domain/parse. The CSR endpoint returns the
          // CN VERBATIM and does NOT validate it as a domain, so a wildcard CN on a single-site SKU would otherwise slip
          // through ; the parse verdict (isValid + isWildcard) drives wildcard symmetry as it does for a typed FQDN.
          return state.domainNormalizer.parse(csrCn).then(function (parsed) {
            // ADVERSARIAL REVIEW : re-check after the SECOND async hop too -- a reset can land between the CSR
            // validate resolving and the CN parse resolving.
            if (gen !== primaryEpoch) {
              return;
            }
            var ok = !!(parsed && parsed.ok && parsed.data);
            var apiIsValid = ok ? parsed.data.isValid : undefined;
            var apiIsWildcard = ok ? !!parsed.data.isWildcard : false;
            var domErr = (ok && apiIsValid) ? primaryDomainErrorToken(apiIsValid, apiIsWildcard, state.facts) : null;
            if (ok && apiIsValid && !domErr) {
              // The server's `normalized` is the full host minus the leading "*." (every sub-label kept ; NOT the
              // registrable base), so re-applying "*." is exact for single-label AND nested wildcards ; keep it on
              // the SUBMITTED Primary FQDN + the chip (owner 2026-06-24 ; nested confirmed 2026-06-25).
              var cn = state.domainNormalizer.applyWildcard(parsed.data.normalized || csrCn, apiIsWildcard);
              // A.0 : carry the server verdict on primaryInput so the gate's A.1/A.3 can re-check it.
              state.primaryInput = { valid: true, kind: 'csr', fieldId: csr.id, domain: cn, apiIsValid: true, apiIsWildcard: apiIsWildcard };
              UI.setFieldState(csr, 'valid');
              // FIX 1 : SHOW the CSR success message + HIDE its error (v1's toggleMessage success).
              if (csrSuccessEl) { showCardStatus(csrSuccessEl, true); } // chip -> --success confirmation ; provided text from the card's own data-order-form-card-status-provided (per-card copy, no hardcoded surface key)
              if (csrErrorEl) { UI.hideMessage(csrErrorEl); }
              // SECURED-DOMAIN CHIP (owner 2026-06-24) : reveal WHICH domain this CSR secures. `cn` is the parse
              // -normalised Common Name with the wildcard RE-APPLIED (applyWildcard) when the server flagged it,
              // so a wildcard CSR shows "*.example.com", not the bare base. textContent via UI.showMessage -- the
              // domain is data, never HTML. Cleared by resetPrimary on any re-edit / mode-switch / variant-switch.
              if (csrDomainEl) { UI.showMessage(csrDomainEl, CSR_DOMAIN_LABEL + cn); }
              // FIX 2 : keep the CSR-DERIVED CN in the (now-hidden, separate-card) AutoCSR FQDN field, DISABLED,
              // so the single-active CSR<->FQDN toggle invariant holds (the FQDN stays the inert derived twin while
              // the CSR is the active input) and resetPrimaryInput drops it cleanly on a mode/variant switch. The
              // standard-card redesign (owner 2026-06-21) removed the VISIBLE CN echo (the old "Securing :" line),
              // so this write is purely internal state now -- the cart Primary FQDN comes from
              // state.primaryInput.domain (writeThrough), never this field, and the AutoCSR card is hidden in CSR mode.
              if (fqdn) {
                fqdn.value = cn;
                fqdn.disabled = true;
                UI.setFieldState(fqdn, 'valid');
              }
              // FIX 3 : a validated primary UNLOCKS the DCV method (v1 : validationMethodSelect.disabled=false).
              unlockDcv();
              // A primary domain now exists : if the shopper had already picked EMAIL, load the
              // approver list (the EMAIL-first-then-validate order ; PR#17 #1).
              loadPrimaryApproversIfEmail(state);
              // owner 2026-06-22 : a validated primary REVEALS the gated rest of the form, marks the Validate
              // button validated (tick), and re-checks the SAN rows against the (possibly changed) primary so a
              // duplicate surfaces immediately (the submit gate already blocks it ; this makes it visible now).
              applyPrimaryGate(form, state);
              setButtonValidated(validateBtn, true);
              revalidateSanRows(state);
            } else {
              // The CSR is well-formed but its CN is NOT a valid domain OR violates the SKU's wildcard rule (e.g. a
              // wildcard CN on a single-site SKU). resetPrimary re-locked DCV + hid success. CENTRALISED copy : the
              // specific wildcard token when domErr, else the generic CN-invalid key -- never raw server text (owner 2026-07-01).
              resetPrimary(csr.id);
              UI.setFieldState(csr, 'error');
              var token = domErr ? copyOf(state.config, domErr) : copyOf(state.config, 'validation.domain.csrcn.invalid');
              if (csrSuccessEl) { UI.hideMessage(csrSuccessEl); } // an error owns the status slot : hide the prompt/confirmation chip
              if (csrErrorEl) { UI.showMessage(csrErrorEl, token); }
            }
          });
          // owner 2026-06-24 (PR #28 review R3 #2) : clear the in-flight flag only once the WHOLE chain (validate
          // + CN parse) settles -- not at the first hop -- so a button press in the parse-hop window cannot start a
          // second validate. The success, validate-fail, CN-invalid, and both epoch-bail arms all flow here.
          // owner 2026-06-24 (PR #28 review R5 #2) : the REJECT arm clears csrValidating but deliberately does NOT
          // restore the "Validating…" label. The asymmetry is intentional : a stranded csrValidating=true would
          // HARD-BREAK all future validation (the in-flight guard would block every later attempt), so clearing it
          // defensively earns its line ; a stranded label is COSMETIC only. CsrValidator.validate and
          // DomainNormalizer.parse each wrap their fetch in .catch and ALWAYS resolve (never reject), so this arm
          // is unreachable today -- a cosmetic label restore here would be speculative dead code. The happy-path
          // label restore lives in the validate onFulfilled arm (it runs on success AND on the epoch-bail return).
        }).then(function () { csrValidating = false; }, function () { csrValidating = false; });
      });
      // AUTO-VALIDATE ON BLUR (owner 2026-06-24) : when the box holds a COMPLETE-looking CSR (BEGIN+END anchored +
      // within size, via CsrValidator.quickFormatCheck) AND it is not the value we last attempted, trigger the SAME
      // path the button runs (by dispatching its click) -- so a pasted CSR auto-checks like the FQDN field, WITHOUT
      // an API call on partial / junk input or on every blur. The guards keep it bounded :
      //   - quickFormatCheck : never call the API for anything that is not a complete PEM (no spam on partial text).
      //   - value !== lastAttemptedCsr : skip a value we already sent (validated OR failed) -- an unchanged re-blur
      //     never re-hits the API ; an edit changes the value and re-arms it.
      //   - blurMovesToDiscard : skip when focus is moving to Clear / the Generate-CSR-Tool popup -- the value is
      //     about to be discarded/replaced, so do not spend a Sectigo quota call on it (PR #28 review R5 #1).
      // The mouse blur+click double-fire (this blur dispatches the click, then the real mouseup-click fires too) is
      // deduped by the csrValidating in-flight flag INSIDE the click handler -- NOT by disabling the button, which
      // would steal keyboard focus on a Tab out of the box (PR #28 review #1).
      csr.addEventListener('blur', function (ev) {
        if (blurMovesToDiscard(ev, ['csr-clear', 'csr-popup'])) { return; }
        if (state.csrValidator.quickFormatCheck(csr.value)
            && (csr.value || '').trim() !== lastAttemptedCsr) {
          validateBtn.dispatchEvent(new Event('click'));
        }
      });
    }
    if (fqdn) {
      fqdn.addEventListener('input', function () {
        if (fqdn.value && csr) {
          csr.disabled = true;
        } else if (csr) {
          csr.disabled = false;
        }
        // ANY edit in the FQDN field invalidates the current primary validation, SELF-CONTAINED
        // (same posture as the CSR handler above ; PR#17 #2). A validated CSR is dropped the
        // moment the shopper types in the FQDN, without relying on the CSR field being disabled.
        // A disabled field cannot fire 'input', so the unconditional reset is correct in every
        // case ; the next blur re-parses the FQDN if it still has a value.
        resetPrimary(fqdn.id);
        UI.setFieldState(fqdn, 'neutral');
        // FIX 1 : re-editing the FQDN clears its prior error (resetPrimary already reverted the status chip to its
        // neutral PROMPT), so the slot shows exactly the prompt -- not the error and not a stale confirmation.
        if (fqdnErrorEl) { UI.hideMessage(fqdnErrorEl); }
      });
      // owner 2026-06-22 : the FQDN validation is now a reusable function so BOTH the blur AND the new "Validate
      // Fully Qualified Domain Name (FQDN)" button run the SAME validation (parity with the CSR Validate button :
      // a click "sets the domain in place"). When invoked via the button it shows the transient "Validating…"
      // working state ; the blur path has no button to toggle (fqdnValidateBtn null-guarded throughout).
      // owner 2026-06-24 (PR #28 review R6 #1, pattern sweep) : the FQDN Validate button uses the SAME a11y model
      // as the CSR one -- an IN-FLIGHT FLAG (fqdnValidating) to dedup the blur+click double-fire, NOT the disabled
      // attribute. validateFqdnNow is bound to BOTH fqdn.blur AND the button click, so a keyboard Tab from the FQDN
      // box onto the button fires blur -> validateFqdnNow ; disabling the button mid focus-transition would steal
      // focus from the button being tabbed to (the exact A+ keyboard regression the CSR refactor removed in review
      // #1). The green validated tick (setButtonValidated) is the "done" signal ; the flag is the dedup. The flag's
      // safety depends on parse() always settling -- guaranteed by ApiClient's AbortController + .catch (review R6 #3).
      var fqdnValidating = false;
      function validateFqdnNow() {
        if (!fqdn.value) { return; }
        if (fqdnValidating) { return; }                                 // in-flight dedup (replaces the old disable)
        // WILDCARD ENTRY (operator decision 2026-10-26) : on a wildcard product a domain typed without its "*." is
        // prefixed HERE, written back into the box before the parse, so the customer sees exactly what will be ordered
        // and the A.5 symmetry check below then passes on the server's wildcard verdict (wildcardEntryFor : the rule,
        // and what it leaves alone). The box write is a programmatic value set, which fires no 'input' event, so the
        // reset-on-edit listener above does not run ; the blur path and the Validate button both come through here, so
        // a pre-filled link value (applyPrefill places, never validates) is prefixed the moment it is validated too.
        // Live state.facts, as the A.5 check reads them : absent facts (the window before /api/sku resolves) mean no
        // prefix, exactly as the symmetry check is deferred then ; the shopper re-validates once facts load.
        var wildcardEntry = wildcardEntryFor(fqdn.value, skuRequiresWildcard(state.facts));
        if (wildcardEntry !== fqdn.value) { fqdn.value = wildcardEntry; }
        // owner 2026-06-24 (PR #28 review R7 #1) : the FQDN card re-parses on EVERY blur (gated ONLY by the in-flight
        // flag above) -- it has NO lastAttempted value-dedup like the CSR card's `value !== lastAttemptedCsr`. This
        // asymmetry is DELIBERATE : ApiClient.parseDomain -> GET /api/domain/parse is an INTERNAL "PSL normalise/parse"
        // (see the parseDomain JSDoc), NOT a metered Sectigo DecodeCSR call like validateCsr -> POST /api/csr/validate.
        // There is no third-party quota to protect here, so an unchanged FQDN re-blur costs only a tiny internal
        // round-trip ; the value-level dedup stays CSR-only, where it guards real Sectigo quota. (Owner-decided
        // 2026-06-24 : document the difference rather than mirror the guard, since parse is not metered.)
        // The label swap targets the dedicated label span (buttonLabelEl) so it never wipes the validated-tick
        // icon span ; fqdnValidateBtn is null on the blur path (no button to toggle), so guard the lookup.
        var fqdnLabelEl = fqdnValidateBtn ? buttonLabelEl(fqdnValidateBtn) : null;
        var restoreLabel = fqdnLabelEl ? fqdnLabelEl.textContent : null;
        fqdnValidating = true;
        if (fqdnValidateBtn) { fqdnLabelEl.textContent = copyOf(state.config, 'ssl.validate.working'); } // working LABEL only -- button NOT disabled (a11y)
        // ADVERSARIAL REVIEW : capture the generation ; bail in the .then if a reset (mode/variant switch /
        // re-edit) landed while the parse was in flight, so a stale FQDN cannot re-validate into the cart path.
        var gen = primaryEpoch;
        // DomainNormalizer.parse resolves the ApiClient OUTCOME ({ok, data:{isValid, isWildcard,
        // normalized,...}}) or null ; read the parsed result off outcome.data.
        // owner 2026-06-24 (PR #28 review R8 #1, pattern sweep) : same sync-throw guard as the CSR leg -- a
        // synchronous throw from parse() would latch fqdnValidating true and hard-break all future validation.
        var fqdnParsePromise;
        try {
          fqdnParsePromise = state.domainNormalizer.parse(fqdn.value);
        } catch (fqdnSyncErr) {
          fqdnValidating = false;
          if (fqdnValidateBtn) { fqdnLabelEl.textContent = restoreLabel; }
          log.error('ORDER FORM : FQDN PARSE THREW SYNCHRONOUSLY - IN-FLIGHT FLAG CLEARED', fqdnSyncErr);
          return;
        }
        fqdnParsePromise.then(function (outcome) {
          if (fqdnValidateBtn) { fqdnLabelEl.textContent = restoreLabel; } // restore label (the button was never disabled)
          if (gen !== primaryEpoch) {
            return;
          }
          var ok = !!(outcome && outcome.ok && outcome.data);
          var apiIsValid = ok ? outcome.data.isValid : undefined;
          var apiIsWildcard = ok ? !!outcome.data.isWildcard : false;
          // A.5 : the wildcard-symmetry + validity verdict for THIS SKU (server PSL via /api/domain/parse),
          // evaluated immediately so the field errors on entry, not only at submit. domErr is non-null when
          // the (valid) domain violates the SKU's wildcard rule. Only computed once the domain itself parsed
          // valid (an invalid domain is already the !apiIsValid case below).
          var domErr = (ok && apiIsValid) ? primaryDomainErrorToken(apiIsValid, apiIsWildcard, state.facts) : null;
          if (ok && apiIsValid && !domErr) {
            // A.0 : carry the server verdict on primaryInput so the gate's A.1/A.3 can re-check it. The server
            // strips "*." from `normalized` ; re-apply it (applyWildcard) so a typed "*.example.com" keeps the
            // wildcard in the SUBMITTED Primary FQDN, same as the CSR path (owner 2026-06-24).
            // rawDomain : the EXACT typed value, for the calculated-approver endpoint ONLY (PR #34 review :
            // reconcile primary vs SAN -- both must send the customer's untransformed value, never the normalized one,
            // so the same domain entered as a primary or a SAN computes the SAME approver list). `domain` stays the
            // normalized + wildcard-applied canonical value the cart / gate use.
            state.primaryInput = { valid: true, kind: 'fqdn', fieldId: fqdn.id, domain: state.domainNormalizer.applyWildcard(outcome.data.normalized || fqdn.value, apiIsWildcard), rawDomain: fqdn.value, apiIsValid: true, apiIsWildcard: apiIsWildcard };
            UI.setFieldState(fqdn, 'valid');
            // FIX 1 : SHOW the FQDN success message + HIDE its error (v1's toggleMessage success).
            if (fqdnSuccessEl) { showCardStatus(fqdnSuccessEl, true); } // chip -> --success confirmation ; provided text from the slot's data-order-form-card-status-provided attr (JS-filled per the active method by activatePrimaryMethod -- SSL autocsr declares primary.method.autocsr.status.provided, CaaS domain declares primary.method.domain.status.provided ; no cross-surface leak)
            if (fqdnErrorEl) { UI.hideMessage(fqdnErrorEl); }
            // FIX 3 : a validated primary UNLOCKS the DCV method (v1 : validationMethodSelect.disabled=false).
            unlockDcv();
            // Mirror of the CSR path : a validated FQDN supplies the domain, so an EMAIL method
            // chosen before validation now gets its approver list loaded (PR#17 #1).
            loadPrimaryApproversIfEmail(state);
            // owner 2026-06-22 : reveal the gated rest of the form, tick the Validate FQDN button, and re-check
            // the SAN rows against the (possibly changed) primary so a duplicate surfaces immediately.
            applyPrimaryGate(form, state);
            setButtonValidated(fqdnValidateBtn, true);
            revalidateSanRows(state);
          } else {
            resetPrimary(fqdn.id);
            UI.setFieldState(fqdn, 'error');
            // SHOW the error region (resetPrimary hid the success + re-locked DCV). CENTRALISED copy : a wildcard
            // mismatch (domErr) surfaces the SPECIFIC token, else the generic invalid-domain key -- never the raw
            // server message (owner 2026-07-01 : the form surfaces curated copy, not uncontrolled server text).
            var token = domErr ? copyOf(state.config, domErr) : copyOf(state.config, 'validation.domain.primary.invalid');
            if (fqdnSuccessEl) { UI.hideMessage(fqdnSuccessEl); } // an error owns the status slot : hide the prompt/confirmation chip
            if (fqdnErrorEl) { UI.showMessage(fqdnErrorEl, token); }
          }
          // owner 2026-06-24 (PR #28 review R6 #1) : clear the in-flight flag once parse settles (success, error, AND
          // the epoch-bail return all flow here). The reject arm clears it too -- same deliberate asymmetry as the
          // CSR settle (review R5 #2) : a stranded flag would hard-break all future validation, while parse() never
          // rejects (.catch), so this arm is unreachable today ; the label restore lives in the fulfillment arm above.
        }).then(function () { fqdnValidating = false; }, function () { fqdnValidating = false; });
      }
      // RESET-ON-EDIT is already handled by the fqdn 'input' listener registered above (it runs resetPrimary(fqdn.id)
      // + UI.setFieldState(fqdn, 'neutral') on every edit, un-validating the primary so the next blur re-parses through
      // /api/domain/parse -- which rejects e-mails via its @ guard). So the form already APPLIES the parse verdict for
      // an edited-to-e-mail FQDN ; no extra handler is needed here (a duplicate one was added then removed, PR #38 review).
      // owner 2026-06-24 (PR #28 review R5 #1, pattern sweep) : the FQDN blur carries the SAME discard guard as the
      // CSR blur -- clicking the FQDN card's Clear button must not burn a /api/domain/parse call on a value about
      // to be wiped. (The FQDN card has no Generate-tool popup, so only fqdn-clear.) The explicit Validate FQDN
      // button still always validates -- a direct click carries no relatedTarget, so blurMovesToDiscard is N/A there.
      fqdn.addEventListener('blur', function (ev) {
        if (blurMovesToDiscard(ev, ['fqdn-clear'])) { return; }
        validateFqdnNow();
      });
      if (fqdnValidateBtn) { fqdnValidateBtn.addEventListener('click', validateFqdnNow); }
    }

    // SIMPLE PRIMARY (email) -- the CONSISTENCY fix (owner 2026-06-28 : "every form is the SAME form" ;
    // every family must reveal the rest of the form after validating its primary, EXACTLY like SSL). The future
    // S/MIME e-mail (inputType 'email') validates LOCALLY (no middleware round-trip), so a valid value OPENS the
    // progressive-disclosure gate and an empty/invalid one re-hides it. Validity uses the SAME primitives the
    // field tick + submit gate use (isValidEmail / the byte-cap rule), so the reveal matches the field's own
    // green-tick state. THE ACME PRIMARY NO LONGER TAKES THIS PATH (CaaS Subscription Register go-live,
    // 2026-08-29) : local validity alone cannot answer whether a subscription may be extended, so the acme
    // primary validates through the middleware subscription check (checkAcmeNow below), joining the CSR / FQDN
    // middleware-verdict model -- which is itself the SAME-form principle applied : every primary now validates
    // through an explicit button-triggered middleware round-trip before the gate opens.
    function simplePrimaryValid(el) {
      var val = (el.value || '').trim();
      if (!val) { return false; }                                       // an untouched primary keeps the gate closed
      var kind = el.getAttribute('data-order-form-validate');
      // The length cap comes from config.validation (the single source, owner 2026-06-29), resolved by
      // the field's validator id -- the SAME values the field tick + submit gate use.
      var validation = state.config && state.config.validation;
      var fieldId = PROPERTY_TO_VALIDATOR_FIELD[el.getAttribute('data-order-form-property')];
      var rule = FIELD_RULES_BY_ID[fieldId] || null;
      var maxLength = maxLengthOf(validation, fieldId);
      var lengthKey = rule ? rule.lengthKey : undefined;
      if (kind === 'email') { return isValidEmail(val) && fieldContentError(val, maxLength, lengthKey) === null; }
      // Only the 'email' primary reaches simplePrimaryValid today (the acme primary routes through checkAcmeNow) ;
      // an unknown validate kind fails closed (no speculative generic path -- removed 2026-06-29).
      return false;
    }
    // Drive the gate from a simple primary : a VALID value sets state.primaryInput (kind = the inputType, NEVER
    // 'csr'/'fqdn', so writeThrough's domain-only Primary-FQDN write skips it -- the value reaches the cart via its
    // OWN data-order-form-property direct-copy + its field-state gate) and OPENS the gate ; otherwise resetPrimary
    // closes it. Exposed (closure) for the input/blur listeners AND revalidatePrimary's variant-switch auto re-check.
    function evaluateSimplePrimary(el, kind) {
      if (!el) { return; }
      if (simplePrimaryValid(el)) {
        state.primaryInput = { valid: true, kind: kind, fieldId: el.id, domain: '' };
        applyPrimaryGate(form, state);
      } else {
        resetPrimary(el.id);                                            // empty/invalid -> primary un-validated + gate re-hidden
      }
    }

    // ================================================================================================
    // ACME PRIMARY : THE SUBSCRIPTION CHECK (CaaS Subscription Register go-live, 2026-08-29 ;
    // middleware-01 docs/CAAS-SUBSCRIPTION-REGISTER-DESIGN.md section 9). The form is the ONLY gate on
    // this path, so its posture is FAIL CLOSED everywhere : the gate opens ONLY on the middleware's
    // eligible:true for the EXACT current value, and every edit / reset drops the verdict
    // (resetPrimary clears state.subscriptionCheck). The wiring mirrors validateFqdnNow -- an in-flight
    // flag deduping the blur+click double-fire (never a disabled attribute : the CSR a11y model), the
    // working-label swap on the dedicated label span, the primaryEpoch stale-callback guard, and the
    // sync-throw guard -- so the acme primary behaves like every other middleware-validated primary.
    // ================================================================================================
    var acmeChecking = false;
    // The last value a check was ATTEMPTED for (the CSR lastAttemptedCsr idiom) : the blur auto-check
    // dedups on it so tabbing around an unchanged value never re-spends the metered per-visitor check
    // budget (60/hour) ; the Check button is the deliberate retry path (its guard is keyed on the
    // primary being already VALID, so a failed check's unchanged value still re-checks on click).
    var lastAttemptedAcme = null;
    function checkAcmeNow() {
      if (!textPrimary || !textPrimary.value) { return; }
      if (acmeChecking) { return; }                                     // in-flight dedup (blur + click double-fire)
      var acmeValue = (textPrimary.value || '').trim();
      // Already-eligible dedup (the CSR click-guard shape) : re-checking an unchanged value whose verdict
      // already opened the gate is pure budget waste. An unchanged value with a FAILED verdict still
      // re-checks (the deliberate retry path).
      if (state.primaryInput && state.primaryInput.valid && state.primaryInput.kind === 'text' && acmeValue === lastAttemptedAcme) { return; }
      // CHEAP LOCAL RULE FIRST (the quickFormatCheck posture) : charset + length from config.validation
      // (the single source), so obvious junk never fires a metered check call. A failed rule resets the
      // primary (gate stays closed) and shows the existing invalid-charset copy.
      var acmeValidation = state.config && state.config.validation;
      if (!isValidAcmeAccountId(acmeValue, acmeValidation && acmeValidation['caas-acme-account'])) {
        resetPrimary(textPrimary.id);
        UI.setFieldState(textPrimary, 'error');
        if (textErrorEl) { UI.showMessage(textErrorEl, copyOf(state.config, 'validation.caas.acme.invalid')); }
        return;
      }
      // Record AFTER the local gate (the lastAttemptedCsr rule) : junk that never reached the API is
      // never recorded as attempted.
      lastAttemptedAcme = acmeValue;
      // THE DESCRIPTOR (fail-closed, section 9) : the endpoint comes ONLY from /api/sku's root
      // subscriptionCheck -- never a hardcoded path. Null facts (fetch failed / not yet loaded) or a
      // descriptor-less body (a stale pre-deploy config, a middleware rollback) resolve the
      // SUBSCRIPTION_NO_ENDPOINT failure outcome inside SubscriptionChecker.check, which the non-200
      // arm below renders as try-again : the check is REQUIRED (fields.acmeAccount === 'required'), so
      // a missing descriptor can never skip it. Warn loudly so the misconfiguration is diagnosable.
      var acmeDesc = state.facts && state.facts.subscriptionCheck;
      var acmeEndpoint = acmeDesc && typeof acmeDesc.endpoint === 'string' && acmeDesc.endpoint ? acmeDesc.endpoint : null;
      if (!acmeEndpoint) {
        log.warn('ORDER FORM : fields.acmeAccount IS required BUT /api/sku CARRIES NO subscriptionCheck DESCRIPTOR - CHECK FAILS CLOSED', { sku: state.facts && state.facts.sku });
      }
      acmeChecking = true;
      // Working LABEL only -- the button is NOT disabled (the CSR a11y model : disabling mid focus-transition
      // steals keyboard focus). The swap targets the dedicated label span so the validated tick survives.
      var acmeLabelEl = acmeCheckBtn ? buttonLabelEl(acmeCheckBtn) : null;
      var acmeRestoreLabel = acmeLabelEl ? acmeLabelEl.textContent : null;
      if (acmeCheckBtn) { acmeLabelEl.textContent = copyOf(state.config, 'ssl.validate.working'); }
      // ADVERSARIAL REVIEW : capture the generation ; bail in the .then if a reset (mode/variant switch /
      // re-edit) landed while the check was in flight, so a stale verdict cannot re-validate into the cart path.
      var gen = primaryEpoch;
      // Same sync-throw guard as the CSR / FQDN legs : a synchronous throw would latch acmeChecking true and
      // hard-break all future checks.
      var acmeCheckPromise;
      try {
        acmeCheckPromise = state.subscriptionChecker.check(acmeEndpoint, acmeValue, state.facts && state.facts.sku);
      } catch (acmeSyncErr) {
        acmeChecking = false;
        if (acmeCheckBtn) { acmeLabelEl.textContent = acmeRestoreLabel; }
        log.error('ORDER FORM : SUBSCRIPTION CHECK THREW SYNCHRONOUSLY - IN-FLIGHT FLAG CLEARED', acmeSyncErr);
        return;
      }
      acmeCheckPromise.then(function (outcome) {
        if (acmeCheckBtn) { acmeLabelEl.textContent = acmeRestoreLabel; }
        if (gen !== primaryEpoch) {
          return;
        }
        if (outcome && outcome.ok && outcome.data && outcome.data.eligible === true) {
          // ELIGIBLE : hold the verdict for THIS exact value (the gate's acmeAccount validator re-checks the
          // equality at submit -- the no-event-edit belt), validate the primary (kind 'text', NEVER 'csr'/'fqdn' :
          // the value reaches the cart via its OWN direct-copy property, writeThrough writes no Primary FQDN),
          // and open the progressive-disclosure gate. The eligible-detail chip names what the check confirmed
          // (productName + expiry -- the middleware's decided disclosure, section 5.3), rendered LABELED via
          // caasCheckDetailText (owner-approved 2026-08-29, superseding the first draft's bare values).
          state.subscriptionCheck = { value: acmeValue, eligible: true };
          state.primaryInput = { valid: true, kind: 'text', fieldId: textPrimary.id, domain: '' };
          UI.setFieldState(textPrimary, 'valid');
          if (textErrorEl) { UI.hideMessage(textErrorEl); }
          if (textDetailEl) {
            var eligibleDetail = caasCheckDetailText(state.config, outcome.data);
            var eligibleMsg = copyOf(state.config, 'caas.check.eligible');
            UI.showMessage(textDetailEl, eligibleDetail ? eligibleMsg + ' - ' + eligibleDetail : eligibleMsg);
          }
          applyPrimaryGate(form, state);
          setButtonValidated(acmeCheckBtn, true);
          return;
        }
        // REFUSED, or the transport failed : the primary stays (or returns) un-validated -- resetPrimary drops
        // any prior verdict, re-hides the gate, and un-ticks the button -- and the error region renders ONE
        // message resolved entirely from config.copy (caasCheckFailureText : the reason's message + the product
        // being refused, the 400 check-the-value, or the try-again for 5xx / 429 / a bare 404 / network /
        // the fail-closed no-descriptor outcome). Curated copy only, never raw server text (owner 2026-07-01).
        resetPrimary(textPrimary.id);
        UI.setFieldState(textPrimary, 'error');
        if (textErrorEl) { UI.showMessage(textErrorEl, caasCheckFailureText(state.config, outcome, state.facts)); }
      }).then(function () { acmeChecking = false; }, function () { acmeChecking = false; });
    }
    if (textPrimary) {
      // RESET-ON-EDIT (the fqdn 'input' listener shape) : every edit drops the verdict + re-hides the gate
      // (resetPrimary), and clears a prior attempt's error so the field reads neutral while typing. The live
      // evaluate (wireOneFieldCheck, kind acme) paints only the error icon for a malformed value and NEVER the
      // green tick : the tick means CHECKED, set by the eligible arm of checkAcmeNow alone (operator ruling
      // 2026-10-17, the domain box model) ; only the check verdict opens the gate.
      textPrimary.addEventListener('input', function () {
        resetPrimary(textPrimary.id);
        if (textErrorEl) { UI.hideMessage(textErrorEl); }
      });
      // Blur auto-checks a CHANGED value (the CSR blur model, value-deduped on lastAttemptedAcme to guard the
      // metered budget) ; a blur landing on the slot's own Clear button skips (blurMovesToDiscard : the value
      // is about to vanish). Tabbing onto the Check button fires blur -> checkAcmeNow ; the click then dedups
      // on the in-flight flag, exactly the CSR/FQDN double-fire model.
      textPrimary.addEventListener('blur', function (ev) {
        if (blurMovesToDiscard(ev, ['acme-clear'])) { return; }
        if ((textPrimary.value || '').trim() === lastAttemptedAcme) { return; }
        checkAcmeNow();
      });
    }
    if (acmeCheckBtn) { acmeCheckBtn.addEventListener('click', checkAcmeNow); }
    if (emailPrimary) {
      emailPrimary.addEventListener('input', function () { evaluateSimplePrimary(emailPrimary, 'email'); });
      emailPrimary.addEventListener('blur', function () { evaluateSimplePrimary(emailPrimary, 'email'); });
    }

    // owner 2026-06-22 : the Clear buttons wipe their field + reset the primary (which re-locks DCV + RE-HIDES
    // the gated rest of the form via resetPrimary's applyPrimaryGate). A clear is just a programmatic edit, so
    // it routes through the SAME resetPrimary path as a manual edit. Each is null-guarded. Each slot's wipe is its
    // own function so the form-level Clear Form button (clearForm, owner 2026-10-04) runs the IDENTICAL wipe on
    // every slot at once ; only the per-card buttons move focus into their own field.
    // CSR Clear routes the wipe through the SAME `input` path a manual delete-to-empty takes (owner 2026-10-04 : the
    // ACME Clear's dispatch idiom, below). The csr input listener does every step the clear used to spell out by hand
    // (re-enable the FQDN, resetPrimary, neutral icon, hide the error region) AND wireAutoResize listens on that same
    // event, so the box shrinks back to its empty height : before this, a cleared CSR box kept the height it had grown
    // to for the pasted CSR (up to the cap) until the next keystroke, on the per-card Clear Options and on Clear Form.
    function clearCsrField() {
      csr.value = '';
      // owner 2026-06-24 : forget the last-attempted CSR on a Clear, so re-pasting the SAME CSR afterwards still
      // auto-validates on blur (otherwise the unchanged-value guard would skip it).
      lastAttemptedCsr = null;
      csr.dispatchEvent(new Event('input'));
    }
    function clearFqdnField() {
      fqdn.value = '';
      if (csr) { csr.disabled = false; }
      resetPrimary(fqdn.id);
      UI.setFieldState(fqdn, 'neutral');
      if (fqdnErrorEl) { UI.hideMessage(fqdnErrorEl); }
    }
    // ACME Clear (CaaS Subscription Register go-live, 2026-08-29) : mirrors the FQDN Clear above. The synthetic
    // 'input' event routes the wipe through the SAME reset path a manual edit takes (the input listener runs
    // resetPrimary + clears the error region) AND refreshes the wireOneFieldCheck tick + the wireCardStatus chip,
    // both input-event-driven for this leaf (the S/MIME derived-e-mail fill uses the same dispatch idiom).
    // lastAttemptedAcme is forgotten so re-typing the SAME id afterwards still auto-checks on blur (the CSR
    // Clear's lastAttemptedCsr rule).
    function clearAcmeField() {
      textPrimary.value = '';
      lastAttemptedAcme = null;
      textPrimary.dispatchEvent(new Event('input'));
    }
    if (csrClearBtn && csr) {
      csrClearBtn.addEventListener('click', function () { clearCsrField(); csr.focus(); });
    }
    if (fqdnClearBtn && fqdn) {
      fqdnClearBtn.addEventListener('click', function () { clearFqdnField(); fqdn.focus(); });
    }
    if (acmeClearBtn && textPrimary) {
      acmeClearBtn.addEventListener('click', function () { clearAcmeField(); textPrimary.focus(); });
    }
    // Exposed for the form-level Clear Form button (clearForm, owner 2026-10-04) : wipe EVERY primary slot through the
    // per-slot paths above (whichever slots the markup renders ; wiping a hidden slot is harmless), then the simple
    // e-mail primary through its own `input` path (evaluateSimplePrimary re-closes the gate on an empty value). Each
    // wipe anchors the (invalid) primary to ITS field, so the chain would end anchored on the LAST slot wiped, which
    // may be a hidden one ; the final resetPrimary puts the anchor back on the default field, exactly as
    // state.resetPrimaryInput does. No focus here : clearForm decides where the customer lands.
    state.clearPrimaryInputs = function () {
      if (csr) { clearCsrField(); }
      if (fqdn) { clearFqdnField(); }
      if (textPrimary) { clearAcmeField(); }
      if (emailPrimary) {
        emailPrimary.value = '';
        emailPrimary.dispatchEvent(new Event('input'));
      }
      resetPrimary(defaultPrimaryFieldId);
    };
    // PROGRESSIVE DISCLOSURE : the primary is un-validated on initial wire-up, so the gated rest of the form
    // ships + stays hidden until the shopper validates a CSR / domain (owner 2026-06-22). Idempotent.
    applyPrimaryGate(form, state);
  }

  // The shared DCV chip wording (the EMAIL confirmation, the "approver must be selected" prompt, the "validation
  // method must be selected" prompt) is now customer copy in order-form-v2-copy.liquid (dcv.email.selected /
  // dcv.approver.required / dcv.method.required), delivered to the wire functions via config.copy
  // (content-centralization 2026-06-27). No DCV chip wording lives in this file anymore.
  //
  // copyOf : read a customer string from config.copy by key. Returns '' (a safe, non-rendering empty string, NOT a
  // hardcoded literal) when the map or key is absent (e.g. a CaaS form whose config carries no copy for a key it
  // never shows). The single resolver every wire site uses so the wording stays in order-form-v2-copy alone.
  function copyOf(config, key) {
    // DEV REGRESSION GUARD (PR #33 review #2) : when the copy map IS present but the KEY is absent, that is a DROPPED
    // EMIT in order-form-v2-config.liquid -- the exact approver.placeholder class that shipped a blank string to prod
    // once already. The build-time emit-superset contract test catches it for the KNOWN consumption patterns, but it
    // harvests via regex ; a key read through a NEW indirection the harvester does not know about would be invisible
    // to it. This runtime warn surfaces ANY such miss in dev / preview (in prod every key is emitted, so it never
    // fires). MAP-ABSENT is the legit degenerate case (a unit test, or a form that never shows the key) and does NOT
    // warn ; an emitted-but-EMPTY value ('') is intentional and also does not warn (only an ABSENT key warns).
    // `key &&` : a FALSY key ('') is a deliberate "no copy wanted" sentinel (e.g. showCardStatus is passed
    // MODE_STATUS_KEY[mode] || '' when no issuance method is chosen -> no chip), NOT a dropped emit -- it resolves
    // to '' silently. Only a NON-EMPTY key absent from a PRESENT map is the real regression signal.
    if (config && config.copy && key && config.copy[key] === undefined) {
      log.warn('ORDER FORM : COPY KEY MISSING FROM config.copy - order-form-v2-config.liquid MUST EMIT IT', { key: key });
    }
    return (config && config.copy && config.copy[key]) || '';
  }

  // Toggle the email / dns / http / https detail panels inside scopeEl to match the chosen method. SHARED by the
  // primary DCV section and each SAN row (pattern-sweep : both surfaces must reveal the SAME per-method
  // confirmation). A panel shows only when its method is active : EMAIL's panel holds the approver picker +
  // the JS-driven -email-success shell ; DNS/HTTP/HTTPS panels carry static status-chip text the reveal itself
  // shows (HTTP and HTTPS are SEPARATE panels as of the 2026-06-24 audit fix, see applyDcvPanels below).
  // `which` is the panel's data-order-form-dcv-details key ; the method comparison references the DCV_METHODS
  // constants (not string literals) so a future method-value rename cannot silently stop matching (PR#17 r9 #4).
  // PER-SELECT VALIDATION TICK (owner 2026-06-22) : a dropdown shows the SAME success icon the primary domain
  // shows, once a value is chosen -- so every select reads consistently. Value-based + idempotent : a non-empty
  // selection -> 'valid' tick, the placeholder ('') -> 'neutral'. Safe to call after ANY operation that changes
  // the select's value (the shopper's change, a buildMethodSelect / populateApproverSelect rebuild, a lock/reset).
  // Null-guarded ; a select without the icon markup is a no-op (UI.setFieldState finds no .icon-valid and returns).
  function applySelectTick(selectEl) {
    if (selectEl) {
      var hasValue = !!selectEl.value;
      UI.setFieldState(selectEl, hasValue ? 'valid' : 'neutral');
      applySelectChevron(selectEl, hasValue);
    }
  }

  // owner 2026-06-22 ("keep icon, hide chevron on valid") : a VALIDATED dropdown HIDES its chevron and its tick
  // moves to the field's RIGHT edge -- the SAME spot as a text field's tick -- so every validation tick lines up
  // (the chevron otherwise pushes a select's tick inward to clear it). An UNCHOSEN dropdown keeps its chevron.
  // SHARED by applySelectTick (the issuance / dcv-method / approver / SAN selects) AND wireOneFieldCheck (the
  // country select) so EVERY validated select behaves IDENTICALLY (pattern-sweep). The chevron is hidden via
  // INLINE display (the same mechanism the field icons use, so order-form-v2.css stays free of display:none -- the
  // inline-only-hide guardrail) ; a marker class on the theme `.select` wrapper drives the CSS that shifts the
  // tick to the edge. Scoped to THIS select's wrapper ; a bare select OR a non-select field (input) just no-ops
  // (closest('.select') is null), so it is safe to call from the shared direct-input path too.
  function applySelectChevron(selectEl, hasValue) {
    var wrapper = (selectEl && selectEl.closest) ? selectEl.closest('.select') : null;
    if (!wrapper) {
      return;
    }
    var caret = wrapper.querySelector('.svg-wrapper');
    if (caret) {
      caret.style.display = hasValue ? 'none' : '';
    }
    wrapper.classList.toggle('order-form-select--validated', hasValue);
  }

  // PROGRESSIVE DISCLOSURE (owner 2026-06-22) : reveal/hide the gated rest of the form (the
  // [data-order-form-post-primary] wrapper the assembler ships hidden) by whether the PRIMARY is validated.
  // Shown ONLY when state.primaryInput.valid -- so a shopper must provide + validate a CSR or a domain before
  // the Validation Method / SANs / End User / Purchase appear (which makes the "Validate" buttons unmissable),
  // and any reset re-hides it. Null-guarded : a custom assembler without the wrapper is a no-op. The inner
  // cards keep their own SAN / org-address visibility ; that only takes visible effect once the wrapper opens.
  /**
   * PROGRESSIVE-DISCLOSURE GATE : reveal everything past the primary (Validation Method / SANs / End User /
   * org / address / Add-to-Cart, the [data-order-form-post-primary] wrapper) ONLY once the primary input has
   * VALIDATED, and re-hide it on any reset. Owner design 2026-06-22 (the per-step "Validate" buttons drive the
   * flow ; the rest stays hidden so the shopper cannot skip ahead).
   *
   * MIDDLEWARE-DEPENDENCY RATIONALE -- documented after the PR #24 r5 review flagged this as "availability
   * coupling" (owner-confirmed 2026-06-23 : NOT a defect, NOT a loss of function, NO change wanted). The only
   * ways `state.primaryInput.valid` becomes true are the CSR path (ApiClient.validateCsr -> /api/csr/validate to
   * DECODE the CSR + extract the CN, then domainNormalizer.parse -> /api/domain/parse) and the FQDN path
   * (domainNormalizer.parse -> /api/domain/parse). Both REQUIRE the middleware, because a CSR cannot be decoded
   * and a domain cannot be PSL-confirmed in the browser -- that is the middleware's (Sectigo's) job. KEY POINT :
   * this is NOT a NEW dependency that progressive disclosure introduced. The primary ALWAYS had to be validated
   * via the middleware before an order could submit ; the gate only changes WHAT IS VISIBLE (it hides the later
   * cards until the primary validates) -- it does not change what is REQUIRED. So when the middleware is down the
   * order cannot be placed in ANY layout (no validated primary -> the submit gate blocks) ; the only difference
   * is the new layout stops the shopper EARLIER, on fields they could not have used anyway, instead of letting
   * them fill the whole form and fail at Add-to-Cart. An SSL Certificate order inherently cannot be fulfilled
   * without the middleware, so blocking checkout while it is down is correct, not a bug. The FormValidator
   * cart-authority is unaffected. DEPLOYMENT NOTE (the one real action item, not a code issue) : the
   * `middleware_api_base` setting MUST be configured before launch -- a blank origin fail-closes the whole form
   * (no validation -> the gate never opens), which is true of any layout.
   * @param {Element} form @param {Object} state
   */
  // (GATE_OPEN_ANNOUNCE_TEXT removed 2026-06-27 : the gate-reveal announce is now customer copy gate.open.announce,
  // read via copyOf(state.config, 'gate.open.announce') in applyPrimaryGate.)

  /**
   * Smooth-scroll a freshly-REVEALED element into view to guide the customer to the next step, WITHOUT moving
   * keyboard focus. The owner chose this over a focus jump (extends the PR #24 r7 #1 announce-only decision,
   * owner 2026-06-25) : moving focus on a reveal would violate WCAG 3.2.2 (On Input) and disorient screen-reader
   * users, whereas a viewport nudge guides the eye and leaves the keyboard model untouched. Guards :
   *   - prefers-reduced-motion : honour the OS "reduce motion" setting (instant jump, no animation) ;
   *   - feature-detect scrollIntoView (absent / no-op under jsdom and very old browsers) -> silent no-op ;
   *   - block:'nearest' : the GENTLEST alignment (minimal scroll ; never yanks the just-validated field fully
   *     off-screen).
   * Callers fire this on a REVEAL, never on a plain re-render. The gate fires ONCE on its hidden->shown edge. The
   * primary DCV method <select> fires on EACH method swap -- which is itself a reveal (a DIFFERENT panel transitions
   * hidden->shown), so re-scrolling per swap is intentional (PR #30 review), not a missing edge-guard.
   * @param {Element} el the just-revealed element
   */
  function scrollRevealIntoView(el) {
    if (!el || typeof el.scrollIntoView !== 'function') {
      return;
    }
    var reduce = !!(root && typeof root.matchMedia === 'function' &&
      root.matchMedia('(prefers-reduced-motion: reduce)').matches);
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
  }

  function applyPrimaryGate(form, state) {
    var gate = form.querySelector('[data-order-form-post-primary]');
    if (!gate) {
      return;
    }
    var show = !!(state.primaryInput && state.primaryInput.valid);
    gate.style.display = show ? '' : 'none';
    // a11y (PR #24 r7 #1, owner 2026-06-23) : ANNOUNCE the reveal. The gate un-hides a large block but moves no
    // focus -- and on the Enter path focus has dropped to <body> -- so a keyboard / screen-reader user gets no cue
    // the rest of the form just appeared. Write the announcement to the visually-hidden role="status" (POLITE)
    // region ONLY on the hidden->shown TRANSITION (state.gateShown edge-trigger), so it fires ONCE per reveal --
    // not on every validate, and never again while the gate stays open (a re-validate / revalidateSanRows must not
    // re-announce). Polite + visually-hidden, so it signals assistive tech without moving focus or cluttering the
    // UI for sighted/mouse users (the option the owner chose over a focus jump). Null-guarded.
    var wasShown = !!state.gateShown;
    state.gateShown = show;
    // On the hidden->shown TRANSITION only (edge-trigger) : announce to assistive tech AND nudge the newly
    // -revealed block into view (owner 2026-06-25). Both are no-focus-move cues -- the announce for screen
    // readers, the scroll for sighted users -- so neither steals the keyboard model. Fires ONCE per reveal
    // (not on a re-validate / revalidateSanRows while the gate stays open).
    if (show && !wasShown) {
      if (state.gateAnnounceEl) {
        UI.announce(state.gateAnnounceEl, copyOf(state.config, 'gate.open.announce'));
      }
      scrollRevealIntoView(gate);
    }
  }

  // The Validate buttons (CSR / FQDN) show a "validated" state -- a green tick -- once their validation succeeds,
  // and revert on any reset (owner 2026-06-22 : "neither does the validate csr button [have a tick]" ; then : the
  // tick "does not look like all the other checkmarks ... or in the same position" + the button size/border broke
  // because the first attempt used a `.button::after` that collided with the theme's `.button:after` BORDER
  // pseudo-element). The tick is now the SAME green circle-check SVG the fields use, a CHILD
  // <span class="order-form-action__check"> of the button (NO pseudo-element), hidden in markup via inline
  // style="display:none" and shown here by toggling its inline display -- the SAME inline-display mechanism the
  // field icons (icon-valid / icon-error) use, so assets/order-form-v2.css carries no display:none. assets
  // /order-form-v2.css pins it to the button's RIGHT edge (owner 2026-06-22 : "on the right not the left"), the same
  // right-edge spot as the field icons. The .order-form-action--validated class is still toggled as the semantic
  // state hook. Null-guarded throughout.
  function setButtonValidated(btn, validated) {
    if (btn && btn.classList) {
      if (validated) { btn.classList.add('order-form-action--validated'); }
      else { btn.classList.remove('order-form-action--validated'); }
      var check = btn.querySelector('.order-form-action__check');
      if (check) { check.style.display = validated ? 'inline-flex' : 'none'; }
    }
  }

  // The Validate buttons swap their label to a transient "VALIDATING..." while the async check runs. The label
  // lives in a dedicated <span class="order-form-action__label"> child so the swap targets THAT span's textContent
  // and never wipes the sibling green-check icon span -- writing button.textContent would destroy ALL children
  // (the icon span included), so the validated tick would vanish after the first validation. Falls back to the
  // button itself when no label span exists (defensive : a custom assembler / legacy markup without the wrapper,
  // which also has no icon span to lose). Null-guarded by the callers.
  function buttonLabelEl(btn) {
    return btn.querySelector('.order-form-action__label') || btn;
  }

  // ================================================================================================
  // THE VALIDATION ID CHECK (operator decision 2026-10-18) : the Check button on the Validation ID card, the
  // FOURTH button-triggered middleware check (CSR validate, FQDN validate, ACME check are the precedents).
  // POSTs { validationId, sku } to the endpoint /api/sku names in its root validationIdCheck descriptor, holds
  // the valid:true verdict for the EXACT trimmed value in state.validationIdCheck, clears it on every edit (the
  // 'input' listener, which Clear Form also fires) and on every variant switch (state.resetValidationIdCheck,
  // called SYNCHRONOUSLY at the start of refreshForVariant beside the primary epoch bump, so a check in flight
  // across the switch bails : a verdict is per SKU), and lets the gate's ovAnchor validator refuse any
  // typed value without a current verdict. NOT a primary : it never touches state.primaryInput, applyPrimaryGate
  // or primaryEpoch ; it keeps its own in-flight flag and epoch. CLICK ONLY -- no blur auto-check : the operator
  // asked for "a button to click to validate it", and the per-visitor ceiling is ten an hour, so a tab-through
  // must never spend it. The checkAcmeNow order otherwise : blank guard, in-flight dedup, already-valid dedup,
  // the cheap local rule FIRST (the same cap + digits rule the gate applies, so junk never spends the budget),
  // the descriptor read with a loud warn, the working-label swap, the sync-throw guard, the stale-epoch bail.
  // ================================================================================================
  function wireValidationIdCheck(form, state) {
    var input = form.querySelector('[data-order-form-property="ov-anchor-order"]');
    if (!input) { return; }
    var checkBtn = form.querySelector('[data-order-form-action="ov-anchor-check"]');
    var errorEl = form.querySelector('[id="' + input.id + '-error"]');
    var detailEl = form.querySelector('[id="' + input.id + '-detail"]');
    var checking = false;
    var epoch = 0;
    function clearVerdict() {
      epoch += 1;
      state.validationIdCheck = null;
      if (detailEl) { UI.hideMessage(detailEl); }
      setButtonValidated(checkBtn, false);
    }
    function checkValidationIdNow() {
      if (!input.value) { return; }
      if (checking) { return; }
      var value = (input.value || '').trim();
      // Already-valid dedup : re-checking an unchanged value whose verdict is held is pure budget waste ; an
      // unchanged value with a FAILED verdict still re-checks (the deliberate retry path).
      if (state.validationIdCheck && state.validationIdCheck.valid === true && state.validationIdCheck.value === value) { return; }
      // CHEAP LOCAL RULE FIRST : the byte cap and the digits pattern from config.validation (the single source,
      // the same two rules the gate's ovAnchor validator applies, length first), so obvious junk never fires a
      // metered call. A failed rule drops any verdict and shows the existing gate copy.
      var validation = state.config && state.config.validation;
      var rule = validation && validation['ov-anchor-order'];
      var localKey = fieldContentError(value, maxLengthOf(validation, 'ov-anchor-order'), (FIELD_RULES_BY_ID['ov-anchor-order'] || {}).lengthKey);
      if (!localKey) {
        var re = anchoredRegExp(rule && rule.pattern);
        if (!re || !re.test(value)) { localKey = 'validation.ovanchor.invalid'; }
      }
      if (localKey) {
        clearVerdict();
        UI.setFieldState(input, 'error');
        if (errorEl) { UI.showMessage(errorEl, copyOf(state.config, localKey)); }
        return;
      }
      // THE DESCRIPTOR (fail-closed) : the endpoint comes ONLY from /api/sku's root validationIdCheck -- never a
      // hardcoded path. A descriptor-less body resolves the VALIDATION_ID_NO_ENDPOINT failure outcome inside
      // ValidationIdChecker.check, rendered below as try-again. Warn loudly so the misconfiguration is diagnosable.
      var desc = state.facts && state.facts.validationIdCheck;
      var endpoint = desc && typeof desc.endpoint === 'string' && desc.endpoint ? desc.endpoint : null;
      if (!endpoint) {
        log.warn('ORDER FORM : THE VALIDATION ID CARD IS SHOWN BUT /api/sku CARRIES NO validationIdCheck DESCRIPTOR - CHECK FAILS CLOSED', { sku: state.facts && state.facts.sku });
      }
      checking = true;
      var labelEl = checkBtn ? buttonLabelEl(checkBtn) : null;
      var restoreLabel = labelEl ? labelEl.textContent : null;
      if (labelEl) { labelEl.textContent = copyOf(state.config, 'ssl.validate.working'); }
      var gen = epoch;
      var promise;
      try {
        promise = state.validationIdChecker.check(endpoint, value, state.facts && state.facts.sku);
      } catch (syncErr) {
        checking = false;
        if (labelEl) { labelEl.textContent = restoreLabel; }
        log.error('ORDER FORM : VALIDATION ID CHECK THREW SYNCHRONOUSLY - IN-FLIGHT FLAG CLEARED', syncErr);
        return;
      }
      promise.then(function (outcome) {
        if (labelEl) { labelEl.textContent = restoreLabel; }
        if (gen !== epoch) {
          return;
        }
        if (outcome && outcome.ok && outcome.data && outcome.data.valid === true) {
          // VALID : hold the verdict for THIS exact value (the gate re-checks the equality at submit), tick the
          // field and the button, and name what the check confirmed (the expiry, the one fact the wire carries
          // beyond the verdict and the type) in the detail chip.
          state.validationIdCheck = { value: value, valid: true };
          UI.setFieldState(input, 'valid');
          if (errorEl) { UI.hideMessage(errorEl); }
          if (detailEl) {
            var detail = validationIdCheckDetailText(state.config, outcome.data);
            var validMsg = copyOf(state.config, 'ovanchor.check.valid');
            UI.showMessage(detailEl, detail ? validMsg + ' - ' + detail : validMsg);
          }
          setButtonValidated(checkBtn, true);
          return;
        }
        // REFUSED, or the transport failed : no verdict, the field in error, ONE message resolved entirely from
        // config.copy (validationIdCheckFailureText). Curated copy only, never raw server text.
        clearVerdict();
        UI.setFieldState(input, 'error');
        if (errorEl) { UI.showMessage(errorEl, validationIdCheckFailureText(state.config, outcome)); }
      }).then(function () { checking = false; }, function () { checking = false; });
    }
    // RESET-ON-EDIT : every edit drops the verdict (the gate then refuses the new value until it is checked) and
    // clears a prior attempt's error so the field reads neutral while typing ; the live evaluate
    // (wireOneFieldCheck, kind validationid) paints only the error icon for a malformed value and NEVER the green
    // tick, which the valid arm above alone sets (operator ruling 2026-10-17 : the tick means CHECKED, the domain
    // box model). Clear Form dispatches this same event on the field, so a cleared form forgets the verdict for free.
    input.addEventListener('input', function () {
      clearVerdict();
      if (errorEl) { UI.hideMessage(errorEl); }
    });
    if (checkBtn) { checkBtn.addEventListener('click', checkValidationIdNow); }
    // Exposed for refreshForVariant, which calls it synchronously at the START of the switch : a variant switch drops
    // the verdict (per SKU), bumps the epoch so a check in flight across the switch bails, and clears any prior error.
    state.resetValidationIdCheck = function () {
      clearVerdict();
      if (errorEl) { UI.hideMessage(errorEl); }
      // The field's tick means CHECKED, so the dropped verdict takes the tick with it (resetPrimaryInput paints the
      // primary neutral the same way) ; the customer's next Check under the new variant paints it again.
      UI.setFieldState(input, 'neutral');
    };
  }

  // PER-FIELD VALIDATION TICK for the DIRECT-INPUT fields (owner 2026-06-22) : the contact / organisation /
  // address text + e-mail + country fields show the SAME green tick the primary domain shows, for consistency
  // (the screenshot had ticks on the domain but none on the end-user inputs or the selects). Wired here for the
  // text/email/country fields ; the dcv-method + approver SELECT ticks are wired in wireDcv / wireSanRow next to
  // their lock/reset logic. UX FEEDBACK ONLY -- the submit gate (FormValidator) is unchanged. The primary
  // FQDN/CSR (validate="domain"/"csr") are NOT matched here : they have their own deep validation in
  // wirePrimaryInput, so there is no double-wiring.
  function wireFieldChecks(form, config) {
    var fields = form.querySelectorAll('[data-order-form-validate="text"], [data-order-form-validate="email"], [data-order-form-validate="country"], [data-order-form-validate="acme"], [data-order-form-validate="validationid"], [data-order-form-validate="phone"]');
    for (var i = 0; i < fields.length; i++) {
      wireOneFieldCheck(fields[i], config);
    }
  }
  function wireOneFieldCheck(el, config) {
    var kind = el.getAttribute('data-order-form-validate');
    // Resolve the field's validator id (for the maxLength + the ACME rule) and its lengthKey. The max-length VALUE
    // comes from config.validation (the single source order-form-v2-validation.liquid, owner 2026-06-29), so the live
    // tick runs the IDENTICAL length check the submit gate + card-status use (PR #24 r11 #2 : a multi-byte value over
    // the BYTE cap but under the native CHARACTER maxlength must show the error icon, not a green tick the gate would
    // contradict). No length entry -> non-blank ticks. Resolved by data-order-form-property.
    var fieldId = PROPERTY_TO_VALIDATOR_FIELD[el.getAttribute('data-order-form-property')];
    var rule = FIELD_RULES_BY_ID[fieldId] || null; // carries lengthKey (the "too long" copy)
    var validation = config && config.validation;
    var maxLength = maxLengthOf(validation, fieldId);
    var lengthKey = rule ? rule.lengthKey : undefined;
    function evaluate() {
      if (kind === 'phone') {
        // End User Telephone Number : the tick reflects the COMPOSED value (dial + separator-stripped digits),
        // not the raw national input -- one value, one rule (composeTelephoneValue + isValidTelephoneValue :
        // the SAME primitives the gate / chip / write-through consume, so the tick can never contradict the
        // gate ; the acme model). A blank composition (including a separators-only input) is neutral : an empty
        // optional phone is not yet "wrong" (the house blank-is-neutral stance). The dial select gets the
        // standard chosen-select chevron treatment, the same as every other validated select.
        var phoneRow = el.closest ? el.closest('[data-order-form-field-state="telephone"]') : null;
        var composed = composeTelephoneValue(phoneRow);
        if (composed === '') {
          UI.setFieldState(el, 'neutral');
        } else {
          UI.setFieldState(el, isValidTelephoneValue(composed, validation && validation.telephone) ? 'valid' : 'error');
        }
        var dialSel = phoneRow && phoneRow.querySelector('[data-order-form-telephone-dial]');
        if (dialSel) {
          applySelectChevron(dialSel, !!dialSel.value);
        }
        return;
      }
      var val = (el.value || '').trim();
      if (!val) {
        // Blank -> neutral (no error until the submit gate ; an empty field is not yet "wrong").
        UI.setFieldState(el, 'neutral');
      } else if (kind === 'email') {
        // An e-mail must be a VALID address for the tick ; a non-blank-but-invalid one shows the error icon.
        // (Length is capped by the native maxlength + the submit gate ; the live tick only needs the format.)
        // LIVE on every keystroke, INTENTIONAL (PR #24 r7 #2, owner-confirmed 2026-06-23) : this runs on `input`,
        // so a half-typed address (jo@) shows the red mark mid-entry and flips to green the instant it is valid.
        // The reviewer suggested deferring the red to blur ; the owner chose to KEEP the immediate live feedback.
        // Length (the e-mail byte cap from config.validation) is also enforced here via fieldContentError, so the tick matches the gate.
        UI.setFieldState(el, (isValidEmail(val) && fieldContentError(val, maxLength, lengthKey) === null) ? 'valid' : 'error');
      } else if (kind === 'acme' || kind === 'validationid') {
        // CHECK-BACKED FIELDS (operator ruling 2026-10-17 : "shouldn't it only get a green tick once it is
        // validated") : the ACME Account ID (confirmed by the subscription check) and the Validation ID (confirmed
        // by the Validation ID check) NEVER get the green tick from the live evaluate. A malformed value shows the
        // error icon at once, under the IDENTICAL local rule the gate and the check apply (the ACME charset + length
        // via isValidAcmeAccountId from config.validation['caas-acme-account'] ; the Validation ID byte cap + digits
        // pattern from config.validation['ov-anchor-order'], the gate's ovAnchor rule) ; a well-formed value reads
        // NEUTRAL until checkAcmeNow / checkValidationIdNow sets 'valid' on a confirming answer, and the next edit
        // returns it to neutral through this same listener. The domain box model : the primary FQDN and CSR inputs
        // go neutral on input and tick only after Validate (wirePrimaryInput).
        var wellFormed;
        if (kind === 'acme') {
          wellFormed = isValidAcmeAccountId(val, validation && validation['caas-acme-account']);
        } else {
          var idRule = validation && validation['ov-anchor-order'];
          var idRe = anchoredRegExp(idRule && idRule.pattern);
          wellFormed = fieldContentError(val, maxLength, lengthKey) === null && !!idRe && idRe.test(val);
        }
        UI.setFieldState(el, wellFormed ? 'neutral' : 'error');
      } else {
        // text / country : owner decision 2026-06-23 : the CA imposes NO character-set rule on the identity fields,
        // so the charset filter was removed -- a present value ticks UNLESS it busts its byte cap. Run the SAME
        // fieldContentError the card-status + gate use (PR #24 r11 #2) so an over-byte multi-byte value shows the
        // error icon, not a tick the card / gate contradict. The cap comes from config.validation (single source).
        UI.setFieldState(el, fieldContentError(val, maxLength, lengthKey) === null ? 'valid' : 'error');
      }
      // The country <select> (kind 'country') hides its chevron + moves its tick to the field edge once a value
      // is chosen -- the SAME treatment as every other validated select (pattern-sweep with applySelectTick). A
      // no-op for the text / e-mail INPUTS (no `.select` chrome to find). A country value present == valid (it
      // never errors), so the non-blank check is the right condition.
      applySelectChevron(el, !!val);
    }
    el.addEventListener('input', evaluate);
    if (kind !== 'acme' && kind !== 'validationid') {
      // The CHECK-BACKED fields evaluate on EDITS only : a blur or change with no edit must not repaint a field the
      // check has just ticked (or errored) to neutral while the verdict, the button tick, the chip and the gate all
      // stand (adversarial pass finding, 2026-10-17). Their state after a check is the check flow's alone, until the
      // next 'input'. Every other kind keeps the blur + change evaluate (the country <select> fires `change`).
      el.addEventListener('blur', evaluate);
      el.addEventListener('change', evaluate); // the country <select> fires `change`, not `input`
    }
    // The dial select is HALF of the composed telephone value : re-evaluate this tick on its change too
    // (kind 'phone' only ; every other kind is a single control).
    if (kind === 'phone') {
      var tickRow = el.closest ? el.closest('[data-order-form-field-state="telephone"]') : null;
      var tickDial = tickRow && tickRow.querySelector('[data-order-form-telephone-dial]');
      if (tickDial) {
        tickDial.addEventListener('change', evaluate);
      }
    }
    // Evaluate once at wire-up (handles a browser-autofilled field).
    evaluate();
  }

  function applyDcvPanels(scopeEl, methodValue, showApprover) {
    var panels = scopeEl.querySelectorAll('[data-order-form-dcv-details]');
    var matched = null; // the REVEALED panel, returned so a caller can scroll it without re-scanning inline display
    for (var i = 0; i < panels.length; i++) {
      var which = panels[i].getAttribute('data-order-form-dcv-details');
      // SECOND-EYES AUDIT FIX (2026-06-24) : HTTP and HTTPS each match their OWN panel now. Previously the single
      // "https" panel matched BOTH methods (which === 'https' && (HTTP || HTTPS)), so an "HTTP File" selection
      // revealed a panel whose static chip read "HTTPS FILE ... SELECTED" -- the customer was confirmed the
      // OPPOSITE protocol to the one they chose. This was display-only (getToken / the cart always wrote the
      // chosen method id), but it told the shopper the wrong thing. The snippets now render a separate
      // data-order-form-dcv-details="http" panel (order-form-v2-dcv-section.liquid + order-form-v2-san-builder.liquid),
      // so the match is now one method per panel. DCV_METHODS constants (not literals) so a value rename cannot
      // silently stop matching (PR#17 r9 #4).
      var match = (which === 'email' && showApprover) ||
        (which === 'dns' && methodValue === DCV_METHODS.DNS_CNAME) ||
        // DNS TXT random-value DCV (added 2026-07-05, middleware-01 PR #172) : the 'dnstxt' panel
        // (order-form-v2-dcv-section.liquid + order-form-v2-san-builder.liquid) matches DCV_METHODS.DNS_TXT,
        // one method per panel like the rest. Gated ENTIRELY by facts.dcvMethods -- this only reveals when the
        // server put DNS_TXT in the picker (wildcards included ; the form applies no method exclusion).
        (which === 'dnstxt' && methodValue === DCV_METHODS.DNS_TXT) ||
        (which === 'http' && methodValue === DCV_METHODS.HTTP) ||
        (which === 'https' && methodValue === DCV_METHODS.HTTPS);
      panels[i].style.display = match ? '' : 'none';
      if (match) { matched = panels[i]; }
    }
    return matched; // null when no method is chosen (placeholder) ; the revealed panel otherwise (PR #30 review)
  }

  // Show/hide the EMAIL panel's status chips from an onApproverChange {valid} result. SHARED by the primary +
  // SAN rows. A membership-checked approver (token non-null) SHOWS the v1-verbatim confirmation + HIDES the
  // "must be selected" prompt ; anything else HIDES the confirmation + SHOWS the prompt (owner 2026-06-22 : the
  // panel must tell the shopper to pick an approver while none is chosen). Both shells may be null (a custom
  // assembler / fixture omitting one) -- each is guarded independently. Feedback ONLY : the token is written by
  // getToken at submit. NOTE : only call this while the EMAIL panel is VISIBLE -- a method change away from
  // EMAIL hides the panel (and its chips) separately ; this never shows the prompt for a non-EMAIL method.
  // selectedText / requiredText are the resolved customer strings (dcv.email.selected / dcv.approver.required) the
  // state-carrying callers (wireDcv, wireSanRow) read from config.copy and pass in -- this helper is state-less, so
  // the wording reaches it as params, keeping order-form-v2-copy the single source (content-centralization 2026-06-27).
  function applyEmailConfirmation(emailSuccessEl, emailPromptEl, approverResult, selectedText, requiredText) {
    var valid = !!(approverResult && approverResult.valid);
    if (emailSuccessEl) {
      if (valid) { UI.showMessage(emailSuccessEl, selectedText); }
      else { UI.hideMessage(emailSuccessEl); }
    }
    if (emailPromptEl) {
      if (valid) { UI.hideMessage(emailPromptEl); }
      else { UI.showMessage(emailPromptEl, requiredText); }
    }
  }

  // Wire the primary DCV method <select> : on change, record the method + toggle the
  // matching data-order-form-dcv-details panel (EMAIL shows the approver picker, and -- if
  // the primary domain has already validated -- loads its approver addresses).
  function wireDcv(form, state) {
    var select = findPrimaryDcvSelect(form);
    if (!select) {
      return;
    }
    // Scope the panel lookups to the PRIMARY DCV container (PR#17 r12 #3) : the email/dns/https
    // detail panels live inside [data-order-form-field="dcv"]. A form-wide query is correct today
    // (only the primary renders dcv-details panels ; SAN rows render the approver picker directly),
    // but scoping to the primary container matches the order-independence robustness applied to
    // findPrimaryFqdn / findPrimaryDcvSelect, so a future assembler that wrapped a SAN-row approver
    // in data-order-form-dcv-details="email" could not have the PRIMARY's method change toggle it.
    var dcvField = select.closest('[data-order-form-field="dcv"]') || form;
    // The single approver select (owner 2026-06-30 : ONE merged list -- the additional/official addresses are appended
    // into this select by the "retrieve additional" button, no second dropdown). Scoped to the primary EMAIL panel.
    var approverSelect = dcvField.querySelector('[data-order-form-dcv-details="email"] [data-order-form-validate="approver"]');
    // FIX 8 : the EMAIL confirmation shell (the <dcvId>-email-success empty status-chip <p> the dcv-section
    // renders inside the email panel). Scoped to the primary DCV container, NEVER document-wide -- a
    // multi-form page would otherwise cross-wire. May be null (a custom assembler omitting it) ; every use
    // below is guarded. v1-VERBATIM wording (v1's "E-MAIL VALIDATION METHOD SUCCESSFULLY SELECTED", already
    // UPPERCASE + hyphenated "E-MAIL" per the Trustico message style ; owner content-pass placeholder).
    var emailSuccessEl = dcvField.querySelector('[id="' + select.id + '-email-success"]');
    // owner 2026-06-22 : the inverse "approver must be selected" prompt chip (the <methodId>-email-prompt the
    // dcv-section renders inside the email panel). Same primary-scoped resolution as -email-success ; may be null.
    var emailPromptEl = dcvField.querySelector('[id="' + select.id + '-email-prompt"]');
    // owner 2026-06-23 : the same <methodId>-hint chip wirePrimaryInput uses for the LOCKED hint -- wireDcv
    // toggles it on a method change so it shows "VALIDATION METHOD MUST BE SELECTED" while unlocked-but-unchosen
    // and hides once a method is chosen. Both functions resolve the SAME chip by id, coordinating via the DOM.
    var dcvHintEl = dcvField.querySelector('[id="' + select.id + '-hint"]');
    // FIX 8 (pre-push review, pattern-sweep) : expose a primary-scoped hide hook so the PRIMARY-INPUT reset
    // paths can clear a now-stale EMAIL confirmation, exactly as resetPrimary already clears the CSR/FQDN
    // success (the FIX 1 pattern, previously applied to two of the three success surfaces and missed on the
    // EMAIL sibling). emailSuccessEl is a wireDcv-local ; wirePrimaryInput runs BEFORE wireDcv, so it reaches
    // the element only through this hook. Every caller guards on the hook's existence, so the ordering (set
    // here at wire time, invoked later on field events / approver reloads) is safe.
    state.hideDcvConfirmation = function () {
      if (emailSuccessEl) { UI.hideMessage(emailSuccessEl); }
      if (emailPromptEl) { UI.hideMessage(emailPromptEl); }
    };
    // Remember the approver <select> so loadPrimaryApproversIfEmail (fired from the primary input's success paths)
    // can refill it without re-querying the DOM (PR#17 #1).
    state.primaryApproverSelect = approverSelect;
    // Wire the primary additional-addresses button (data-order-form-action="approver-more") : on click it fetches the
    // full official set for the validated domain and APPENDS it (de-duped) into the single approver select (owner 2026-06-30).
    wireApproverMore(
      dcvField.querySelector('[data-order-form-dcv-details="email"] [data-order-form-action="approver-more"]'),
      state,
      function () { return state.primaryDcv; },
      approverSelect,
      // VERBATIM domain (owner 2026-07-01) : send the raw typed value (rawDomain ; the CN for a CSR primary) to the
      // OFFICIAL approver endpoint UNCHANGED -- do NOT strip the leading "*.". The middleware owns wildcard handling
      // PER-ENDPOINT : GET /api/dcv/approver-emails forwards it to Sectigo AS-IS (Sectigo returns the correct approvers
      // for a wildcard). This matches the CALCULATED endpoint (loadApprovers, rawDomain || domain), so the same domain
      // sends the SAME string to both endpoints -- the form makes NO wildcard decision.
      function () { return state.primaryInput.rawDomain || state.primaryInput.domain; }
    );
    select.addEventListener('change', function () {
      // wireDcv is attached SYNCHRONOUSLY up front (PR#17 r4 #1), so state.primaryDcv may not exist
      // yet if the shopper interacts before the first render -- and the DcvManager is reassigned by
      // every setupPrimaryDcv (initial render OR a variant switch). Read it LAZILY and bail until a
      // render has built it (the method <select> is empty until then anyway).
      if (!state.primaryDcv) {
        return;
      }
      var descriptor = state.primaryDcv.onMethodChange(select.value);
      // Track whether the chosen method shows the approver picker, so a LATER domain validation
      // knows to (re)load the approver list (the EMAIL-first-then-validate order ; PR#17 #1).
      state.primaryShowApprover = descriptor.showApprover;
      // FIX 8 : a method change HIDES any prior EMAIL confirmation. Switching away from EMAIL hides the
      // (now-irrelevant) message ; re-entering EMAIL repopulates the approver dropdown to its placeholder
      // (loadApprovers), so there is no valid approver yet -- the confirmation must re-earn its display on
      // the next valid onApproverChange, never linger from a previous EMAIL selection.
      if (emailSuccessEl) { UI.hideMessage(emailSuccessEl); }
      // Validation tick : a chosen method shows the success icon, the placeholder clears it (owner 2026-06-22).
      applySelectTick(select);
      // Toggle the email/dns/https panels via the shared helper (identical logic now drives the SAN rows).
      var revealedPanel = applyDcvPanels(dcvField, select.value, descriptor.showApprover);
      // owner 2026-06-25 : nudge the just-revealed method panel into view (scrollRevealIntoView uses block:'nearest',
      // so it only scrolls if the panel is below the fold ; no focus move, reduced-motion-aware). Only when a method
      // is chosen -- the placeholder reveals no panel (applyDcvPanels returns null). This is the primary DCV section
      // ONLY : the SAN-row method panels (applyDcvPanels at wireSanRow) deliberately do NOT scroll, because per-row
      // scrolling inside a list would be disruptive rather than helpful. The scroll target is the panel applyDcvPanels
      // actually matched+revealed (PR #30 review : keyed off the match, NOT a display:none re-scan, so a future move
      // to class-based visibility cannot silently scroll the wrong panel). NOTE : this re-scrolls on EVERY method swap
      // by design (each swap reveals a different panel) -- not edge-guarded like the gate ; see scrollRevealIntoView.
      if (select.value && revealedPanel) {
        scrollRevealIntoView(revealedPanel);
      }
      // owner 2026-06-22 : entering EMAIL (no approver picked yet) SHOWS the "approver must be selected" prompt ;
      // any other method HIDES it. The panel hides the chip too, but toggle explicitly so a re-entry to EMAIL
      // re-shows the prompt (the approver is re-set to its placeholder on re-entry, so none is valid yet).
      if (emailPromptEl) {
        if (descriptor.showApprover) { UI.showMessage(emailPromptEl, copyOf(state.config, 'dcv.approver.required')); }
        else { UI.hideMessage(emailPromptEl); }
      }
      // owner 2026-06-23 : a chosen method HIDES the "select a method" hint ; back to the placeholder RE-SHOWS
      // it (the picker is still unlocked here -- lockDcv owns the locked-hint state for an invalid primary).
      if (dcvHintEl) {
        if (select.value) { UI.hideMessage(dcvHintEl); }
        else { UI.showMessage(dcvHintEl, copyOf(state.config, 'dcv.method.required')); }
      }
      // EMAIL chosen AFTER the domain validated : load the approver addresses now. The reverse
      // order (EMAIL first, domain second) is handled by loadPrimaryApproversIfEmail.
      if (descriptor.showApprover && state.primaryInput.valid && state.primaryInput.domain) {
        // The calculated endpoint gets the EXACT typed value (rawDomain) for an FQDN primary -- the SAME untransformed
        // form the SAN rows send -- so it computes an identical list for a domain whether it is the primary or a SAN.
        // A CSR primary has no typed domain field, so it falls back to the validated CN (state.primaryInput.domain).
        loadApprovers(state, state.primaryDcv, approverSelect, state.primaryInput.rawDomain || state.primaryInput.domain);
      }
    });
    // Wire the single approver select (single-list, owner 2026-06-30). On change it records the approver + refreshes
    // the EMAIL confirmation chips + the tick ; the {valid, token} result drives the v1 EMAIL confirmation (FIX 8 ;
    // feedback only -- getToken writes the token at submit). wireApproverSelect null-guards the select.
    wireApproverSelect(
      state,
      function () { return state.primaryDcv; },
      approverSelect,
      emailSuccessEl,
      emailPromptEl,
      copyOf(state.config, 'dcv.email.selected'),
      copyOf(state.config, 'dcv.approver.required')
    );
  }

  // Resolve an approver picker's "-info" status shell (the <approverId>-info region the approver-picker
  // renders), SCOPED to the approver field container (never document-wide -- a multi-form / multi-row page
  // would otherwise cross-wire). SHARED by wireApproverMore (which WRITES the "no additional addresses"
  // status) and populateApproverSelect (which CLEARS it on every repopulate). Null on a custom picker that
  // omits the shell (or the SAN test-fixture simplification) -- every caller guards.
  function findApproverInfo(approverSelect) {
    var field = (approverSelect && approverSelect.closest) ? approverSelect.closest('[data-order-form-field="approver"]') : null;
    return (field && approverSelect.id) ? field.querySelector('[id="' + approverSelect.id + '-info"]') : null;
  }

  // Append a FLAT run of approver <option>s (value = textContent = the address) to a select. Used by
  // populateApproverSelect to render the merged approver list (calculated + any appended official addresses) as a
  // plain dropdown -- ONE list (owner 2026-06-30), so there is no in-dropdown grouping/separator to render.
  function appendApproverOptions(approverSelect, items) {
    if (!items || !items.length) {
      return;
    }
    var doc = approverSelect.ownerDocument;
    for (var gi = 0; gi < items.length; gi++) {
      var gopt = doc.createElement('option');
      gopt.value = items[gi];
      gopt.textContent = items[gi];
      approverSelect.appendChild(gopt);
    }
  }

  // Render the approver <select>'s options from `list` (a placeholder + one FLAT option per address) and enable it.
  // ONE list (owner 2026-06-30) : the calculated default load (loadApprovers) renders the calculated set, and the
  // "retrieve additional" button (wireApproverMore) re-renders this SAME select with the calc+official MERGED set --
  // both flow through here. `placeholderKey` names the copy key for the placeholder text (always approver.placeholder
  // now). No in-dropdown grouping or separator -- a single plain dropdown.
  function populateApproverSelect(approverSelect, list, placeholderKey, copy) {
    // C.3 : capture the chosen approver BEFORE the rebuild so a repopulate of a set that still CONTAINS it does not
    // silently lose it. A different-domain reload produces a different set, where the old address is absent : the
    // <select> drops to the placeholder here AND the underlying token is dropped at the DcvManager level
    // (setApproverEmails clears a non-member _approver -- the adversarial-review cart-correctness fix), so getToken()
    // goes null and the gate blocks until the shopper re-picks. (Restoring + re-firing below applies ONLY to the
    // still-a-member case.)
    var prev = approverSelect.value;
    while (approverSelect.firstChild) {
      approverSelect.removeChild(approverSelect.firstChild);
    }
    var placeholder = approverSelect.ownerDocument.createElement('option');
    placeholder.value = '';
    // Placeholder wording lives in order-form-v2-copy, delivered via config.copy. The caller names the key
    // (approver.placeholder) so the select shows its "- Select Approver E-Mail Address -" prompt. '' when copy / key
    // is absent.
    placeholder.textContent = (copy && placeholderKey && copy[placeholderKey]) || '';
    // DROPDOWN-PLACEHOLDER PARITY (owner 2026-07-02) : greyed/lighter + non-selectable, matching the country <option>
    // idiom -- see the same-dated note in DcvManager.buildMethodSelect for why BOTH flags are required and why it is
    // gate-safe. The C.3 restore below (approverSelect.value = prev) still governs when a real address survives ; the
    // placeholder case is left displayed by `selected`.
    placeholder.disabled = true;
    placeholder.selected = true;
    approverSelect.appendChild(placeholder);
    appendApproverOptions(approverSelect, list || []);
    approverSelect.disabled = false;
    // C.3 : restore the chosen approver IF it is still an option (the superset / same-set case) and re-fire
    // the approver-change handler so the EMAIL "...SUCCESSFULLY SELECTED" confirmation + the cart token come
    // back -- otherwise a Show-More click (or a same-domain re-validate) would blank a valid selection. A
    // value not in the new set (a different-domain reload) is left at the placeholder, where the change
    // handler hides the now-stale confirmation. Guarded `typeof Event` : the synthetic change is browser /
    // jsdom only ; the approver-change handler never re-enters populateApproverSelect, so there is no loop.
    if (prev) {
      var stillAnOption = false;
      for (var k = 0; k < approverSelect.options.length; k++) {
        if (approverSelect.options[k].value === prev) { stillAnOption = true; break; }
      }
      if (stillAnOption) {
        approverSelect.value = prev;
        if (typeof Event === 'function') {
          approverSelect.dispatchEvent(new Event('change'));
        }
      }
    }
    // FIX (PR#20 r2 review) : repopulating the dropdown is a FRESH approver state, so clear any stale
    // "NO ADDITIONAL APPROVER E-MAIL ADDRESSES ON FILE" -info status -- it must re-earn its display on the
    // NEXT "Show More Options" click. This is the stale-message-clearing pattern applied everywhere else
    // (CSR/FQDN/EMAIL success cleared on reset / reload / method-change ; FIX 1/8) ; the approver -info was
    // the one spot that missed it. EVERY repopulate path flows through here (the default load, a re-validate
    // via loadPrimaryApproversIfEmail, a method-change reload, AND the Show More merge) ; the Show More path
    // re-decides infoEl IMMEDIATELY after calling this, so hiding here never suppresses a legitimate show.
    var infoEl = findApproverInfo(approverSelect);
    if (infoEl) { UI.hideMessage(infoEl); }
    // Validation tick (owner 2026-06-22) : after a repopulate the value is the placeholder ('') unless a still
    // -valid prior approver was restored above. Reflect that : restored -> the dispatched change already ticked
    // it ; dropped-to-placeholder -> clear the tick. Idempotent + value-based, so this is correct either way.
    applySelectTick(approverSelect);
  }

  // Populate the approver select with the CALCULATED set from the MIDDLEWARE (GET /api/dcv/approver-emails-calculated)
  // + enable it. Shared by the primary picker and the per-SAN rows. The endpoint returns the FINISHED calculatedEmails
  // array (+ isValid) for the EXACT domain (wildcard "*." and all, untransformed -- the callers pass the customer's
  // value, never stripped/paired) ; the form renders it DIRECTLY -- it no longer constructs the list client-side
  // (2026-06-28 migration : the prefix x levels pairing + /api/domain/auth-domains were removed). isValid:false /
  // empty -> [] -> the placeholder-only empty state the select already shows when a domain has no valid placements.
  // A new domain REBUILDS this select from calculated-only (dropping any official addresses appended for the prior
  // domain), and setCalculatedApproverEmails resets the DcvManager's official set -- so a stale official set never
  // carries across domains ; the shopper re-clicks "retrieve additional" to re-append the official set for the new domain.
  function loadApprovers(state, dcvManager, approverSelect, domain) {
    if (!approverSelect || !domain || !state.apiClient) {
      return Promise.resolve();
    }
    // VARIANT-SWITCH GUARD (Finding 2, owner 2026-07-01) : capture the variant epoch at dispatch. A variant switch
    // mid-reload reassigns state.primaryDcv AND resets the PERSISTENT primary approver <select> to placeholder ;
    // without this guard an in-flight reload for the OLD domain would resolve AFTER the switch and repopulate +
    // re-enable that select with the previous variant's list (UI staleness -- cart-safe, since the NEW DcvManager's
    // membership set rejects any stale pick, but confusing). Bail if the epoch advanced -- the SAME epoch discipline
    // refreshForVariant + the primary-validate callbacks already use (claimVariantEpoch). (SAN rows are rebuilt fresh
    // on a switch, so a stale SAN reload would write to a detached select -- harmless -- but the guard covers them too.)
    var epoch = state.variantEpoch;
    return state.apiClient.getCalculatedApproverEmails(domain).then(function (outcome) {
      if (epoch !== state.variantEpoch) {
        return; // superseded by a variant switch : do not repopulate the just-reset select with the old variant's list
      }
      var emails = (outcome && outcome.ok && outcome.data && Array.isArray(outcome.data.calculatedEmails)) ? outcome.data.calculatedEmails : [];
      dcvManager.setCalculatedApproverEmails(emails);
      populateApproverSelect(approverSelect, emails, 'approver.placeholder', state.config.copy);
    }).catch(function (err) {
      if (epoch !== state.variantEpoch) {
        return; // superseded : do not touch the just-reset select in the error path either
      }
      // getCalculatedApproverEmails is contracted never to reject (ApiClient resolves outcomes) ; a rejection (a
      // future change / an injected double) must still NOT block the customer -- render the empty state, never throw.
      log.error('ORDER FORM : CALCULATED APPROVER LOAD FAILED', err && err.message ? err.message : err);
      dcvManager.setCalculatedApproverEmails([]);
      populateApproverSelect(approverSelect, [], 'approver.placeholder', state.config.copy);
    });
  }

  // Wire the SINGLE approver select (single-list, owner 2026-06-30 : the additional addresses are appended into this
  // one select, so there is no second select to keep in sync). On change it records the chosen approver via
  // onApproverChange (membership-checked against the calc+official UNION, so an appended official address tokenises),
  // refreshes the EMAIL confirmation chips, and updates the tick. getDcvManager is read LAZILY (the primary DcvManager
  // is reassigned by setupPrimaryDcv ; SAN rows pass their own row manager). selectedText / requiredText are the
  // resolved EMAIL chips' copy the state-carrying callers pass in (content stays centralized in order-form-v2-copy).
  function wireApproverSelect(state, getDcvManager, calcSelect, emailSuccessEl, emailPromptEl, selectedText, requiredText) {
    if (!calcSelect) {
      return;
    }
    calcSelect.addEventListener('change', function () {
      // Lazy guard (PR#17 r4 #1) : the listener attaches before the first render builds the DcvManager.
      var dcvManager = getDcvManager();
      if (!dcvManager) {
        return;
      }
      // Record the chosen approver (or '' when reset to the placeholder) + refresh the EMAIL confirmation chips.
      // onApproverChange returns {valid, token} ; feedback ONLY (the cart token is written by getToken at submit).
      applyEmailConfirmation(emailSuccessEl, emailPromptEl, dcvManager.onApproverChange(calcSelect.value), selectedText, requiredText);
      // Validation tick : reflects the value (a valid member -> success icon, the placeholder -> cleared). Idempotent + value-based.
      applySelectTick(calcSelect);
    });
  }

  // Wire the additional-addresses (approver-more) button : on CLICK it fetches GET /api/dcv/approver-emails for the
  // validated domain, builds the FULL official set (levelEmails + whoisEmails + dnsTxtEmails), and APPENDS it (de-duped)
  // into the SINGLE approver select -- the additional addresses JOIN the calculated list rather than a second dropdown
  // (owner 2026-06-30 : the two-list model 2026-06-28 was collapsed to one). OPT-IN, apiBurst-friendly : the fetch fires
  // only on click, not on render. getDcvManager / getDomain are read LAZILY (the primary DcvManager is reassigned by
  // setupPrimaryDcv ; the domain is whatever the picker's input has validated to). The result chip
  // (approver.more.none / approver.more.found) reports whether the official fetch returned anything.
  function wireApproverMore(button, state, getDcvManager, approverSelect, getDomain) {
    // Guard the button + the select : the body re-renders the single approverSelect with the merged set AND reads its
    // id via findApproverInfo for the result chip. Both always render together today ; guarding keeps the
    // "every use guarded" posture for a custom assembler / fixture shipping one without the other.
    if (!button || !approverSelect) {
      return;
    }
    // The info status region (the <of_id>-info shell). findApproverInfo derives '<id>-info' from the select's id (the
    // single info chip the picker renders), scoped to the approver field container (never document-wide). May be
    // null (a custom picker / SAN test-fixture that omits it) ; the use below is guarded.
    var infoEl = findApproverInfo(approverSelect);
    button.addEventListener('click', function () {
      var dcvManager = getDcvManager();
      var domain = getDomain();
      if (!dcvManager || !domain || !state.apiClient) {
        return;
      }
      state.apiClient.getApproverEmails(domain).then(function (outcome) {
        var payload = (outcome && outcome.ok && outcome.data) ? outcome.data : null;
        // FULL official set : every levelEmails address + whoisEmails + dnsTxtEmails (owner 2026-06-28 : the official
        // list "absolutely does include" whois + level, NOT dnsTxt alone).
        var official = dcvManager.buildOfficialApproverEmails(payload);
        // Fold the official set into the membership UNION AND get the calc+official MERGED display list (de-duped,
        // calculated first). Re-render the SINGLE select with it : the official extras now JOIN the calculated list
        // (de-duped). populateApproverSelect preserves the shopper's current pick when it is still a member, so an
        // already-chosen calculated address survives the append.
        var merged = dcvManager.setOfficialApproverEmails(official);
        populateApproverSelect(approverSelect, merged, 'approver.placeholder', state.config.copy);
        // Result chip : NEUTRAL "none found" when the official fetch returned nothing, SUCCESS "found" otherwise.
        // populateApproverSelect cleared the info chip on its repopulate, so this re-decides it for the click result.
        if (infoEl) {
          if (!official.length) {
            infoEl.classList.remove('order-form-status--success');
            infoEl.classList.add('order-form-status--neutral');
            UI.showMessage(infoEl, copyOf(state.config, 'approver.more.none'));
          } else {
            infoEl.classList.remove('order-form-status--neutral');
            infoEl.classList.add('order-form-status--success');
            UI.showMessage(infoEl, copyOf(state.config, 'approver.more.found'));
          }
        }
      }).catch(function (err) {
        // Fire-and-forget click : guard like the variant listener (PR#17 r8 #1). getApproverEmails
        // is contracted never to reject, so this only fires on an unexpected DOM-mutation throw.
        log.error('ORDER FORM : OFFICIAL APPROVER LOAD FAILED', err && err.message ? err.message : err);
      });
    });
  }

  // (Re)load the PRIMARY approver list when a domain becomes available, but only if the chosen
  // DCV method is EMAIL. wireDcv's change listener loads approvers only when a validated domain
  // ALREADY exists ; the realistic shopper order -- pick EMAIL first, THEN validate the domain --
  // would otherwise leave the approver <select> empty + disabled (PR#17 #1). Called from the
  // CSR-validate + FQDN-blur success paths. state.primaryShowApprover + state.primaryApproverSelect
  // are set by wireDcv (so no DOM re-query is needed here) ; absent (no method chosen yet) it is a
  // guarded no-op.
  function loadPrimaryApproversIfEmail(state) {
    if (!state.primaryShowApprover || !state.primaryInput.valid || !state.primaryInput.domain) {
      return Promise.resolve();
    }
    // FIX 8 (pre-push review) : (re)loading the approver list repopulates the dropdown to its placeholder
    // (no approver selected), so any EMAIL confirmation showing for the PREVIOUS approver is now stale.
    // Clear it until the shopper re-picks a valid address (which re-fires onApproverChange -> re-shows).
    // This covers the re-validate / re-blur-without-edit path that resetPrimary (re-edit only) does not.
    if (state.hideDcvConfirmation) { state.hideDcvConfirmation(); }
    // The calculated endpoint gets the EXACT typed value (rawDomain ; FQDN primary), matching the SAN rows ; CSR falls back to the CN.
    return loadApprovers(state, state.primaryDcv, state.primaryApproverSelect, state.primaryInput.rawDomain || state.primaryInput.domain);
  }

  // Wire every rendered SAN row : the per-row method <select> change records the method on
  // THAT row's DcvManager (so getToken() works per row -- REQUIRED for a multi-domain cert
  // to submit) ; the row fqdn blur validates the SAN ; EMAIL loads the row's approvers.
  function wireSanRows(form, state) {
    if (!state.sanBuilder) {
      return;
    }
    var i = 1;
    var row = state.sanBuilder.getRow(i);
    while (row) {
      wireSanRow(form, state, row, i);
      i += 1;
      row = state.sanBuilder.getRow(i);
    }
  }

  // AUTO re-validate each PRESERVED SAN row after an MD -> MD variant switch (owner UX fix 2026-06-21 : the
  // same complaint as the primary -- a switch must not force a manual re-edit per row to restore each SAN's
  // tick + unlocked method). refreshForVariant has just rebuilt the rows (restoreTypedSans carried the typed
  // values across) and re-wired them via wireSanRows, which ships every row LOCKED + neutral. The SAN blur
  // path is SYNCHRONOUS (validateSan is a local DOM check, no /api round-trip and no epoch race), so dispatching
  // blur on each POPULATED row re-runs validateSan -> setFieldState + unlock/lock immediately : a row still valid
  // for the new variant regains its tick + unlocked method, a row now invalid (e.g. a wildcard SAN carried into a
  // plain multi-domain variant, or a misplaced asterisk) shows its error -- mirroring the primary. Since the
  // WILDCARD ENTRY rule of 2026-10-26 a plain SAN carried into a wildcard-MD variant is NOT the invalid case : the
  // row's blur seam prefixes it "*." and it validates, as the primary does. EMPTY rows are skipped (an untyped row stays
  // neutral, never errored). Re-dispatching blur (not calling validateSan directly) faithfully reuses the row's
  // wired handler, exactly as the shopper's own blur would, and matches how the tests drive a SAN row.
  function revalidateSanRows(state) {
    if (!state.sanBuilder) {
      return;
    }
    // CONTIGUOUS-ROW INVARIANT (PR #24 r9 : stop-at-first-null is SAFE) : SanBuilder.rebuildToCount renders rows
    // with CONTIGUOUS 1-based indices (1..N, no gaps), so getRow returning null means "past the last row" -- the
    // walk visits every row. The only way a gap could exist is a broken _renderRow returning null mid-rebuild
    // (a markup/template failure) ; even then this is re-validation (UX) ONLY -- buildSanRows iterates the SAME
    // getRow contract for the submit gate, so a skipped row cannot reach the cart unvalidated. No guard needed.
    var i = 1;
    var row = state.sanBuilder.getRow(i);
    while (row) {
      if (row.fqdnInput && row.fqdnInput.value) {
        row.fqdnInput.dispatchEvent(new Event('blur'));
      }
      i += 1;
      row = state.sanBuilder.getRow(i);
    }
  }

  function wireSanRow(form, state, row, index) {
    var rowState = { domain: '', showApprover: false };
    // FIX 8 : THIS row's container + its EMAIL confirmation shell (the per-row mirror of the primary's
    // -email-success, restored so a multi-domain cert confirms EACH SAN row, not just the primary -- owner
    // decision 2026-06-20). Resolved ONCE, scoped to this row (a multi-row form never cross-wires) ; derived
    // from whichever row control exists. Both may be null on a custom row markup -- every use is guarded.
    // Anchor on the row's fqdn input : every SAN row renders one (it is the reason the row exists), so it is
    // the most reliable single ancestor handle ; closest() walks up to the [data-order-form-san-row] container.
    var rowEl = row.fqdnInput ? row.fqdnInput.closest('[data-order-form-san-row]') : null;
    var rowEmailSuccess = rowEl ? rowEl.querySelector('[data-order-form-dcv-details="email"] [id$="-email-success"]') : null;
    // owner 2026-06-22 : THIS row's inverse "approver must be selected" prompt chip (-email-prompt), the per-row
    // mirror of the primary's. Same row-scoped resolution as rowEmailSuccess ; may be null on a custom row.
    var rowEmailPrompt = rowEl ? rowEl.querySelector('[data-order-form-dcv-details="email"] [id$="-email-prompt"]') : null;
    // PARITY WITH THE PRIMARY (brief : SAN-row DCV gating). The primary locks its DCV method <select> until
    // the primary validates (wirePrimaryInput lockDcv/unlockDcv + the <dcvId>-hint shell) ; the SAN rows had
    // NO equivalent, so an un-validated / wildcard-invalid SAN could pick a method and show "successfully
    // selected". Mirror it per row : resolve THIS row's hint shell (the <methodId>-hint status chip the row
    // template now renders), and lock/unlock the row's method select off the SAME validateSan(index) seam the blur uses.
    // No epoch guard is needed (unlike the primary) : validateSan is SYNCHRONOUS (a local DOM check, no /api
    // round-trip), so there is no in-flight callback that could re-unlock a superseded row.
    // The row's status chip, found by its seam (it survives the method block's removal on a DCV-hidden row) ; the
    // method-id lookup stays as the fallback for a custom row template that carries the id and no seam.
    var rowHintEl = rowEl ? (rowEl.querySelector('[data-order-form-san-status]') || (row.methodSelect ? rowEl.querySelector('[id="' + row.methodSelect.id + '-hint"]') : null)) : null;
    // The locked-DCV hint wording for a SAN row (owner content-pass PLACEHOLDER ; UPPERCASE Trustico style).
    // (SAN_DCV_LOCKED_HINT removed 2026-06-27 : the per-row locked hint is now customer copy san.dcv.method.locked
    // -- FQDN-only, so it asks for a valid DOMAIN, unlike the primary's CSR-or-domain dcv.method.locked.)
    function lockRowDcv() {
      // Disable the method picker AND reset it to its placeholder + reset the row DcvManager, so a re-validated
      // SAN re-chooses fresh (no stale method left selected behind a locked select).
      if (row.methodSelect) {
        row.methodSelect.disabled = true;
        row.methodSelect.value = '';
        if (row.dcvManager) { row.dcvManager.onMethodChange(''); }
        // Validation tick : a re-locked row is reset to the placeholder -> clear the method tick (owner 2026-06-22).
        applySelectTick(row.methodSelect);
      }
      rowState.showApprover = false;
      // GATE the confirmation + DNS/HTTP/HTTPS panels on validity : hide ALL of this row's dcv-details panels and
      // clear the EMAIL confirmation, so an invalid / wildcard SAN can NEVER show "successfully selected".
      if (rowEl) { applyDcvPanels(rowEl, '', false); }
      if (rowEmailSuccess) { UI.hideMessage(rowEmailSuccess); }
      if (rowEmailPrompt) { UI.hideMessage(rowEmailPrompt); }
      // DCV-hidden row (a CaaS multi-domain sale, operator 2026-09-02) : no method to choose, so the chip is the
      // row's neutral PROMPT while the box is empty or invalid -- the traditional hint minus its method clause.
      if (rowHintEl) {
        if (state.sanDcvHidden) {
          rowHintEl.classList.remove('order-form-status--success');
          rowHintEl.classList.add('order-form-status--neutral');
          UI.showMessage(rowHintEl, copyOf(state.config, 'san.status.prompt'));
        } else {
          UI.showMessage(rowHintEl, copyOf(state.config, 'san.dcv.method.locked'));
        }
      }
    }
    function unlockRowDcv() {
      if (row.methodSelect) { row.methodSelect.disabled = false; }
      // owner 2026-06-23 : parity with the primary -- once this row's picker UNLOCKS but no method is chosen yet,
      // the hint becomes the "VALIDATION METHOD MUST BE SELECTED" prompt (was hidden) ; hidden if one is chosen.
      // DCV-hidden row : the chip flips to its PROVIDED state instead (the primary Domain Name card's pattern).
      if (rowHintEl) {
        if (state.sanDcvHidden) {
          rowHintEl.classList.remove('order-form-status--neutral');
          rowHintEl.classList.add('order-form-status--success');
          UI.showMessage(rowHintEl, copyOf(state.config, 'san.status.provided'));
        } else if (row.methodSelect && row.methodSelect.value) { UI.hideMessage(rowHintEl); }
        else { UI.showMessage(rowHintEl, copyOf(state.config, 'dcv.method.required')); }
      }
    }
    // Ship LOCKED : the row template renders the method select ENABLED (like the primary's), so lock it
    // explicitly on wire-up ; the blur seam unlocks it once the SAN validates, an edit re-locks it.
    lockRowDcv();
    if (row.methodSelect) {
      row.methodSelect.addEventListener('change', (function (r, rs) {
        return function () {
          var descriptor = r.dcvManager.onMethodChange(r.methodSelect.value);
          // Validation tick : a chosen method shows the success icon (owner 2026-06-22 ; parity with the primary).
          applySelectTick(r.methodSelect);
          // Track EMAIL on this row so a LATER fqdn blur (domain-after-method order) can load the
          // approvers ; mirrors the primary's loadPrimaryApproversIfEmail (PR#17 #1).
          rs.showApprover = descriptor.showApprover;
          // FIX 8 : toggle THIS row's email/dns/https panels via the SHARED helper (identical logic to the
          // primary's wireDcv) + hide any prior EMAIL confirmation (re-entering EMAIL must re-earn it on the
          // next valid approver, never linger from a previous selection). DNS/HTTP/HTTPS panels self-reveal here.
          if (rowEl) { applyDcvPanels(rowEl, r.methodSelect.value, descriptor.showApprover); }
          if (rowEmailSuccess) { UI.hideMessage(rowEmailSuccess); }
          // owner 2026-06-22 : entering EMAIL on this row shows the "approver must be selected" prompt (none
          // picked yet) ; any other method hides it (parity with the primary's wireDcv).
          if (rowEmailPrompt) {
            if (descriptor.showApprover) { UI.showMessage(rowEmailPrompt, copyOf(state.config, 'dcv.approver.required')); }
            else { UI.hideMessage(rowEmailPrompt); }
          }
          // owner 2026-06-23 : a chosen method hides this row's "select a method" hint ; back to the placeholder
          // re-shows it (parity with the primary).
          if (rowHintEl) {
            if (r.methodSelect.value) { UI.hideMessage(rowHintEl); }
            else { UI.showMessage(rowHintEl, copyOf(state.config, 'dcv.method.required')); }
          }
          if (descriptor.showApprover && rs.domain) {
            loadApprovers(state, r.dcvManager, r.approverSelect, r.fqdnInput.value);
          }
        };
      })(row, rowState));
    }
    if (row.fqdnInput) {
      row.fqdnInput.addEventListener('blur', (function (r, rs) {
        return function () {
          // WILDCARD ENTRY, per row (operator decision 2026-10-26 ; the primary's validateFqdnNow rule) : on a wildcard
          // multi-domain product every additional domain must be a wildcard too (validateSan's _isWildcardMd rule), so
          // a row typed without its "*." is prefixed and written back before the row validates. The requirement is the
          // SKU's isWildcardMultiDomain fact, the same expression setupSan hands SanBuilder as isWildcardMd ; absent
          // facts mean no prefix, and the row then fails validateSan as before until the shopper re-blurs it.
          var rowEntry = wildcardEntryFor(r.fqdnInput.value, !!(state.facts && state.facts.isWildcardMultiDomain));
          if (rowEntry !== r.fqdnInput.value) { r.fqdnInput.value = rowEntry; }
          var ok = state.sanBuilder.validateSan(index);
          UI.setFieldState(r.fqdnInput, ok ? 'valid' : (r.fqdnInput.value ? 'error' : 'neutral'));
          // Cache the normalised row domain as this row's VALIDITY FLAG : a truthy validated-domain marker the gate +
          // the approver-load guards key off (cleared to '' on edit, below). It is NO LONGER sent to any endpoint --
          // BOTH the calculated AND the official approver endpoints now send the EXACT typed value (r.fqdnInput.value,
          // wildcard "*." and all) so the middleware computes the placements (owner 2026-07-01 : do NOT strip the
          // leading "*." for EITHER endpoint ; the middleware owns wildcard normalisation per-endpoint -- official ->
          // Sectigo as-is, calculated -> stripped internally via getValidAuthDomains). The stripWildcard that used to
          // shape the official-lookup value is GONE ; this stays normalised only as an internal validity flag.
          rs.domain = state.domainNormalizer.normalize(r.fqdnInput.value || '');
          // PARITY : unlock the DCV picker once this SAN is valid, otherwise re-lock it (which also resets the
          // method + hides the panels). Mirrors the primary's unlockDcv-on-validate / lockDcv-on-invalid. The
          // else branch sets rs.showApprover = false, so the approver-reload below is skipped for an invalid SAN.
          if (ok && rs.domain) { unlockRowDcv(); } else { lockRowDcv(); }
          // EMAIL chosen before this row had a domain : load the row's approvers now that one
          // exists (the per-row mirror of loadPrimaryApproversIfEmail ; PR#17 #1).
          if (rs.showApprover && rs.domain) {
            // FIX 8 : reloading repopulates the approver dropdown to its placeholder, so a prior EMAIL
            // confirmation is stale -- hide it until the shopper re-picks (mirrors loadPrimaryApproversIfEmail).
            if (rowEmailSuccess) { UI.hideMessage(rowEmailSuccess); }
            loadApprovers(state, r.dcvManager, r.approverSelect, r.fqdnInput.value);
          }
        };
      })(row, rowState));
      // PARITY : mirror the primary's resetPrimary-on-input. Any edit to the SAN domain re-locks the DCV picker
      // IMMEDIATELY (not only on blur), so a previously-valid SAN being edited cannot keep a chosen method or a
      // "successfully selected" panel while the new domain is unverified, AND drops the field's valid/error icon
      // to neutral the instant editing starts (the primary input handlers do the same via UI.setFieldState).
      // NOTE (corrected pre-push review) : unlike the primary's CSR<->FQDN single-active toggle, the SAN fqdn
      // input is NEVER disabled (lockRowDcv only disables the METHOD select), so this fires on EVERY keystroke,
      // including first entry while the row is locked. That is safe because lockRowDcv is IDEMPOTENT (method
      // already '', panels already hidden, hint already shown), so re-locking an already-locked row is a no-op.
      row.fqdnInput.addEventListener('input', (function (r, rs) {
        return function () {
          lockRowDcv();
          UI.setFieldState(r.fqdnInput, 'neutral'); // drop the stale valid/error icon on edit (primary parity)
          rs.domain = '';
        };
      })(row, rowState));
    }
    if (row.approverSelect) {
      // Wire THIS row's single approver select (single-list, owner 2026-06-30 ; per-row mirror of the primary's
      // wireDcv). On change it records the approver + refreshes the row's EMAIL confirmation chips + the tick. FIX 8 :
      // the {valid} result still drives the row confirmation (feedback only -- the per-SAN token is written by getToken at submit).
      wireApproverSelect(
        state,
        (function (r) { return function () { return r.dcvManager; }; })(row),
        row.approverSelect,
        rowEmailSuccess,
        rowEmailPrompt,
        copyOf(state.config, 'dcv.email.selected'),
        copyOf(state.config, 'dcv.approver.required')
      );
      // Wire THIS row's additional-addresses button (data-order-form-action="approver-more") : each SAN row renders
      // its own approver picker, so scope to the row (via the [data-order-form-san-row] ancestor resolved above) +
      // use the row's cached domain + DcvManager, and append the official set into the row's OWN single select.
      wireApproverMore(
        rowEl ? rowEl.querySelector('[data-order-form-action="approver-more"]') : null,
        state,
        (function (r) { return function () { return r.dcvManager; }; })(row),
        row.approverSelect,
        // VERBATIM domain (owner 2026-07-01) : the OFFICIAL approver endpoint gets the EXACT typed value -- the SAME
        // untransformed form the CALCULATED endpoint already uses (r.fqdnInput.value), NOT the normalised rs.domain.
        // The middleware owns wildcard handling per-endpoint (see the rs.domain note above), so no "*." stripping here.
        (function (r) { return function () { return r.fqdnInput ? r.fqdnInput.value : ''; }; })(row)
      );
    }
  }

  // Auto-grow the CSR textarea up to the CSS single-source cap (--order-form-textarea-max-height,
  // 320px in assets/order-form-v2.css). The numeric fallback (also 320) only fires in environments
  // that do not compute custom properties, e.g. jsdom ; it MATCHES the CSS token (owner 2026-06-29 A+
  // sweep : it previously hardcoded a stale 800 that disagreed with the single source).
  function wireAutoResize(form) {
    var csr = form.querySelector('textarea[data-order-form-validate="csr"]');
    if (!csr) {
      return;
    }
    function readCap() {
      var view = csr.ownerDocument && csr.ownerDocument.defaultView;
      var root = form.classList && form.classList.contains('order-form') ? form : (form.querySelector('.order-form') || form);
      var raw = view && view.getComputedStyle ? view.getComputedStyle(root).getPropertyValue('--order-form-textarea-max-height') : '';
      var cap = parseInt(raw, 10);
      return Number.isFinite(cap) && cap > 0 ? cap : 320;
    }
    // Resolve the cap ONCE per form rather than on every keystroke : getComputedStyle is
    // comparatively expensive and the --order-form-textarea-max-height token is static for the
    // life of the form, so reading it on each input event was wasted work (PR#17 #4).
    var cap = readCap();
    function resize() {
      csr.style.height = 'auto';
      var ideal = csr.scrollHeight || 0;
      if (ideal > cap) {
        csr.style.height = cap + 'px';
        csr.style.overflowY = 'auto';
      } else {
        csr.style.height = ideal + 'px';
        csr.style.overflowY = 'hidden';
      }
    }
    csr.addEventListener('input', resize);
    resize();
  }

  /**
   * @param {Window} win
   */
  function bootstrap(win) {
    win = win || root;
    if (!win || !win.document) {
      return;
    }

    var config = win.ORDER_FORM_CONFIG || null;
    if (!config) {
      log.warn('ORDER FORM : NO CONFIG FOUND ON WINDOW - SCAFFOLD INACTIVE');
      return;
    }

    // One ApiClient per page, shared by every form (config carries the production origin).
    var apiClient = new ApiClient({ middlewareApiBase: config.middlewareApiBase });
    // PRE-FILL BY LINK (owner request 2026-10-03) : read the page address ONCE, here, and hand the parsed values to every
    // form wired below (one form per page in practice : order-form-v2-unified.liquid SINGLE FORM PER PAGE). The address
    // format has one home : parsePrefillLink. Nothing re-reads the address later.
    var prefill = parsePrefillLink(win.location ? win.location.hash : null);

    // Wire ONE form, FAIL-LOUD to the customer on an init failure (PR #24 r8 #1, owner 2026-06-23). wireForm is
    // async (it resolves SKU facts + the country list) : a SYNC throw before it returns the promise is caught by
    // the try ; a REJECTED promise by the .catch -- BOTH reveal the form's hidden [data-order-form-init-error] so
    // the shopper sees a "could not load" message instead of a silently-dead form. (Progressive disclosure hides
    // everything past the primary, so a failed init would otherwise leave just the primary card + a Validate button
    // that does nothing -- the gap the omitted <noscript> cannot cover, since <noscript> never fires for a THROWN
    // init.) SCOPE (PR #24 r13 #1) : this covers the INIT failure ONLY. A RUNTIME middleware error (a CSR / domain
    // validate that fails mid-session because the middleware is down) is surfaced SEPARATELY by its own handler --
    // the csr-validate / fqdn-validate arms write the provider error / a generic fallback into the field's -error
    // region, so the shopper sees a clear "UNABLE TO VALIDATE ..." message and the gate simply does not open. So
    // both paths fail loud : init failure -> this init-error message ; a runtime validate failure -> the field error.
    // Per-form function so the closure captures THIS form (the loop's `var form` would otherwise be shared).
    function wireOneForm(form) {
      function showInitError() {
        var errEl = form.querySelector('[data-order-form-init-error]');
        if (errEl) {
          errEl.style.display = '';
        }
      }
      try {
        wireForm(form, { config: config, apiClient: apiClient, prefill: prefill }).catch(function (err) {
          log.error('ORDER FORM : WIREFORM FAILED', err && err.message ? err.message : err);
          showInitError();
        });
      } catch (err) {
        log.error('ORDER FORM : WIREFORM THREW SYNCHRONOUSLY', err && err.message ? err.message : err);
        showInitError();
      }
    }

    var ready = function () {
      var forms = win.document.querySelectorAll('.order-form');
      var newlyWired = 0;
      for (var i = 0; i < forms.length; i++) {
        var form = forms[i];
        // Per-form idempotency (PR A review item 2) : a form already bootstrapped
        // (by a prior bootstrap call, e.g. a second injected <script> tag on a
        // multi-form page) is skipped so its handlers are never double-wired.
        if (form.getAttribute('data-order-form-bootstrapped') === 'true') {
          continue;
        }
        form.setAttribute('data-order-form-bootstrapped', 'true');
        newlyWired += 1;
        // Phase 3 : wire the real orchestration (per-form, FAIL-LOUD on an init failure -- see wireOneForm). A
        // rejected promise OR a synchronous throw reveals the init-error message and never breaks the loop / the
        // other forms.
        wireOneForm(form);
      }
      log.info('ORDER FORM SCAFFOLD LOADED', { formsFound: forms.length, newlyWired: newlyWired, apiBase: config.middlewareApiBase || null });
    };

    if (win.document.readyState === 'loading') {
      win.document.addEventListener('DOMContentLoaded', ready);
    } else {
      ready();
    }
  }

  /* =========================================================================
   * Module surface
   * ========================================================================= */
  return {
    // Constants
    DCV_METHODS: DCV_METHODS,
    RETRY: RETRY,
    SSL_ALWAYS_REQUIRED_FIELDS: SSL_ALWAYS_REQUIRED_FIELDS,
    SSL_ORG_REQUIRED_FIELDS: SSL_ORG_REQUIRED_FIELDS,
    // property -> gate validator id (exported for the snippet-contract maxlength cross-check : a direct-copy field's
    // native maxlength single-source id must equal the id the GATE caps that property by -- the "both sides" anti-drift).
    PROPERTY_TO_VALIDATOR_FIELD: PROPERTY_TO_VALIDATOR_FIELD,
    // A.1-A.4 domain-validation primitive. primaryDomainErrorToken now returns a copy KEY (resolved via config.copy
    // at its emission sites) ; tests assert the returned key. DOMAIN_TOKENS was removed 2026-06-27 (it duplicated the
    // copy values as a second source of truth) : tests read the messages from order-form-v2-copy via parseCopy().
    primaryDomainErrorToken: primaryDomainErrorToken,
    skuRequiresWildcard: skuRequiresWildcard,
    wildcardEntryFor: wildcardEntryFor,
    // Utilities (exported for unit testing and reuse by later-PR modules)
    isValidEmail: isValidEmail,
    backoffDelay: backoffDelay,
    // Real modules
    ApiClient: ApiClient,
    FormValidator: FormValidator,
    DomainNormalizer: DomainNormalizer,
    CsrValidator: CsrValidator,
    // The CSR-rejection reason -> copy-key mapping (owner decision 2026-09-01), exported for direct unit testing
    // on the same terms as caasCheckReasonKey below (message VALUES read from order-form-v2-copy, never literals).
    csrRejectionReasonKey: csrRejectionReasonKey,
    SubscriptionChecker: SubscriptionChecker,
    // The CaaS-check outcome -> message mapping primitives (CaaS Subscription Register go-live, 2026-08-29),
    // exported for direct unit testing of the reason-code -> copy-key mapping + the status-branch selection
    // (the tests.md whitelist's "which message a given failure yields" item ; tests read the message VALUES
    // from order-form-v2-copy via parseCopy, never literals).
    caasCheckReasonKey: caasCheckReasonKey,
    caasCheckFailureText: caasCheckFailureText,
    // The Validation ID check (operator decision 2026-10-18) : the checker and its two mapping primitives, exported
    // on the same terms as the subscription check's above.
    ValidationIdChecker: ValidationIdChecker,
    validationIdCheckReasonKey: validationIdCheckReasonKey,
    validationIdCheckFailureText: validationIdCheckFailureText,
    DcvManager: DcvManager,
    SanBuilder: SanBuilder,
    SkuParser: SkuParser,
    UI: UI,
    // Assembler wiring (3b part 2 ; exported for the integration test)
    wireForm: wireForm,
    // FIX 5 : the CSR-popup URL-scheme guard + its button wiring, exported for direct unit testing.
    isValidPopupUrl: isValidPopupUrl,
    wireCsrPopup: wireCsrPopup,
    // PRE-FILL BY LINK (owner request 2026-10-03) : the link marker + the pure parser, exported for the test suite the same
    // way the assistant exports OPEN_LINK_FRAGMENT + parseOpenLink.
    PREFILL_FRAGMENT: PREFILL_FRAGMENT,
    parsePrefillLink: parsePrefillLink,
    // Browser entry point (called only on the browser branch of the wrapper)
    _bootstrap: bootstrap
  };
});
